// process.env safe client component
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Database,
  Layers,
  Sparkles,
  Bot,
  MessageSquare,
  Smartphone,
  Globe,
  Cpu,
  RefreshCw,
  Search,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  ClipboardCheck,
  Copy,
  Check,
  Send,
  Radio,
  Zap,
  BookOpen,
  FolderOpen,
  FolderPlus,
  Folder,
  Edit2,
  LayoutTemplate,
  Activity,
  Filter,
  Sliders,
  ExternalLink,
  Shield,
  ShieldCheck,
  Clock,
  ChevronRight,
  FileText,
  FileUp,
  UploadCloud,
  Brain,
  Trash2,
  Edit3,
  Eye,
  Tag,
  Maximize2,
  HelpCircle,
  Package,
  Car,
  Calendar,
  CalendarCheck,
  Building2,
  Phone,
  Save,
  Download,
  Upload,
  Plus,
  Archive,
  Camera,
  ChevronDown,
  ChevronUp,
  LayoutGrid,
  Info,
  Compass,
  GripVertical,
  Image as ImageIcon,
  Film,
  RotateCcw,
  Link2,
  UserCheck,
  UserX,
  Type,
  AlertTriangle,
  QrCode,
  Wifi,
  WifiOff,
  Key,
  EyeOff,
  Loader2,
  LogOut,
  PowerOff,
  PhoneCall,
  Users,
  Mail,
  FileSpreadsheet,
  PlusCircle,
  Share2,
  CheckCheck,
  User,
  Play,
  X,
  XCircle,
  ZoomIn,
  Star,
  Lock,
  Bell,
  BellRing,
  ShoppingBag,
  DollarSign,
  CreditCard,
  Wallet,
  PhoneOff,
  ShieldAlert,
  Truck,
  MapPin,
  Navigation,
  Moon,
  Gift,
  History,
  SlidersHorizontal,
  Wand2,
  Paperclip,
  Code,
  PhoneForwarded,
  Server,
} from 'lucide-react';
import { formatTime, formatDate, formatDateTime } from '../../utils/timezoneHelper';
import { MessageVisualContent } from './MessageVisualContent';
import { LunaCacheInspectionModal } from '../luna-builder/modals/LunaCacheInspectionModal';
import { KangavenaWhatsAppWeb, getDisplayCustomerName } from './KangavenaWhatsAppWeb';
import { KangavenaWhatsAppMobileModal } from './KangavenaWhatsAppMobileModal';
import { KangavenaWhatsAppSimulatorModal } from './KangavenaWhatsAppSimulatorModal';

interface PortalDataProps {
  token: string;
  tenant?: any;
  initialNode?: string | null;
  initialNodeId?: string | null;
  onBackToWorkspace: () => void;
  onOpenAccountModal?: (tab?: string) => void;
}

export interface LeadSubmission {
  id: number;
  tenantId?: number;
  sessionId?: string;
  serviceCategory?: string;
  formType: string;
  visitorName?: string;
  visitorEmail?: string;
  visitorPhone?: string;
  formData?: any;
  lunaVoiceTranscript?: string;
  status: string;
  notes?: string;
  inquiryNotes?: string;
  createdAt: string;
  submittedAt?: string;
}

export interface ParsedDialogueTurn {
  sender: 'user' | 'luna';
  speaker: string;
  text: string;
  time?: string;
}

export function parseLeadDialogue(lead: LeadSubmission | null | undefined): { cleanSummary: string; turns: ParsedDialogueTurn[] } {
  if (!lead) return { cleanSummary: '', turns: [] };
  const rawNotes = lead.notes || lead.inquiryNotes || '';
  const transcript = lead.lunaVoiceTranscript || '';
  const recentDialogue = lead.formData?.recentDialogue || lead.formData?.recentTurns;

  const turns: ParsedDialogueTurn[] = [];
  let cleanSummary = '';

  // 1. If structured recentDialogue exists in formData:
  if (Array.isArray(recentDialogue) && recentDialogue.length > 0) {
    recentDialogue.forEach((d: any) => {
      const isUser = d.sender === 'user' || d.role === 'user';
      turns.push({
        sender: isUser ? 'user' : 'luna',
        speaker: isUser ? (lead.visitorName || 'Customer') : 'Luna AI',
        text: d.text || d.message || '',
        time: d.timestamp ? formatTime(d.timestamp) : undefined,
      });
    });
  }

  // 2. If turns are still empty and lunaVoiceTranscript exists:
  if (turns.length === 0 && transcript.trim()) {
    const lines = transcript.split('\n');
    let currentTurn: ParsedDialogueTurn | null = null;

    lines.forEach((line) => {
      const match = line.match(/^(\[[^\]]+\])?\s*(👤|🤖)?\s*(Customer|User|Luna AI|Luna|[^:]+):\s*(.*)$/i);
      if (match) {
        if (currentTurn) turns.push(currentTurn);
        const time = match[1]?.replace(/[\[\]]/g, '').trim();
        const speakerRaw = match[3]?.trim();
        const isUser = /customer|user/i.test(speakerRaw) || match[2] === '👤';
        currentTurn = {
          sender: isUser ? 'user' : 'luna',
          speaker: isUser ? (lead.visitorName || 'Customer') : 'Luna AI',
          text: match[4] || '',
          time: time || undefined,
        };
      } else if (currentTurn) {
        currentTurn.text += '\n' + line;
      }
    });
    if (currentTurn) turns.push(currentTurn);
  }

  // 3. If turns are still empty, parse legacy notes format:
  if (turns.length === 0 && rawNotes) {
    if (rawNotes.includes('WhatsApp Conversation:')) {
      const userMatch = rawNotes.match(/User:\s*([^\n]+(?:\n(?!Luna:)[^\n]+)*)/i);
      const lunaMatch = rawNotes.match(/Luna:\s*([^\n]+(?:\n(?!Recent Dialogue:)[^\n]+)*)/i);
      if (userMatch) {
        turns.push({
          sender: 'user',
          speaker: lead.visitorName || 'Customer',
          text: userMatch[1]?.trim() || '',
        });
      }
      if (lunaMatch) {
        turns.push({
          sender: 'luna',
          speaker: 'Luna AI',
          text: lunaMatch[1]?.trim() || '',
        });
      }
    }
  }

  // 4. Extract clean summary
  if (rawNotes) {
    if (rawNotes.startsWith('Inquiry:')) {
      const inqMatch = rawNotes.match(/^Inquiry:\s*([^\n]+)/i);
      cleanSummary = inqMatch ? inqMatch[1].trim() : rawNotes.split('\n')[0];
    } else if (rawNotes.startsWith('WhatsApp Conversation:')) {
      const userMatch = rawNotes.match(/User:\s*([^\n]+)/i);
      cleanSummary = userMatch ? `Inquiry: ${userMatch[1].trim()}` : 'WhatsApp Lead Inquiry';
    } else {
      const beforeRecent = rawNotes.split('Recent Dialogue:')[0].trim();
      cleanSummary = beforeRecent.split('\n')[0] || rawNotes;
    }
  }

  if (!cleanSummary && lead.formData?.inquirySummary) {
    cleanSummary = lead.formData.inquirySummary;
  }

  return { cleanSummary: cleanSummary || 'Customer inquiry captured by Luna AI', turns };
}

export const renderFormattedWhatsAppText = (text: string) => {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <div className="space-y-1 text-[12px] leading-relaxed select-text font-sans">
      {lines.map((line, lIdx) => {
        if (!line.trim()) return <div key={lIdx} className="h-1.5" />;
        const parts = line.split(/(\*\*?[^\*]+\*\*?)/g);
        return (
          <p key={lIdx} className="break-words">
            {parts.map((part, pIdx) => {
              if (/^\*\*?[^\*]+\*\*?$/.test(part)) {
                const clean = part.replace(/^\*\*?|\*\*?$/g, '');
                return <strong key={pIdx} className="font-bold text-white">{clean}</strong>;
              }
              return part;
            })}
          </p>
        );
      })}
    </div>
  );
};

export const detectMediaType = (url?: string | null, defaultType: 'image' | 'pdf' | 'video' = 'image'): 'image' | 'pdf' | 'video' => {
  if (!url) return defaultType;
  const clean = url.toLowerCase().split('?')[0];
  if (clean.endsWith('.pdf')) return 'pdf';
  if (/\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(clean)) return 'video';
  if (/\.(jpg|jpeg|png|webp|gif|svg|bmp)$/i.test(clean)) return 'image';
  return defaultType;
};

interface ConversationMessage {
  sender: 'visitor' | 'luna' | 'human_admin' | 'system' | string;
  role?: 'user' | 'luna' | 'model' | 'system' | string;
  text: string;
  time?: string;
  timestamp?: string;
  phone?: string;
  nodeUsed?: string;
  isVoiceTurn?: boolean;
  tokens?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  toolsUsed?: string[];
  executionPath?: string[];
}

interface ConversationSession {
  sessionId: string;
  visitorName: string;
  channel: 'whatsapp' | 'web_widget' | 'voice_session' | 'sip_voice' | string;
  status: string;
  callDuration?: number;
  callStatus?: string;
  isVoiceCall?: boolean;
  isHumanTakeover?: boolean;
  takeoverAt?: string | null;
  takeoverExpiresAt?: string | null;
  takeoverMode?: 'timed_30m' | 'permanent';
  humanRequested?: boolean;
  phone: string | null;
  email: string | null;
  startedAt: string;
  lastActiveAt?: string;
  totalTokens: number;
  promptTokens?: number;
  completionTokens?: number;
  usedNodes: string[];
  messages: ConversationMessage[];
}

interface KnowledgeData {
  nodeId?: string;
  contextSource?: string;
  knowledgeType?: string;
  lastTrainedAt?: string;
  lastTrainedFileName?: string | null;
  lastTrainedFileSize?: number | null;
  autoKeywords?: string;
  isCustomizable?: boolean;
}

export interface MediaVaultAsset {
  id: string;
  mediaType: 'image' | 'pdf' | 'video';
  title: string;
  tags: string;
  secureUrl: string;
  galleryUrls?: string[];
  vaultName?: string;
  description?: string;
}

interface MediaVaultData {
  nodeId?: string;
  assets?: MediaVaultAsset[];
  isCustomizable?: boolean;
  lastUpdatedAt?: string;
}

export interface ActionData {
  nodeId?: string;
  actionType?: string;
  targetDestination?: string;
  autoExecute?: boolean;
  isCustomizable?: boolean;
  lastUpdatedAt?: string;
}


export type KnowledgeCategory = 'all' | 'identity' | 'products' | 'operations' | 'faq' | 'contact' | 'general';

export interface ParsedSection {
  id: string;
  rawHeader: string;
  title: string;
  category: KnowledgeCategory;
  content: string;
  charCount: number;
  wordCount: number;
}

export interface ExtractedFAQ {
  id: string;
  question: string;
  answer: string;
  sectionTitle: string;
}

const KNOWLEDGE_CATEGORIES: { id: KnowledgeCategory; label: string; icon: React.ElementType; color: string; bg: string; border: string }[] = [
  { id: 'all', label: 'All Sections', icon: Layers, color: 'text-slate-200', bg: 'bg-slate-800/60', border: 'border-slate-700' },
  { id: 'identity', label: 'Who We Are', icon: Building2, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/30' },
  { id: 'products', label: 'Products & Pricing', icon: Package, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30' },
  { id: 'operations', label: 'Policies & Operations', icon: Shield, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/30' },
  { id: 'faq', label: 'FAQs Matrix', icon: HelpCircle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  { id: 'contact', label: 'Contact & Support', icon: Phone, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/30' },
  { id: 'general', label: 'Technical Notes', icon: FileText, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/30' },
];

function inferCategory(title: string, content: string): KnowledgeCategory {
  const combined = `${title} ${content}`.toLowerCase();
  if (/(who we are|about us|company|mission|vision|story|background|purpose|overview|philosophy|leadership|team|values|heritage)/i.test(title)) {
    return 'identity';
  }
  if (/(faq|frequently asked|questions|q&a|inquir|common question)/i.test(title)) {
    return 'faq';
  }
  if (/(product|service|pricing|price|catalog|plan|tier|sku|package|cost|fee|feature|spec|menu|rate|subscription)/i.test(title)) {
    return 'products';
  }
  if (/(contact|phone|email|location|branch|address|support|escalat|hotline|hours|reach us|map|direction)/i.test(title)) {
    return 'contact';
  }
  if (/(policy|policies|operation|guideline|terms|warranty|return|refund|shipping|delivery|condition|procedure|cancellation|rule)/i.test(title)) {
    return 'operations';
  }
  if (/(who we are|about our company|our mission|established in)/i.test(combined)) return 'identity';
  if (/(price:|lkr|\$|usd|per month|package include|available in)/i.test(combined)) return 'products';
  if (/(q:\s|question:\s|\*\*q:\*\*)/i.test(combined)) return 'faq';
  if (/(tel:|phone:|email:|hotline:|visit us at)/i.test(combined)) return 'contact';
  if (/(warranty|return policy|terms and condition|operating hour)/i.test(combined)) return 'operations';
  return 'general';
}

function parseMarkdownSections(markdown: string): ParsedSection[] {
  if (!markdown || !markdown.trim()) return [];
  const headerRegex = /(?:^|\n)(#{1,4}\s+[^\n]+|---+\s*\[[^\]]+\]\s*---+)/g;
  const sections: ParsedSection[] = [];
  const matches: { index: number; fullHeader: string; title: string }[] = [];
  let match: RegExpExecArray | null;

  while ((match = headerRegex.exec(markdown)) !== null) {
    const rawHeader = match[1].trim();
    const cleanTitle = rawHeader
      .replace(/^#{1,4}\s*/, '')
      .replace(/^---+\s*\[?/, '')
      .replace(/\]?\s*---+$/, '')
      .replace(/^\d+[\.\)]\s*/, '')
      .trim();

    matches.push({
      index: match.index,
      fullHeader: rawHeader,
      title: cleanTitle || 'Section'
    });
  }

  if (matches.length === 0) {
    const trimmed = markdown.trim();
    return [{
      id: 'section-0',
      rawHeader: '### Synthesized Knowledge Base',
      title: 'Synthesized Knowledge Base',
      category: inferCategory('Synthesized Knowledge Base', trimmed),
      content: trimmed,
      charCount: trimmed.length,
      wordCount: trimmed.split(/\s+/).filter(Boolean).length
    }];
  }

  for (let i = 0; i < matches.length; i++) {
    const current = matches[i];
    const next = matches[i + 1];
    const startIndex = current.index + current.fullHeader.length;
    const endIndex = next ? next.index : markdown.length;
    const content = markdown.slice(startIndex, endIndex).trim();

    sections.push({
      id: `section-${i}`,
      rawHeader: current.fullHeader,
      title: current.title,
      category: inferCategory(current.title, content),
      content,
      charCount: content.length,
      wordCount: content.split(/\s+/).filter(Boolean).length
    });
  }

  return sections;
}

function extractAllFAQs(sections: ParsedSection[]): ExtractedFAQ[] {
  const faqs: ExtractedFAQ[] = [];
  let count = 0;

  sections.forEach((sec) => {
    const lines = sec.content.split('\n');
    let currentQ = '';
    let currentA = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const isQuestionLine =
        /^(\-|\*|\d+[\.\)])?\s*(\*\*|__)?(Q|Question)\s*(\:|\.|\-)/i.test(line) ||
        (/^(\-|\*|\d+[\.\)])?\s*(\*\*|__)[^\*\n\?]{4,150}\?(\*\*|__)/.test(line)) ||
        (line.endsWith('?') && line.length < 150 && !line.startsWith('http'));

      if (isQuestionLine) {
        if (currentQ && currentA) {
          faqs.push({
            id: `faq-${count++}`,
            question: currentQ,
            answer: currentA.trim(),
            sectionTitle: sec.title
          });
          currentA = '';
        }
        currentQ = line
          .replace(/^(\-|\*|\d+[\.\)])?\s*/, '')
          .replace(/^(\*\*|__)?(Q|Question)\s*(\:|\.|\-)?\s*/i, '')
          .replace(/^(\*\*|__)/, '')
          .replace(/(\*\*|__)$/, '')
          .trim();
      } else if (currentQ) {
        const cleanAnswerLine = line
          .replace(/^(\-|\*|\d+[\.\)])?\s*/, '')
          .replace(/^(\*\*|__)?(A|Answer)\s*(\:|\.|\-)?\s*/i, '')
          .replace(/^(\*\*|__)/, '')
          .replace(/(\*\*|__)$/, '')
          .trim();

        if (cleanAnswerLine) {
          currentA += (currentA ? '\n' : '') + cleanAnswerLine;
        }
      }
    }

    if (currentQ && currentA) {
      faqs.push({
        id: `faq-${count++}`,
        question: currentQ,
        answer: currentA.trim(),
        sectionTitle: sec.title
      });
    }
  });

  return faqs;
}

export const TenetPortalData: React.FC<PortalDataProps> = ({
  token,
  tenant,
  initialNode,
  initialNodeId,
  onBackToWorkspace,
  onOpenAccountModal,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [activeFlowNodes, setActiveFlowNodes] = useState<string[]>([]);
  const [flowName, setFlowName] = useState<string>('Autonomous Luna Agent Flow');
  const [billingCycle, setBillingCycle] = useState<any>(null);
  const [tokenScope, setTokenScope] = useState<'cycle' | 'all_time'>('cycle');
  
  // Selected Module: null = Grid Overview Hub, 'luna_core' | 'action_leads' | 'customer_memory' | 'knowledge' | 'media_vault' | 'whatsapp_instance' | 'whatsapp_web' | 'sip_call' | 'intent_alerts' | 'product_catalog' | 'payment_gateway' | 'call_guard' | 'followup_recovery' | 'review_booster'
  const [selectedModule, setSelectedModule] = useState<'luna_core' | 'neural_cache' | 'action_leads' | 'customer_memory' | 'knowledge' | 'media_vault' | 'whatsapp_instance' | 'whatsapp_web' | 'sip_call' | 'intent_alerts' | 'product_catalog' | 'payment_gateway' | 'call_guard' | 'followup_recovery' | 'review_booster' | null>(null);

  // Drag & Drop Module Ordering State
  const defaultModuleOrder = ['luna_core', 'neural_cache', 'action_leads', 'customer_memory', 'knowledge', 'media_vault', 'whatsapp_instance', 'sip_call', 'intent_alerts', 'product_catalog', 'payment_gateway', 'call_guard', 'followup_recovery', 'review_booster'];
  const [moduleOrder, setModuleOrder] = useState<string[]>(defaultModuleOrder);
  const [draggedModule, setDraggedModule] = useState<string | null>(null);
  const [dragOverModule, setDragOverModule] = useState<string | null>(null);
  const [showWhatsAppMobileModal, setShowWhatsAppMobileModal] = useState<boolean>(false);
  const [showWhatsAppSimulatorModal, setShowWhatsAppSimulatorModal] = useState<boolean>(false);

  // Load customized module order from localStorage and ensure all default modules exist
  useEffect(() => {
    try {
      const saved = localStorage.getItem('tenet_portal_module_order');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const missing = defaultModuleOrder.filter((m) => !parsed.includes(m));
          setModuleOrder([...parsed, ...missing]);
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

  const effectiveModuleOrder = useMemo(() => {
    const combined = [...moduleOrder];
    defaultModuleOrder.forEach((m) => {
      if (!combined.includes(m)) combined.push(m);
    });
    return combined;
  }, [moduleOrder]);

  const saveModuleOrder = (newOrder: string[]) => {
    setModuleOrder(newOrder);
    try {
      localStorage.setItem('tenet_portal_module_order', JSON.stringify(newOrder));
    } catch (e) {
      // ignore
    }
  };

  const resetModuleOrder = () => {
    setModuleOrder(defaultModuleOrder);
    try {
      localStorage.removeItem('tenet_portal_module_order');
    } catch (e) {
      // ignore
    }
    showToast('Module layout reset to default', 'success');
  };

  // Luna Core Hub State
  const [stats, setStats] = useState<any>({
    totalConversations: 0,
    totalMessages: 0,
    totalTokensUsed: 0,
    totalInputTokens: 0,
    totalOutputTokens: 0,
    totalCalls: 0,
    completedCalls: 0,
    missedCalls: 0,
    totalCallDuration: 0,
    totalLeads: 0,
    whatsappLeads: 0,
    webLeads: 0,
    newLeads: 0,
    whatsappPhone: null,
    whatsappConnected: false,
    activeChannels: ['web_widget'],
    nodeBreakdown: {},
    lunaTokenUsage: { total: 0, input: 0, output: 0, label: 'Luna Token Usage' },
    lunaCallTokenUsage: { total: 0, input: 0, output: 0, durationSeconds: 0, label: 'Luna Call Token Usage' },
    lunaTrainingKnowledgeUsage: { total: 0, input: 0, output: 0, recordsCount: 0, label: 'Luna Training Knowledge Usage' },
  });
  const [conversations, setConversations] = useState<ConversationSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const chatMessagesEndRef = useRef<HTMLDivElement>(null);
  const chatScrollContainerRef = useRef<HTMLDivElement>(null);

  // Telemetry View Mode: 'financial' (Default 1-row: 4 AI Cost cards) | 'activity' (1-row: 3 Traffic cards) | 'all' (2-Tier Compact) | 'collapsed' (Minimal 28px strip)
  const [telemetryViewMode, setTelemetryViewMode] = useState<'financial' | 'activity' | 'all' | 'collapsed'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('kangavena_telemetry_view');
      if (saved === 'financial' || saved === 'activity' || saved === 'all' || saved === 'collapsed') {
        return saved;
      }
    }
    return 'financial';
  });

  const handleSetTelemetryViewMode = (mode: 'all' | 'financial' | 'activity' | 'collapsed') => {
    setTelemetryViewMode(mode);
    if (typeof window !== 'undefined') {
      localStorage.setItem('kangavena_telemetry_view', mode);
    }
  };

  // Luna Neural Context Cache & Token Engine State
  const [isCacheModalOpen, setIsCacheModalOpen] = useState(false);
  const [cacheStudioData, setCacheStudioData] = useState<any>(null);
  const [isLoadingCacheStudio, setIsLoadingCacheStudio] = useState(false);
  const [isPurgingCacheStudio, setIsPurgingCacheStudio] = useState(false);
  const [cacheStudioActiveTab, setCacheStudioActiveTab] = useState<'overview' | 'nodes' | 'prompt' | 'tools'>('overview');
  const [cacheStudioSearchPrompt, setCacheStudioSearchPrompt] = useState('');
  const [copiedCacheStudioPrompt, setCopiedCacheStudioPrompt] = useState(false);
  const [cacheStudioPurgeSuccess, setCacheStudioPurgeSuccess] = useState<string | null>(null);

  // Action & Leads Hub State
  const [actionData, setActionData] = useState<ActionData | null>(null);
  const [leadsList, setLeadsList] = useState<LeadSubmission[]>([]);
  const [leadsFilter, setLeadsFilter] = useState<'all' | 'new' | 'contacted' | 'converted' | 'whatsapp' | 'web'>('all');
  const [leadsSearchQuery, setLeadsSearchQuery] = useState<string>('');
  const [selectedLeadId, setSelectedLeadId] = useState<number | null>(null);
  const [updatingLeadId, setUpdatingLeadId] = useState<number | null>(null);
  const [onlyShowLeadsInChats, setOnlyShowLeadsInChats] = useState<boolean>(false);
  const [isSavingActionNode, setIsSavingActionNode] = useState<boolean>(false);

  const selectedLead = useMemo(() => {
    if (selectedLeadId !== null) {
      return leadsList.find((l) => l.id === selectedLeadId) || null;
    }
    return leadsList[0] || null;
  }, [leadsList, selectedLeadId]);

  // Knowledge & Context State
  const [knowledgeData, setKnowledgeData] = useState<KnowledgeData | null>(null);
  const [knowledgeText, setKnowledgeText] = useState<string>('');
  const [knowledgeViewMode, setKnowledgeViewMode] = useState<'explorer' | 'editor' | 'faq' | 'train'>('explorer');
  const [knowledgeTrainingTab, setKnowledgeTrainingTab] = useState<'upload' | 'text' | 'scrape'>('upload');
  const [rawTextInput, setRawTextInput] = useState<string>('');
  const [rawTextTitle, setRawTextTitle] = useState<string>('');
  const [targetWebsiteUrl, setTargetWebsiteUrl] = useState<string>('');
  const [urlCrawlDepth, setUrlCrawlDepth] = useState<'single' | 'deep'>('single');
  const [selectedKnowledgeCategory, setSelectedKnowledgeCategory] = useState<KnowledgeCategory>('all');
  const [knowledgeSearchQuery, setKnowledgeSearchQuery] = useState<string>('');
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [sectionEditText, setSectionEditText] = useState<string>('');
  const [isSavingKnowledge, setIsSavingKnowledge] = useState<boolean>(false);

  // Media Vault State
  const [mediaVaultData, setMediaVaultData] = useState<MediaVaultData | null>(null);
  const [mediaAssets, setMediaAssets] = useState<MediaVaultAsset[]>([]);
  const [mediaFilterType, setMediaFilterType] = useState<'all' | 'image' | 'pdf' | 'video'>('all');
  const [selectedVaultCategory, setSelectedVaultCategory] = useState<string>('all');
  const [mediaSearchQuery, setMediaSearchQuery] = useState<string>('');
  const [uploadingAssetId, setUploadingAssetId] = useState<string | null>(null);
  const [isBatchUploadingMedia, setIsBatchUploadingMedia] = useState<boolean>(false);
  const [isSavingMedia, setIsSavingMedia] = useState<boolean>(false);
  const [previewMedia, setPreviewMedia] = useState<{ url: string; title: string; type: 'image' | 'pdf' | 'video' } | null>(null);
  const mediaFileInputRef = useRef<HTMLInputElement>(null);

  // Customer Memory Vault State
  const [customerMemoryData, setCustomerMemoryData] = useState<any>(null);
  const [memoriesList, setMemoriesList] = useState<any[]>([]);
  const [totalMemoriesCount, setTotalMemoriesCount] = useState<number>(0);
  const [memoriesLoading, setMemoriesLoading] = useState<boolean>(false);
  const [memoriesSearchQuery, setMemoriesSearchQuery] = useState<string>('');
  const [selectedMemoryId, setSelectedMemoryId] = useState<number | null>(null);
  const [editingMemory, setEditingMemory] = useState<any | null>(null);
  const [isSavingMemory, setIsSavingMemory] = useState<boolean>(false);
  const [isDeletingMemory, setIsDeletingMemory] = useState<boolean>(false);
  const [showAddMemoryModal, setShowAddMemoryModal] = useState<boolean>(false);
  const [newMemoryPhone, setNewMemoryPhone] = useState<string>('');
  const [newMemoryName, setNewMemoryName] = useState<string>('');
  const [newMemoryDistilled, setNewMemoryDistilled] = useState<string>('');
  const [newMemoryPrefBudget, setNewMemoryPrefBudget] = useState<string>('');
  const [newMemoryPrefProduct, setNewMemoryPrefProduct] = useState<string>('');
  const [newMemoryPrefLocation, setNewMemoryPrefLocation] = useState<string>('');
  const [newMemoryIsVip, setNewMemoryIsVip] = useState<boolean>(false);
  const [isCreatingMemory, setIsCreatingMemory] = useState<boolean>(false);

  // WhatsApp Instance State
  const [whatsAppData, setWhatsAppData] = useState<any>(null);
  const [whatsAppInstances, setWhatsAppInstances] = useState<any[]>([]);
  const [selectedWhatsAppNodeId, setSelectedWhatsAppNodeId] = useState<string | null>(null);
  const [whatsAppQrCode, setWhatsAppQrCode] = useState<string | null>(null);
  const [isConnectingWhatsApp, setIsConnectingWhatsApp] = useState<boolean>(false);
  const [isDisconnectingWhatsApp, setIsDisconnectingWhatsApp] = useState<boolean>(false);
  const [isSavingWhatsAppSettings, setIsSavingWhatsAppSettings] = useState<boolean>(false);
  const [isSavingInstanceTitle, setIsSavingInstanceTitle] = useState<boolean>(false);
  const [titleSavedSuccess, setTitleSavedSuccess] = useState<boolean>(false);
  const [whatsAppActiveTab, setWhatsAppActiveTab] = useState<'gateway' | 'chats'>('gateway');
  const [whatsAppSearchQuery, setWhatsAppSearchQuery] = useState<string>('');
  const [metaTokenVisible, setMetaTokenVisible] = useState<boolean>(false);
  const [copiedMetaWebhookUrl, setCopiedMetaWebhookUrl] = useState<boolean>(false);
  const [copiedUniversalWebhookUrl, setCopiedUniversalWebhookUrl] = useState<boolean>(false);
  const [copiedMetaVerifyToken, setCopiedMetaVerifyToken] = useState<boolean>(false);
  const [copiedAppId, setCopiedAppId] = useState<boolean>(false);
  const [isLaunchingPortalMetaSignup, setIsLaunchingPortalMetaSignup] = useState<boolean>(false);
  const [isDiscoveringPortalAccounts, setIsDiscoveringPortalAccounts] = useState<boolean>(false);
  const [discoveredPortalAccounts, setDiscoveredPortalAccounts] = useState<any[]>([]);
  const [showPortalDiscoveryModal, setShowPortalDiscoveryModal] = useState<boolean>(false);
  const [showPortalManualDeveloperSettings, setShowPortalManualDeveloperSettings] = useState<boolean>(false);
  const [copiedSipHost, setCopiedSipHost] = useState<boolean>(false);
  const [copiedSipPort, setCopiedSipPort] = useState<boolean>(false);
  const [copiedSipParamName, setCopiedSipParamName] = useState<boolean>(false);
  const [copiedSipParamValue, setCopiedSipParamValue] = useState<boolean>(false);
  const [copiedMobileLink, setCopiedMobileLink] = useState<boolean>(false);

  // 🛡️ Ignored Contacts State (Personal WhatsApp Mode)
  const [ignoredContacts, setIgnoredContacts] = useState<any[]>([]);
  const [isLoadingIgnoredContacts, setIsLoadingIgnoredContacts] = useState<boolean>(false);
  const [ignoredSearchQuery, setIgnoredSearchQuery] = useState<string>('');
  const [showAddIgnoredModal, setShowAddIgnoredModal] = useState<boolean>(false);
  const [newIgnoredPhone, setNewIgnoredPhone] = useState<string>('');
  const [newIgnoredName, setNewIgnoredName] = useState<string>('');
  const [newIgnoredReason, setNewIgnoredReason] = useState<string>('manual');
  const [isSubmittingIgnoredContact, setIsSubmittingIgnoredContact] = useState<boolean>(false);

  const KANGAVENA_OFFICIAL_META_APP_ID = '1556883522786680';

  const normalizeSriLankaPhone = useCallback((phone?: string): string => {
    if (!phone) return '';
    const trimmed = phone.trim();
    const digits = trimmed.replace(/\D/g, '');
    if (digits.startsWith('0') && digits.length === 10) {
      return '+94' + digits.slice(1);
    }
    if (digits.startsWith('94') && digits.length === 11) {
      return '+' + digits;
    }
    if (trimmed.startsWith('+')) return trimmed;
    if (digits.length >= 9) return '+' + digits;
    return trimmed;
  }, []);

  const cleanInitialPhone = useCallback((phone?: string): string => {
    if (!phone) return '';
    const clean = phone.trim();
    if (
      clean === '+1987654321' || clean === '1987654321' ||
      clean === '+1234567890' || clean === '1234567890' ||
      clean === '+94771234567' || clean === '94771234567' ||
      clean === '+94770000000' || clean === '94770000000'
    ) return '';
    return clean;
  }, []);

  const rawTenantCandidatePhone = 
    tenant?.whatsappPhone ||
    tenant?.customFields?.metaPhoneNumber ||
    tenant?.registeredPhone ||
    tenant?.customFields?.registeredPhone ||
    tenant?.customFields?.managerWhatsapp ||
    tenant?.customFields?.ownerPhone ||
    tenant?.phone ||
    '';
  const autoDetectedRegisteredPhone = normalizeSriLankaPhone(cleanInitialPhone(rawTenantCandidatePhone));

  // Training / Ingestion State
  const [isTraining, setIsTraining] = useState<boolean>(false);
  const [trainingPhase, setTrainingPhase] = useState<'uploading' | 'synthesizing' | null>(null);
  const [trainingError, setTrainingError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [ingestMode, setIngestMode] = useState<'merge' | 'replace'>('merge');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filters & Global
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [channelFilter, setChannelFilter] = useState<'all' | 'whatsapp' | 'web_widget'>('all');
  const [nodeFilter, setNodeFilter] = useState<string>('all');
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);

  // Quick Reply & Toast
  const [replyText, setReplyText] = useState<string>('');
  const [sendingReply, setSendingReply] = useState<boolean>(false);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Smart Intent Alerts Hub State
  const DEFAULT_SAMPLE_PORTAL_RULES = useMemo(() => [
    {
      id: 'rule_red_roses',
      intentTitle: '🌹 Red Rose & Romance Inquiries',
      matchConditions: 'asking for red roses, romantic flower bouquet, Valentine roses, price of red roses',
      notificationChannel: 'whatsapp',
      alertMessageTemplate: '🌹 High Intent: Customer is interested in Red Roses bouquet!',
      urgency: 'high',
      isEnabled: true,
    },
    {
      id: 'rule_vip_bulk',
      intentTitle: '💎 VIP Bulk / Wedding Order (> $500)',
      matchConditions: 'wedding decorations, bulk order, high budget, event floral arrangements, large order, corporate event, packages',
      notificationChannel: 'whatsapp',
      alertMessageTemplate: '💎 VIP Order Alert: Customer inquiring about bulk/wedding flowers!',
      urgency: 'high',
      isEnabled: true,
    },
  ], []);

  const [intentAlertData, setIntentAlertData] = useState<any>(null);
  const [intentRules, setIntentRules] = useState<any[]>([]);
  const [isSavingIntentRules, setIsSavingIntentRules] = useState<boolean>(false);
  const [isTestingIntent, setIsTestingIntent] = useState<boolean>(false);
  const [testIntentResult, setTestIntentResult] = useState<any>(null);
  const [newPortalIntentTitle, setNewPortalIntentTitle] = useState<string>('');
  const [newPortalIntentKeywords, setNewPortalIntentKeywords] = useState<string>('');
  const [newPortalIntentChannel, setNewPortalIntentChannel] = useState<'whatsapp' | 'sms' | 'email' | 'webhook'>('whatsapp');
  const [newPortalIntentUrgency, setNewPortalIntentUrgency] = useState<'high' | 'medium' | 'low'>('high');
  const [newPortalIntentPhone, setNewPortalIntentPhone] = useState<string>('');
  const [isAddingPortalRule, setIsAddingPortalRule] = useState<boolean>(false);

  // Product Catalog & Menu Hub State
  const DEFAULT_SAMPLE_PORTAL_PRODUCTS = useMemo(() => [
    {
      id: 'prod_red_rose',
      name: '🌹 Red Rose Romance Bouquet',
      category: 'Bouquets',
      price: 4500,
      currency: 'LKR',
      unitType: 'Pieces',
      isAvailable: true,
      description: 'Fresh 24 imported red roses with premium velvet wrapping & greeting card.',
      sku: 'FLW-ROSE-01',
    },
    {
      id: 'prod_white_lily',
      name: '🌸 White Lily & Jasmine Bunch',
      category: 'Bouquets',
      price: 3800,
      currency: 'LKR',
      unitType: 'Pieces',
      isAvailable: true,
      description: 'Fragrant white lilies and royal jasmine in artisan rustic paper wrapping.',
      sku: 'FLW-LILY-02',
    },
    {
      id: 'prod_wedding_pkg',
      name: '💎 VIP Wedding Stage Floral Package',
      category: 'Weddings',
      price: 75000,
      currency: 'LKR',
      unitType: 'Set',
      isAvailable: true,
      description: 'Complete luxury wedding stage, poruwa, and table centerpiece flower decorations.',
      sku: 'FLW-WED-VIP',
    },
  ], []);

  const [productCatalogData, setProductCatalogData] = useState<any>(null);
  const [catalogProducts, setCatalogProducts] = useState<any[]>([]);
  const [isSavingCatalogProducts, setIsSavingCatalogProducts] = useState<boolean>(false);
  const [catalogSearchQuery, setCatalogSearchQuery] = useState<string>('');
  const [selectedCatalogCategory, setSelectedCatalogCategory] = useState<string>('all');
  const [isAddingCatalogProduct, setIsAddingCatalogProduct] = useState<boolean>(false);
  const [editingCatalogProduct, setEditingCatalogProduct] = useState<any | null>(null);
  const [showPortalCategoryManager, setShowPortalCategoryManager] = useState<boolean>(false);
  const [newPortalCategoryInput, setNewPortalCategoryInput] = useState<string>('');
  const [newPortalCategoryRuleInput, setNewPortalCategoryRuleInput] = useState<string>('');
  const [renamingPortalCategory, setRenamingPortalCategory] = useState<{ oldName: string; newName: string } | null>(null);
  const [editingCategoryRule, setEditingCategoryRule] = useState<{ catName: string; rule: string } | null>(null);
  const [portalCatalogViewMode, setPortalCatalogViewMode] = useState<'flat' | 'grouped'>('flat');
  const [portalCategoryTab, setPortalCategoryTab] = useState<'active' | 'recycle'>('active');

  // New Catalog Product Form State
  const [newCatalogProdName, setNewCatalogProdName] = useState<string>('');
  const [newCatalogProdCategory, setNewCatalogProdCategory] = useState<string>('General');
  const [newCatalogProdCategories, setNewCatalogProdCategories] = useState<string[]>(['General']);
  const [newCategoryTagInput, setNewCategoryTagInput] = useState<string>('');
  const [newCatalogProdPrice, setNewCatalogProdPrice] = useState<string>('');
  const [newCatalogProdOriginalPrice, setNewCatalogProdOriginalPrice] = useState<string>('');
  const [newCatalogProdStockQty, setNewCatalogProdStockQty] = useState<string>('50');
  const [newCatalogProdMinPrice, setNewCatalogProdMinPrice] = useState<string>('');
  const [newCatalogProdMaxPrice, setNewCatalogProdMaxPrice] = useState<string>('');
  const [newCatalogProdUnit, setNewCatalogProdUnit] = useState<string>('Pieces');
  const [newCatalogProdDesc, setNewCatalogProdDesc] = useState<string>('');
  const [newCatalogProdMediaUrl, setNewCatalogProdMediaUrl] = useState<string>('');
  const [newCatalogProdKeywords, setNewCatalogProdKeywords] = useState<string>('');
  const [newCatalogProdAvailable, setNewCatalogProdAvailable] = useState<boolean>(true);
  const [selectedProductMediaPreview, setSelectedProductMediaPreview] = useState<any | null>(null);

  // Edit Catalog Product Form State
  const [editCatalogProdName, setEditCatalogProdName] = useState<string>('');
  const [editCatalogProdCategory, setEditCatalogProdCategory] = useState<string>('General');
  const [editCatalogProdCategories, setEditCatalogProdCategories] = useState<string[]>(['General']);
  const [editCategoryTagInput, setEditCategoryTagInput] = useState<string>('');
  const [quickCategoryProductId, setQuickCategoryProductId] = useState<string | null>(null);
  const [quickNewCategoryInput, setQuickNewCategoryInput] = useState<string>('');
  const [editCatalogProdPrice, setEditCatalogProdPrice] = useState<string>('');
  const [editCatalogProdOriginalPrice, setEditCatalogProdOriginalPrice] = useState<string>('');
  const [editCatalogProdStockQty, setEditCatalogProdStockQty] = useState<string>('50');
  const [editCatalogProdMinPrice, setEditCatalogProdMinPrice] = useState<string>('');
  const [editCatalogProdMaxPrice, setEditCatalogProdMaxPrice] = useState<string>('');
  const [editCatalogProdUnit, setEditCatalogProdUnit] = useState<string>('Pieces');
  const [editCatalogProdDesc, setEditCatalogProdDesc] = useState<string>('');
  const [editCatalogProdMediaUrl, setEditCatalogProdMediaUrl] = useState<string>('');
  const [editCatalogProdKeywords, setEditCatalogProdKeywords] = useState<string>('');
  const [editCatalogProdAvailable, setEditCatalogProdAvailable] = useState<boolean>(true);
  const [newCatalogProdRawText, setNewCatalogProdRawText] = useState<string>('');
  const [editCatalogProdRawText, setEditCatalogProdRawText] = useState<string>('');

  // Multi-Media & Luna AI State for Catalog Products
  const [newCatalogProdMedia, setNewCatalogProdMedia] = useState<any[]>([]);
  const [editCatalogProdMedia, setEditCatalogProdMedia] = useState<any[]>([]);
  const [isAiEnrichingNewCatalog, setIsAiEnrichingNewCatalog] = useState<boolean>(false);
  const [isAiEnrichingEditCatalog, setIsAiEnrichingEditCatalog] = useState<boolean>(false);

  const [isUploadingCatalogMedia, setIsUploadingCatalogMedia] = useState<boolean>(false);
  const [isEditUploadingCatalogMedia, setIsEditUploadingCatalogMedia] = useState<boolean>(false);
  const newCatalogFileInputRef = useRef<HTMLInputElement>(null);
  const editCatalogFileInputRef = useRef<HTMLInputElement>(null);

  const handleCatalogFileUpload = async (file: File, isEdit: boolean = false) => {
    if (!file) return;
    if (isEdit) setIsEditUploadingCatalogMedia(true);
    else setIsUploadingCatalogMedia(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/admin/luna-flows/upload-asset', {
        method: 'POST',
        headers: getAuthUploadHeaders(),
        body: formData,
      });

      const data = await res.json();
      if (data.success && data.url) {
        const fullUrl = data.url.startsWith('http')
          ? data.url
          : `${window.location.origin}${data.url}`;

        let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
        if (file.type?.startsWith('video/')) mType = 'video';
        else if (file.type === 'application/pdf') mType = 'pdf';
        else if (file.type?.includes('document') || file.type?.includes('sheet') || file.type?.includes('text')) mType = 'document';

        const newItem = {
          id: `med_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          type: mType,
          url: fullUrl,
          name: file.name || 'Attached Media',
          size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
        };

        if (isEdit) {
          setEditCatalogProdMedia(prev => [...prev, newItem]);
          if (!editCatalogProdMediaUrl && mType === 'image') setEditCatalogProdMediaUrl(fullUrl);
        } else {
          setNewCatalogProdMedia(prev => [...prev, newItem]);
          if (!newCatalogProdMediaUrl && mType === 'image') setNewCatalogProdMediaUrl(fullUrl);
        }
        showToast('Media asset uploaded!', 'success');
      } else {
        showToast(data.error || 'Failed to upload media asset.', 'error');
      }
    } catch (err: any) {
      console.error('[TenetPortalData] Product upload error:', err);
      showToast('Upload failed: ' + (err?.message || 'Network error'), 'error');
    } finally {
      if (isEdit) setIsEditUploadingCatalogMedia(false);
      else setIsUploadingCatalogMedia(false);
    }
  };

  const handleCatalogMultiFileUpload = async (files: FileList | File[], isEdit: boolean = false) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;
    if (isEdit) setIsEditUploadingCatalogMedia(true);
    else setIsUploadingCatalogMedia(true);

    try {
      const formData = new FormData();
      for (const f of fileArray) {
        formData.append('files', f);
      }

      const res = await fetch('/api/admin/luna-flows/upload-assets-batch', {
        method: 'POST',
        headers: getAuthUploadHeaders(),
        body: formData,
      });

      const data = await res.json();
      if (data.success && Array.isArray(data.assets) && data.assets.length > 0) {
        const newItems = data.assets.map((a: any, idx: number) => {
          const fullUrl = a.url.startsWith('http') ? a.url : `${window.location.origin}${a.url}`;
          let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
          if (a.mimeType?.startsWith('video/') || a.mediaType === 'video') mType = 'video';
          else if (a.mimeType === 'application/pdf' || a.mediaType === 'pdf') mType = 'pdf';
          else if (a.mimeType?.includes('document') || a.mimeType?.includes('sheet') || a.mimeType?.includes('text')) mType = 'document';

          return {
            id: `med_${Date.now()}_${idx}`,
            type: mType,
            url: fullUrl,
            name: a.originalName || a.filename || 'Attached Asset',
            size: a.size ? `${(a.size / (1024 * 1024)).toFixed(2)} MB` : undefined,
          };
        });

        if (isEdit) {
          setEditCatalogProdMedia(prev => [...prev, ...newItems]);
          const firstImg = newItems.find((m: any) => m.type === 'image');
          if (!editCatalogProdMediaUrl && firstImg) setEditCatalogProdMediaUrl(firstImg.url);
        } else {
          setNewCatalogProdMedia(prev => [...prev, ...newItems]);
          const firstImg = newItems.find((m: any) => m.type === 'image');
          if (!newCatalogProdMediaUrl && firstImg) setNewCatalogProdMediaUrl(firstImg.url);
        }
        showToast(`Uploaded ${newItems.length} media asset(s)!`, 'success');
      } else {
        // Fallback to single upload loop
        for (const file of fileArray) {
          await handleCatalogFileUpload(file, isEdit);
        }
      }
    } catch (err: any) {
      console.error('[TenetPortalData] Multi-upload error:', err);
      for (const file of fileArray) {
        await handleCatalogFileUpload(file, isEdit);
      }
    } finally {
      if (isEdit) setIsEditUploadingCatalogMedia(false);
      else setIsUploadingCatalogMedia(false);
    }
  };

  const handleAiImproveCatalogProduct = async (isEdit: boolean = false) => {
    const targetMedia = isEdit ? editCatalogProdMedia : newCatalogProdMedia;
    const targetUrl = isEdit ? editCatalogProdMediaUrl : newCatalogProdMediaUrl;
    const currentName = isEdit ? editCatalogProdName : newCatalogProdName;
    const currentCat = isEdit ? editCatalogProdCategory : newCatalogProdCategory;
    const currentPrice = isEdit ? editCatalogProdPrice : newCatalogProdPrice;
    const currentOrig = isEdit ? editCatalogProdOriginalPrice : newCatalogProdOriginalPrice;
    const currentStock = isEdit ? editCatalogProdStockQty : newCatalogProdStockQty;
    const currentUnit = isEdit ? editCatalogProdUnit : newCatalogProdUnit;
    const currentDesc = isEdit ? editCatalogProdDesc : newCatalogProdDesc;
    const currentKeywords = isEdit ? editCatalogProdKeywords : newCatalogProdKeywords;
    const targetRawText = (isEdit ? editCatalogProdRawText : newCatalogProdRawText).trim();

    if (targetMedia.length === 0 && !targetUrl.trim() && !currentName.trim() && !currentDesc.trim() && !targetRawText) {
      showToast('Please upload an image, enter a product name, or paste ChatGPT / supplier specs for Luna AI to analyze!', 'error');
      return;
    }

    if (isEdit) setIsAiEnrichingEditCatalog(true);
    else setIsAiEnrichingNewCatalog(true);

    try {
      const res = await fetch('/api/tenet/products/ai-improve-product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          name: currentName,
          category: currentCat,
          price: currentPrice,
          originalPrice: currentOrig,
          stockQuantity: currentStock,
          unitType: currentUnit,
          description: currentDesc,
          searchKeywords: currentKeywords,
          media: targetMedia,
          imageUrl: targetUrl,
          rawProductText: targetRawText,
          currency: productCatalogData?.defaultCurrency || 'LKR',
        }),
      });

      const data = await res.json();
      if (data.success && data.improvedProduct) {
        const p = data.improvedProduct;
        if (isEdit) {
          if (p.name) setEditCatalogProdName(p.name);
          if (p.category) setEditCatalogProdCategory(p.category);
          if (p.price && (!editCatalogProdPrice || editCatalogProdPrice === '0')) setEditCatalogProdPrice(String(p.price));
          if (p.originalPrice) setEditCatalogProdOriginalPrice(String(p.originalPrice));
          if (p.stockQuantity !== undefined && p.stockQuantity !== null) setEditCatalogProdStockQty(String(p.stockQuantity));
          if (p.unitType) setEditCatalogProdUnit(p.unitType);
          if (p.description) setEditCatalogProdDesc(p.description);
          if (Array.isArray(p.searchKeywords) && p.searchKeywords.length > 0) {
            setEditCatalogProdKeywords(p.searchKeywords.join(', '));
          }
        } else {
          if (p.name) setNewCatalogProdName(p.name);
          if (p.category) setNewCatalogProdCategory(p.category);
          if (p.price && (!newCatalogProdPrice || newCatalogProdPrice === '0')) setNewCatalogProdPrice(String(p.price));
          if (p.originalPrice) setNewCatalogProdOriginalPrice(String(p.originalPrice));
          if (p.stockQuantity !== undefined && p.stockQuantity !== null) setNewCatalogProdStockQty(String(p.stockQuantity));
          if (p.unitType) setNewCatalogProdUnit(p.unitType);
          if (p.description) setNewCatalogProdDesc(p.description);
          if (Array.isArray(p.searchKeywords) && p.searchKeywords.length > 0) {
            setNewCatalogProdKeywords(p.searchKeywords.join(', '));
          }
        }
        showToast('✨ Product successfully enriched with Luna AI!', 'success');
      } else {
        showToast(data.error || 'Luna AI could not enrich product details.', 'error');
      }
    } catch (err: any) {
      console.error('[TenetPortalData] AI improve error:', err);
      showToast('Luna AI Error: ' + (err?.message || 'Network error'), 'error');
    } finally {
      if (isEdit) setIsAiEnrichingEditCatalog(false);
      else setIsAiEnrichingNewCatalog(false);
    }
  };

  // ── Product Orders, Vehicle Bookings & Live Tracking State ──
  const [catalogStudioTab, setCatalogStudioTab] = useState<'products' | 'bookings' | 'orders' | 'recycle_bin' | 'snapshots' | 'order_templates' | 'pre_filled_templates'>('products');
  const [portalOrderTemplateSubTab, setPortalOrderTemplateSubTab] = useState<'fields' | 'owner_alert' | 'courier' | 'customer' | 'preview'>('fields');
  const [portalNewCustomFieldKey, setPortalNewCustomFieldKey] = useState('');
  const [portalNewCustomFieldLabel, setPortalNewCustomFieldLabel] = useState('');
  const [portalNewCustomFieldRequired, setPortalNewCustomFieldRequired] = useState(false);
  const [portalCopiedPreview, setPortalCopiedPreview] = useState(false);
  const [portalPreviewMode, setPortalPreviewMode] = useState<'owner_alert' | 'courier' | 'customer'>('owner_alert');
  const [ownerAlertTemplateDraft, setOwnerAlertTemplateDraft] = useState<string>('');
  const [courierTemplateDraft, setCourierTemplateDraft] = useState<string>('');
  const [customerTemplateDraft, setCustomerTemplateDraft] = useState<string>('');
  const [isSavingTemplate, setIsSavingTemplate] = useState<boolean>(false);
  const [templateSaveFeedback, setTemplateSaveFeedback] = useState<string | null>(null);

  // ── Pre-filled Zero-Token Templates State ──
  const [portalPreFilledEditingId, setPortalPreFilledEditingId] = useState<string | null>(null);
  const [portalPreFilledDraft, setPortalPreFilledDraft] = useState<any>({});
  const [portalPreFilledKeywordsInput, setPortalPreFilledKeywordsInput] = useState<string>('');
  const [portalPreFilledSearch, setPortalPreFilledSearch] = useState<string>('');
  const [portalPreFilledShowMediaVault, setPortalPreFilledShowMediaVault] = useState<boolean>(false);
  const [portalPreFilledMediaVaultSearch, setPortalPreFilledMediaVaultSearch] = useState<string>('');
  const [portalPreFilledMediaVaultType, setPortalPreFilledMediaVaultType] = useState<'all' | 'image' | 'video' | 'pdf'>('all');
  const ownerAlertDraftDirtyRef = useRef<boolean>(false);
  const courierDraftDirtyRef = useRef<boolean>(false);
  const customerDraftDirtyRef = useRef<boolean>(false);
  const isTypingTemplateRef = useRef<boolean>(false);
  const ownerAlertTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const courierTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const customerTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const ownerAlertCursorRef = useRef<{ start: number; end: number } | null>(null);
  const courierCursorRef = useRef<{ start: number; end: number } | null>(null);
  const customerCursorRef = useRef<{ start: number; end: number } | null>(null);

  React.useLayoutEffect(() => {
    if (ownerAlertCursorRef.current && ownerAlertTextareaRef.current && document.activeElement === ownerAlertTextareaRef.current) {
      const { start, end } = ownerAlertCursorRef.current;
      ownerAlertTextareaRef.current.setSelectionRange(start, end);
    }
  });

  React.useLayoutEffect(() => {
    if (courierCursorRef.current && courierTextareaRef.current && document.activeElement === courierTextareaRef.current) {
      const { start, end } = courierCursorRef.current;
      courierTextareaRef.current.setSelectionRange(start, end);
    }
  });

  React.useLayoutEffect(() => {
    if (customerCursorRef.current && customerTextareaRef.current && document.activeElement === customerTextareaRef.current) {
      const { start, end } = customerCursorRef.current;
      customerTextareaRef.current.setSelectionRange(start, end);
    }
  });

  const handleInsertOwnerAlertTag = (v: string) => {
    const tag = `{{${v}}}`;
    const ta = ownerAlertTextareaRef.current;
    if (!ta) {
      setOwnerAlertTemplateDraft((prev) => (prev ? `${prev} ${tag}` : tag));
      ownerAlertDraftDirtyRef.current = true;
      return;
    }
    const start = ta.selectionStart ?? ownerAlertTemplateDraft.length;
    const end = ta.selectionEnd ?? ownerAlertTemplateDraft.length;
    const next = ownerAlertTemplateDraft.slice(0, start) + tag + ownerAlertTemplateDraft.slice(end);
    ownerAlertCursorRef.current = { start: start + tag.length, end: start + tag.length };
    setOwnerAlertTemplateDraft(next);
    ownerAlertDraftDirtyRef.current = true;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + tag.length, start + tag.length);
    });
  };

  const handleInsertCourierTag = (v: string) => {
    const tag = `{{${v}}}`;
    const ta = courierTextareaRef.current;
    if (!ta) {
      setCourierTemplateDraft((prev) => (prev ? `${prev} ${tag}` : tag));
      courierDraftDirtyRef.current = true;
      return;
    }
    const start = ta.selectionStart ?? courierTemplateDraft.length;
    const end = ta.selectionEnd ?? courierTemplateDraft.length;
    const next = courierTemplateDraft.slice(0, start) + tag + courierTemplateDraft.slice(end);
    courierCursorRef.current = { start: start + tag.length, end: start + tag.length };
    setCourierTemplateDraft(next);
    courierDraftDirtyRef.current = true;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + tag.length, start + tag.length);
    });
  };

  const handleInsertCustomerTag = (v: string) => {
    const tag = `{{${v}}}`;
    const ta = customerTextareaRef.current;
    if (!ta) {
      setCustomerTemplateDraft((prev) => (prev ? `${prev} ${tag}` : tag));
      customerDraftDirtyRef.current = true;
      return;
    }
    const start = ta.selectionStart ?? customerTemplateDraft.length;
    const end = ta.selectionEnd ?? customerTemplateDraft.length;
    const next = customerTemplateDraft.slice(0, start) + tag + customerTemplateDraft.slice(end);
    customerCursorRef.current = { start: start + tag.length, end: start + tag.length };
    setCustomerTemplateDraft(next);
    customerDraftDirtyRef.current = true;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + tag.length, start + tag.length);
    });
  };

  const DEFAULT_PORTAL_OWNER_CONFIRM_TEMPLATE = `🛍️ *NEW ORDER CONFIRMED!*
━━━━━━━━━━━━━━━━━━━━━━━━━
🆔 *Order ID:* *#{{orderNumber}}*
🏢 *Store:* {{storeName}}
📅 *Type:* 📦 *{{orderType}}*
━━━━━━━━━━━━━━━━━━━━━━━━━
💬 *Customer Active WhatsApp:* *{{whatsappNumber}}*
👤 *Customer Name:* {{customerName}}
📞 *Primary Contact Phone:* {{customerPhone}}
📱 *Secondary Phone:* {{secondaryPhone}}
📍 *Delivery Address:* {{deliveryAddress}}
🏙️ *City / Town:* {{city}}
━━━━━━━━━━━━━━━━━━━━━━━━━
📦 *Ordered Items:*
{{itemsList}}
💵 *Total Amount / COD:* *{{currency}} {{totalAmount}}*
💳 *Payment Method:* {{paymentMethod}}
📝 *Customer Notes:* {{notes}}
━━━━━━━━━━━━━━━━━━━━━━━━━
✅ *Recorded in Tenet Live Dashboard:*
👉 https://kangavena.com/tenet (Orders & Live Tracking Studio)
⏰ *Time:* {{orderTime}}
⚡ *Autonomous Order Engine — Kangavena Luna AI*`;

  const DEFAULT_PORTAL_COURIER_TEMPLATE = `📦 *NEW COURIER DISPATCH ORDER*
━━━━━━━━━━━━━━━━━━━━━━━━━
🆔 *Order ID:* {{orderNumber}}
🏢 *Store:* {{storeName}}
━━━━━━━━━━━━━━━━━━━━━━━━━
👤 *Recipient Name:* {{customerName}}
📍 *Delivery Address:* {{deliveryAddress}}
🏙️ *City / Town:* {{city}}
📞 *Primary Phone:* {{customerPhone}}
📱 *Secondary Phone:* {{secondaryPhone}}
💬 *WhatsApp Number:* {{whatsappNumber}}
━━━━━━━━━━━━━━━━━━━━━━━━━
📦 *Ordered Items:*
{{itemsList}}
💵 *COD / Total Payable:* {{currency}} {{totalAmount}}
💳 *Payment Method:* {{paymentMethod}}
📝 *Delivery Notes / Landmark:* {{notes}}
━━━━━━━━━━━━━━━━━━━━━━━━━
⏰ *Time:* {{orderTime}}
⚡ *Kangavena Luna AI — 1-Tap Courier Dispatch*`;

  const DEFAULT_PORTAL_CUSTOMER_RECEIPT_TEMPLATE = `🛍️ *ORDER CONFIRMED — THANK YOU!*
━━━━━━━━━━━━━━━━━━━━━━━━━
Hi {{customerName}}, your order has been successfully placed with *{{storeName}}*! 🎉

🆔 *Order ID:* {{orderNumber}}
📍 *Delivery To:* {{deliveryAddress}}, {{city}}
📞 *Contact Phone:* {{customerPhone}}
📱 *Backup Phone:* {{secondaryPhone}}
💬 *WhatsApp Number:* {{whatsappNumber}}
📦 *Order Summary:*
{{itemsList}}
💵 *Total Payable:* {{currency}} {{totalAmount}}
💳 *Payment Method:* {{paymentMethod}}

🚚 Our team is preparing your package for courier dispatch. You will receive live tracking updates once the courier partner collects your parcel!`;

  const currentOrderTemplateConfig = useMemo(() => {
    const raw = (productCatalogData?.orderTemplateConfig && typeof productCatalogData.orderTemplateConfig === 'object')
      ? productCatalogData.orderTemplateConfig
      : {};
    return {
      enabled: raw.enabled !== false,
      fields: {
        customerName: raw.fields?.customerName !== false,
        deliveryAddress: raw.fields?.deliveryAddress !== false,
        city: raw.fields?.city !== false,
        primaryPhone: raw.fields?.primaryPhone !== false,
        secondaryPhone: raw.fields?.secondaryPhone !== false,
        whatsappNumber: raw.fields?.whatsappNumber !== false,
        landmark: raw.fields?.landmark === true,
        notes: raw.fields?.notes !== false,
        customFields: Array.isArray(raw.fields?.customFields) ? raw.fields.customFields : [],
      },
      ownerConfirmTemplate: (raw.ownerConfirmTemplate !== undefined && raw.ownerConfirmTemplate !== null)
        ? raw.ownerConfirmTemplate
        : DEFAULT_PORTAL_OWNER_CONFIRM_TEMPLATE,
      ownerCourierTemplate: (raw.ownerCourierTemplate !== undefined && raw.ownerCourierTemplate !== null)
        ? raw.ownerCourierTemplate
        : DEFAULT_PORTAL_COURIER_TEMPLATE,
      customerConfirmationTemplate: (raw.customerConfirmationTemplate !== undefined && raw.customerConfirmationTemplate !== null)
        ? raw.customerConfirmationTemplate
        : DEFAULT_PORTAL_CUSTOMER_RECEIPT_TEMPLATE,
    };
  }, [productCatalogData?.orderTemplateConfig]);

  const currentPreFilledTemplates = useMemo(() => {
    const raw = Array.isArray(productCatalogData?.preFilledTemplates)
      ? productCatalogData.preFilledTemplates
      : [];
    return raw.map((t: any) => ({
      ...t,
      packMediaAlbum: t.packMediaAlbum !== false,
      isEnabled: t.isEnabled !== false && t.isEnabled !== 'false' && t.isEnabled !== 0 && t.isEnabled !== '0' && t.isEnabled !== 'disabled' && t.isEnabled !== 'paused',
    }));
  }, [productCatalogData?.preFilledTemplates]);

  const currentPreFilledEnabled = useMemo(() => {
    const raw = productCatalogData?.preFilledTemplatesEnabled;
    return raw !== false && raw !== 'false' && raw !== 0 && raw !== '0';
  }, [productCatalogData?.preFilledTemplatesEnabled]);

  // Sync draft states with incoming server data ONLY when the user is not actively editing or dirty
  useEffect(() => {
    const raw = productCatalogData?.orderTemplateConfig;
    if (raw) {
      if (!ownerAlertDraftDirtyRef.current && !isTypingTemplateRef.current) {
        if (raw.ownerConfirmTemplate !== undefined && raw.ownerConfirmTemplate !== null) {
          setOwnerAlertTemplateDraft(raw.ownerConfirmTemplate);
        } else {
          setOwnerAlertTemplateDraft(DEFAULT_PORTAL_OWNER_CONFIRM_TEMPLATE);
        }
      }
      if (!courierDraftDirtyRef.current && !isTypingTemplateRef.current) {
        if (raw.ownerCourierTemplate !== undefined && raw.ownerCourierTemplate !== null) {
          setCourierTemplateDraft(raw.ownerCourierTemplate);
        } else {
          setCourierTemplateDraft(DEFAULT_PORTAL_COURIER_TEMPLATE);
        }
      }
      if (!customerDraftDirtyRef.current && !isTypingTemplateRef.current) {
        if (raw.customerConfirmationTemplate !== undefined && raw.customerConfirmationTemplate !== null) {
          setCustomerTemplateDraft(raw.customerConfirmationTemplate);
        } else {
          setCustomerTemplateDraft(DEFAULT_PORTAL_CUSTOMER_RECEIPT_TEMPLATE);
        }
      }
    } else {
      if (!ownerAlertDraftDirtyRef.current && !isTypingTemplateRef.current) {
        setOwnerAlertTemplateDraft(DEFAULT_PORTAL_OWNER_CONFIRM_TEMPLATE);
      }
      if (!courierDraftDirtyRef.current && !isTypingTemplateRef.current) {
        setCourierTemplateDraft(DEFAULT_PORTAL_COURIER_TEMPLATE);
      }
      if (!customerDraftDirtyRef.current && !isTypingTemplateRef.current) {
        setCustomerTemplateDraft(DEFAULT_PORTAL_CUSTOMER_RECEIPT_TEMPLATE);
      }
    }
  }, [productCatalogData?.orderTemplateConfig]);



  const [portalSnapshotLabel, setPortalSnapshotLabel] = useState<string>('');
  const [isCreatingPortalSnapshot, setIsCreatingPortalSnapshot] = useState<boolean>(false);
  const [ordersList, setOrdersList] = useState<any[]>([]);
  const [ordersSummary, setOrdersSummary] = useState<any>({ total: 0 });
  const [ordersFilterStatus, setOrdersFilterStatus] = useState<string>('all');
  const [ordersSearchQuery, setOrdersSearchQuery] = useState<string>('');
  const [bookingsFilterStatus, setBookingsFilterStatus] = useState<string>('all');
  const [bookingsSearchQuery, setBookingsSearchQuery] = useState<string>('');
  const [isLoadingOrders, setIsLoadingOrders] = useState<boolean>(false);
  const [editingOrderTracking, setEditingOrderTracking] = useState<any | null>(null);
  const [isUpdatingTracking, setIsUpdatingTracking] = useState<boolean>(false);
  const [isNotifyingCustomer, setIsNotifyingCustomer] = useState<number | null>(null);
  const [orderEdits, setOrderEdits] = useState<Record<number, any>>({});

  const isBookingOrder = useCallback((order: any) => {
    if (order?.metadata?.orderType === 'delivery_order') return false;
    return (
      order?.metadata?.orderType === 'booking' ||
      order?.metadata?.orderType === 'rental' ||
      order?.metadata?.orderType === 'reservation' ||
      order?.orderNumber?.startsWith('BKG-') ||
      (Boolean(order?.metadata?.bookingDates) && !order?.orderNumber?.startsWith('ORD-'))
    );
  }, []);

  const bookingsList = useMemo(() => {
    return ordersList.filter((o: any) => isBookingOrder(o));
  }, [ordersList, isBookingOrder]);

  const physicalOrdersList = useMemo(() => {
    return ordersList.filter((o: any) => !isBookingOrder(o));
  }, [ordersList, isBookingOrder]);

  const hasBookingCapability = useMemo(() => {
    if (bookingsList.length > 0) return true;
    return (catalogProducts || []).some((p: any) => {
      const cat = String(p.category || '').toLowerCase();
      const name = String(p.name || '').toLowerCase();
      return /\b(cab|rental|rent|hire|fleet|vehicle|room|reservation)\b/i.test(cat) ||
             /\b(cab service|car rental|van rental|rent-a-car|taxi hire|self-drive)\b/i.test(name);
    });
  }, [bookingsList.length, catalogProducts]);

  useEffect(() => {
    if (!isLoadingOrders && catalogStudioTab === 'bookings' && !hasBookingCapability) {
      setCatalogStudioTab('orders');
    }
  }, [catalogStudioTab, hasBookingCapability, isLoadingOrders]);

  const filteredBookings = useMemo(() => {
    return bookingsList.filter((b: any) => {
      const edit = orderEdits[b.id] || {};
      const currentStatus = edit.status || b.status;
      if (bookingsFilterStatus !== 'all') {
        if (bookingsFilterStatus === 'active') {
          if (!['processing', 'dispatched', 'out_for_delivery'].includes(currentStatus)) return false;
        } else if (currentStatus !== bookingsFilterStatus) {
          return false;
        }
      }
      if (bookingsSearchQuery && bookingsSearchQuery.trim()) {
        const q = bookingsSearchQuery.toLowerCase().trim();
        const num = (b.orderNumber || '').toLowerCase();
        const name = (b.customerName || '').toLowerCase();
        const phone = (b.customerPhone || '').toLowerCase();
        const vehicle = ((b.items || []).map((it: any) => it.product_name).join(' ')).toLowerCase();
        const dates = (b.metadata?.bookingDates || '').toLowerCase();
        const pickup = (b.metadata?.pickupLocation || b.deliveryAddress || '').toLowerCase();
        return num.includes(q) || name.includes(q) || phone.includes(q) || vehicle.includes(q) || dates.includes(q) || pickup.includes(q);
      }
      return true;
    });
  }, [bookingsList, orderEdits, bookingsFilterStatus, bookingsSearchQuery]);

  const filteredPhysicalOrders = useMemo(() => {
    return physicalOrdersList.filter((order: any) => {
      const edit = orderEdits[order.id] || {};
      const currentStatus = edit.status || order.status;
      if (ordersFilterStatus !== 'all' && currentStatus !== ordersFilterStatus) return false;
      if (ordersSearchQuery && ordersSearchQuery.trim()) {
        const q = ordersSearchQuery.toLowerCase().trim();
        const num = (order.orderNumber || '').toLowerCase();
        const name = (order.customerName || '').toLowerCase();
        const phone = (order.customerPhone || '').toLowerCase();
        const addr = (order.deliveryAddress || '').toLowerCase();
        return num.includes(q) || name.includes(q) || phone.includes(q) || addr.includes(q);
      }
      return true;
    });
  }, [physicalOrdersList, orderEdits, ordersFilterStatus, ordersSearchQuery]);

  // ── Autonomous Follow-Up & Re-engagement Hub State ──
  const [followUpData, setFollowUpData] = useState<any>(null);
  const [isSavingFollowUp, setIsSavingFollowUp] = useState<boolean>(false);
  const [isTriggeringScan, setIsTriggeringScan] = useState<boolean>(false);
  const [isDispatchingFollowUp, setIsDispatchingFollowUp] = useState<string | null>(null);
  const [followUpStudioTab, setFollowUpStudioTab] = useState<'pending' | 'history' | 'settings'>('pending');
  const [isTogglingNeuralCache, setIsTogglingNeuralCache] = useState<boolean>(false);

  // ── Autonomous Review Booster & Google Maps Rating State ──
  const [reviewBoosterData, setReviewBoosterData] = useState<any>(null);
  const [isSavingReviewBooster, setIsSavingReviewBooster] = useState<boolean>(false);
  const [isTriggeringReviewScan, setIsTriggeringReviewScan] = useState<boolean>(false);
  const [reviewStudioTab, setReviewStudioTab] = useState<'feedback' | 'settings'>('feedback');

  // ── Enterprise SIP PBX Gateway State ──
  const [sipCallData, setSipCallData] = useState<any>(null);

  const showToast = (text: string, type: 'success' | 'error' = 'success') => {
    setToastMessage({ text, type });
    setTimeout(() => setToastMessage(null), 3000);
  };

  const copyToClipboard = (text: string, label: string = 'Content') => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    showToast(`${label} copied!`, 'success');
    setTimeout(() => setCopiedText(null), 2500);
  };

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    }
    if (tenant?.apiKey) {
      headers['x-tenant-api-key'] = tenant.apiKey;
    }
    return headers;
  }, [token, tenant]);

  const getAuthUploadHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    }
    if (tenant?.apiKey) {
      headers['x-tenant-api-key'] = tenant.apiKey;
    }
    return headers;
  }, [token, tenant]);

  // Safe JSON API Fetcher with automatic retry on service restart / transient 502
  const fetchJsonSafe = useCallback(async (url: string, init?: RequestInit, retries = 3, delayMs = 1200): Promise<any> => {
    try {
      const res = await fetch(url, init);
      const contentType = res.headers.get('content-type') || '';
      
      if ((res.status === 502 || res.status === 503 || res.status === 504 || !contentType.includes('application/json')) && retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return fetchJsonSafe(url, init, retries - 1, delayMs * 1.5);
      }

      if (!contentType.includes('application/json')) {
        throw new Error(`API format error: Server returned ${res.status} with non-JSON response.`);
      }
      const data = await res.json();
      return data;
    } catch (err: any) {
      if (retries > 0 && !err.message?.includes('401') && !err.message?.includes('403')) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return fetchJsonSafe(url, init, retries - 1, delayMs * 1.5);
      }
      throw err;
    }
  }, []);

  // ── Fetch Luna Neural Context Cache Studio Data ──
  const fetchCacheStudioData = useCallback(async () => {
    setIsLoadingCacheStudio(true);
    try {
      const endpoint = `/api/admin/luna-flows/cache-inspection?tenantId=${encodeURIComponent(tenant?.id || 'global')}&channel=default`;
      const data = await fetchJsonSafe(endpoint, {
        headers: getAuthHeaders(),
      });
      if (data?.success && data?.cache) {
        setCacheStudioData(data.cache);
      }
    } catch (err) {
      console.warn('[TenetPortalData] Error fetching cache data:', err);
    } finally {
      setIsLoadingCacheStudio(false);
    }
  }, [tenant, getAuthHeaders, fetchJsonSafe]);

  useEffect(() => {
    if (selectedModule === 'neural_cache') {
      fetchCacheStudioData();
    }
  }, [selectedModule, fetchCacheStudioData]);

  const handlePurgeCacheStudio = async () => {
    setIsPurgingCacheStudio(true);
    setCacheStudioPurgeSuccess(null);
    try {
      const res = await fetch('/api/admin/luna-flows/purge-cache', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          tenantId: tenant?.id || 'global',
          channel: 'default',
        }),
      });
      const data = await res.json();
      if (data?.success) {
        setCacheStudioPurgeSuccess('Cache successfully purged & re-indexed across all active nodes!');
        await fetchCacheStudioData();
        showToast('Neural Cache purged & re-indexed successfully!', 'success');
      }
    } catch (err) {
      console.error('[TenetPortalData] Purge error:', err);
      showToast('Failed to purge cache', 'error');
    } finally {
      setIsPurgingCacheStudio(false);
    }
  };

  const handleCopyCacheStudioPrompt = () => {
    const text = cacheStudioData?.fullSystemPrompt || cacheStudioData?.systemPromptPreview || '';
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedCacheStudioPrompt(true);
    setTimeout(() => setCopiedCacheStudioPrompt(false), 2000);
  };

  const handleToggleNeuralCache = async () => {
    setIsTogglingNeuralCache(true);
    try {
      const currentEnabled = cacheStudioData?.isNeuralCacheEnabled !== false;
      const targetEnabled = !currentEnabled;
      const res = await fetch('/api/tenet/luna-core/toggle-cache', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ isNeuralCacheEnabled: targetEnabled }),
      });
      const data = await res.json();
      if (data?.success) {
        showToast(
          targetEnabled
            ? 'Neural Cache Enabled! Instant <0.05ms memory recall active.'
            : 'Neural Cache Disabled! Luna will freshly traverse all nodes per message.',
          'success'
        );
        await fetchCacheStudioData();
      } else {
        showToast(data?.error || 'Failed to toggle cache mode', 'error');
      }
    } catch (err) {
      console.error('[TenetPortalData] Toggle cache error:', err);
      showToast('Network error toggling neural cache', 'error');
    } finally {
      setIsTogglingNeuralCache(false);
    }
  };

  // ── Fetch Portal Data & Node Telemetry ──
  const fetchPortalData = useCallback(async (isBackground = false) => {
    if (!isBackground) setRefreshing(true);
    try {
      const data = await fetchJsonSafe('/api/tenet/portal-data', {
        headers: getAuthHeaders(),
      });
      if (data.success) {
        setActiveFlowNodes(data.activeFlowNodes || []);
        setFlowName(data.flowName || 'Autonomous Luna Agent Flow');
        setStats(data.stats || {});
        setConversations(data.conversations || []);
        const bCycle = data.billingCycle || data.stats?.billingCycle || null;
        if (bCycle) {
          setBillingCycle(bCycle);
        }

        if (data.actionData) {
          setActionData(data.actionData);
        }

        if (data.leadsData?.submissions) {
          setLeadsList(data.leadsData.submissions);
        }

        if (data.knowledgeData) {
          setKnowledgeData(data.knowledgeData);
          setKnowledgeText(data.knowledgeData.contextSource || '');
        } else {
          setKnowledgeData(null);
          setKnowledgeText('');
        }

        if (data.mediaVaultData) {
          setMediaVaultData(data.mediaVaultData);
          setMediaAssets(data.mediaVaultData.assets || []);
        } else {
          setMediaVaultData(null);
          setMediaAssets([]);
        }

        if (data.customerMemoryData) {
          setCustomerMemoryData(data.customerMemoryData);
          if (Array.isArray(data.customerMemoryData.memories)) {
            setMemoriesList(data.customerMemoryData.memories);
          }
          if (typeof data.customerMemoryData.total === 'number') {
            setTotalMemoriesCount(data.customerMemoryData.total);
          } else if (Array.isArray(data.customerMemoryData.memories)) {
            setTotalMemoriesCount(data.customerMemoryData.memories.length);
          }
        } else {
          setCustomerMemoryData(null);
          setMemoriesList([]);
          setTotalMemoriesCount(0);
        }

        if (data.whatsAppInstances && Array.isArray(data.whatsAppInstances) && data.whatsAppInstances.length > 0) {
          const processedInstances = data.whatsAppInstances.map((inst: any) => {
            const existingPhone = cleanInitialPhone(inst.phoneNumber);
            const resolvedPhone = existingPhone || autoDetectedRegisteredPhone;
            const isOldDummyApp = inst.sipAppId === '2463786290783462';
            const resolvedAppId = (inst.sipAppId && !isOldDummyApp) ? inst.sipAppId : KANGAVENA_OFFICIAL_META_APP_ID;
            const rawToken = inst.metaAccessToken || '';
            const safeToken = String(rawToken).startsWith('EAA') && !inst.useCustomMetaToken ? '' : rawToken;
            return {
              ...inst,
              metaAccessToken: safeToken,
              useCustomMetaToken: inst.useCustomMetaToken === true,
              phoneNumber: resolvedPhone,
              sipAppId: resolvedAppId,
            };
          });
          setWhatsAppInstances(processedInstances);

          // Select matching instance based on selectedWhatsAppNodeId or initialNodeId
          const activeNodeTarget = selectedWhatsAppNodeId || initialNodeId || (initialNode && initialNode.includes('whatsapp') && !initialNode.includes('instance') && !initialNode.includes('web') ? initialNode : null);
          const activeInst = (activeNodeTarget ? processedInstances.find((i: any) => i.nodeId === activeNodeTarget) : null) || processedInstances[0];
          setWhatsAppData(activeInst);
          if (activeInst?.nodeId) {
            setSelectedWhatsAppNodeId(activeInst.nodeId);
          }
          if (activeInst?.qrCode) {
            setWhatsAppQrCode(activeInst.qrCode);
          }
          fetchIgnoredContacts();
        } else if (data.whatsAppData) {
          const existingPhone = cleanInitialPhone(data.whatsAppData.phoneNumber);
          const resolvedPhone = existingPhone || autoDetectedRegisteredPhone;
          const isOldDummyApp = data.whatsAppData.sipAppId === '2463786290783462';
          const resolvedAppId = (data.whatsAppData.sipAppId && !isOldDummyApp) ? data.whatsAppData.sipAppId : KANGAVENA_OFFICIAL_META_APP_ID;

          const rawToken = data.whatsAppData.metaAccessToken || '';
          const safeToken = String(rawToken).startsWith('EAA') && !data.whatsAppData.useCustomMetaToken ? '' : rawToken;
          const singleInst = {
            ...data.whatsAppData,
            metaAccessToken: safeToken,
            useCustomMetaToken: data.whatsAppData.useCustomMetaToken === true,
            phoneNumber: resolvedPhone,
            sipAppId: resolvedAppId,
          };
          setWhatsAppData(singleInst);
          setWhatsAppInstances([singleInst]);
          setSelectedWhatsAppNodeId(singleInst.nodeId || 'whatsappNode-1');
          if (data.whatsAppData.qrCode) {
            setWhatsAppQrCode(data.whatsAppData.qrCode);
          }
          fetchIgnoredContacts();
        } else {
          setWhatsAppData(null);
          setWhatsAppInstances([]);
          setSelectedWhatsAppNodeId(null);
          setWhatsAppQrCode(null);
        }

        if (data.intentAlertData) {
          setIntentAlertData(data.intentAlertData);
          const resolvedRules = Array.isArray(data.intentAlertData.rules) && data.intentAlertData.rules.length > 0
            ? data.intentAlertData.rules
            : (data.intentAlertData.rules === undefined ? DEFAULT_SAMPLE_PORTAL_RULES : (data.intentAlertData.rules || []));
          setIntentRules(resolvedRules);
        } else {
          setIntentAlertData(null);
          setIntentRules([]);
        }

        if (data.productCatalogData) {
          setProductCatalogData(data.productCatalogData);
          const resolvedProducts = Array.isArray(data.productCatalogData.products)
            ? data.productCatalogData.products
            : (data.productCatalogData.products === undefined ? DEFAULT_SAMPLE_PORTAL_PRODUCTS : []);
          setCatalogProducts(resolvedProducts);
        } else {
          setProductCatalogData(null);
          setCatalogProducts([]);
        }

        if (data.followUpData) {
          setFollowUpData(data.followUpData);
        } else {
          setFollowUpData(null);
        }

        if (data.reviewBoosterData) {
          setReviewBoosterData(data.reviewBoosterData);
        } else {
          setReviewBoosterData(null);
        }

        if (data.sipCallData) {
          setSipCallData(data.sipCallData);
        } else {
          setSipCallData(null);
        }

        // Auto-select first session if none selected
        if (!selectedSessionId && data.conversations?.length > 0) {
          setSelectedSessionId(data.conversations[0].sessionId);
        }
      } else {
        if (!isBackground) showToast(data.error || 'Failed to load portal data', 'error');
      }
    } catch (err: any) {
      console.warn('[TenetPortalData] Fetch error:', err);
      if (!isBackground) showToast('Network error loading portal data', 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders, selectedSessionId]);

  // ── Customer Memory Vault Handlers ──
  const fetchMemories = useCallback(async (searchQueryStr?: string) => {
    setMemoriesLoading(true);
    try {
      const q = typeof searchQueryStr === 'string' ? searchQueryStr : memoriesSearchQuery;
      const url = q ? `/api/tenet/memories?search=${encodeURIComponent(q)}` : '/api/tenet/memories';
      const res = await fetchJsonSafe(url, { headers: getAuthHeaders() });
      if (res.success && Array.isArray(res.memories)) {
        setMemoriesList(res.memories);
        if (typeof res.total === 'number') {
          setTotalMemoriesCount(res.total);
        } else {
          setTotalMemoriesCount(res.memories.length);
        }
      }
    } catch (err) {
      console.warn('[TenetPortalData] Error fetching customer memories:', err);
    } finally {
      setMemoriesLoading(false);
    }
  }, [getAuthHeaders, memoriesSearchQuery, fetchJsonSafe]);

  const handleSaveMemory = async (memoryId: number, updates: any) => {
    setIsSavingMemory(true);
    try {
      const res = await fetch(`/api/tenet/memories/${memoryId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Customer memory updated successfully!', 'success');
        setEditingMemory(null);
        fetchMemories();
      } else {
        showToast(data.error || 'Failed to update memory', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Error updating memory', 'error');
    } finally {
      setIsSavingMemory(false);
    }
  };

  const handleCreateMemory = async () => {
    if (!newMemoryPhone.trim()) {
      showToast('Please enter customer phone number or identifier', 'error');
      return;
    }
    setIsCreatingMemory(true);
    try {
      const res = await fetch('/api/tenet/memories', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          customerIdentifier: newMemoryPhone.trim(),
          customerName: newMemoryName.trim() || undefined,
          distilledMemory: newMemoryDistilled.trim() || undefined,
          keyPreferences: {
            budget: newMemoryPrefBudget.trim() || undefined,
            product: newMemoryPrefProduct.trim() || undefined,
            location: newMemoryPrefLocation.trim() || undefined,
          },
          isVip: newMemoryIsVip,
          sentimentTrend: newMemoryIsVip ? 'vip' : 'positive',
        }),
      });
      const data = await res.json();
      if (data.success && data.memory) {
        showToast('Customer memory profile created successfully!', 'success');
        setShowAddMemoryModal(false);
        setNewMemoryPhone('');
        setNewMemoryName('');
        setNewMemoryDistilled('');
        setNewMemoryPrefBudget('');
        setNewMemoryPrefProduct('');
        setNewMemoryPrefLocation('');
        setNewMemoryIsVip(false);
        fetchMemories();
        setSelectedMemoryId(data.memory.id);
      } else {
        showToast(data.error || 'Failed to create memory profile', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Error creating memory profile', 'error');
    } finally {
      setIsCreatingMemory(false);
    }
  };

  const handleDeleteMemory = async (memoryId: number) => {
    if (!confirm('Are you sure you want to permanently wipe all episodic memory for this client? (GDPR Forget Me)')) return;
    setIsDeletingMemory(true);
    try {
      const res = await fetch(`/api/tenet/memories/${memoryId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Client memory permanently erased.', 'success');
        if (selectedMemoryId === memoryId) setSelectedMemoryId(null);
        fetchMemories();
      } else {
        showToast(data.error || 'Failed to delete memory', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Error deleting memory', 'error');
    } finally {
      setIsDeletingMemory(false);
    }
  };

  useEffect(() => {
    fetchPortalData();
  }, [fetchPortalData]);

  // Auto-refresh interval (for conversations)
  useEffect(() => {
    if (!autoRefresh || selectedModule !== 'luna_core') return;
    const interval = setInterval(() => {
      fetchPortalData(true);
    }, 8000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchPortalData, selectedModule]);

  // ── Smart Intent Alert Handlers ──
  const handleSaveIntentRules = async (updatedRulesList?: any[]) => {
    setIsSavingIntentRules(true);
    try {
      const res = await fetch('/api/tenet/intent-alerts/save-rules', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nodeId: intentAlertData?.nodeId,
          rules: updatedRulesList || intentRules,
          silentMode: intentAlertData?.silentMode !== false,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Intent rules saved to active flow!', 'success');
        fetchPortalData(true);
      } else {
        showToast(data.error || 'Failed to save rules', 'error');
      }
    } catch (err: any) {
      showToast('Network error saving intent rules', 'error');
    } finally {
      setIsSavingIntentRules(false);
    }
  };

  const handleToggleRule = (ruleId: string, enabled: boolean) => {
    const updated = intentRules.map((r) => (r.id === ruleId ? { ...r, isEnabled: enabled } : r));
    setIntentRules(updated);
    handleSaveIntentRules(updated);
  };

  const handleDeletePortalRule = (ruleId: string) => {
    if (!confirm('Are you sure you want to remove this intent rule?')) return;
    const updated = intentRules.filter((r) => r.id !== ruleId);
    setIntentRules(updated);
    handleSaveIntentRules(updated);
  };

  const handleCreatePortalRule = () => {
    if (!newPortalIntentTitle.trim() || !newPortalIntentKeywords.trim()) return;
    const newR = {
      id: `rule_${Date.now()}`,
      intentTitle: newPortalIntentTitle.trim(),
      matchConditions: newPortalIntentKeywords.trim(),
      notificationChannel: newPortalIntentChannel,
      urgency: newPortalIntentUrgency,
      isEnabled: true,
    };
    const updated = [...intentRules, newR];
    setIntentRules(updated);
    handleSaveIntentRules(updated);
    setNewPortalIntentTitle('');
    setNewPortalIntentKeywords('');
    setIsAddingPortalRule(false);
  };

  const handleTestIntentAlert = async (rule: any) => {
    setIsTestingIntent(true);
    setTestIntentResult(null);
    try {
      const res = await fetch('/api/tenet/intent-alerts/trigger', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          rule_id: rule.id,
          intent_title: rule.intentTitle,
          matched_interest: rule.matchConditions.split(',')[0]?.trim() || 'Simulated Inquiry',
          customer_phone: '94771234567',
          customer_name: 'Test Customer',
          conversation_snippet: `Simulated inquiry matching [${rule.intentTitle}]: Client requested details.`,
          urgency: rule.urgency || 'high',
        }),
      });
      const data = await res.json();
      if (data.success) {
        setTestIntentResult(data);
        showToast('✅ Test Alert Dispatched to Management!', 'success');
      } else {
        showToast(data.error || 'Failed to dispatch test alert', 'error');
      }
    } catch (err) {
      showToast('Error dispatching test alert', 'error');
    } finally {
      setIsTestingIntent(false);
    }
  };

  // ── Product Catalog Handlers ──
  const explicitCatalogCategories: string[] = useMemo(() => {
    if (productCatalogData?.categories && Array.isArray(productCatalogData.categories)) {
      return productCatalogData.categories;
    }
    return [];
  }, [productCatalogData]);

  const explicitCategoryRecycleBin: any[] = useMemo(() => {
    if (productCatalogData?.categoryRecycleBin && Array.isArray(productCatalogData.categoryRecycleBin)) {
      return productCatalogData.categoryRecycleBin;
    }
    return [];
  }, [productCatalogData]);

  const getProductCategories = useCallback((p: any): string[] => {
    const set = new Set<string>();
    if (Array.isArray(p?.categories)) {
      p.categories.forEach((c: any) => {
        if (typeof c === 'string' && c.trim()) set.add(c.trim());
      });
    }
    if (typeof p?.category === 'string' && p.category.trim()) {
      p.category.split(',').forEach((c: string) => {
        if (c.trim()) set.add(c.trim());
      });
    }
    return set.size > 0 ? Array.from(set) : ['General'];
  }, []);

  const allCatalogCategories = useMemo(() => {
    const set = new Set<string>();
    explicitCatalogCategories.forEach((c: any) => { if (c && typeof c === 'string' && c.trim()) set.add(c.trim()); });
    catalogProducts.forEach((p: any) => {
      getProductCategories(p).forEach(c => set.add(c));
    });
    if (set.size === 0) set.add('General');
    return Array.from(set);
  }, [explicitCatalogCategories, catalogProducts, getProductCategories]);

  const handleSaveCatalogProducts = async (
    updatedList?: any[],
    updatedRecycleBin?: any[],
    updatedSnapshots?: any[],
    updatedCategories?: any[],
    updatedCategoryRecycleBin?: any[],
    updatedCategoryRules?: Record<string, string>,
    updatedOrderTemplateConfig?: any,
    updatedPreFilledTemplates?: any[],
    updatedPreFilledTemplatesEnabled?: boolean,
  ) => {
    setIsSavingCatalogProducts(true);
    const catsToSave = updatedCategories !== undefined ? updatedCategories : (productCatalogData?.categories || explicitCatalogCategories);
    const rulesToSave = updatedCategoryRules !== undefined ? updatedCategoryRules : (productCatalogData?.categoryRules || {});
    const recycleCatsToSave = updatedCategoryRecycleBin !== undefined ? updatedCategoryRecycleBin : (productCatalogData?.categoryRecycleBin || explicitCategoryRecycleBin);
    const templateConfigToSave = updatedOrderTemplateConfig !== undefined ? updatedOrderTemplateConfig : (productCatalogData?.orderTemplateConfig || null);
    const preFilledTemplatesToSave = updatedPreFilledTemplates !== undefined ? updatedPreFilledTemplates : (productCatalogData?.preFilledTemplates || []);
    const preFilledEnabledToSave = updatedPreFilledTemplatesEnabled !== undefined ? updatedPreFilledTemplatesEnabled : (productCatalogData?.preFilledTemplatesEnabled !== false);

    // Optimistic local state update for instant UI feedback
    if (updatedList !== undefined) setCatalogProducts(updatedList);
    setProductCatalogData((prev: any) => ({
      ...(prev || {}),
      products: updatedList !== undefined ? updatedList : prev?.products,
      categories: catsToSave,
      categoryRules: rulesToSave,
      recycleBin: updatedRecycleBin !== undefined ? updatedRecycleBin : prev?.recycleBin,
      categoryRecycleBin: recycleCatsToSave,
      snapshots: updatedSnapshots !== undefined ? updatedSnapshots : prev?.snapshots,
      orderTemplateConfig: templateConfigToSave,
      preFilledTemplates: preFilledTemplatesToSave,
      preFilledTemplatesEnabled: preFilledEnabledToSave,
    }));

    try {
      const res = await fetch('/api/tenet/products/save-catalog', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nodeId: productCatalogData?.nodeId,
          products: updatedList || catalogProducts,
          categories: catsToSave,
          categoryRules: rulesToSave,
          recycleBin: updatedRecycleBin !== undefined ? updatedRecycleBin : (productCatalogData?.recycleBin || []),
          categoryRecycleBin: recycleCatsToSave,
          snapshots: updatedSnapshots !== undefined ? updatedSnapshots : (productCatalogData?.snapshots || []),
          defaultCurrency: productCatalogData?.defaultCurrency || 'LKR',
          allowAiDiscounts: productCatalogData?.allowAiDiscounts === true,
          enableOrderTaking: productCatalogData?.enableOrderTaking !== false,
          enableOrderTracking: productCatalogData?.enableOrderTracking !== false,
          orderNotificationPhone: productCatalogData?.orderNotificationPhone || '',
          orderTemplateConfig: templateConfigToSave,
          preFilledTemplates: preFilledTemplatesToSave,
          preFilledTemplatesEnabled: preFilledEnabledToSave,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Product catalog saved to active flow!', 'success');
        fetchPortalData(true);
      } else {
        showToast(data.error || 'Failed to save catalog', 'error');
      }
    } catch (err: any) {
      showToast('Network error saving catalog products', 'error');
    } finally {
      setIsSavingCatalogProducts(false);
    }
  };

  const handleAddPortalCategory = (catName: string, ruleText?: string) => {
    const trimmed = catName.trim();
    if (!trimmed) return;
    const existing = allCatalogCategories.find(c => c.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      setSelectedCatalogCategory(existing);
      setNewPortalCategoryInput('');
      setNewPortalCategoryRuleInput('');
      setShowPortalCategoryManager(false);
      return;
    }
    const updated = [...explicitCatalogCategories, trimmed];
    const currentRules = productCatalogData?.categoryRules || {};
    const updatedCategoryRules = { ...currentRules };
    if (ruleText && ruleText.trim()) {
      updatedCategoryRules[trimmed] = ruleText.trim();
    }
    handleSaveCatalogProducts(catalogProducts, productCatalogData?.recycleBin, productCatalogData?.snapshots, updated, undefined, updatedCategoryRules);
    setSelectedCatalogCategory(trimmed);
    setNewPortalCategoryInput('');
    setNewPortalCategoryRuleInput('');
  };

  const handleRenamePortalCategory = (oldName: string, newName: string) => {
    const trimmedOld = oldName.trim();
    const trimmedNew = newName.trim();
    if (!trimmedNew || trimmedOld.toLowerCase() === trimmedNew.toLowerCase()) {
      setRenamingPortalCategory(null);
      return;
    }
    const updatedCats = explicitCatalogCategories.map(c => c.toLowerCase() === trimmedOld.toLowerCase() ? trimmedNew : c);
    if (!updatedCats.some(c => c.toLowerCase() === trimmedNew.toLowerCase())) {
      updatedCats.push(trimmedNew);
    }
    const updatedProducts = catalogProducts.map(p => {
      if ((p.category || 'General').trim().toLowerCase() === trimmedOld.toLowerCase()) {
        return { ...p, category: trimmedNew };
      }
      return p;
    });

    // Also rename rule if exists
    const currentRules = productCatalogData?.categoryRules || {};
    const updatedCategoryRules = { ...currentRules };
    if (updatedCategoryRules[trimmedOld] !== undefined) {
      updatedCategoryRules[trimmedNew] = updatedCategoryRules[trimmedOld];
      delete updatedCategoryRules[trimmedOld];
    }

    handleSaveCatalogProducts(updatedProducts, productCatalogData?.recycleBin, productCatalogData?.snapshots, updatedCats, undefined, updatedCategoryRules);
    if (selectedCatalogCategory.toLowerCase() === trimmedOld.toLowerCase()) {
      setSelectedCatalogCategory(trimmedNew);
    }
    setRenamingPortalCategory(null);
  };

  const handleSaveCategoryRule = (catName: string, rule: string) => {
    const currentRules = productCatalogData?.categoryRules || {};
    const updatedCategoryRules = { ...currentRules };
    if (rule.trim() === '') {
      delete updatedCategoryRules[catName];
    } else {
      updatedCategoryRules[catName] = rule.trim();
    }
    handleSaveCatalogProducts(catalogProducts, productCatalogData?.recycleBin, productCatalogData?.snapshots, undefined, undefined, updatedCategoryRules);
    setEditingCategoryRule(null);
  };

  const handleSaveOwnerAlertTemplate = useCallback((textToSave: string) => {
    const updated = {
      ...currentOrderTemplateConfig,
      ownerConfirmTemplate: textToSave,
    };
    setIsSavingTemplate(true);
    handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
    ownerAlertDraftDirtyRef.current = false;
    setTemplateSaveFeedback('✅ Owner Order Alert Template Saved!');
    setTimeout(() => {
      setIsSavingTemplate(false);
      setTemplateSaveFeedback(null);
    }, 2500);
  }, [currentOrderTemplateConfig, handleSaveCatalogProducts]);

  const handleSaveCourierTemplate = useCallback((textToSave: string) => {
    const updated = {
      ...currentOrderTemplateConfig,
      ownerCourierTemplate: textToSave,
    };
    setIsSavingTemplate(true);
    handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
    courierDraftDirtyRef.current = false;
    setTemplateSaveFeedback('✅ Courier Forwarding Template Saved!');
    setTimeout(() => {
      setIsSavingTemplate(false);
      setTemplateSaveFeedback(null);
    }, 2500);
  }, [currentOrderTemplateConfig, handleSaveCatalogProducts]);

  const handleSaveCustomerTemplate = useCallback((textToSave: string) => {
    const updated = {
      ...currentOrderTemplateConfig,
      customerConfirmationTemplate: textToSave,
    };
    setIsSavingTemplate(true);
    handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
    customerDraftDirtyRef.current = false;
    setTemplateSaveFeedback('✅ Customer Receipt Template Saved!');
    setTimeout(() => {
      setIsSavingTemplate(false);
      setTemplateSaveFeedback(null);
    }, 2500);
  }, [currentOrderTemplateConfig, handleSaveCatalogProducts]);

  const handleDeletePortalCategory = (catToDelete: string) => {
    const trimmed = catToDelete.trim();
    if (trimmed.toLowerCase() === 'general') return;
    if (!window.confirm(`Delete category "${trimmed}"? Existing products in this category will be moved to "General". You can restore this category anytime from the Category Recycle Bin.`)) return;

    const updatedCats = explicitCatalogCategories.filter(c => c.toLowerCase() !== trimmed.toLowerCase());
    const affectedCount = catalogProducts.filter(p => (p.category || 'General').trim().toLowerCase() === trimmed.toLowerCase()).length;
    const newRecycleEntry = {
      id: `cat_del_${Date.now()}`,
      name: trimmed,
      deletedAt: new Date().toISOString(),
      productCount: affectedCount,
    };
    const updatedCategoryRecycle = [newRecycleEntry, ...explicitCategoryRecycleBin];

    const updatedProducts = catalogProducts.map(p => {
      if ((p.category || 'General').trim().toLowerCase() === trimmed.toLowerCase()) {
        return { ...p, category: 'General' };
      }
      return p;
    });

    handleSaveCatalogProducts(updatedProducts, productCatalogData?.recycleBin, productCatalogData?.snapshots, updatedCats, updatedCategoryRecycle);
    if (selectedCatalogCategory.toLowerCase() === trimmed.toLowerCase()) {
      setSelectedCatalogCategory('all');
    }
  };

  const handleRestorePortalCategory = (catRecycleId: string) => {
    const entry = explicitCategoryRecycleBin.find((e: any) => e.id === catRecycleId);
    if (!entry) return;
    const catName = entry.name;
    const updatedCategoryRecycle = explicitCategoryRecycleBin.filter((e: any) => e.id !== catRecycleId);
    const updatedCats = [...explicitCatalogCategories];
    if (!updatedCats.some(c => c.toLowerCase() === catName.toLowerCase())) {
      updatedCats.push(catName);
    }
    handleSaveCatalogProducts(catalogProducts, productCatalogData?.recycleBin, productCatalogData?.snapshots, updatedCats, updatedCategoryRecycle);
    setSelectedCatalogCategory(catName);
    showToast(`Restored category "${catName}"!`, 'success');
  };

  const handleToggleProductCategory = (prodId: string, categoryName: string) => {
    const trimmed = categoryName.trim();
    if (!trimmed) return;
    const updated = catalogProducts.map(p => {
      if (p.id === prodId) {
        const current = getProductCategories(p);
        let nextCats: string[];
        if (current.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
          nextCats = current.filter(c => c.toLowerCase() !== trimmed.toLowerCase());
          if (nextCats.length === 0) nextCats = ['General'];
        } else {
          nextCats = [...current.filter(c => c !== 'General'), trimmed];
        }
        return {
          ...p,
          categories: nextCats,
          category: nextCats[0] || 'General',
        };
      }
      return p;
    });
    setCatalogProducts(updated);
    handleSaveCatalogProducts(updated);
    showToast(`Category updated for item!`, 'success');
  };

  const handleMoveProductToCategoryOnly = (prodId: string, targetCategory: string) => {
    const trimmed = targetCategory.trim();
    if (!trimmed) return;
    const updated = catalogProducts.map(p => {
      if (p.id === prodId) {
        return {
          ...p,
          categories: [trimmed],
          category: trimmed,
        };
      }
      return p;
    });
    setCatalogProducts(updated);
    handleSaveCatalogProducts(updated);
    showToast(`Product moved to "${trimmed}"!`, 'success');
  };

  const handleQuickAddAndAssignCategory = (prodId: string, newCat: string) => {
    const trimmed = newCat.trim();
    if (!trimmed) return;
    let updatedExplicitCats = [...explicitCatalogCategories];
    if (!allCatalogCategories.some(c => c.toLowerCase() === trimmed.toLowerCase())) {
      updatedExplicitCats.push(trimmed);
    }
    const updated = catalogProducts.map(p => {
      if (p.id === prodId) {
        const current = getProductCategories(p);
        const nextCats = Array.from(new Set([...current.filter(c => c !== 'General'), trimmed]));
        return {
          ...p,
          categories: nextCats,
          category: nextCats[0] || 'General',
        };
      }
      return p;
    });
    setCatalogProducts(updated);
    handleSaveCatalogProducts(updated, undefined, undefined, updatedExplicitCats);
    setQuickNewCategoryInput('');
    showToast(`Created category "${trimmed}" and assigned to product!`, 'success');
  };

  // ── Product Orders & Live Courier Tracking Callbacks ──
  const fetchTenantOrders = useCallback(async (statusFilter = ordersFilterStatus, searchQ = ordersSearchQuery) => {
    setIsLoadingOrders(true);
    try {
      const qParams = new URLSearchParams();
      qParams.set('limit', '500');
      if (statusFilter && statusFilter !== 'all') qParams.set('status', statusFilter);
      if (searchQ && searchQ.trim()) qParams.set('search', searchQ.trim());

      const res = await fetchJsonSafe(`/api/tenet/orders?${qParams.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (res && res.success) {
        setOrdersList(res.orders || []);
        setOrdersSummary(res.summary || { total: 0 });
      }
    } catch (err: any) {
      console.warn('[TenetPortalData] Error fetching orders:', err?.message || err);
    } finally {
      setIsLoadingOrders(false);
    }
  }, [ordersFilterStatus, ordersSearchQuery, fetchJsonSafe, getAuthHeaders]);

  const handleUpdateOrderTracking = async (orderId: number, trackingPayload: any) => {
    setIsUpdatingTracking(true);
    try {
      const res = await fetchJsonSafe(`/api/tenet/orders/${orderId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(trackingPayload),
      });
      if (res && res.success) {
        showToast('Order tracking updated successfully!', 'success');
        setEditingOrderTracking(null);
        await fetchTenantOrders();
      } else {
        showToast(res?.error || 'Failed to update tracking', 'error');
      }
    } catch (err: any) {
      showToast('Error updating tracking', 'error');
    } finally {
      setIsUpdatingTracking(false);
    }
  };

  const handleSendCustomerTrackingWhatsApp = async (orderId: number) => {
    setIsNotifyingCustomer(orderId);
    try {
      const res = await fetchJsonSafe(`/api/tenet/orders/${orderId}/notify`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res && res.success) {
        showToast(res.message || 'WhatsApp tracking sent to customer!', 'success');
      } else {
        showToast(res?.error || 'Failed to send WhatsApp tracking', 'error');
      }
    } catch (err: any) {
      showToast('Network error sending WhatsApp', 'error');
    } finally {
      setIsNotifyingCustomer(null);
    }
  };

  const handleToggleCatalogStock = (productId: string, isAvailable: boolean) => {
    const updated = catalogProducts.map((p) => {
      if (p.id === productId) {
        return {
          ...p,
          isAvailable,
          stockQuantity: isAvailable ? (p.stockQuantity && p.stockQuantity > 0 ? p.stockQuantity : 50) : 0
        };
      }
      return p;
    });
    setCatalogProducts(updated);
    handleSaveCatalogProducts(updated);
  };

  const handleDeleteCatalogProduct = (productId: string) => {
    if (!confirm('Are you sure you want to remove this product? It will be moved to the Recycle Bin for safe recovery.')) return;
    const prodToDelete = catalogProducts.find((p) => p.id === productId);
    if (!prodToDelete) return;

    const updated = catalogProducts.filter((p) => p.id !== productId);

    // Auto snapshot
    const autoSnapshot = {
      id: `snap_${Date.now()}`,
      timestamp: new Date().toISOString(),
      label: `Pre-Delete: ${prodToDelete.name}`,
      productCount: catalogProducts.length,
      products: JSON.parse(JSON.stringify(catalogProducts)),
      trigger: 'pre_delete',
    };
    const nextSnapshots = [autoSnapshot, ...(productCatalogData?.snapshots || [])].slice(0, 25);

    // Recycle item
    const recycleItem = {
      id: `rec_${Date.now()}_${prodToDelete.id}`,
      product: prodToDelete,
      deletedAt: new Date().toISOString(),
      deletedBy: 'tenant_portal',
    };
    const nextRecycleBin = [recycleItem, ...(productCatalogData?.recycleBin || [])].slice(0, 50);

    setProductCatalogData((prev: any) => ({
      ...prev,
      recycleBin: nextRecycleBin,
      snapshots: nextSnapshots,
    }));
    setCatalogProducts(updated);
    handleSaveCatalogProducts(updated, nextRecycleBin, nextSnapshots);
    showToast(`Moved "${prodToDelete.name}" to Recycle Bin`, 'success');
  };

  const handleRestorePortalRecycle = (recycleEntryId: string) => {
    const bin: any[] = productCatalogData?.recycleBin || [];
    const item = bin.find(r => r.id === recycleEntryId || r.product?.id === recycleEntryId);
    if (!item) return;

    const restoredProduct = item.product || item;
    const alreadyExists = catalogProducts.some(p => p.id === restoredProduct.id);
    const prodToAdd = alreadyExists
      ? { ...restoredProduct, id: `prod_${Date.now()}`, name: `${restoredProduct.name} (Restored)` }
      : { ...restoredProduct };

    const nextProducts = [prodToAdd, ...catalogProducts];
    const nextRecycleBin = bin.filter(r => r.id !== recycleEntryId && r.product?.id !== recycleEntryId);

    setProductCatalogData((prev: any) => ({
      ...prev,
      recycleBin: nextRecycleBin,
    }));
    setCatalogProducts(nextProducts);
    handleSaveCatalogProducts(nextProducts, nextRecycleBin);
    showToast(`Restored "${restoredProduct.name}" to catalog!`, 'success');
  };

  const handlePermanentDeletePortalRecycle = (recycleEntryId: string) => {
    const bin: any[] = productCatalogData?.recycleBin || [];
    const nextRecycleBin = bin.filter(r => r.id !== recycleEntryId && r.product?.id !== recycleEntryId);
    setProductCatalogData((prev: any) => ({
      ...prev,
      recycleBin: nextRecycleBin,
    }));
    handleSaveCatalogProducts(catalogProducts, nextRecycleBin);
    showToast('Product permanently removed from recycle bin', 'success');
  };

  const handleEmptyPortalRecycleBin = () => {
    setProductCatalogData((prev: any) => ({
      ...prev,
      recycleBin: [],
    }));
    handleSaveCatalogProducts(catalogProducts, []);
    showToast('Recycle bin emptied', 'success');
  };

  const handleRestoreAllPortalRecycle = () => {
    const bin: any[] = productCatalogData?.recycleBin || [];
    if (bin.length === 0) return;
    const restored = bin.map(b => b.product).filter(Boolean);
    const merged = [...catalogProducts];
    for (const p of restored) {
      if (!merged.some(m => m.id === p.id)) {
        merged.push(p);
      }
    }
    setProductCatalogData((prev: any) => ({
      ...prev,
      recycleBin: [],
    }));
    setCatalogProducts(merged);
    handleSaveCatalogProducts(merged, []);
    showToast('Restored all products from recycle bin!', 'success');
  };

  const handleCreatePortalSnapshot = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const newSnapshot = {
      id: `snap_${Date.now()}`,
      timestamp: new Date().toISOString(),
      label: portalSnapshotLabel.trim() || `Manual Snapshot (${catalogProducts.length} Items)`,
      productCount: catalogProducts.length,
      products: JSON.parse(JSON.stringify(catalogProducts)),
      trigger: 'manual',
    };
    const nextSnapshots = [newSnapshot, ...(productCatalogData?.snapshots || [])].slice(0, 25);
    setProductCatalogData((prev: any) => ({
      ...prev,
      snapshots: nextSnapshots,
    }));
    handleSaveCatalogProducts(catalogProducts, productCatalogData?.recycleBin, nextSnapshots);
    setPortalSnapshotLabel('');
    setIsCreatingPortalSnapshot(false);
    showToast('Snapshot checkpoint saved!', 'success');
  };

  const handleRestorePortalSnapshot = (snapId: string) => {
    const snaps: any[] = productCatalogData?.snapshots || [];
    const targetSnap = snaps.find(s => s.id === snapId);
    if (!targetSnap || !Array.isArray(targetSnap.products)) return;

    // Safety pre-rollback snapshot
    const preRollback = {
      id: `snap_${Date.now()}`,
      timestamp: new Date().toISOString(),
      label: `Pre-Rollback State (Restoring: ${targetSnap.label})`,
      productCount: catalogProducts.length,
      products: JSON.parse(JSON.stringify(catalogProducts)),
      trigger: 'restore',
    };
    const nextSnapshots = [preRollback, ...snaps].slice(0, 25);

    setProductCatalogData((prev: any) => ({
      ...prev,
      snapshots: nextSnapshots,
    }));
    setCatalogProducts(targetSnap.products);
    handleSaveCatalogProducts(targetSnap.products, productCatalogData?.recycleBin, nextSnapshots);
    showToast(`Catalog rolled back to "${targetSnap.label}"!`, 'success');
  };

  const handleDeletePortalSnapshot = (snapId: string) => {
    const snaps: any[] = productCatalogData?.snapshots || [];
    const nextSnapshots = snaps.filter(s => s.id !== snapId);
    setProductCatalogData((prev: any) => ({
      ...prev,
      snapshots: nextSnapshots,
    }));
    handleSaveCatalogProducts(catalogProducts, productCatalogData?.recycleBin, nextSnapshots);
    showToast('Snapshot removed', 'success');
  };

  const handleStartEditCatalogProduct = (prod: any) => {
    setEditingCatalogProduct(prod);
    setEditCatalogProdName(prod.name || '');
    const prodCats = getProductCategories(prod);
    setEditCatalogProdCategories(prodCats);
    setEditCatalogProdCategory(prodCats[0] || 'General');
    setEditCategoryTagInput('');
    setEditCatalogProdPrice(String(prod.price || ''));
    setEditCatalogProdOriginalPrice(prod.originalPrice ? String(prod.originalPrice) : '');
    setEditCatalogProdStockQty(prod.stockQuantity !== undefined ? String(prod.stockQuantity) : '50');
    setEditCatalogProdMinPrice(prod.minPrice ? String(prod.minPrice) : '');
    setEditCatalogProdMaxPrice(prod.maxPrice ? String(prod.maxPrice) : '');
    setEditCatalogProdUnit(prod.unitType || 'Pieces');
    setEditCatalogProdDesc(prod.description || '');
    setEditCatalogProdMediaUrl(prod.imageUrl || (prod.media && prod.media[0]?.url) || '');
    setEditCatalogProdKeywords(Array.isArray(prod.searchKeywords) ? prod.searchKeywords.join(', ') : '');
    setEditCatalogProdAvailable(prod.isAvailable !== false);
    const existingMedia = Array.isArray(prod.media) && prod.media.length > 0
      ? [...prod.media]
      : (prod.imageUrl ? [{
          id: `med_${Date.now()}`,
          type: 'image',
          url: prod.imageUrl,
          name: prod.name || 'Product Image'
        }] : []);
    setEditCatalogProdMedia(existingMedia);
    setEditCatalogProdRawText((prod as any).rawSpecs || '');
  };

  const handleSaveEditCatalogProduct = () => {
    if (!editingCatalogProduct || !editCatalogProdName.trim()) return;

    const parsedPrice = parseFloat(editCatalogProdPrice) || 0;
    const parsedOriginal = parseFloat(editCatalogProdOriginalPrice) || undefined;
    const parsedStock = parseInt(editCatalogProdStockQty) || 0;
    const parsedMin = parseFloat(editCatalogProdMinPrice) || undefined;
    const parsedMax = parseFloat(editCatalogProdMaxPrice) || undefined;
    const cleanMediaUrl = editCatalogProdMediaUrl.trim();

    const mediaList: any[] = [...editCatalogProdMedia];
    if (cleanMediaUrl && !mediaList.some(m => m.url === cleanMediaUrl)) {
      const ext = cleanMediaUrl.split('.').pop()?.toLowerCase() || '';
      let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
      if (['mp4', 'mov', 'webm', 'mkv'].includes(ext)) mType = 'video';
      else if (['pdf'].includes(ext)) mType = 'pdf';
      else if (['doc', 'docx', 'txt'].includes(ext)) mType = 'document';

      mediaList.unshift({
        id: `med_${Date.now()}`,
        type: mType,
        url: cleanMediaUrl,
        name: `${editCatalogProdName.trim()} Media`,
      });
    }

    const firstImage = mediaList.find((m: any) => m.type === 'image');
    const primaryImgUrl = firstImage ? firstImage.url : (cleanMediaUrl || editingCatalogProduct.imageUrl);

    const keywordList = editCatalogProdKeywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);

    const finalCategories = editCatalogProdCategories.length > 0
      ? editCatalogProdCategories
      : [editCatalogProdCategory.trim() || 'General'];

    const updated = catalogProducts.map((p) => {
      if (p.id === editingCatalogProduct.id) {
        return {
          ...p,
          name: editCatalogProdName.trim(),
          category: finalCategories[0] || 'General',
          categories: finalCategories,
          price: parsedPrice,
          originalPrice: parsedOriginal,
          minPrice: parsedMin,
          maxPrice: parsedMax,
          stockQuantity: parsedStock,
          unitType: editCatalogProdUnit.trim() || 'Pieces',
          isAvailable: editCatalogProdAvailable && parsedStock > 0,
          description: editCatalogProdDesc.trim(),
          rawSpecs: editCatalogProdRawText.trim() || (editingCatalogProduct as any).rawSpecs,
          imageUrl: primaryImgUrl,
          media: mediaList.length > 0 ? mediaList : undefined,
          searchKeywords: keywordList.length > 0 ? keywordList : undefined,
        };
      }
      return p;
    });

    setCatalogProducts(updated);
    handleSaveCatalogProducts(updated);
    setEditingCatalogProduct(null);
    setEditCatalogProdMedia([]);
    setEditCatalogProdMediaUrl('');
    setEditCatalogProdRawText('');
    showToast('Product updated successfully!', 'success');
  };

  const handleCreateCatalogProduct = () => {
    if (!newCatalogProdName.trim()) return;
    const parsedPrice = parseFloat(newCatalogProdPrice) || 0;
    const parsedOriginal = parseFloat(newCatalogProdOriginalPrice) || undefined;
    const parsedStock = parseInt(newCatalogProdStockQty) || 50;
    const parsedMin = parseFloat(newCatalogProdMinPrice) || undefined;
    const parsedMax = parseFloat(newCatalogProdMaxPrice) || undefined;
    const cleanMediaUrl = newCatalogProdMediaUrl.trim();

    const mediaList: any[] = [...newCatalogProdMedia];
    if (cleanMediaUrl && !mediaList.some(m => m.url === cleanMediaUrl)) {
      const ext = cleanMediaUrl.split('.').pop()?.toLowerCase() || '';
      let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
      if (['mp4', 'mov', 'webm', 'mkv'].includes(ext)) mType = 'video';
      else if (['pdf'].includes(ext)) mType = 'pdf';
      else if (['doc', 'docx', 'txt'].includes(ext)) mType = 'document';

      mediaList.unshift({
        id: `med_${Date.now()}`,
        type: mType,
        url: cleanMediaUrl,
        name: `${newCatalogProdName.trim()} Media`,
      });
    }

    const firstImage = mediaList.find((m: any) => m.type === 'image');
    const primaryImgUrl = firstImage ? firstImage.url : (cleanMediaUrl || undefined);

    const keywordList = newCatalogProdKeywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);

    const finalCategories = newCatalogProdCategories.length > 0
      ? newCatalogProdCategories
      : [newCatalogProdCategory.trim() || 'General'];

    const newP = {
      id: `prod_${Date.now()}`,
      name: newCatalogProdName.trim(),
      category: finalCategories[0] || 'General',
      categories: finalCategories,
      price: parsedPrice,
      originalPrice: parsedOriginal,
      minPrice: parsedMin,
      maxPrice: parsedMax,
      stockQuantity: parsedStock,
      currency: productCatalogData?.defaultCurrency || 'LKR',
      unitType: newCatalogProdUnit.trim() || 'Pieces',
      isAvailable: newCatalogProdAvailable && parsedStock > 0,
      description: newCatalogProdDesc.trim(),
      sku: `SKU-${Math.floor(1000 + Math.random() * 9000)}`,
      imageUrl: primaryImgUrl,
      media: mediaList.length > 0 ? mediaList : undefined,
      searchKeywords: keywordList.length > 0 ? keywordList : undefined,
    };
    const updated = [newP, ...catalogProducts];
    setCatalogProducts(updated);
    handleSaveCatalogProducts(updated);

    // Reset Form
    setNewCatalogProdName('');
    setNewCatalogProdCategories(['General']);
    setNewCatalogProdCategory('General');
    setNewCategoryTagInput('');
    setNewCatalogProdPrice('');
    setNewCatalogProdOriginalPrice('');
    setNewCatalogProdStockQty('50');
    setNewCatalogProdMinPrice('');
    setNewCatalogProdMaxPrice('');
    setNewCatalogProdDesc('');
    setNewCatalogProdMediaUrl('');
    setNewCatalogProdKeywords('');
    setNewCatalogProdMedia([]);
    setNewCatalogProdRawText('');
    setIsAddingCatalogProduct(false);
  };

  const renderPortalProductCard = (prod: any) => {
    const thumbUrl = prod.imageUrl || prod.media?.find((m: any) => m.type === 'image')?.url;
    const imageCount = prod.media?.filter((m: any) => m.type === 'image').length || (prod.imageUrl ? 1 : 0);
    const videoCount = prod.media?.filter((m: any) => m.type === 'video').length || 0;
    const docCount = prod.media?.filter((m: any) => m.type === 'pdf' || m.type === 'document').length || 0;
    const allMedia: any[] = Array.isArray(prod.media) ? prod.media : (prod.imageUrl ? [{ type: 'image', url: prod.imageUrl, name: prod.name }] : []);

    return (
      <div
        key={prod.id}
        className={`p-4 rounded-2xl border transition-all ${
          prod.isAvailable !== false
            ? 'bg-[#0E2018]/90 border-emerald-500/30 hover:border-emerald-400/70 shadow-lg'
            : 'bg-[#08120E]/70 border-slate-800 opacity-60'
        }`}
      >
        <div className="flex items-start gap-3.5">
          {/* Thumbnail */}
          {thumbUrl ? (
            <div
              onClick={() => setSelectedProductMediaPreview({ title: prod.name, url: thumbUrl, type: 'image' })}
              className="w-14 h-14 rounded-xl bg-slate-950 border border-emerald-500/30 overflow-hidden shrink-0 cursor-pointer group/thumb relative"
            >
              <img
                src={thumbUrl}
                alt={prod.name}
                className="w-full h-full object-cover group-hover/thumb:scale-110 transition duration-300"
                onError={(e) => {
                  (e.target as HTMLElement).style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/thumb:opacity-100 flex items-center justify-center transition">
                <ExternalLink size={12} className="text-white" />
              </div>
            </div>
          ) : (
            <div className="w-14 h-14 rounded-xl bg-[#08120E] border border-slate-800 flex items-center justify-center text-slate-600 shrink-0">
              <ShoppingBag size={20} />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-bold text-white truncate font-sans">
                {prod.name}
              </h4>
              {/* Multi-Category Badges & Quick Move/Assign Trigger */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {getProductCategories(prod).map((catName) => (
                  <span
                    key={catName}
                    className="text-[10px] font-mono font-bold text-emerald-300 bg-emerald-500/20 px-2 py-0.5 rounded-full border border-emerald-500/30 flex items-center gap-1 shadow-sm"
                  >
                    <Folder size={10} className="text-emerald-400" />
                    <span>{catName}</span>
                  </span>
                ))}

                {/* Quick Category Move / Multiple Assignment Popover */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setQuickCategoryProductId(quickCategoryProductId === prod.id ? null : prod.id);
                      setQuickNewCategoryInput('');
                    }}
                    className="text-[10px] font-mono font-bold text-emerald-400 hover:text-white bg-emerald-950/70 hover:bg-emerald-900/90 px-2 py-0.5 rounded-full border border-emerald-500/30 hover:border-emerald-400/60 flex items-center gap-1 transition cursor-pointer active:scale-95 shadow-sm"
                    title="Quick Move or Assign Multiple Categories"
                  >
                    <Tag size={10} className="text-emerald-400" />
                    <span>Assign / Move</span>
                    <ChevronDown size={10} className={`transition-transform duration-200 ${quickCategoryProductId === prod.id ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Popover Dropdown */}
                  {quickCategoryProductId === prod.id && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute left-0 top-full mt-2 w-72 bg-[#0A1A13] border border-emerald-500/40 rounded-2xl shadow-2xl p-3 z-50 animate-fade-in space-y-2.5 backdrop-blur-xl"
                    >
                      <div className="flex items-center justify-between pb-1.5 border-b border-emerald-500/20">
                        <span className="text-[11px] font-mono font-bold text-emerald-300 flex items-center gap-1.5">
                          <Tag size={12} className="text-emerald-400" />
                          <span>Categories for Item</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => setQuickCategoryProductId(null)}
                          className="text-slate-400 hover:text-white text-xs p-1 cursor-pointer"
                        >
                          ✕
                        </button>
                      </div>

                      {/* Toggable categories list */}
                      <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                        {allCatalogCategories.map((catName) => {
                          const isAssigned = getProductCategories(prod).some((c) => c.toLowerCase() === catName.toLowerCase());
                          return (
                            <div
                              key={catName}
                              className={`flex items-center justify-between px-2.5 py-1.5 rounded-xl border text-[11px] font-mono transition ${
                                isAssigned
                                  ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200 font-bold'
                                  : 'bg-[#08120E] border-slate-800/80 text-slate-400 hover:border-emerald-500/30 hover:text-slate-200'
                              }`}
                            >
                              <label className="flex items-center gap-2 cursor-pointer flex-1 select-none">
                                <input
                                  type="checkbox"
                                  checked={isAssigned}
                                  onChange={() => handleToggleProductCategory(prod.id, catName)}
                                  className="w-3.5 h-3.5 rounded bg-slate-900 border-slate-700 text-emerald-500 focus:ring-0 focus:ring-offset-0 cursor-pointer"
                                />
                                <Folder size={11} className={isAssigned ? 'text-emerald-400' : 'text-slate-500'} />
                                <span className="truncate">{catName}</span>
                              </label>

                              {/* Quick Move (Relocate) Button */}
                              {!isAssigned && (
                                <button
                                  type="button"
                                  onClick={() => handleMoveProductToCategoryOnly(prod.id, catName)}
                                  className="text-[9px] font-mono text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/20 px-1.5 py-0.5 rounded border border-emerald-500/20 transition cursor-pointer shrink-0 ml-1"
                                  title={`Move product exclusively to "${catName}"`}
                                >
                                  Move Here
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Quick Inline New Category */}
                      <div className="pt-2 border-t border-emerald-500/20 flex items-center gap-1.5">
                        <input
                          type="text"
                          value={quickNewCategoryInput}
                          onChange={(e) => setQuickNewCategoryInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              if (quickNewCategoryInput.trim()) {
                                handleQuickAddAndAssignCategory(prod.id, quickNewCategoryInput);
                              }
                            }
                          }}
                          placeholder="+ New category..."
                          className="flex-1 bg-[#08120E] border border-emerald-500/30 focus:border-emerald-400 rounded-xl px-2 py-1 text-[11px] text-white placeholder-slate-500 focus:outline-none font-mono"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (quickNewCategoryInput.trim()) {
                              handleQuickAddAndAssignCategory(prod.id, quickNewCategoryInput);
                            }
                          }}
                          disabled={!quickNewCategoryInput.trim()}
                          className="px-2.5 py-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 rounded-xl text-[10px] font-mono font-bold transition cursor-pointer"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {prod.sku && (
                <span className="text-[9px] font-mono text-slate-400 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800">
                  {prod.sku}
                </span>
              )}
            </div>

            {prod.description && (
              <p className="text-xs text-slate-300 mt-1 leading-relaxed font-sans line-clamp-2">
                {prod.description}
              </p>
            )}

            {/* Multiple Media & Keyword Badges */}
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              {imageCount > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    const imgMed = allMedia.find(m => m.type === 'image') || { url: thumbUrl, type: 'image' };
                    setSelectedProductMediaPreview({ title: prod.name, url: imgMed.url, type: 'image' });
                  }}
                  className="text-[10px] font-mono text-sky-400 bg-sky-500/10 hover:bg-sky-500/20 px-2 py-0.5 rounded-lg border border-sky-500/20 flex items-center gap-1 transition cursor-pointer"
                >
                  <ImageIcon size={11} />
                  <span>{imageCount} {imageCount === 1 ? 'Photo' : 'Photos'}</span>
                </button>
              )}

              {videoCount > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    const vidMed = allMedia.find(m => m.type === 'video');
                    if (vidMed) setSelectedProductMediaPreview({ title: prod.name, url: vidMed.url, type: 'video' });
                  }}
                  className="text-[10px] font-mono text-purple-400 bg-purple-500/10 hover:bg-purple-500/20 px-2 py-0.5 rounded-lg border border-purple-500/20 flex items-center gap-1 transition cursor-pointer"
                >
                  <Film size={11} />
                  <span>{videoCount} {videoCount === 1 ? 'Video' : 'Videos'}</span>
                </button>
              )}

              {docCount > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    const docMed = allMedia.find(m => m.type === 'pdf' || m.type === 'document');
                    if (docMed) setSelectedProductMediaPreview({ title: prod.name, url: docMed.url, type: 'pdf' });
                  }}
                  className="text-[10px] font-mono text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 px-2 py-0.5 rounded-lg border border-amber-500/20 flex items-center gap-1 transition cursor-pointer"
                >
                  <FileText size={11} />
                  <span>{docCount} {docCount === 1 ? 'PDF Spec' : 'PDFs'}</span>
                </button>
              )}

              {Array.isArray(prod.searchKeywords) && prod.searchKeywords.length > 0 && (
                <span className="text-[10px] font-mono text-slate-400 bg-slate-900/80 px-2 py-0.5 rounded-lg border border-slate-800 flex items-center gap-1">
                  <Tag size={10} className="text-slate-500" />
                  <span>{prod.searchKeywords.length} tags</span>
                </span>
              )}

              <span className={`text-[10px] font-mono px-2 py-0.5 rounded-lg border flex items-center gap-1 ${
                (prod.stockQuantity !== undefined ? prod.stockQuantity : (prod.isAvailable !== false ? 50 : 0)) > 0
                  ? 'text-emerald-300 bg-emerald-950/40 border-emerald-500/30'
                  : 'text-rose-400 bg-rose-950/40 border-rose-500/30'
              }`}>
                <Package size={10} />
                <span>Stock: {prod.stockQuantity !== undefined ? prod.stockQuantity : (prod.isAvailable !== false ? 50 : 0)}</span>
              </span>
            </div>

            {/* Dynamic Specifications & Metrics Vault */}
            {prod.attributes && typeof prod.attributes === 'object' && Object.keys(prod.attributes).length > 0 && (
              <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                {Object.entries(prod.attributes).map(([k, v]) => (
                  <span key={k} className="text-[10px] font-mono text-cyan-300 bg-cyan-950/40 px-2 py-0.5 rounded-lg border border-cyan-500/30 flex items-center gap-1">
                    <Zap size={10} className="text-cyan-400" />
                    <span>{k}: <strong className="text-cyan-100">{String(v)}</strong></span>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="text-right shrink-0 flex flex-col items-end gap-2">
            <div>
              {prod.originalPrice && prod.originalPrice > prod.price && (
                <span className="text-[10px] text-slate-500 line-through block font-mono">
                  {prod.currency || productCatalogData?.defaultCurrency || 'LKR'} {Number(prod.originalPrice).toLocaleString()}
                </span>
              )}
              <span className="text-base font-black text-emerald-300 font-mono">
                {prod.currency || productCatalogData?.defaultCurrency || 'LKR'} {Number(prod.price).toLocaleString()}
              </span>
              <span className="text-[10px] text-slate-400 block font-mono">
                /{prod.unitType || 'Pieces'}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleStartEditCatalogProduct(prod)}
                disabled={isSavingCatalogProducts}
                className="p-1.5 text-slate-400 hover:text-sky-400 hover:bg-sky-500/10 rounded-xl transition cursor-pointer"
                title="Edit Product & Stock"
              >
                <Edit3 size={14} />
              </button>

              <button
                type="button"
                onClick={() => handleToggleCatalogStock(prod.id, !prod.isAvailable)}
                disabled={isSavingCatalogProducts}
                className={`px-2.5 py-1 rounded-xl border text-[10px] font-mono font-bold transition flex items-center gap-1 cursor-pointer ${
                  prod.isAvailable !== false && (prod.stockQuantity === undefined || prod.stockQuantity > 0)
                    ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30'
                    : 'bg-rose-500/20 border-rose-500/40 text-rose-300 hover:bg-rose-500/30'
                }`}
              >
                {prod.isAvailable !== false && (prod.stockQuantity === undefined || prod.stockQuantity > 0) ? (
                  <>
                    <CheckCircle2 size={12} className="text-emerald-400" />
                    <span>In Stock</span>
                  </>
                ) : (
                  <>
                    <XCircle size={12} className="text-rose-400" />
                    <span>Sold Out</span>
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={() => handleDeleteCatalogProduct(prod.id)}
                disabled={isSavingCatalogProducts}
                className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-xl transition cursor-pointer"
                title="Delete Product"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Autonomous Follow-Up & Re-engagement Handlers ──
  const handleSaveFollowUpConfig = async (updates: any) => {
    setIsSavingFollowUp(true);
    try {
      const payload = {
        nodeId: followUpData?.nodeId,
        delayHours: followUpData?.delayHours ?? 24,
        quietHours: followUpData?.quietHours ?? true,
        includeIncentive: followUpData?.includeIncentive ?? false,
        incentiveText: followUpData?.incentiveText ?? 'Free express delivery if confirmed today!',
        filterAbandonedInquiries: followUpData?.filterAbandonedInquiries ?? true,
        filterAbandonedOrders: followUpData?.filterAbandonedOrders ?? true,
        filterUnfinishedForms: followUpData?.filterUnfinishedForms ?? true,
        customTemplate: followUpData?.customTemplate ?? '',
        isEnabled: followUpData?.isEnabled ?? true,
        ...updates,
      };

      const res = await fetch('/api/tenet/followup/save-config', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const resJson = await res.json();
      if (resJson.success) {
        showToast('Follow-up settings saved and synchronized with canvas', 'success');
        setFollowUpData((prev: any) => ({ ...prev, ...payload }));
      } else {
        showToast(resJson.error || 'Failed to save follow-up settings', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error saving follow-up settings', 'error');
    } finally {
      setIsSavingFollowUp(false);
    }
  };

  const handleTriggerRecoveryScan = async () => {
    setIsTriggeringScan(true);
    try {
      const res = await fetch('/api/tenet/followup/trigger-scan', {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const resJson = await res.json();
      if (resJson.success) {
        showToast('Autonomous recovery scan finished!', 'success');
        if (resJson.telemetry) {
          setFollowUpData((prev: any) => ({
            ...prev,
            telemetry: resJson.telemetry,
          }));
        }
      } else {
        showToast(resJson.error || 'Scan error', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error executing recovery scan', 'error');
    } finally {
      setIsTriggeringScan(false);
    }
  };

  const handleDispatchSingleFollowUp = async (conversationId: string) => {
    if (isDispatchingFollowUp) return;
    setIsDispatchingFollowUp(conversationId);
    try {
      const res = await fetch('/api/tenet/followup/dispatch-single', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ conversationId }),
      });
      const resJson = await res.json();
      if (resJson.success) {
        showToast('Autonomous recovery message sent successfully!', 'success');
        if (resJson.telemetry) {
          setFollowUpData((prev: any) => ({
            ...prev,
            telemetry: resJson.telemetry,
          }));
        }
      } else {
        showToast(resJson.error || 'Failed to dispatch follow-up', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error dispatching follow-up', 'error');
    } finally {
      setIsDispatchingFollowUp(null);
    }
  };

  const handleDismissFollowUpCandidate = async (conversationId: string | number) => {
    try {
      const res = await fetch('/api/tenet/followup/dismiss', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ conversationId }),
      });
      const resJson = await res.json();
      if (resJson.success) {
        showToast('Candidate dismissed from queue', 'info');
        if (resJson.telemetry) {
          setFollowUpData((prev: any) => ({
            ...prev,
            telemetry: resJson.telemetry,
          }));
        }
      }
    } catch (err: any) {
      showToast(err?.message || 'Error dismissing candidate', 'error');
    }
  };

  const [testReEngagePhone, setTestReEngagePhone] = useState<string>('');
  const [testReEngageTopic, setTestReEngageTopic] = useState<string>('');
  const [isSendingTestReEngage, setIsSendingTestReEngage] = useState<boolean>(false);

  const handleSendTestReEngagement = async () => {
    if (!testReEngagePhone.trim()) {
      showToast('Please enter a destination phone number', 'error');
      return;
    }
    setIsSendingTestReEngage(true);
    try {
      const res = await fetch('/api/tenet/followup/test-send', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          targetPhone: testReEngagePhone.trim(),
          testTopic: testReEngageTopic.trim() || 'product inquiry',
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message || 'Test re-engagement dispatched!', 'success');
      } else {
        showToast(data.error || 'Failed to dispatch test', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error sending test', 'error');
    } finally {
      setIsSendingTestReEngage(false);
    }
  };

  const handleSaveReviewBoosterConfig = async (updates: any) => {
    setIsSavingReviewBooster(true);
    try {
      const payload = {
        nodeId: reviewBoosterData?.nodeId,
        googleReviewUrl: reviewBoosterData?.googleReviewUrl || '',
        delayHours: reviewBoosterData?.delayHours ?? 2,
        negativeFeedbackFirewall: reviewBoosterData?.negativeFeedbackFirewall ?? true,
        minRatingForGoogleLink: reviewBoosterData?.minRatingForGoogleLink ?? 4,
        includeIncentive: reviewBoosterData?.includeIncentive ?? false,
        incentiveText: reviewBoosterData?.incentiveText || 'Get 5% discount on your next visit!',
        quietHours: reviewBoosterData?.quietHours ?? true,
        customGuidance: reviewBoosterData?.customGuidance || '',
        isEnabled: reviewBoosterData?.isEnabled ?? true,
        ...updates,
      };

      const res = await fetch('/api/tenet/review-booster/save-config', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const resJson = await res.json();
      if (resJson.success) {
        showToast('Review Booster settings saved to canvas!', 'success');
        setReviewBoosterData((prev: any) => ({ ...prev, ...payload }));
      } else {
        showToast(resJson.error || 'Failed to save review booster settings', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error saving review booster settings', 'error');
    } finally {
      setIsSavingReviewBooster(false);
    }
  };

  const handleTriggerReviewScan = async () => {
    setIsTriggeringReviewScan(true);
    try {
      const res = await fetch('/api/tenet/review-booster/trigger-scan', {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const resJson = await res.json();
      if (resJson.success) {
        showToast('Review Booster scan completed!', 'success');
        if (resJson.telemetry) {
          setReviewBoosterData((prev: any) => ({
            ...prev,
            telemetry: resJson.telemetry,
          }));
        }
      } else {
        showToast(resJson.error || 'Scan error', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error executing review scan', 'error');
    } finally {
      setIsTriggeringReviewScan(false);
    }
  };

  // ── Check which nodes exist in active flow ──
  const hasLunaCore = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('lunacore') || n.toLowerCase().includes('core') || n.includes('LUNA_CORE_HUB')
    );
  }, [activeFlowNodes]);

  const hasNeuralCache = hasLunaCore;

  const hasActionNode = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('action') || n.toLowerCase().includes('lead') || n.toLowerCase().includes('formgenerator') || n.includes('ACTION_NODE') || n.includes('FORM_GENERATOR')
    );
  }, [activeFlowNodes]);

  const hasCustomerMemory = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('memory') || n.includes('MEMORY') || n === 'lunaMemoryNode'
    );
  }, [activeFlowNodes]);

  const hasKnowledge = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('knowledge') || n.includes('KNOWLEDGE')
    );
  }, [activeFlowNodes]);

  const hasMediaVault = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('mediavault') || n.toLowerCase().includes('media_vault') || n.toLowerCase().includes('media')
    );
  }, [activeFlowNodes]);

  const hasWhatsApp = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('whatsapp') || n.includes('WHATSAPP')
    );
  }, [activeFlowNodes]);

  const hasIntentAlert = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('intent') || n.includes('INTENT_ALERT')
    );
  }, [activeFlowNodes]);

  const hasProductCatalog = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('catalog') || n.toLowerCase().includes('product') || n.includes('PRODUCT_CATALOG')
    );
  }, [activeFlowNodes]);

  const hasPaymentGateway = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('payment') || n.includes('PAYMENT_GATEWAY')
    );
  }, [activeFlowNodes]);

  const hasCallGuard = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('guard') || n.toLowerCase().includes('callguard') || n.includes('CALL_GUARD')
    );
  }, [activeFlowNodes]);

  const hasFollowUp = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('followup') || n.toLowerCase().includes('follow_up')
    );
  }, [activeFlowNodes]);

  const hasReviewBooster = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('review') || n.toLowerCase().includes('rating')
    );
  }, [activeFlowNodes]);

  const hasSipCall = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('sipcall') || n.toLowerCase().includes('sip_call') || n.includes('SIP_CALL') || n.toLowerCase().includes('pbx')
    );
  }, [activeFlowNodes]);

  const hasWebWidget = useMemo(() => {
    return activeFlowNodes.some((n) =>
      n.toLowerCase().includes('webwidget') || n.toLowerCase().includes('web_widget') || n.includes('WEB_WIDGET')
    );
  }, [activeFlowNodes]);

  const effectiveWhatsAppCount = whatsAppInstances.length > 0 ? whatsAppInstances.length : (hasWhatsApp ? 1 : 0);
  const activeModulesCount = (hasLunaCore ? 1 : 0) + (hasNeuralCache ? 1 : 0) + (hasActionNode ? 1 : 0) + (hasCustomerMemory ? 1 : 0) + (hasKnowledge ? 1 : 0) + (hasMediaVault ? 1 : 0) + effectiveWhatsAppCount + (hasSipCall ? 1 : 0) + (hasIntentAlert ? 1 : 0) + (hasProductCatalog ? 1 : 0) + (hasPaymentGateway ? 1 : 0) + (hasCallGuard ? 1 : 0) + (hasFollowUp ? 1 : 0) + (hasReviewBooster ? 1 : 0) + (hasWebWidget ? 1 : 0);

  // Handle Initial Node Selection from Canvas Quick Jump
  useEffect(() => {
    if (initialNode) {
      if (initialNode.toLowerCase().includes('action') || initialNode.toLowerCase().includes('lead') || initialNode.toLowerCase().includes('form')) {
        setSelectedModule('action_leads');
      } else if (initialNode.toLowerCase().includes('cache')) {
        setSelectedModule('neural_cache');
      } else if (initialNode.toLowerCase().includes('memory')) {
        setSelectedModule('customer_memory');
      } else if (initialNode.toLowerCase().includes('knowledge')) {
        setSelectedModule('knowledge');
      } else if (initialNode.toLowerCase().includes('media')) {
        setSelectedModule('media_vault');
      } else if (initialNode.toLowerCase().includes('whatsapp_web') || initialNode.toLowerCase().includes('wa_web')) {
        if (hasWhatsApp) {
          setSelectedModule('whatsapp_web');
        } else {
          setSelectedModule(null);
        }
      } else if (initialNode.toLowerCase().includes('whatsapp')) {
        if (hasWhatsApp) {
          setSelectedModule('whatsapp_instance');
          const targetId = initialNodeId || (initialNode.startsWith('whatsapp') || initialNode.startsWith('node') ? initialNode : null);
          if (targetId && whatsAppInstances.length > 0) {
            const found = whatsAppInstances.find((i: any) => i.nodeId === targetId);
            if (found) {
              setSelectedWhatsAppNodeId(found.nodeId);
              setWhatsAppData(found);
              if (found.qrCode) {
                setWhatsAppQrCode(found.qrCode);
              }
            }
          }
        } else {
          setSelectedModule(null);
        }
      } else if (initialNode.toLowerCase().includes('intent')) {
        setSelectedModule('intent_alerts');
      } else if (initialNode.toLowerCase().includes('catalog') || initialNode.toLowerCase().includes('product')) {
        setSelectedModule('product_catalog');
      } else if (initialNode.toLowerCase().includes('payment')) {
        setSelectedModule('payment_gateway');
      } else if (initialNode.toLowerCase().includes('guard') || initialNode.toLowerCase().includes('callguard')) {
        setSelectedModule('call_guard');
      } else if (initialNode.toLowerCase().includes('sip') || initialNode.toLowerCase().includes('pbx')) {
        setSelectedModule('sip_call');
      } else if (initialNode.toLowerCase().includes('transfer')) {
        setSelectedModule('sip_call');
      } else if (initialNode.toLowerCase().includes('widget')) {
        setSelectedModule('luna_core');
        setChannelFilter('web_widget');
      } else if (initialNode.toLowerCase().includes('followup') || initialNode.toLowerCase().includes('follow_up')) {
        setSelectedModule('followup_recovery');
      } else if (initialNode.toLowerCase().includes('review') || initialNode.toLowerCase().includes('rating')) {
        setSelectedModule('review_booster');
      } else if (initialNode.toLowerCase().includes('luna') || initialNode.toLowerCase().includes('core')) {
        setSelectedModule('luna_core');
      }
    }
  }, [initialNode, initialNodeId, hasWhatsApp, whatsAppInstances.length]);

  // Auto-remove / close WhatsApp studio modules if WhatsApp Instance Node is removed from visual flow
  useEffect(() => {
    if (!loading && !hasWhatsApp && (selectedModule === 'whatsapp_web' || selectedModule === 'whatsapp_instance')) {
      setSelectedModule(null);
    }
  }, [hasWhatsApp, selectedModule, loading]);

  useEffect(() => {
    if (selectedModule === 'product_catalog') {
      fetchTenantOrders();
    }
  }, [selectedModule, fetchTenantOrders]);


  // ── Filtered Conversations List (Luna Core) ──
  const filteredConversations = useMemo(() => {
    return conversations.filter((c) => {
      if (onlyShowLeadsInChats) {
        const isLead = c.sessionId.startsWith('lead_') ||
          c.usedNodes?.some((n) => n.toLowerCase().includes('lead') || n.toLowerCase().includes('action')) ||
          c.messages?.some((m) => m.toolsUsed?.some((t) => t.includes('lead') || t.includes('form')));
        if (!isLead) return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const matchPhone = (c.phone || '').toLowerCase().includes(q);
        const matchName = (c.visitorName || '').toLowerCase().includes(q);
        const matchMsg = c.messages?.some((m) => (m.text || '').toLowerCase().includes(q));
        if (!matchPhone && !matchName && !matchMsg) return false;
      }
      if (channelFilter === 'voice') {
        const isVoice = c.isVoiceCall || c.channel === 'sip_voice' || c.channel === 'voice_call' || c.sessionId.startsWith('call_');
        if (!isVoice) return false;
      } else if (channelFilter === 'whatsapp') {
        const isWa = c.channel === 'whatsapp' || c.sessionId.startsWith('wa_');
        if (!isWa) return false;
      } else if (channelFilter === 'orders') {
        const isOrder = Boolean((c as any).hasOrder || (c as any).latestOrder || ((c as any).orders && (c as any).orders.length > 0) || c.messages?.some((m) => m.toolsUsed?.some((t) => typeof t === 'string' && t.includes('order'))));
        if (!isOrder) return false;
      } else if (channelFilter === 'web_widget') {
        const isWeb = c.channel === 'web_widget' || c.channel === 'widget' || c.sessionId.startsWith('lead_');
        if (!isWeb) return false;
      }
      if (nodeFilter !== 'all') {
        const hasNode = c.usedNodes?.some((nd) => nd.toLowerCase().includes(nodeFilter.toLowerCase()));
        if (!hasNode) return false;
      }
      return true;
    });
  }, [conversations, searchQuery, channelFilter, nodeFilter, onlyShowLeadsInChats]);

  const activeSession = useMemo(() => {
    return conversations.find((c) => c.sessionId === selectedSessionId) || filteredConversations[0] || null;
  }, [conversations, selectedSessionId, filteredConversations]);

  // ── Auto-scroll chat inspector to bottom (latest messages) by default ──
  useEffect(() => {
    if (activeSession?.sessionId && activeSession?.messages?.length) {
      const scrollFn = (behavior: ScrollBehavior = 'auto') => {
        if (chatScrollContainerRef.current) {
          chatScrollContainerRef.current.scrollTop = chatScrollContainerRef.current.scrollHeight;
        }
        if (chatMessagesEndRef.current) {
          chatMessagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
        }
      };

      scrollFn('auto');
      const t1 = setTimeout(() => scrollFn('auto'), 50);
      const t2 = setTimeout(() => scrollFn('auto'), 200);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [activeSession?.sessionId, activeSession?.messages?.length]);

  const whatsAppOnlyConversations = useMemo(() => {
    return conversations.filter((c) => c.channel === 'whatsapp' || c.sessionId.startsWith('wa_'));
  }, [conversations]);

  const voiceConversations = useMemo(() => {
    return conversations.filter((c) => c.isVoiceCall || c.channel === 'sip_voice' || c.sessionId.startsWith('call_'));
  }, [conversations]);

  const orderConversations = useMemo(() => {
    return conversations.filter((c: any) => {
      if (c.hasOrder || c.latestOrder || (c.orders && c.orders.length > 0)) return true;
      if (Array.isArray(c.messages)) {
        return c.messages.some((m: any) => m?.toolsUsed?.some((t: string) => typeof t === 'string' && t.includes('order')));
      }
      return false;
    });
  }, [conversations]);

  // Match chat to specific WhatsApp instance
  const isChatMatchingSelectedInstance = useCallback((c: any, targetNodeId?: string, targetPhone?: string, targetPhoneId?: string) => {
    const activeNode = targetNodeId || selectedWhatsAppNodeId;
    const activePhone = targetPhone || whatsAppData?.phoneNumber;
    const activeMetaPhoneId = targetPhoneId || whatsAppData?.metaPhoneNumberId;

    if (!activeNode || activeNode === 'all') return true;

    // 1. Direct instanceNodeId or nodeId match
    if (c.instanceNodeId && (c.instanceNodeId === activeNode || c.nodeId === activeNode)) return true;
    if (c.nodeId && c.nodeId === activeNode) return true;

    // 2. Meta Phone ID match
    if (activeMetaPhoneId && (String(c.instancePhoneNumberId || '') === String(activeMetaPhoneId) || (typeof c.sessionId === 'string' && c.sessionId.includes(String(activeMetaPhoneId))))) {
      return true;
    }

    // 3. Phone Number match (clean last 8 digits)
    const cleanActivePhone = activePhone ? String(activePhone).replace(/\D/g, '') : '';
    const cleanChatPhone = c.instancePhoneNumber ? String(c.instancePhoneNumber).replace(/\D/g, '') : '';
    if (cleanActivePhone && cleanActivePhone.length >= 8 && cleanChatPhone && cleanChatPhone.length >= 8) {
      if (cleanChatPhone.includes(cleanActivePhone.slice(-8)) || cleanActivePhone.includes(cleanChatPhone.slice(-8))) {
        return true;
      }
    }

    // Fallback: If only 1 instance exists or chat has no attribution, keep visible
    if (whatsAppInstances.length <= 1) return true;

    return false;
  }, [selectedWhatsAppNodeId, whatsAppData?.phoneNumber, whatsAppData?.metaPhoneNumberId, whatsAppInstances.length]);

  const activeInstanceWhatsAppConversations = useMemo(() => {
    if (selectedWhatsAppNodeId === 'all' || whatsAppInstances.length <= 1) {
      return whatsAppOnlyConversations;
    }
    return whatsAppOnlyConversations.filter((c) => isChatMatchingSelectedInstance(c));
  }, [whatsAppOnlyConversations, selectedWhatsAppNodeId, whatsAppInstances.length, isChatMatchingSelectedInstance]);

  // ── Leads Filtering & Analytics ──
  const filteredLeads = useMemo(() => {
    return leadsList.filter((lead) => {
      if (leadsFilter === 'new' && lead.status !== 'new') return false;
      if (leadsFilter === 'contacted' && lead.status !== 'contacted') return false;
      if (leadsFilter === 'converted' && lead.status !== 'converted') return false;
      if (leadsFilter === 'whatsapp') {
        const isWa = lead.formType === 'whatsapp_lead' || lead.formType === 'whatsapp' || (lead.sessionId && lead.sessionId.includes('whatsapp'));
        if (!isWa) return false;
      }
      if (leadsFilter === 'web') {
        const isWa = lead.formType === 'whatsapp_lead' || lead.formType === 'whatsapp' || (lead.sessionId && lead.sessionId.includes('whatsapp'));
        if (isWa) return false;
      }

      if (leadsSearchQuery.trim()) {
        const q = leadsSearchQuery.toLowerCase().trim();
        const matchName = (lead.visitorName || '').toLowerCase().includes(q);
        const matchPhone = (lead.visitorPhone || '').toLowerCase().includes(q);
        const matchEmail = (lead.visitorEmail || '').toLowerCase().includes(q);
        const matchNotes = (lead.notes || '').toLowerCase().includes(q);
        if (!matchName && !matchPhone && !matchEmail && !matchNotes) return false;
      }
      return true;
    });
  }, [leadsList, leadsFilter, leadsSearchQuery]);

  const handleUpdateLeadStatus = async (leadId: number, status: string) => {
    setUpdatingLeadId(leadId);
    try {
      const res = await fetch(`/api/tenet/forms/${leadId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (data.success) {
        setLeadsList((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
        showToast(`Lead status updated to ${status.toUpperCase()}`, 'success');
      } else {
        showToast(data.error || 'Failed to update lead status', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error updating lead', 'error');
    } finally {
      setUpdatingLeadId(null);
    }
  };

  const handleExportLeadsCSV = () => {
    if (leadsList.length === 0) {
      showToast('No leads to export', 'error');
      return;
    }
    const headers = ['ID', 'Visitor Name', 'Phone', 'Email', 'Channel/Type', 'Status', 'Notes', 'Date'];
    const rows = leadsList.map((l) => [
      l.id,
      `"${(l.visitorName || '').replace(/"/g, '""')}"`,
      `"${(l.visitorPhone || '').replace(/"/g, '""')}"`,
      `"${(l.visitorEmail || '').replace(/"/g, '""')}"`,
      `"${(l.formType || '').replace(/"/g, '""')}"`,
      `"${(l.status || '').replace(/"/g, '""')}"`,
      `"${(l.notes || '').replace(/"/g, '""')}"`,
      `"${formatDateTime(l.createdAt || l.submittedAt || Date.now())}"`,
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `luna_captured_leads_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Leads CSV exported successfully!', 'success');
  };

  const handleSaveActionNodeSettings = async (targetDestination = actionData?.targetDestination || '', actionType = actionData?.actionType || 'whatsapp_lead') => {
    setIsSavingActionNode(true);
    try {
      const res = await fetch('/api/admin/luna-flows/save-node-data', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nodeId: actionData?.nodeId || 'actionNode-1',
          widgetType: 'ACTION_NODE',
          data: {
            targetDestination,
            actionType,
            autoExecute: true,
          },
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Action Node lead routing saved to database!', 'success');
        setActionData((prev) => ({
          ...(prev || {}),
          targetDestination,
          actionType,
        }));
      } else {
        showToast(data.error || 'Failed to save action settings', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error saving action node', 'error');
    } finally {
      setIsSavingActionNode(false);
    }
  };

  // ── Knowledge Parsed Sections & FAQs ──
  const parsedSections = useMemo(() => {
    return parseMarkdownSections(knowledgeText);
  }, [knowledgeText]);

  const allExtractedFAQs = useMemo(() => {
    return extractAllFAQs(parsedSections);
  }, [parsedSections]);

  const filteredSections = useMemo(() => {
    return parsedSections.filter((sec) => {
      if (selectedKnowledgeCategory !== 'all' && sec.category !== selectedKnowledgeCategory) {
        return false;
      }
      if (knowledgeSearchQuery.trim()) {
        const q = knowledgeSearchQuery.toLowerCase().trim();
        const matchTitle = sec.title.toLowerCase().includes(q);
        const matchContent = sec.content.toLowerCase().includes(q);
        if (!matchTitle && !matchContent) return false;
      }
      return true;
    });
  }, [parsedSections, selectedKnowledgeCategory, knowledgeSearchQuery]);

  const filteredFAQs = useMemo(() => {
    return allExtractedFAQs.filter((faq) => {
      if (knowledgeSearchQuery.trim()) {
        const q = knowledgeSearchQuery.toLowerCase().trim();
        return faq.question.toLowerCase().includes(q) || faq.answer.toLowerCase().includes(q);
      }
      return true;
    });
  }, [allExtractedFAQs, knowledgeSearchQuery]);

  // ── Media Vault Distinct Vaults & Filtered Assets ──
  // ── Media Vault Distinct Vaults & Filtered Assets ──
  const distinctMediaVaults = useMemo(() => {
    const set = new Set<string>();
    mediaAssets.forEach((a) => {
      if (typeof a.vaultName === 'string' && a.vaultName.trim()) {
        set.add(a.vaultName.trim());
      }
    });
    return Array.from(set);
  }, [mediaAssets]);

  // Real Aggregated Files List Across All Cards
  const allMediaFiles = useMemo(() => {
    const files: { url: string; type: 'image' | 'pdf' | 'video'; cardTitle: string; vaultName?: string }[] = [];
    mediaAssets.forEach((a) => {
      const cardUrls = Array.from(
        new Set(
          [
            ...(Array.isArray(a.galleryUrls) ? a.galleryUrls : []),
            ...(a.secureUrl ? [a.secureUrl] : []),
          ].filter(Boolean)
        )
      );
      cardUrls.forEach((url) => {
        files.push({
          url,
          type: detectMediaType(url, a.mediaType || 'image'),
          cardTitle: a.title || 'Media Asset',
          vaultName: a.vaultName,
        });
      });
    });
    return files;
  }, [mediaAssets]);

  const totalFilesCount = allMediaFiles.length;
  const totalCardsCount = mediaAssets.length;
  const imageCount = useMemo(() => allMediaFiles.filter((f) => f.type === 'image').length, [allMediaFiles]);
  const pdfCount = useMemo(() => allMediaFiles.filter((f) => f.type === 'pdf').length, [allMediaFiles]);
  const videoCount = useMemo(() => allMediaFiles.filter((f) => f.type === 'video').length, [allMediaFiles]);

  const filteredMediaAssets = useMemo(() => {
    return mediaAssets.filter((a) => {
      const cardUrls = Array.from(
        new Set(
          [
            ...(Array.isArray(a.galleryUrls) ? a.galleryUrls : []),
            ...(a.secureUrl ? [a.secureUrl] : []),
          ].filter(Boolean)
        )
      );

      // Filter by media type (if user clicked Images, PDFs, or Videos)
      if (mediaFilterType !== 'all') {
        const hasMatchingType = cardUrls.some((u) => detectMediaType(u, a.mediaType || 'image') === mediaFilterType);
        if (!hasMatchingType) return false;
      }

      // Filter by category / vault
      const aVault = typeof a.vaultName === 'string' ? a.vaultName.trim() : '';
      if (selectedVaultCategory !== 'all' && aVault !== selectedVaultCategory) return false;

      // Filter by search query
      if (mediaSearchQuery.trim()) {
        const q = mediaSearchQuery.toLowerCase().trim();
        const matchTitle = (typeof a.title === 'string' ? a.title : '').toLowerCase().includes(q);
        const matchTags = (typeof a.tags === 'string' ? a.tags : '').toLowerCase().includes(q);
        const matchVault = aVault.toLowerCase().includes(q);
        const matchUrl = cardUrls.some((u) => u.toLowerCase().includes(q));
        if (!matchTitle && !matchTags && !matchVault && !matchUrl) return false;
      }
      return true;
    });
  }, [mediaAssets, mediaFilterType, selectedVaultCategory, mediaSearchQuery]);

  // ── Save Knowledge Base to DB ──
  const handleSaveKnowledge = async (textToSave = knowledgeText) => {
    setIsSavingKnowledge(true);
    try {
      const res = await fetch('/api/admin/luna-flows/save-node-data', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nodeId: knowledgeData?.nodeId || 'knowledgeNode-1',
          data: {
            contextSource: textToSave,
            lastTrainedAt: new Date().toISOString(),
            knowledgeType: knowledgeData?.knowledgeType || 'pdf',
          },
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Knowledge Base synchronized and saved to database!', 'success');
        setKnowledgeData((prev) => ({
          ...(prev || {}),
          contextSource: textToSave,
          lastTrainedAt: new Date().toISOString(),
        }));
      } else {
        showToast(data.error || 'Failed to save knowledge', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error saving knowledge', 'error');
    } finally {
      setIsSavingKnowledge(false);
    }
  };

  // ── Save Media Vault Assets to DB ──
  const handleSaveMediaAssets = async (updatedAssets = mediaAssets) => {
    setIsSavingMedia(true);
    try {
      const res = await fetch('/api/admin/luna-flows/save-node-data', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nodeId: mediaVaultData?.nodeId || 'mediaVaultNode-1',
          widgetType: 'KNOWLEDGE_UPLOAD',
          data: {
            assets: updatedAssets,
          },
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Media Vault assets saved to database!', 'success');
        setMediaVaultData((prev) => ({
          ...(prev || {}),
          assets: updatedAssets,
          lastUpdatedAt: new Date().toISOString(),
        }));
      } else {
        showToast(data.error || 'Failed to save media assets', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error saving media assets', 'error');
    } finally {
      setIsSavingMedia(false);
    }
  };

  const handleAddMediaAsset = (vaultNameInput?: any) => {
    const safeVault = typeof vaultNameInput === 'string' && vaultNameInput.trim()
      ? vaultNameInput.trim()
      : (selectedVaultCategory !== 'all' ? selectedVaultCategory : 'General Vault');

    const newAsset: MediaVaultAsset = {
      id: `asset-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      mediaType: 'image',
      title: 'New Media Asset',
      tags: 'product, catalog',
      secureUrl: '',
      galleryUrls: [],
      vaultName: safeVault,
    };
    const next = [newAsset, ...mediaAssets];
    setMediaAssets(next);
    handleSaveMediaAssets(next);
    showToast('New media card added!', 'success');
  };

  // Batch Upload Multiple Media Files at Once
  const handleBatchMediaUpload = async (files: FileList | File[], targetAssetId?: string) => {
    if (!files || files.length === 0) return;
    setIsBatchUploadingMedia(true);

    try {
      const fileArray = Array.from(files);
      const uploadedAssets: { url: string; originalName: string; mediaType: 'image' | 'pdf' | 'video' }[] = [];

      for (const file of fileArray) {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch('/api/admin/luna-flows/upload-asset', {
          method: 'POST',
          headers: getAuthUploadHeaders(),
          body: formData,
        });
        const result = await res.json();

        if (result.success && result.url) {
          const fullUrl = result.url.startsWith('http') ? result.url : `${window.location.origin}${result.url}`;
          let detectedType: 'image' | 'pdf' | 'video' = 'image';
          if (result.mimeType === 'application/pdf' || file.name.endsWith('.pdf') || file.type === 'application/pdf') {
            detectedType = 'pdf';
          } else if (result.mimeType?.startsWith('video/') || file.type.startsWith('video/') || /\.(mp4|mov|webm|mkv|avi)$/i.test(file.name)) {
            detectedType = 'video';
          }

          uploadedAssets.push({
            url: fullUrl,
            originalName: result.originalName || file.name,
            mediaType: detectedType,
          });
        } else {
          showToast(result.error || `Upload failed for ${file.name}`, 'error');
        }
      }

      if (uploadedAssets.length > 0) {
        if (targetAssetId) {
          // Append to target asset gallery
          const targetUrls = uploadedAssets.map((u) => u.url);
          const next = mediaAssets.map((a) => {
            if (a.id !== targetAssetId) return a;
            const existing = Array.isArray(a.galleryUrls) ? a.galleryUrls : [];
            const combined = Array.from(new Set([...existing, ...targetUrls]));
            return {
              ...a,
              secureUrl: a.secureUrl || targetUrls[0],
              galleryUrls: combined,
            };
          });
          setMediaAssets(next);
          handleSaveMediaAssets(next);
          showToast(`Added ${targetUrls.length} file(s) to asset gallery!`, 'success');
        } else {
          // Create multiple new assets
          const newAssets: MediaVaultAsset[] = uploadedAssets.map((u, i) => {
            const rawTitle = u.originalName.replace(/\.[^/.]+$/, '');
            return {
              id: `asset-${Date.now()}-${i}-${Math.random().toString(36).substr(2, 4)}`,
              mediaType: u.mediaType,
              title: rawTitle,
              tags: rawTitle.toLowerCase().replace(/[^a-z0-9]+/g, ', '),
              secureUrl: u.url,
              galleryUrls: [u.url],
              vaultName: selectedVaultCategory !== 'all' ? selectedVaultCategory : 'General Vault',
            };
          });

          const next = [...newAssets, ...mediaAssets];
          setMediaAssets(next);
          handleSaveMediaAssets(next);
          showToast(`Batch created ${newAssets.length} media assets!`, 'success');
        }
      }
    } catch (err: any) {
      showToast(err?.message || 'Error during batch media upload', 'error');
    } finally {
      setIsBatchUploadingMedia(false);
    }
  };

  const handleMultiFileUploadForAsset = async (assetId: string, files: FileList | File[]) => {
    return handleBatchMediaUpload(files, assetId);
  };

  const handleUpdateMediaAsset = (id: string, field: keyof MediaVaultAsset, val: any) => {
    const next = mediaAssets.map((a) => (a.id === id ? { ...a, [field]: val } : a));
    setMediaAssets(next);
  };

  const handleDeleteMediaAsset = (id: string) => {
    const next = mediaAssets.filter((a) => a.id !== id);
    setMediaAssets(next);
    handleSaveMediaAssets(next);
    showToast('Asset removed from Media Vault', 'success');
  };

  const handleMediaFileUpload = async (assetId: string, file: File) => {
    if (!file) return;
    setUploadingAssetId(assetId);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/admin/luna-flows/upload-asset', {
        method: 'POST',
        headers: getAuthUploadHeaders(),
        body: formData,
      });
      const result = await res.json();

      if (result.success && result.url) {
        const fullUrl = result.url.startsWith('http') ? result.url : `${window.location.origin}${result.url}`;
        let detectedType: MediaVaultAsset['mediaType'] = 'image';
        if (result.mimeType === 'application/pdf' || file.name.endsWith('.pdf') || file.type === 'application/pdf') {
          detectedType = 'pdf';
        } else if (result.mimeType?.startsWith('video/') || file.type.startsWith('video/') || /\.(mp4|mov|webm|mkv|avi)$/i.test(file.name)) {
          detectedType = 'video';
        }

        const next = mediaAssets.map((a) => {
          if (a.id === assetId) {
            return {
              ...a,
              title: a.title === 'New Media Asset' ? file.name : a.title,
              secureUrl: fullUrl,
              mediaType: detectedType,
              galleryUrls: a.galleryUrls && a.galleryUrls.length > 0 ? a.galleryUrls : [fullUrl],
            };
          }
          return a;
        });

        setMediaAssets(next);
        handleSaveMediaAssets(next);
        showToast(`Uploaded ${file.name}!`, 'success');
      } else {
        showToast(result.error || 'Upload failed', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error during upload', 'error');
    } finally {
      setUploadingAssetId(null);
    }
  };

  // ── Execute Document Training & Ingestion ──
  const handleFileDrop = async (file: File) => {
    if (!file) return;
    setIsTraining(true);
    setTrainingPhase('uploading');
    setTrainingError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('nodeId', knowledgeData?.nodeId || 'knowledgeNode-1');
      formData.append('mergeMode', ingestMode);
      formData.append('existingKnowledge', ingestMode === 'replace' ? '' : knowledgeText);
      formData.append('knowledgeType', 'pdf');

      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      if (tenant?.apiKey) headers['x-tenant-api-key'] = tenant.apiKey;

      setTrainingPhase('synthesizing');

      const res = await fetch('/api/admin/luna-flows/train-knowledge', {
        method: 'POST',
        headers,
        body: formData,
      });
      const result = await res.json();

      if (!res.ok || !result.success) {
        throw new Error(result.error || `Training failed with status ${res.status}`);
      }

      setKnowledgeText(result.structuredKnowledge);
      setKnowledgeData((prev) => ({
        ...(prev || {}),
        contextSource: result.structuredKnowledge,
        lastTrainedAt: new Date().toISOString(),
        lastTrainedFileName: file.name,
        lastTrainedFileSize: file.size,
        autoKeywords: result.autoKeywords || '',
      }));

      showToast(`Successfully trained AI with ${file.name}!`, 'success');
      setKnowledgeViewMode('explorer');
    } catch (err: any) {
      const cleanErr = String(err?.message || 'Failed to train knowledge base.')
        .replace(/models\/[a-zA-Z0-9._-]+/gi, 'Luna AI')
        .replace(/gemini-[a-zA-Z0-9._-]+/gi, 'Luna AI')
        .replace(/GoogleGenAI/gi, 'Luna AI')
        .replace(/Gemini/gi, 'Luna AI');
      setTrainingError(cleanErr);
      showToast('Training failed: ' + cleanErr, 'error');
    } finally {
      setIsTraining(false);
      setTrainingPhase(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Execute Raw Text Knowledge Synthesis & Training ──
  const handleRawTextTraining = async () => {
    if (!rawTextInput.trim()) {
      showToast('Please paste some text to train AI.', 'error');
      return;
    }
    setIsTraining(true);
    setTrainingPhase('synthesizing');
    setTrainingError(null);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      if (tenant?.apiKey) headers['x-tenant-api-key'] = tenant.apiKey;

      const res = await fetch('/api/admin/luna-flows/train-raw-text', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          rawText: rawTextInput.trim(),
          sourceTitle: rawTextTitle.trim() || 'Pasted Notes',
          nodeId: knowledgeData?.nodeId || 'knowledgeNode-1',
          mergeMode: ingestMode,
          existingKnowledge: ingestMode === 'replace' ? '' : knowledgeText,
          knowledgeType: 'pdf',
        }),
      });

      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.error || `Training failed with status ${res.status}`);
      }

      setKnowledgeText(result.structuredKnowledge);
      setKnowledgeData((prev) => ({
        ...(prev || {}),
        contextSource: result.structuredKnowledge,
        lastTrainedAt: new Date().toISOString(),
        lastTrainedFileName: rawTextTitle.trim() || 'Pasted Notes',
        lastTrainedFileSize: result.fileSizeBytes || rawTextInput.length,
        autoKeywords: result.autoKeywords || '',
      }));

      setRawTextInput('');
      setRawTextTitle('');
      showToast(`Successfully synthesized and trained AI from text!`, 'success');
      setKnowledgeViewMode('explorer');
    } catch (err: any) {
      console.error('[TenetPortalData] Raw text training error:', err);
      const cleanErr = String(err?.message || 'Failed to train knowledge base from pasted text.')
        .replace(/models\/[a-zA-Z0-9._-]+/gi, 'Luna AI')
        .replace(/gemini-[a-zA-Z0-9._-]+/gi, 'Luna AI')
        .replace(/GoogleGenAI/gi, 'Luna AI')
        .replace(/Gemini/gi, 'Luna AI');
      setTrainingError(cleanErr);
      showToast('Training failed: ' + cleanErr, 'error');
    } finally {
      setIsTraining(false);
      setTrainingPhase(null);
    }
  };

  // ── Execute Website URL Scraping Knowledge Synthesis & Training ──
  const handleWebsiteScrapeTraining = async () => {
    if (!targetWebsiteUrl.trim()) {
      showToast('Please enter a valid website URL to scrape.', 'error');
      return;
    }
    setIsTraining(true);
    setTrainingPhase('uploading');
    setTrainingError(null);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      if (tenant?.apiKey) headers['x-tenant-api-key'] = tenant.apiKey;

      setTrainingPhase('synthesizing');

      const res = await fetch('/api/admin/luna-flows/train-website-url', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          url: targetWebsiteUrl.trim(),
          nodeId: knowledgeData?.nodeId || 'knowledgeNode-1',
          mergeMode: ingestMode,
          existingKnowledge: ingestMode === 'replace' ? '' : knowledgeText,
          knowledgeType: 'pdf',
          depth: urlCrawlDepth,
        }),
      });

      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.error || `Website scraping failed with status ${res.status}`);
      }

      setKnowledgeText(result.structuredKnowledge);
      setKnowledgeData((prev) => ({
        ...(prev || {}),
        contextSource: result.structuredKnowledge,
        lastTrainedAt: new Date().toISOString(),
        lastTrainedFileName: `Web Scrape: ${targetWebsiteUrl.trim().replace(/^https?:\/\//, '')}`,
        lastTrainedFileSize: result.fileSizeBytes || 0,
        autoKeywords: result.autoKeywords || '',
      }));

      setTargetWebsiteUrl('');
      showToast(`Successfully scraped and synthesized AI knowledge from website!`, 'success');
      setKnowledgeViewMode('explorer');
    } catch (err: any) {
      console.error('[TenetPortalData] Website scrape training error:', err);
      const cleanErr = String(err?.message || 'Failed to scrape and train knowledge base from website.')
        .replace(/models\/[a-zA-Z0-9._-]+/gi, 'Luna AI')
        .replace(/gemini-[a-zA-Z0-9._-]+/gi, 'Luna AI')
        .replace(/GoogleGenAI/gi, 'Luna AI')
        .replace(/Gemini/gi, 'Luna AI');
      setTrainingError(cleanErr);
      showToast('Training failed: ' + cleanErr, 'error');
    } finally {
      setIsTraining(false);
      setTrainingPhase(null);
    }
  };

  // ── Toggle Human Takeover (Mute / Unmute Luna AI) ──
  const [togglingTakeover, setTogglingTakeover] = useState(false);
  const [takeoverRemainingSeconds, setTakeoverRemainingSeconds] = useState<number | null>(null);

  // ── Takeover Countdown Timer (30 min auto-resume tick) ──
  useEffect(() => {
    if (!activeSession || !activeSession.isHumanTakeover) {
      setTakeoverRemainingSeconds(null);
      return;
    }

    const updateTimer = () => {
      if (activeSession.takeoverMode === 'permanent') {
        setTakeoverRemainingSeconds(null);
        return;
      }

      let expiresMs = 0;
      if (activeSession.takeoverExpiresAt) {
        expiresMs = new Date(activeSession.takeoverExpiresAt).getTime();
      } else if (activeSession.takeoverAt || activeSession.lastActiveAt) {
        const base = new Date(activeSession.takeoverAt || activeSession.lastActiveAt || '').getTime();
        expiresMs = base + 30 * 60 * 1000;
      } else {
        expiresMs = Date.now() + 30 * 60 * 1000;
      }

      const diffSec = Math.max(0, Math.floor((expiresMs - Date.now()) / 1000));
      setTakeoverRemainingSeconds(diffSec);

      if (diffSec === 0) {
        // Auto-resume Luna AI locally when 30m countdown expires
        setConversations((prev) =>
          prev.map((s) =>
            s.sessionId === activeSession.sessionId
              ? { ...s, isHumanTakeover: false, takeoverExpiresAt: null }
              : s
          )
        );
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [activeSession?.sessionId, activeSession?.isHumanTakeover, activeSession?.takeoverExpiresAt, activeSession?.takeoverMode]);

  const formatCountdown = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleToggleTakeover = async (
    sessionId: string,
    currentVal: boolean,
    targetMode: 'timed_30m' | 'permanent' = 'timed_30m',
    durationMinutes: number = 30
  ) => {
    setTogglingTakeover(true);
    const newVal = !currentVal;
    const expiresAt = newVal && targetMode === 'timed_30m' 
      ? new Date(Date.now() + durationMinutes * 60 * 1000).toISOString()
      : null;

    try {
      // Optimistic state update
      setConversations((prev) =>
        prev.map((s) =>
          s.sessionId === sessionId
            ? {
                ...s,
                isHumanTakeover: newVal,
                takeoverMode: targetMode,
                takeoverExpiresAt: expiresAt,
                humanRequested: false,
              }
            : s
        )
      );

      const res = await fetch('/api/tenet/live-chats/toggle-takeover', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sessionId,
          isHumanTakeover: newVal,
          takeoverMode: targetMode,
          durationMinutes,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(
          newVal
            ? targetMode === 'permanent'
              ? '🔒 Permanent Human Takeover ACTIVATED! Luna AI auto-replies muted indefinitely.'
              : '⏱️ 30-Minute Human Takeover ACTIVATED! (Luna AI auto-resumes in 30m)'
            : '🤖 Human Takeover DISABLED. Luna AI resumed auto-replies.',
          'success'
        );
        if (data.takeoverExpiresAt || data.takeoverMode) {
          setConversations((prev) =>
            prev.map((s) =>
              s.sessionId === sessionId
                ? { ...s, takeoverExpiresAt: data.takeoverExpiresAt, takeoverMode: data.takeoverMode }
                : s
            )
          );
        }
      } else {
        showToast(data.error || 'Failed to toggle takeover', 'error');
        setConversations((prev) =>
          prev.map((s) => (s.sessionId === sessionId ? { ...s, isHumanTakeover: currentVal } : s))
        );
      }
    } catch (err: any) {
      showToast('Error toggling takeover mode', 'error');
      setConversations((prev) =>
        prev.map((s) => (s.sessionId === sessionId ? { ...s, isHumanTakeover: currentVal } : s))
      );
    } finally {
      setTogglingTakeover(false);
    }
  };

  // ── Send Human / Admin WhatsApp Reply ──
  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeSession || !replyText.trim()) return;

    setSendingReply(true);
    const textToSend = replyText.trim();
    const effectiveMode = activeSession.takeoverMode || 'timed_30m';
    const expiresAt = effectiveMode === 'timed_30m' ? new Date(Date.now() + 30 * 60 * 1000).toISOString() : null;

    try {
      const res = await fetch('/api/tenet/live-chats/reply', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sessionId: activeSession.sessionId,
          phone: activeSession.phone,
          message: textToSend,
          takeoverMode: effectiveMode,
          durationMinutes: 30,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message || '✅ Human reply delivered to customer WhatsApp!', 'success');
        setConversations((prev) =>
          prev.map((s) => {
            if (s.sessionId === activeSession.sessionId) {
              return {
                ...s,
                isHumanTakeover: true,
                takeoverMode: effectiveMode,
                takeoverExpiresAt: data.takeoverExpiresAt || expiresAt,
                humanRequested: false,
                messages: [
                  ...s.messages,
                  {
                    sender: 'human_admin',
                    role: 'user',
                    text: textToSend,
                    time: new Date().toLocaleTimeString(),
                    timestamp: new Date().toISOString(),
                    nodeUsed: 'Human Admin Takeover',
                    tokens: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
                  },
                ],
              };
            }
            return s;
          })
        );
        setReplyText('');
        setTimeout(() => {
          if (chatScrollContainerRef.current) {
            chatScrollContainerRef.current.scrollTop = chatScrollContainerRef.current.scrollHeight;
          }
          chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 80);
      } else {
        showToast(data.error || 'Failed to dispatch reply to customer WhatsApp', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error sending WhatsApp reply', 'error');
    } finally {
      setSendingReply(false);
    }
  };

  // Download Markdown file
  const handleDownloadMarkdown = () => {
    const blob = new Blob([knowledgeText], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `luna-knowledge-base-${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast('Knowledge Base exported as Markdown!', 'success');
  };

  // Drag & Drop handlers for Module Cards Reordering
  const handleDragStart = (moduleKey: string) => {
    setDraggedModule(moduleKey);
  };

  const handleDragOver = (e: React.DragEvent, targetModuleKey: string) => {
    e.preventDefault();
    if (draggedModule && draggedModule !== targetModuleKey) {
      setDragOverModule(targetModuleKey);
    }
  };

  const handleDrop = (targetModuleKey: string) => {
    if (!draggedModule || draggedModule === targetModuleKey) return;
    const currentOrder = [...moduleOrder];
    const fromIndex = currentOrder.indexOf(draggedModule);
    const toIndex = currentOrder.indexOf(targetModuleKey);
    if (fromIndex !== -1 && toIndex !== -1) {
      currentOrder.splice(fromIndex, 1);
      currentOrder.splice(toIndex, 0, draggedModule);
      saveModuleOrder(currentOrder);
      showToast('Module layout updated & saved!', 'success');
    }
    setDraggedModule(null);
    setDragOverModule(null);
  };

  const handleDragEnd = () => {
    setDraggedModule(null);
    setDragOverModule(null);
  };

  // ── WhatsApp Connect & Settings Handlers ──
  const handleConnectWhatsApp = async () => {
    setIsConnectingWhatsApp(true);
    try {
      const res = await fetch('/api/tenet/whatsapp/connect', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nodeId: whatsAppData?.nodeId,
          instanceName: whatsAppData?.instanceName,
          isPersonalWhatsApp: Boolean(whatsAppData?.isPersonalWhatsApp),
          phoneNumber: whatsAppData?.phoneNumber,
          apiMode: whatsAppData?.apiMode,
        }),
      });
      const data = await res.json();
      if (data.success) {
        const receivedQr = data.qrCode || data.qrcode;
        if (receivedQr) {
          setWhatsAppQrCode(receivedQr);
          showToast('Live QR Code generated! Scan with WhatsApp.', 'success');
        } else if (data.status === 'connected' || data.connected) {
          showToast('WhatsApp is already connected & active!', 'success');
          fetchPortalData(true);
        }
      } else {
        showToast(data.error || 'Failed to connect WhatsApp', 'error');
      }
    } catch (err: any) {
      showToast('Network error connecting WhatsApp', 'error');
    } finally {
      setIsConnectingWhatsApp(false);
    }
  };

  const handleDisconnectWhatsApp = async () => {
    if (!confirm('Are you sure you want to disconnect this WhatsApp instance?')) return;
    setIsDisconnectingWhatsApp(true);
    try {
      const res = await fetch('/api/tenet/whatsapp/disconnect', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nodeId: whatsAppData?.nodeId,
          instanceName: whatsAppData?.instanceName,
        }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('WhatsApp instance disconnected successfully', 'success');
        setWhatsAppQrCode(null);
        setWhatsAppData((prev: any) => ({
          ...prev,
          phoneNumber: '',
          metaPhoneNumberId: '',
          metaWabaId: '',
          displayName: '',
          connectionStatus: 'disconnected',
        }));
        fetchPortalData(true);
      } else {
        showToast(data.error || 'Failed to disconnect', 'error');
      }
    } catch (err: any) {
      showToast('Error disconnecting WhatsApp', 'error');
    } finally {
      setIsDisconnectingWhatsApp(false);
    }
  };

  const handleSaveWhatsAppSettings = async () => {
    if (!whatsAppData) return;
    setIsSavingWhatsAppSettings(true);
    try {
      const isOldDummyApp = whatsAppData.sipAppId === '2463786290783462';
      const resolvedAppId = (whatsAppData.sipAppId && !isOldDummyApp) ? whatsAppData.sipAppId : KANGAVENA_OFFICIAL_META_APP_ID;
      const resolvedTitle = (whatsAppData.displayName || whatsAppData.instanceLabel || whatsAppData.instanceName || '').trim();
      const payloadData = {
        ...whatsAppData,
        sipAppId: resolvedAppId,
        displayName: resolvedTitle,
        instanceLabel: resolvedTitle,
      };

      await fetch('/api/tenet/whatsapp/settings', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nodeId: whatsAppData.nodeId || 'whatsappNode-1',
          instanceName: whatsAppData.instanceName,
          displayName: resolvedTitle,
          instanceLabel: resolvedTitle,
          autoReply: whatsAppData.autoReply,
          voiceReply: whatsAppData.voiceReply,
          callAction: whatsAppData.callAction,
          phoneNumber: whatsAppData.phoneNumber,
          metaAccessToken: whatsAppData.metaAccessToken,
          useCustomMetaToken: whatsAppData.useCustomMetaToken,
          metaPhoneNumberId: whatsAppData.metaPhoneNumberId,
          metaWabaId: whatsAppData.metaWabaId,
          metaVerifyToken: whatsAppData.metaVerifyToken,
          sipAppId: resolvedAppId,
          enableSipCalling: whatsAppData.enableSipCalling !== false,
          sipServerHost: whatsAppData.sipServerHost || 'wa.kangavena.com',
          sipPort: whatsAppData.sipPort || 5061,
          sipAction: whatsAppData.sipAction || 'gemini_live_answer',
          sipCustomParamName: whatsAppData.sipCustomParamName || 'tenant_id',
          sipCustomParamValue: whatsAppData.sipCustomParamValue,
          whatsappFormattingStyle: whatsAppData.whatsappFormattingStyle,
          apiMode: whatsAppData.apiMode,
          isPersonalWhatsApp: whatsAppData.isPersonalWhatsApp === true,
          ownerPauseMinutes: whatsAppData.ownerPauseMinutes || 30,
        }),
      });

      try {
        await fetch('/api/tenet/widgets/save-node-data', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            nodeId: whatsAppData.nodeId || 'whatsappNode-1',
            widgetType: 'WHATSAPP_INSTANCE',
            widgetData: payloadData,
            data: payloadData,
          }),
        });
      } catch (e) {}

      try {
        await fetch('/api/admin/luna-flows/save-node-data', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            nodeId: whatsAppData.nodeId || 'whatsappNode-1',
            widgetType: 'WHATSAPP_INSTANCE',
            data: payloadData,
          }),
        });
      } catch (e) {}

      // Keep multi-instance array in sync
      setWhatsAppInstances((prev) =>
        prev.map((inst) =>
          (inst.nodeId === (whatsAppData.nodeId || 'whatsappNode-1')) ? { ...inst, ...payloadData } : inst
        )
      );

      showToast('WhatsApp operational settings & AI formatting saved!', 'success');
    } catch (err: any) {
      showToast('Error saving WhatsApp settings', 'error');
    } finally {
      setIsSavingWhatsAppSettings(false);
    }
  };

  const handleSaveInstanceTitle = async (customTitle?: string) => {
    if (!whatsAppData) return;
    setIsSavingInstanceTitle(true);
    const resolvedTitle = (customTitle !== undefined ? customTitle : (whatsAppData.displayName || whatsAppData.instanceLabel || whatsAppData.instanceName || '')).trim();
    const currentNodeId = whatsAppData.nodeId || 'whatsappNode-1';

    try {
      await fetch('/api/tenet/whatsapp/settings', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nodeId: currentNodeId,
          instanceName: resolvedTitle || whatsAppData.instanceName,
          displayName: resolvedTitle,
          instanceLabel: resolvedTitle,
          phoneNumber: whatsAppData.phoneNumber,
          autoReply: whatsAppData.autoReply,
          voiceReply: whatsAppData.voiceReply,
          callAction: whatsAppData.callAction,
          isPersonalWhatsApp: whatsAppData.isPersonalWhatsApp === true,
        }),
      });

      const updatedPayload = {
        ...whatsAppData,
        displayName: resolvedTitle,
        instanceLabel: resolvedTitle,
        instanceName: resolvedTitle || whatsAppData.instanceName,
      };

      try {
        await fetch('/api/tenet/widgets/save-node-data', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            nodeId: currentNodeId,
            widgetType: 'WHATSAPP_INSTANCE',
            widgetData: updatedPayload,
            data: updatedPayload,
          }),
        });
      } catch (e) {}

      try {
        await fetch('/api/admin/luna-flows/save-node-data', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            nodeId: currentNodeId,
            widgetType: 'WHATSAPP_INSTANCE',
            data: updatedPayload,
          }),
        });
      } catch (e) {}

      setWhatsAppData(updatedPayload);
      setWhatsAppInstances((prev) =>
        prev.map((inst) => (inst.nodeId === currentNodeId ? { ...inst, ...updatedPayload } : inst))
      );

      setTitleSavedSuccess(true);
      setTimeout(() => setTitleSavedSuccess(false), 3000);
      showToast('WhatsApp Line Nickname saved successfully!', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Failed to save nickname', 'error');
    } finally {
      setIsSavingInstanceTitle(false);
    }
  };

  const fetchIgnoredContacts = useCallback(async (query = '') => {
    try {
      setIsLoadingIgnoredContacts(true);
      const url = query ? `/api/tenet/whatsapp/ignored-contacts?search=${encodeURIComponent(query)}` : '/api/tenet/whatsapp/ignored-contacts';
      const res = await fetch(url, { headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (data?.success && Array.isArray(data.contacts)) {
        setIgnoredContacts(data.contacts);
      }
    } catch (err: any) {
      console.warn('Failed to fetch ignored contacts:', err?.message);
    } finally {
      setIsLoadingIgnoredContacts(false);
    }
  }, [getAuthHeaders]);

  const handleToggleIgnoreContact = async (contact: any) => {
    try {
      const isCurrentlyIgnored = contact.isIgnored === true;
      const res = await fetch('/api/tenet/whatsapp/toggle-ignore-contact', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          phone: contact.phoneNumber,
          reason: isCurrentlyIgnored ? 'unignored_by_user' : 'manual_override',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success) {
        showToast(
          isCurrentlyIgnored ? `Unignored +${contact.phoneNumber}` : `Ignored +${contact.phoneNumber}`,
          'success'
        );
        fetchIgnoredContacts();
      } else {
        showToast(data?.error || 'Failed to update contact', 'error');
      }
    } catch (err: any) {
      showToast('Network error', 'error');
    }
  };

  const handleUnignorePhone = async (phoneNumber: string) => {
    try {
      const res = await fetch('/api/tenet/whatsapp/unignore-contact', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ phoneNumber }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success) {
        showToast(`Contact +${phoneNumber} is now allowed to chat with Luna!`, 'success');
        fetchIgnoredContacts();
      } else {
        showToast(data?.error || 'Failed to unignore contact', 'error');
      }
    } catch (err: any) {
      showToast('Network error', 'error');
    }
  };

  const handleAddIgnoredContact = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!newIgnoredPhone.trim()) {
      showToast('Please enter a valid phone number', 'error');
      return;
    }
    try {
      setIsSubmittingIgnoredContact(true);
      const res = await fetch('/api/tenet/whatsapp/ignored-contacts', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          phone: newIgnoredPhone.trim(),
          name: newIgnoredName.trim() || undefined,
          reason: newIgnoredReason,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success) {
        showToast(`Added +${data.phone} to Ignored Contacts`, 'success');
        setNewIgnoredPhone('');
        setNewIgnoredName('');
        setShowAddIgnoredModal(false);
        fetchIgnoredContacts();
      } else {
        showToast(data?.error || 'Failed to add ignored contact', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error', 'error');
    } finally {
      setIsSubmittingIgnoredContact(false);
    }
  };

  const handleRemoveIgnoredContact = async (phone: string) => {
    try {
      const res = await fetch(`/api/tenet/whatsapp/ignored-contacts/${encodeURIComponent(phone)}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success) {
        showToast(`Unlocked +${phone}. Luna can now assist this contact.`, 'success');
        setIgnoredContacts(prev => prev.filter(c => c.phone !== phone));
      } else {
        showToast(data?.error || 'Failed to unignore contact', 'error');
      }
    } catch (err: any) {
      showToast('Network error', 'error');
    }
  };

  const handleToggleAutoReply = async () => {
    const nextState = whatsAppData?.autoReply === false ? true : false;
    const updated = { ...whatsAppData, autoReply: nextState };
    setWhatsAppData(updated);
    setWhatsAppInstances((prev) =>
      prev.map((inst) =>
        (inst.nodeId === whatsAppData?.nodeId) ? { ...inst, autoReply: nextState } : inst
      )
    );
    try {
      await fetch('/api/tenet/whatsapp/settings', {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          nodeId: whatsAppData?.nodeId,
          autoReply: nextState,
          voiceReply: whatsAppData?.voiceReply,
          callAction: whatsAppData?.callAction,
        }),
      });
      if (whatsAppData?.nodeId) {
        await fetch('/api/admin/luna-flows/save-node-data', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            nodeId: whatsAppData.nodeId,
            widgetType: 'WHATSAPP_INSTANCE',
            data: updated,
          }),
        });
      }
      showToast(nextState ? '🟢 WhatsApp AI Auto-Reply Activated' : '⏸️ WhatsApp AI Auto-Reply Paused (Muted)', 'success');
    } catch (err) {
      showToast('Failed to toggle Auto-Reply', 'error');
    }
  };

  const handleDiscoverPortalAccounts = async (openModal = true) => {
    try {
      setIsDiscoveringPortalAccounts(true);
      const res = await fetch('/api/tenet/whatsapp/meta-discovered-accounts', {
        headers: getAuthHeaders(),
      });
      const data = await res.json().catch(() => null);
      if (data?.success && Array.isArray(data.accounts)) {
        setDiscoveredPortalAccounts(data.accounts);
        if (openModal) {
          setShowPortalDiscoveryModal(true);
        }
        return data.accounts;
      } else {
        if (openModal) showToast('No authorized WhatsApp accounts found under Meta Partner: ' + (data?.error || 'Unknown response'), 'error');
        return [];
      }
    } catch (err: any) {
      if (openModal) showToast('Failed to query Meta Partner accounts: ' + (err?.message || err), 'error');
      return [];
    } finally {
      setIsDiscoveringPortalAccounts(false);
    }
  };

  const handleBindDiscoveredPortalPhone = async (wabaId: string, phone: any, wabaName: string) => {
    try {
      const bindRes = await fetch('/api/tenet/whatsapp/meta-embedded-signup', {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumberId: phone.id,
          wabaId: wabaId,
          businessId: '',
        }),
      });
      const bindData = await bindRes.json().catch(() => null);
      if (bindData?.success) {
        setWhatsAppData((prev: any) => ({
          ...prev,
          apiMode: 'official',
          metaPhoneNumberId: phone.id,
          metaWabaId: wabaId,
          phoneNumber: phone.displayPhone || phone.cleanPhone,
          connectionStatus: 'connected',
        }));
        setShowPortalDiscoveryModal(false);
        showToast(`🎉 WhatsApp Connected! ${phone.displayPhone || phone.cleanPhone} (${wabaName})`, 'success');
      } else {
        showToast('Binding notice: ' + (bindData?.error || 'Failed to bind WhatsApp'), 'error');
      }
    } catch (err: any) {
      showToast('Failed to bind account: ' + (err?.message || err), 'error');
    }
  };

  const handleLaunchPortalMetaSignup = async () => {
    try {
      setIsLaunchingPortalMetaSignup(true);
      const res = await fetch('/api/tenet/whatsapp/meta-embedded-config', {
        headers: getAuthHeaders(),
      });
      const cfg = await res.json().catch(() => ({}));
      const appId = cfg.appId || '1556883522786680';
      const configId = cfg.configId || '1953966058608610';
      const featureType = cfg.featureType || 'whatsapp_business_app_onboarding';
      const redirectUri = cfg.redirectUri || `${window.location.origin}/api/tenet/whatsapp/meta-callback`;

      const url = `https://business.facebook.com/messaging/whatsapp/onboard/?app_id=${appId}&config_id=${configId}&redirect_uri=${encodeURIComponent(redirectUri)}&extras=${encodeURIComponent(JSON.stringify({ sessionInfoVersion: '3', version: 'v4', featureType }))}`;
      
      const width = 640;
      const height = 750;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(url, 'whatsapp_embedded_signup', `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);

      // 🔄 Real-Time Polling for Meta Onboarding Completion
      const pollTimer = setInterval(async () => {
        try {
          const accounts = await handleDiscoverPortalAccounts(false);
          if (popup && popup.closed) {
            clearInterval(pollTimer);
            setIsLaunchingPortalMetaSignup(false);
            if (accounts && accounts.length > 0) {
              setShowPortalDiscoveryModal(true);
            }
          }
        } catch (e) {
          // ignore polling check glitch
        }
      }, 3000);

      const messageHandler = async (event: MessageEvent) => {
        if (!event.origin.includes('facebook.com') && event.origin !== window.location.origin) return;
        try {
          const payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (payload?.type === 'WA_EMBEDDED_SIGNUP' && payload?.event === 'FINISH') {
            clearInterval(pollTimer);
            window.removeEventListener('message', messageHandler);
            if (popup && !popup.closed) popup.close();

            const { phone_number_id, waba_id, business_id } = payload.data || {};
            if (phone_number_id && waba_id) {
              const bindRes = await fetch('/api/tenet/whatsapp/meta-embedded-signup', {
                method: 'POST',
                headers: {
                  ...getAuthHeaders(),
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  phoneNumberId: phone_number_id,
                  wabaId: waba_id,
                  businessId: business_id,
                }),
              });
              const bindData = await bindRes.json().catch(() => null);
              if (bindData?.success) {
                setWhatsAppData((prev: any) => ({
                  ...prev,
                  apiMode: 'official',
                  metaPhoneNumberId: phone_number_id,
                  metaWabaId: waba_id,
                  phoneNumber: bindData.phoneNumber || phone_number_id,
                  connectionStatus: 'connected',
                }));
                showToast(`🎉 WhatsApp Connected Successfully! ${bindData.phoneNumber || phone_number_id}`, 'success');
              } else {
                showToast('Connection notice: ' + (bindData?.error || 'Failed to auto-bind WhatsApp'), 'error');
              }
            }
          }
        } catch (e) {
          // ignore
        }
      };

      window.addEventListener('message', messageHandler);
    } catch (err: any) {
      showToast('Failed to launch Meta Onboarding: ' + (err?.message || err), 'error');
    } finally {
      setIsLaunchingPortalMetaSignup(false);
    }
  };

  return (
    <div className="w-full h-full min-h-0 flex flex-col bg-[#090D16] text-slate-100 select-none overflow-hidden font-sans">
      
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-2xl shadow-2xl backdrop-blur-xl border flex items-center gap-3 text-xs font-bold animate-in slide-in-from-top-2 ${
            toastMessage.type === 'success'
              ? 'bg-cyan-950/90 border-cyan-400 text-cyan-200 shadow-[0_0_20px_rgba(6,182,212,0.4)]'
              : 'bg-rose-950/90 border-rose-400 text-rose-200 shadow-[0_0_20px_rgba(244,63,94,0.4)]'
          }`}
        >
          {toastMessage.type === 'success' ? <CheckCircle2 size={16} className="text-cyan-400" /> : <AlertCircle size={16} className="text-rose-400" />}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* TOP CYBER HUD BAR                                                 */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <header className="min-h-[56px] sm:h-16 border-b border-cyan-500/20 bg-[#0D121F]/95 backdrop-blur-2xl px-3 sm:px-6 py-2 flex flex-wrap items-center justify-between gap-2.5 shrink-0 z-20">
        
        {/* Left: Navigation Buttons & Breadcrumbs */}
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {selectedModule !== null ? (
            <button
              type="button"
              onClick={() => setSelectedModule(null)}
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-1.5 rounded-xl bg-cyan-950/80 border border-cyan-500/40 hover:border-cyan-400 hover:bg-cyan-900/60 text-cyan-200 text-xs font-extrabold transition-all cursor-pointer shadow-sm group shrink-0"
            >
              <LayoutGrid size={14} className="text-cyan-400 group-hover:scale-110 transition-transform" />
              <span className="hidden sm:inline">← Back to Data Hub Modules</span>
              <span className="sm:hidden">← Modules</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={onBackToWorkspace}
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-1.5 rounded-xl bg-slate-900 border border-slate-800 hover:border-cyan-400/60 hover:bg-slate-800/80 text-cyan-300 text-xs font-bold transition-all cursor-pointer shadow-sm group shrink-0"
            >
              <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
              <span className="hidden sm:inline">Back to Flow Graph</span>
              <span className="sm:hidden">Flow Graph</span>
            </button>
          )}

          <div className="hidden sm:block h-6 w-px bg-slate-800" />

          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-cyan-500/15 border border-cyan-400/40 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.4)]">
              <Database size={16} className="text-cyan-400 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <h1 className="text-xs sm:text-sm font-black tracking-wider uppercase text-white truncate max-w-[140px] xs:max-w-[200px] sm:max-w-none">
                  {selectedModule === 'luna_core'
                    ? 'Luna Core Hub Telemetry'
                    : selectedModule === 'neural_cache'
                    ? 'Luna Neural Context Cache & Token Engine Studio'
                    : selectedModule === 'action_leads'
                    ? 'Action & Captured Leads Studio'
                    : selectedModule === 'knowledge'
                    ? 'Knowledge & Context Base Studio'
                    : selectedModule === 'media_vault'
                    ? 'Media Vault Asset Studio'
                    : selectedModule === 'whatsapp_web'
                    ? 'Kangavena WhatsApp Web Console'
                    : selectedModule === 'whatsapp_instance'
                    ? 'WhatsApp Instance & Gateway Studio'
                    : selectedModule === 'intent_alerts'
                    ? 'Smart Intent & VIP Alerts Studio'
                    : selectedModule === 'product_catalog'
                    ? (catalogStudioTab === 'bookings' ? 'Fleet Reservations & Bookings Studio' : catalogStudioTab === 'orders' ? 'Orders & Live Courier Tracking Studio' : 'Product Catalog & Fleet Studio')
                    : selectedModule === 'payment_gateway'
                    ? 'Payment Gateway & Banking Studio'
                    : selectedModule === 'call_guard'
                    ? 'Call Guard & Defense Studio'
                    : selectedModule === 'sip_call'
                    ? 'Enterprise SIP PBX & Grandstream Call Studio'
                    : selectedModule === 'followup_recovery'
                    ? 'Re-engagement & Abandoned Recovery Studio'
                    : selectedModule === 'review_booster'
                    ? 'Google Maps Review Booster Studio'
                    : 'Portal Data Hub'}
                </h1>
                <span className="hidden xs:inline-block px-1.5 sm:px-2 py-0.5 rounded-md text-[8.5px] sm:text-[9px] font-mono font-black bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 shadow-[0_0_8px_rgba(6,182,212,0.3)]">
                  {selectedModule === null ? 'ACTIVE NODES' : 'LIVE STUDIO'}
                </span>
              </div>
              <p className="text-[9.5px] sm:text-[10px] text-slate-400 font-mono truncate max-w-[160px] sm:max-w-sm">
                Active Flow: <span className="text-cyan-300">{flowName}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Right: Status & Actions */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Direct Mobile WhatsApp Link Button (Only if WhatsApp node is actively in flow) */}
          {hasWhatsApp && (
            <button
              type="button"
              onClick={() => setShowWhatsAppMobileModal(true)}
              className="px-2.5 sm:px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500/20 to-teal-500/20 hover:from-emerald-500/30 hover:to-teal-500/30 border border-emerald-400/40 text-emerald-300 hover:text-white text-xs font-bold transition flex items-center gap-1.5 shadow-[0_0_15px_rgba(16,185,129,0.25)] active:scale-95 cursor-pointer"
              title="Open Mobile Direct WhatsApp Link & QR Code"
            >
              <Smartphone size={14} className="text-emerald-400" />
              <span className="hidden sm:inline">Mobile WP Link</span>
              <QrCode size={13} className="text-emerald-300" />
            </button>
          )}

          {stats.whatsappPhone && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-xl bg-emerald-950/60 border border-emerald-500/30 text-emerald-300 text-[11px] font-mono font-bold">
              <Smartphone size={13} className="text-emerald-400" />
              <span>+{stats.whatsappPhone}</span>
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
            </div>
          )}

          {selectedModule === 'luna_core' && (
            <button
              type="button"
              onClick={() => setAutoRefresh((prev) => !prev)}
              className={`px-2.5 sm:px-3 py-1.5 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer border ${
                autoRefresh
                  ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                  : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
              }`}
              title="Auto-refresh every 8s"
            >
              <Radio size={12} className={autoRefresh ? 'animate-pulse text-emerald-400' : ''} />
              <span className="hidden sm:inline">{autoRefresh ? 'Live Stream' : 'Paused'}</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => fetchPortalData()}
            disabled={refreshing}
            className="p-2 rounded-xl bg-slate-900 border border-slate-800 hover:border-cyan-400 text-slate-300 hover:text-cyan-300 transition cursor-pointer disabled:opacity-50"
            title="Refresh Live Data"
          >
            <RefreshCw size={14} className={refreshing ? 'animate-spin text-cyan-400' : ''} />
          </button>
        </div>
      </header>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* VIEW 1: PORTAL DATA HUB LANDING GRID (OVERVIEW OF ACTIVE NODES)   */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {selectedModule === null ? (
        <div className="flex-1 overflow-y-auto p-3 sm:p-5 md:p-6 space-y-5 bg-gradient-to-b from-[#090D16] via-[#0B0F1C] to-[#0D1322] tenet-mobile-scroll">
          
          {/* Executive Compact Cyber Banner */}
          <div className="rounded-2xl bg-gradient-to-r from-[#10172B]/95 via-[#0D1527]/90 to-[#121E36]/95 border border-cyan-500/30 p-3 sm:p-4 md:p-5 shadow-lg relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="space-y-1.5 max-w-2xl">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded-md bg-cyan-500/20 text-cyan-300 text-[10px] font-mono font-black border border-cyan-400/40">
                  CENTRALIZED INTELLIGENCE HUB
                </span>
                <span className="text-xs text-slate-400 font-mono hidden sm:inline">• Drag cards to reorder your workspace</span>
              </div>
              <h2 className="text-base md:text-lg font-black text-white tracking-wide font-sans">
                Active Node Telemetry & Knowledge Center
              </h2>
              <p className="text-xs text-slate-300 leading-relaxed font-sans">
                Centralized live data management for your active agent flow. Click any module below to inspect customer interactions, manage RAG vector vaults, or dispatch multimedia assets.
              </p>
            </div>

            {/* Inline Stats Strip */}
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 sm:gap-2.5 shrink-0 text-xs font-mono w-full sm:w-auto">
              <div className="px-3 py-1.5 rounded-xl bg-slate-950/90 border border-cyan-500/25 flex items-center gap-1.5">
                <Layers size={13} className="text-cyan-400" />
                <span className="text-slate-400">Modules:</span>
                <span className="text-cyan-300 font-bold">{activeModulesCount}</span>
              </div>
              <div className="px-3 py-1.5 rounded-xl bg-slate-950/90 border border-purple-500/25 flex items-center gap-1.5">
                <Users size={13} className="text-purple-400" />
                <span className="text-slate-400">Leads:</span>
                <span className="text-purple-300 font-bold">{leadsList.length || stats.totalLeads || 0}</span>
              </div>
              <div className="px-3 py-1.5 rounded-xl bg-slate-950/90 border border-emerald-500/25 flex items-center gap-1.5">
                <MessageSquare size={13} className="text-emerald-400" />
                <span className="text-slate-400">Chats:</span>
                <span className="text-emerald-300 font-bold">{stats.totalConversations}</span>
              </div>
              <div className="px-3 py-1.5 rounded-xl bg-slate-950/90 border border-amber-500/25 flex items-center gap-1.5">
                <Zap size={13} className="text-amber-400" />
                <span className="text-slate-400">Tokens:</span>
                <span className="text-amber-300 font-bold">{stats.totalTokensUsed.toLocaleString()}</span>
                <span className="text-[9px] text-slate-400 font-mono hidden sm:inline">
                  (<span className="text-cyan-300">Text: {(stats.lunaTokenUsage?.total || (stats.totalTokensUsed - (stats.lunaCallTokenUsage?.total || 0) - (stats.lunaTrainingKnowledgeUsage?.total || 0)) || 0).toLocaleString()}</span> | <span className="text-purple-300">Calls: {(stats.lunaCallTokenUsage?.total || 0).toLocaleString()}</span> | <span className="text-emerald-300">Training: {(stats.lunaTrainingKnowledgeUsage?.total || 0).toLocaleString()}</span>)
                </span>
              </div>
            </div>
          </div>

          {/* Active Nodes Grid Header with Drag & Drop Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-black uppercase tracking-wider text-white font-sans flex items-center gap-2">
                <Compass size={14} className="text-cyan-400" />
                <span>Deployed Flow Node Modules ({activeModulesCount})</span>
              </h3>
            </div>
            <button
              type="button"
              onClick={resetModuleOrder}
              className="text-[10px] text-slate-400 hover:text-cyan-300 font-mono font-bold flex items-center gap-1 transition cursor-pointer"
              title="Reset card ordering to default"
            >
              <RotateCcw size={11} />
              <span>Reset Order</span>
            </button>
          </div>

          {/* Dynamic Active Nodes Grid (Auto-Scales up to 4 modules per row) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            
            {effectiveModuleOrder.map((moduleKey) => {
              if (moduleKey === 'luna_core' && hasLunaCore) {
                const isDraggingThis = draggedModule === 'luna_core';
                const isOverThis = dragOverModule === 'luna_core';

                return (
                  <div
                    key="luna_core"
                    draggable
                    onDragStart={() => handleDragStart('luna_core')}
                    onDragOver={(e) => handleDragOver(e, 'luna_core')}
                    onDrop={() => handleDrop('luna_core')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('luna_core')}
                    className={`rounded-2xl bg-gradient-to-br from-[#0F172A] via-[#0C1322] to-[#111C33] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(6,182,212,0.3)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-cyan-400'
                        : isOverThis
                        ? 'border-cyan-300 ring-2 ring-cyan-400/50 scale-[1.02]'
                        : 'border-cyan-500/30 hover:border-cyan-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      {/* Card Header with Drag Handle */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-cyan-500/20 border border-cyan-400/50 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.3)] group-hover:scale-105 transition-transform">
                            <Cpu size={20} className="animate-spin [animation-duration:12s]" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-cyan-200 transition-colors">
                                Luna Core Hub
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-cyan-300/80">Master Brain</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className="text-[8px] font-mono font-bold text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded-full border border-emerald-500/40">
                            LIVE
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      {/* Card Description */}
                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Inspect customer interactions across WhatsApp and Web, monitor tokens, and take over chats live.
                      </p>

                      {/* Live Metrics Grid */}
                      <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Chats:</span>
                          <span className="text-white font-bold">{stats.totalConversations}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Tokens:</span>
                          <div className="text-right">
                            <span className="text-amber-300 font-bold">{stats.totalTokensUsed.toLocaleString()}</span>
                            <div className="text-[8px] font-mono flex items-center gap-1 justify-end">
                              <span className="text-cyan-300">In: {(stats.totalInputTokens || Math.round(stats.totalTokensUsed * 0.88)).toLocaleString()}</span>
                              <span className="text-slate-600">•</span>
                              <span className="text-purple-300">Out: {(stats.totalOutputTokens || Math.max(0, stats.totalTokensUsed - Math.round(stats.totalTokensUsed * 0.88))).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between col-span-2">
                          <span className="text-slate-400">WhatsApp:</span>
                          <span className="text-emerald-300 font-bold truncate max-w-[120px]">
                            {stats.whatsappPhone ? `+${stats.whatsappPhone}` : 'Active'}
                          </span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-cyan-950/50 border border-cyan-500/30 flex items-center justify-between col-span-2 text-[10px]">
                          <span className="text-cyan-300 font-mono flex items-center gap-1">
                            <Zap size={10} className="text-cyan-400" /> Neural Cache:
                          </span>
                          <span className="text-emerald-300 font-bold font-mono">
                            &lt;0.05ms Instant Recall
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card Action Button */}
                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-cyan-400 font-bold uppercase">
                        Telemetry
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-cyan-500 group-hover:bg-cyan-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (moduleKey === 'neural_cache' && hasNeuralCache) {
                const isDraggingThis = draggedModule === 'neural_cache';
                const isOverThis = dragOverModule === 'neural_cache';

                return (
                  <div
                    key="neural_cache"
                    draggable
                    onDragStart={() => handleDragStart('neural_cache')}
                    onDragOver={(e) => handleDragOver(e, 'neural_cache')}
                    onDrop={() => handleDrop('neural_cache')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('neural_cache')}
                    className={`rounded-2xl bg-gradient-to-br from-[#0B1528] via-[#08101E] to-[#0E1A33] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(6,182,212,0.4)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-cyan-400'
                        : isOverThis
                        ? 'border-cyan-300 ring-2 ring-cyan-400/50 scale-[1.02]'
                        : 'border-cyan-500/35 hover:border-cyan-400'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      {/* Card Header with Drag Handle */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-cyan-500/20 border border-cyan-400/50 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.4)] group-hover:scale-105 transition-transform">
                            <Zap size={20} className="text-cyan-400 animate-pulse" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-cyan-200 transition-colors">
                                Neural Context Cache
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-cyan-300/80">Token Engine & Sub-ms Recall</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className="text-[8px] font-mono font-bold text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded-full border border-emerald-500/40 flex items-center gap-1">
                            <span className="w-1 h-1 rounded-full bg-emerald-400 animate-ping inline-block" />
                            ACTIVE
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      {/* Card Description */}
                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Pre-compiled multimodal system prompt, connected nodes graph index, and sub-millisecond in-memory cache accelerator.
                      </p>

                      {/* Live Metrics Grid */}
                      <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Tokens:</span>
                          <span className="text-cyan-300 font-bold">~{cacheStudioData?.estimatedTokens || 20698}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Latency:</span>
                          <span className="text-emerald-300 font-bold">&lt; 0.05ms</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between col-span-2">
                          <span className="text-slate-400">Architecture:</span>
                          <span className="text-cyan-300 font-bold truncate">
                            Tier-1 RAM Engine
                          </span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-cyan-950/50 border border-cyan-500/30 flex items-center justify-between col-span-2 text-[10px]">
                          <span className="text-cyan-300 font-mono flex items-center gap-1">
                            <Layers size={10} className="text-cyan-400" /> Ingested Graph:
                          </span>
                          <span className="text-white font-bold font-mono">
                            {cacheStudioData?.nodeCount || 7} Nodes • {cacheStudioData?.toolsCount || 11} Tools
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card Action Button */}
                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-cyan-400 font-bold uppercase">
                        Token Engine
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 group-hover:from-cyan-400 group-hover:to-blue-500 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Inspect Studio</span>
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (moduleKey === 'action_leads' && hasActionNode) {
                const isDraggingThis = draggedModule === 'action_leads';
                const isOverThis = dragOverModule === 'action_leads';

                return (
                  <div
                    key="action_leads"
                    draggable
                    onDragStart={() => handleDragStart('action_leads')}
                    onDragOver={(e) => handleDragOver(e, 'action_leads')}
                    onDrop={() => handleDrop('action_leads')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('action_leads')}
                    className={`rounded-2xl bg-gradient-to-br from-[#1A0F2E] via-[#140B24] to-[#1E1136] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(168,85,247,0.35)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-purple-400'
                        : isOverThis
                        ? 'border-purple-300 ring-2 ring-purple-400/50 scale-[1.02]'
                        : 'border-purple-500/30 hover:border-purple-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      {/* Card Header with Drag Handle */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-purple-500/20 border border-purple-400/50 text-purple-300 shadow-[0_0_15px_rgba(168,85,247,0.3)] group-hover:scale-105 transition-transform">
                            <Users size={20} />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-purple-200 transition-colors">
                                Action & Leads
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-purple-300/80">Captured Leads Hub</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className="text-[8px] font-mono font-bold text-purple-300 bg-purple-950/80 px-2 py-0.5 rounded-full border border-purple-500/40">
                            LEADS
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      {/* Card Description */}
                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Inspect customer inquiries, WhatsApp captured phone numbers, lead status, and export to CSV.
                      </p>

                      {/* Live Metrics Grid */}
                      <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Total Leads:</span>
                          <span className="text-white font-bold">{leadsList.length || stats.totalLeads || 0}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">WhatsApp:</span>
                          <span className="text-emerald-300 font-bold">{stats.whatsappLeads || leadsList.filter((l) => l.formType === 'whatsapp_lead' || l.formType === 'whatsapp').length}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between col-span-2">
                          <span className="text-slate-400">Action Route:</span>
                          <span className="text-purple-300 font-bold truncate max-w-[120px]">
                            {actionData?.targetDestination || (stats.whatsappPhone ? `+${stats.whatsappPhone}` : 'Direct Pipeline')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card Action Button */}
                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-purple-400 font-bold uppercase">
                        Lead Telemetry
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 group-hover:from-purple-400 group-hover:to-indigo-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              }


              if (moduleKey === 'knowledge' && hasKnowledge) {
                const isDraggingThis = draggedModule === 'knowledge';
                const isOverThis = dragOverModule === 'knowledge';

                return (
                  <div
                    key="knowledge"
                    draggable
                    onDragStart={() => handleDragStart('knowledge')}
                    onDragOver={(e) => handleDragOver(e, 'knowledge')}
                    onDrop={() => handleDrop('knowledge')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('knowledge')}
                    className={`rounded-2xl bg-gradient-to-br from-[#0B171E] via-[#091419] to-[#0E2024] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-emerald-400'
                        : isOverThis
                        ? 'border-emerald-300 ring-2 ring-emerald-400/50 scale-[1.02]'
                        : 'border-emerald-500/30 hover:border-emerald-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      {/* Card Header with Drag Handle */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-emerald-500/20 border border-emerald-400/50 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.3)] group-hover:scale-105 transition-transform">
                            <BookOpen size={20} />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-emerald-200 transition-colors">
                                Knowledge Vault
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-emerald-300/80">RAG Repository</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className="text-[8px] font-mono font-bold text-teal-300 bg-teal-950/80 px-2 py-0.5 rounded-full border border-teal-500/40">
                            SYNCED
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      {/* Card Description */}
                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Synthesized business knowledge, categorized facts, FAQs matrix, and PDF/Word document training.
                      </p>

                      {/* Live Metrics Grid */}
                      <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Sections:</span>
                          <span className="text-white font-bold">{parsedSections.length}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Words:</span>
                          <span className="text-teal-300 font-bold">{knowledgeText.trim().split(/\s+/).filter(Boolean).length}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between col-span-2">
                          <span className="text-slate-400">Document:</span>
                          <span className="text-cyan-300 font-bold truncate max-w-[120px]">
                            {knowledgeData?.lastTrainedFileName || 'Direct Markdown'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card Action Button */}
                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-emerald-400 font-bold uppercase">
                        Knowledge Studio
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 group-hover:from-emerald-400 group-hover:to-teal-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (moduleKey === 'media_vault' && hasMediaVault) {
                const isDraggingThis = draggedModule === 'media_vault';
                const isOverThis = dragOverModule === 'media_vault';

                return (
                  <div
                    key="media_vault"
                    draggable
                    onDragStart={() => handleDragStart('media_vault')}
                    onDragOver={(e) => handleDragOver(e, 'media_vault')}
                    onDrop={() => handleDrop('media_vault')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('media_vault')}
                    className={`rounded-2xl bg-gradient-to-br from-[#130E22] via-[#0E0B1A] to-[#18112C] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(168,85,247,0.3)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-purple-400'
                        : isOverThis
                        ? 'border-purple-300 ring-2 ring-purple-400/50 scale-[1.02]'
                        : 'border-purple-500/30 hover:border-purple-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      {/* Card Header with Drag Handle */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-purple-500/20 border border-purple-400/50 text-purple-300 shadow-[0_0_15px_rgba(168,85,247,0.3)] group-hover:scale-105 transition-transform">
                            <FolderOpen size={20} />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-purple-200 transition-colors">
                                Media Vault
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-purple-300/80">Asset Repository</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className="text-[8px] font-mono font-bold text-purple-300 bg-purple-950/80 px-2 py-0.5 rounded-full border border-purple-500/40">
                            {mediaAssets.length} ASSETS
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      {/* Card Description */}
                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Manage high-res product photos, PDF brochures, and videos dispatched by Luna AI during live chats.
                      </p>

                      {/* Live Metrics Grid */}
                      <div className="grid grid-cols-3 gap-1 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 text-center">
                          <span className="text-sky-300 block font-bold">{imageCount}</span>
                          <span className="text-[8px] text-slate-400">Images</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 text-center">
                          <span className="text-rose-300 block font-bold">{pdfCount}</span>
                          <span className="text-[8px] text-slate-400">PDFs</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 text-center">
                          <span className="text-violet-300 block font-bold">{videoCount}</span>
                          <span className="text-[8px] text-slate-400">Videos</span>
                        </div>
                      </div>
                    </div>

                    {/* Card Action Button */}
                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-purple-400 font-bold uppercase">
                        Media Studio
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 group-hover:from-purple-400 group-hover:to-indigo-400 text-white font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (moduleKey === 'customer_memory' && hasCustomerMemory) {
                const isDraggingThis = draggedModule === 'customer_memory';
                const isOverThis = dragOverModule === 'customer_memory';

                const totalRecognized = memoriesList.length;
                const vipCount = memoriesList.filter((m) => m.isVip).length;

                return (
                  <div
                    key="customer_memory"
                    draggable
                    onDragStart={() => handleDragStart('customer_memory')}
                    onDragOver={(e) => handleDragOver(e, 'customer_memory')}
                    onDrop={() => handleDrop('customer_memory')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('customer_memory')}
                    className={`rounded-2xl bg-gradient-to-br from-[#1B0F2A] via-[#120B1D] to-[#180E26] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(168,85,247,0.3)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-purple-400'
                        : isOverThis
                        ? 'border-purple-300 ring-2 ring-purple-400/50 scale-[1.02]'
                        : 'border-purple-500/30 hover:border-purple-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      {/* Card Header with Drag Handle */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-purple-500/20 border border-purple-400/50 text-purple-300 shadow-[0_0_15px_rgba(168,85,247,0.3)] group-hover:scale-105 transition-transform">
                            <Brain size={20} className="animate-pulse" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-purple-200 transition-colors">
                                Customer Memory Vault
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-purple-300/80">Episodic RAG Recall</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className="text-[8px] font-mono font-bold text-purple-300 bg-purple-950/80 px-2 py-0.5 rounded-full border border-purple-500/40">
                            {totalRecognized} CLIENTS
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      {/* Card Description */}
                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Cross-session customer recognition, episodic facts, and budget preferences without token bloat.
                      </p>

                      {/* Live Metrics Grid */}
                      <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Remembered:</span>
                          <span className="text-purple-300 font-bold">{totalRecognized}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">VIP Clients:</span>
                          <span className="text-amber-300 font-bold">{vipCount}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between col-span-2">
                          <span className="text-slate-400">Retention:</span>
                          <span className="text-emerald-300 font-bold">
                            {customerMemoryData?.retentionDays ? `${customerMemoryData.retentionDays} Days` : '1 Year Active'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Card Action Button */}
                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-purple-400 font-bold uppercase">
                        Memory Studio
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 group-hover:from-purple-400 group-hover:to-indigo-400 text-white font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (moduleKey === 'whatsapp_instance' && hasWhatsApp) {
                const instancesToRender = whatsAppInstances.length > 0 ? whatsAppInstances : [whatsAppData || {}];
                return (
                  <React.Fragment key="whatsapp_instances_group">
                    {instancesToRender.map((inst: any, idx: number) => {
                      const isDraggingThis = draggedModule === 'whatsapp_instance';
                      const isOverThis = dragOverModule === 'whatsapp_instance';
                      const isConnected = inst?.connectionStatus === 'connected' || (idx === 0 && stats.whatsappConnected);
                      const isPersonal = inst?.isPersonalWhatsApp === true;
                      const instNodeId = inst?.nodeId || `whatsappNode-${idx + 1}`;
                      const rawPhone = inst?.phoneNumber || (idx === 0 && stats.whatsappPhone ? stats.whatsappPhone : '');
                      const formattedPhone = rawPhone ? (String(rawPhone).startsWith('+') ? String(rawPhone) : `+${rawPhone}`) : '';
                      const customLabel = (inst?.displayName && !inst.displayName.startsWith('tenet_wa_'))
                        ? inst.displayName
                        : (inst?.instanceLabel && !inst.instanceLabel.startsWith('tenet_wa_'))
                        ? inst.instanceLabel
                        : (inst?.instanceName && !inst.instanceName.startsWith('tenet_wa_'))
                        ? inst.instanceName
                        : '';
                      const titleName = formattedPhone
                        ? (customLabel ? `${formattedPhone} (${customLabel})` : formattedPhone)
                        : (customLabel || (isPersonal ? 'Personal WhatsApp' : (instancesToRender.length > 1 ? `WhatsApp Gateway ${idx + 1}` : 'WhatsApp Gateway')));

                      const cleanInstPhone = rawPhone ? String(rawPhone).replace(/\D/g, '') : '';
                      const instChats = whatsAppOnlyConversations.filter((c: any) => {
                        if (instNodeId && (c.instanceNodeId === instNodeId || c.nodeId === instNodeId)) return true;
                        if (cleanInstPhone && cleanInstPhone.length >= 8) {
                          const chatInstPhone = String(c.instancePhoneNumber || '').replace(/\D/g, '');
                          if (chatInstPhone && (chatInstPhone.includes(cleanInstPhone.slice(-8)) || cleanInstPhone.includes(chatInstPhone.slice(-8)))) return true;
                        }
                        if (inst?.metaPhoneNumberId && (String(c.instancePhoneNumberId || '') === String(inst.metaPhoneNumberId) || (typeof c.sessionId === 'string' && c.sessionId.includes(String(inst.metaPhoneNumberId))))) return true;
                        return false;
                      });
                      const chatCountForCard = (instChats.length > 0 || instancesToRender.length > 1) ? instChats.length : whatsAppOnlyConversations.length;

                      return (
                        <div
                          key={`whatsapp_instance_${instNodeId}_${idx}`}
                          draggable
                          onDragStart={() => handleDragStart('whatsapp_instance')}
                          onDragOver={(e) => handleDragOver(e, 'whatsapp_instance')}
                          onDrop={() => handleDrop('whatsapp_instance')}
                          onDragEnd={handleDragEnd}
                          onClick={() => {
                            setSelectedWhatsAppNodeId(instNodeId);
                            setWhatsAppData(inst);
                            if (inst.qrCode) {
                              setWhatsAppQrCode(inst.qrCode);
                            }
                            setSelectedModule('whatsapp_instance');
                          }}
                          className={`rounded-2xl bg-gradient-to-br from-[#0B1A14] via-[#081510] to-[#0D221A] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                            isDraggingThis
                              ? 'opacity-40 scale-95 border-dashed border-emerald-400'
                              : isOverThis
                              ? 'border-emerald-300 ring-2 ring-emerald-400/50 scale-[1.02]'
                              : isPersonal
                              ? 'border-emerald-500/50 hover:border-emerald-400/90 shadow-[0_0_15px_rgba(16,185,129,0.15)]'
                              : 'border-emerald-500/30 hover:border-emerald-400/80'
                          }`}
                        >
                          <div className="space-y-3 relative z-10">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2.5">
                                <div className={`p-2 rounded-xl border group-hover:scale-105 transition-transform ${
                                  isPersonal 
                                    ? 'bg-emerald-500/30 border-emerald-300/70 text-emerald-200 shadow-[0_0_18px_rgba(16,185,129,0.4)]'
                                    : 'bg-emerald-500/20 border-emerald-400/50 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.3)]'
                                }`}>
                                  <Smartphone size={20} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <h4 className="text-sm font-black text-white font-sans group-hover:text-emerald-200 transition-colors">
                                      {titleName}
                                    </h4>
                                    {isPersonal && (
                                      <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-400/40 uppercase">
                                        Personal Guard
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-[9px] font-mono text-emerald-300/80">
                                    {inst?.apiMode === 'official' ? 'Official Meta Cloud API' : 'Evolution API Co-Pilot'}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center gap-1">
                                <span className={`text-[8px] font-mono font-bold px-2 py-0.5 rounded-full border ${
                                  isConnected
                                    ? 'text-emerald-300 bg-emerald-950/80 border-emerald-500/40'
                                    : 'text-amber-300 bg-amber-950/80 border-amber-500/40'
                                }`}>
                                  {isConnected ? 'CONNECTED' : 'DISCONNECTED'}
                                </span>
                                <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                                  <GripVertical size={13} />
                                </div>
                              </div>
                            </div>

                            <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                              {isPersonal
                                ? 'Personal number integration with business message intent filtering, 30m owner snooze, and spam dropping.'
                                : 'Linked device status, live QR scanner, auto-replies, voice notes, and real-time WhatsApp conversation telemetry.'}
                            </p>

                            <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                              <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                                <span className="text-slate-400">Phone:</span>
                                <span className="text-white font-bold truncate max-w-[90px]">
                                  {inst?.phoneNumber ? (String(inst.phoneNumber).startsWith('+') ? inst.phoneNumber : `+${inst.phoneNumber}`) : (idx === 0 && stats.whatsappPhone ? `+${stats.whatsappPhone}` : 'Not Linked')}
                                </span>
                              </div>
                              <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                                <span className="text-slate-400">Chats:</span>
                                <span className="text-emerald-300 font-bold">{chatCountForCard}</span>
                              </div>
                              <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between col-span-2">
                                <span className="text-slate-400">Auto-Reply:</span>
                                <span className="text-cyan-300 font-bold">
                                  {inst?.autoReply !== false ? 'Active (AI Handover)' : 'Disabled'}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                            <span className="text-[9px] font-mono text-emerald-400 font-bold uppercase">
                              {isPersonal ? 'Personal Shield' : 'Device & Telemetry'}
                            </span>
                            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 group-hover:from-emerald-400 group-hover:to-teal-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                              <span>Open Studio</span>
                              <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              }

              if (moduleKey === 'intent_alerts' && hasIntentAlert) {
                const isDraggingThis = draggedModule === 'intent_alerts';
                const isOverThis = dragOverModule === 'intent_alerts';
                const activeRulesCount = intentRules.filter((r) => r.isEnabled !== false).length;

                return (
                  <div
                    key="intent_alerts"
                    draggable
                    onDragStart={() => handleDragStart('intent_alerts')}
                    onDragOver={(e) => handleDragOver(e, 'intent_alerts')}
                    onDrop={() => handleDrop('intent_alerts')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('intent_alerts')}
                    className={`rounded-2xl bg-gradient-to-br from-[#250F18] via-[#1A0A10] to-[#1F0C14] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(244,63,94,0.35)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-rose-400'
                        : isOverThis
                        ? 'border-rose-300 ring-2 ring-rose-400/50 scale-[1.02]'
                        : 'border-rose-500/30 hover:border-rose-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-rose-500/20 border border-rose-400/50 text-rose-300 shadow-[0_0_15px_rgba(244,63,94,0.3)] group-hover:scale-105 transition-transform">
                            <BellRing size={20} className="animate-pulse text-rose-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-rose-200 transition-colors">
                                Smart Intent Alerts
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-rose-300/80">VIP Behavior Watcher</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className="text-[8px] font-mono font-bold text-rose-300 bg-rose-950/80 px-2 py-0.5 rounded-full border border-rose-500/40">
                            {activeRulesCount} ACTIVE
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Silently monitor customer interests (e.g. Red Roses, VIP Packages, bulk orders) and dispatch instant alerts to management.
                      </p>

                      <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Rules:</span>
                          <span className="text-white font-bold">{intentRules.length}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Mode:</span>
                          <span className="text-rose-300 font-bold">SILENT</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between col-span-2">
                          <span className="text-slate-400">Gateway:</span>
                          <span className="text-rose-300 font-bold truncate max-w-[140px] flex items-center gap-1">
                            <Zap size={11} className="text-rose-400" />
                            Action & WhatsApp Node
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-rose-400 font-bold uppercase">
                        Alerts Telemetry
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 group-hover:from-rose-400 group-hover:to-pink-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (moduleKey === 'product_catalog' && hasProductCatalog) {
                const isDraggingThis = draggedModule === 'product_catalog';
                const isOverThis = dragOverModule === 'product_catalog';
                const inStockCount = catalogProducts.filter((p) => p.isAvailable !== false).length;

                return (
                  <div
                    key="product_catalog"
                    draggable
                    onDragStart={() => handleDragStart('product_catalog')}
                    onDragOver={(e) => handleDragOver(e, 'product_catalog')}
                    onDrop={() => handleDrop('product_catalog')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('product_catalog')}
                    className={`rounded-2xl bg-gradient-to-br from-[#0F261C] via-[#0A1A13] to-[#0D2118] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(16,185,129,0.35)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-emerald-400'
                        : isOverThis
                        ? 'border-emerald-300 ring-2 ring-emerald-400/50 scale-[1.02]'
                        : 'border-emerald-500/30 hover:border-emerald-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-emerald-500/20 border border-emerald-400/50 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.3)] group-hover:scale-105 transition-transform">
                            <ShoppingBag size={20} className="animate-pulse text-emerald-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-emerald-200 transition-colors">
                                Product Catalog & Menu
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-emerald-300/80">Store Items & Live Stock</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className="text-[8px] font-mono font-bold text-emerald-300 bg-emerald-950/80 px-2 py-0.5 rounded-full border border-emerald-500/40">
                            {inStockCount} IN-STOCK
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Store items, dishes, prices, descriptions, and real-time inventory synced with Luna AI brain.
                      </p>

                      <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Total Items:</span>
                          <span className="text-white font-bold">{catalogProducts.length}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Currency:</span>
                          <span className="text-emerald-300 font-bold">{productCatalogData?.defaultCurrency || 'LKR'}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between col-span-2">
                          <span className="text-slate-400">Sync:</span>
                          <span className="text-emerald-300 font-bold truncate max-w-[140px] flex items-center gap-1">
                            <Zap size={11} className="text-emerald-400" />
                            Live AI Sensory Sync
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-emerald-400 font-bold uppercase">
                        Catalog Studio
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 group-hover:from-emerald-400 group-hover:to-teal-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (moduleKey === 'payment_gateway' && hasPaymentGateway) {
                const isDraggingThis = draggedModule === 'payment_gateway';
                const isOverThis = dragOverModule === 'payment_gateway';
                const paymentLeadsCount = leadsList.filter((l) => l.formType === 'payment_request' || l.formType === 'payment_slip').length;

                return (
                  <div
                    key="payment_gateway"
                    draggable
                    onDragStart={() => handleDragStart('payment_gateway')}
                    onDragOver={(e) => handleDragOver(e, 'payment_gateway')}
                    onDrop={() => handleDrop('payment_gateway')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('payment_gateway')}
                    className={`rounded-2xl bg-gradient-to-br from-[#0D2818] via-[#081C10] to-[#0A1F13] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(16,185,129,0.35)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-emerald-400'
                        : isOverThis
                        ? 'border-emerald-300 ring-2 ring-emerald-400/50 scale-[1.02]'
                        : 'border-emerald-500/30 hover:border-emerald-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-emerald-500/20 border border-emerald-400/50 text-emerald-300 shadow-[0_0_15px_rgba(16,185,129,0.3)] group-hover:scale-105 transition-transform">
                            <CreditCard size={20} className="text-emerald-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-emerald-200 transition-colors">
                                Payment Gateway
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-emerald-300/80">Bank Wire & Checkouts</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className="text-[8px] font-mono font-bold text-emerald-300 bg-emerald-950/80 px-2 py-0.5 rounded-full border border-emerald-500/40">
                            ONLINE
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Bank account delivery, payment link dispatch, and customer slip uploads over WhatsApp & Web Voice.
                      </p>

                      <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Inquiries:</span>
                          <span className="text-white font-bold">{paymentLeadsCount}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Mode:</span>
                          <span className="text-emerald-300 font-bold">MULTI-RAIL</span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-emerald-400 font-bold uppercase">
                        Payment Telemetry
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 group-hover:from-emerald-400 group-hover:to-teal-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (moduleKey === 'call_guard' && hasCallGuard) {
                const isDraggingThis = draggedModule === 'call_guard';
                const isOverThis = dragOverModule === 'call_guard';

                return (
                  <div
                    key="call_guard"
                    draggable
                    onDragStart={() => handleDragStart('call_guard')}
                    onDragOver={(e) => handleDragOver(e, 'call_guard')}
                    onDrop={() => handleDrop('call_guard')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('call_guard')}
                    className={`rounded-2xl bg-gradient-to-br from-[#2D1217] via-[#1A0A0D] to-[#200D11] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(244,63,94,0.35)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-rose-400'
                        : isOverThis
                        ? 'border-rose-300 ring-2 ring-rose-400/50 scale-[1.02]'
                        : 'border-rose-500/30 hover:border-rose-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-rose-500/20 border border-rose-400/50 text-rose-300 shadow-[0_0_15px_rgba(244,63,94,0.3)] group-hover:scale-105 transition-transform">
                            <ShieldAlert size={20} className="text-rose-400 animate-pulse" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-rose-200 transition-colors">
                                Call Guard & Defense
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-rose-300/80">Silence Watchdog & Anti-Leak</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className="text-[8px] font-mono font-bold text-rose-300 bg-rose-950/80 px-2 py-0.5 rounded-full border border-rose-500/40">
                            SHIELD ACTIVE
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Live call security, silence watchdog auto-hangup, and anti-PII data leak protection on SIP WhatsApp Voice.
                      </p>

                      <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Watchdog:</span>
                          <span className="text-rose-300 font-bold">AUTO-HANGUP</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">PII Guard:</span>
                          <span className="text-emerald-400 font-bold">SECURE</span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-rose-400 font-bold uppercase">
                        Defense Telemetry
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 group-hover:from-rose-400 group-hover:to-pink-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (moduleKey === 'sip_call' && hasSipCall) {
                const isDraggingThis = draggedModule === 'sip_call';
                const isOverThis = dragOverModule === 'sip_call';

                return (
                  <div
                    key="sip_call"
                    draggable
                    onDragStart={() => handleDragStart('sip_call')}
                    onDragOver={(e) => handleDragOver(e, 'sip_call')}
                    onDrop={() => handleDrop('sip_call')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('sip_call')}
                    className={`rounded-2xl bg-gradient-to-br from-[#0c2332] via-[#091823] to-[#0a1c29] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(6,182,212,0.35)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-cyan-400'
                        : isOverThis
                        ? 'border-cyan-300 ring-2 ring-cyan-400/50 scale-[1.02]'
                        : 'border-cyan-500/30 hover:border-cyan-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-cyan-500/20 border border-cyan-400/50 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.3)] group-hover:scale-105 transition-transform">
                            <PhoneCall size={20} className="text-cyan-400 animate-pulse" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-cyan-200 transition-colors">
                                Enterprise SIP PBX
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-cyan-300/80">Grandstream UCM & Landline</span>
                          </div>
                        </div>
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-mono font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">
                          PEER TRUNK
                        </span>
                      </div>

                      <p className="text-[11px] text-slate-300 line-clamp-2 leading-relaxed">
                        Office telephone call answering with autonomous secretary transfer to Ext 101 via SIP REFER & Grandstream API.
                      </p>

                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-cyan-900/30 text-[10px] font-mono">
                        <div className="bg-cyan-950/40 border border-cyan-500/20 rounded-lg p-1.5">
                          <span className="text-slate-400 block text-[9px]">Secretary</span>
                          <span className="text-cyan-300 font-bold">Ext 101</span>
                        </div>
                        <div className="bg-cyan-950/40 border border-cyan-500/20 rounded-lg p-1.5">
                          <span className="text-slate-400 block text-[9px]">Trunk Port</span>
                          <span className="text-emerald-400 font-bold">5060 UDP</span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-cyan-400 font-bold uppercase">
                        Telephony Studio
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-cyan-500 to-sky-500 group-hover:from-cyan-400 group-hover:to-sky-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                );
              }

              if (moduleKey === 'followup_recovery' && hasFollowUp) {
                const isDraggingThis = draggedModule === 'followup_recovery';
                const isOverThis = dragOverModule === 'followup_recovery';
                const telemetry = followUpData?.telemetry || {};

                return (
                  <div
                    key="followup_recovery"
                    draggable
                    onDragStart={() => handleDragStart('followup_recovery')}
                    onDragOver={(e) => handleDragOver(e, 'followup_recovery')}
                    onDrop={() => handleDrop('followup_recovery')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('followup_recovery')}
                    className={`rounded-2xl bg-gradient-to-br from-[#1c0f2b] via-[#130a1f] to-[#170d26] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(168,85,247,0.35)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-purple-400'
                        : isOverThis
                        ? 'border-purple-300 ring-2 ring-purple-400/50 scale-[1.02]'
                        : 'border-purple-500/30 hover:border-purple-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-purple-500/20 border border-purple-400/50 text-purple-300 shadow-[0_0_15px_rgba(168,85,247,0.3)] group-hover:scale-105 transition-transform">
                            <Clock size={20} className="animate-pulse text-purple-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-purple-200 transition-colors">
                                Re-engagement & Recovery
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-purple-300/80">Autonomous Follow-Up Engine</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className={`text-[8px] font-mono font-bold px-2 py-0.5 rounded-full border ${followUpData?.isEnabled !== false ? 'text-purple-300 bg-purple-950/80 border-purple-500/40' : 'text-slate-400 bg-slate-900 border-slate-700'}`}>
                            {followUpData?.isEnabled !== false ? 'ACTIVE' : 'PAUSED'}
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Recovers dropped chats and unconfirmed carts with warm, personalized reminders via Luna AI.
                      </p>

                      <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Follow-Ups:</span>
                          <span className="text-purple-300 font-bold">{telemetry.totalFollowUpsSent || 0}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Recovered:</span>
                          <span className="text-emerald-300 font-bold">{telemetry.totalRecoveredCustomers || 0}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between col-span-2">
                          <span className="text-slate-400">Recovery Rate:</span>
                          <span className="text-cyan-300 font-bold truncate max-w-[140px] flex items-center gap-1">
                            <Sparkles size={11} className="text-cyan-400" />
                            {telemetry.recoveryRatePercentage || 0}% Conversion
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-purple-400 font-bold uppercase">
                        Recovery Studio
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 group-hover:from-purple-400 group-hover:to-indigo-400 text-white font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ChevronRight size={13} />
                      </div>
                    </div>
                  </div>
                );
              }

              if (moduleKey === 'review_booster' && hasReviewBooster) {
                const isDraggingThis = draggedModule === 'review_booster';
                const isOverThis = dragOverModule === 'review_booster';
                const telemetry = reviewBoosterData?.telemetry || {};

                return (
                  <div
                    key="review_booster"
                    draggable
                    onDragStart={() => handleDragStart('review_booster')}
                    onDragOver={(e) => handleDragOver(e, 'review_booster')}
                    onDrop={() => handleDrop('review_booster')}
                    onDragEnd={handleDragEnd}
                    onClick={() => setSelectedModule('review_booster')}
                    className={`rounded-2xl bg-gradient-to-br from-[#241303] via-[#170c02] to-[#120801] border-2 p-4 transition-all duration-300 shadow-lg hover:shadow-[0_0_30px_rgba(245,158,11,0.35)] cursor-pointer group flex flex-col justify-between relative overflow-hidden ${
                      isDraggingThis
                        ? 'opacity-40 scale-95 border-dashed border-amber-400'
                        : isOverThis
                        ? 'border-amber-300 ring-2 ring-amber-400/50 scale-[1.02]'
                        : 'border-amber-500/30 hover:border-amber-400/80'
                    }`}
                  >
                    <div className="space-y-3 relative z-10">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div className="p-2 rounded-xl bg-amber-500/20 border border-amber-400/50 text-amber-300 shadow-[0_0_15px_rgba(245,158,11,0.3)] group-hover:scale-105 transition-transform">
                            <Star size={20} className="fill-amber-400/30 text-amber-400" />
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <h4 className="text-sm font-black text-white font-sans group-hover:text-amber-200 transition-colors">
                                Google Review Booster
                              </h4>
                            </div>
                            <span className="text-[9px] font-mono text-amber-300/80">Reviews & Rating Generator</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          <span className={`text-[8px] font-mono font-bold px-2 py-0.5 rounded-full border ${reviewBoosterData?.isEnabled !== false ? 'text-amber-300 bg-amber-950/80 border-amber-500/40' : 'text-slate-400 bg-slate-900 border-slate-700'}`}>
                            {reviewBoosterData?.isEnabled !== false ? 'ACTIVE' : 'MUTED'}
                          </span>
                          <div className="p-1 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing">
                            <GripVertical size={13} />
                          </div>
                        </div>
                      </div>

                      <p className="text-[11px] text-slate-300 leading-relaxed font-sans line-clamp-2">
                        Collects 5-star Google Maps reviews after delivery with intelligent Negative Feedback Firewall protection.
                      </p>

                      <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono">
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Invites Sent:</span>
                          <span className="text-amber-300 font-bold">{telemetry.totalReviewsRequested || 0}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">5★ Captured:</span>
                          <span className="text-emerald-300 font-bold">{telemetry.totalPositiveCaptured || 0}</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Satisfaction:</span>
                          <span className="text-cyan-300 font-bold">{telemetry.positiveRatePercentage ?? 100}%</span>
                        </div>
                        <div className="p-1.5 rounded-lg bg-slate-950/80 border border-slate-800/80 flex items-center justify-between">
                          <span className="text-slate-400">Firewalled:</span>
                          <span className="text-rose-400 font-bold">{telemetry.totalFirewalledNegative || 0}</span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-3 mt-3 border-t border-slate-800/80 flex items-center justify-between">
                      <span className="text-[9px] font-mono text-amber-400 font-bold uppercase">
                        Review Studio
                      </span>
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 group-hover:from-amber-400 group-hover:to-orange-400 text-white font-black text-[11px] uppercase tracking-wider transition-all shadow-md">
                        <span>Open Studio</span>
                        <ChevronRight size={13} />
                      </div>
                    </div>
                  </div>
                );
              }

              return null;
            })}
          </div>

          {/* Empty State if No Modules are Active */}
          {activeModulesCount === 0 && (
            <div className="p-12 text-center max-w-md mx-auto bg-slate-900/90 border border-slate-800 rounded-3xl space-y-3">
              <Database size={40} className="mx-auto text-slate-500 opacity-40" />
              <h4 className="text-sm font-bold text-white uppercase">No Data Modules in Active Flow</h4>
              <p className="text-xs text-slate-400">
                Deploy Luna Core Hub, Knowledge & Context Node, or Media Vault Node in the Flow Runtime Graph to view live telemetry.
              </p>
              <button
                type="button"
                onClick={onBackToWorkspace}
                className="px-4 py-2 rounded-xl bg-cyan-500 text-slate-950 font-bold text-xs cursor-pointer"
              >
                Go to Flow Runtime Graph
              </button>
            </div>
          )}
        </div>
      ) : selectedModule === 'luna_core' ? (
        /* ═══════════════════════════════════════════════════════════════ */
        /* VIEW 2: LUNA CORE HUB FULL-SCREEN TELEMETRY & CONVERSATIONS     */
        /* ═══════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col overflow-hidden">
          
          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* ULTRA-COMPACT TELEMETRY HUD (ZERO LEAKS, MAX VIEWPORT HEIGHT)   */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <div className="border-b border-cyan-500/20 bg-[#070C18]/95 backdrop-blur-md shrink-0">
            {/* Top Compact Strip: HUD Header & Segmented Switcher */}
            <div className="px-4 py-1.5 flex items-center justify-between gap-2 border-b border-cyan-500/10 bg-[#050914]/90 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 font-mono text-[9px] font-bold tracking-wider uppercase">
                  <Sparkles size={11} className="text-cyan-400 animate-pulse" />
                  <span>Luna AI Telemetry HUD</span>
                </div>
                <div className="hidden md:flex items-center gap-2 text-[10px] font-mono text-slate-400">
                  <span>
                    Total: <strong className="text-amber-300 font-bold">
                      {(tokenScope === 'cycle'
                        ? (billingCycle?.activeCycle?.tokens?.totalTokens ?? billingCycle?.activeCycle?.tokenUsage?.totalTokens ?? stats.currentCycleTokens ?? stats.totalTokensUsed ?? 0)
                        : (billingCycle?.allTimeUsage?.totalTokens ?? billingCycle?.allTimeUsage?.tokens?.totalTokens ?? billingCycle?.allTime?.tokenUsage?.totalTokens ?? stats.allTimeTokens ?? 11093689)
                      ).toLocaleString()}
                    </strong> Tokens
                  </span>
                  {stats.totalCostLkr !== undefined && (
                    <span className="text-emerald-400 font-bold">
                      (💰 LKR {Number(tokenScope === 'cycle' ? (billingCycle?.activeCycle?.tokens?.billableCostLkr ?? stats.totalCostLkr ?? 0) : 0).toFixed(2)})
                    </span>
                  )}
                  {Boolean(stats.totalSavingsLkr || billingCycle?.allTimeUsage?.freeSavedLkr || billingCycle?.allTime?.amounts?.freeTokensSavedLkr) && (
                    <span className="text-emerald-300 font-bold bg-emerald-500/15 border border-emerald-500/30 px-1.5 py-0.2 rounded text-[9px]">
                      🎉 Saved: LKR {Number(tokenScope === 'cycle'
                        ? (billingCycle?.activeCycle?.tokens?.freeSavedLkr ?? billingCycle?.activeCycle?.amounts?.freeTokensSavedLkr ?? stats.totalSavingsLkr ?? 0)
                        : (billingCycle?.allTimeUsage?.freeSavedLkr ?? billingCycle?.allTime?.amounts?.freeTokensSavedLkr ?? stats.totalSavingsLkr ?? 0)
                      ).toFixed(2)}
                    </span>
                  )}
                </div>

                {/* Active Cycle Badge */}
                {billingCycle?.activeCycle && (
                  <span className="hidden xl:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 text-[9px] font-mono font-bold">
                    <Clock size={10} className="text-cyan-400" />
                    <span>
                      {billingCycle.activeCycle.label}: {billingCycle.activeCycle.startDate} ➔ {billingCycle.activeCycle.endDate}
                    </span>
                    {billingCycle.activeCycle.daysUntilDue !== null && billingCycle.activeCycle.daysUntilDue !== undefined && (
                      <span className="text-amber-300 font-bold">
                        ({billingCycle.activeCycle.daysUntilDue > 0 ? `Resets in ${billingCycle.activeCycle.daysUntilDue}d` : 'Due Today'})
                      </span>
                    )}
                  </span>
                )}
              </div>

              {/* Scope Switcher & Audit Link */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Token Scope Switcher: Active Cycle (Auto-Reset) vs All-Time Cumulative */}
                <div className="flex items-center p-0.5 bg-black/60 rounded-lg border border-white/10 text-[9px] font-mono">
                  <button
                    type="button"
                    onClick={() => setTokenScope('cycle')}
                    className={`px-2 py-0.5 rounded transition cursor-pointer font-bold ${
                      tokenScope === 'cycle'
                        ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm'
                        : 'text-slate-400 hover:text-white'
                    }`}
                    title="View tokens consumed in the active monthly billing cycle (resets on renewal date)"
                  >
                    Current Cycle
                  </button>
                  <button
                    type="button"
                    onClick={() => setTokenScope('all_time')}
                    className={`px-2 py-0.5 rounded transition cursor-pointer font-bold ${
                      tokenScope === 'all_time'
                        ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40 shadow-sm'
                        : 'text-slate-400 hover:text-white'
                    }`}
                    title="View all-time lifetime cumulative tokens across all billing cycles"
                  >
                    All-Time
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (onOpenAccountModal) {
                      onOpenAccountModal('token_history');
                    } else {
                      window.dispatchEvent(new CustomEvent('open-tenet-billing-tab', { detail: { tab: 'token_history' } }));
                    }
                  }}
                  className="px-2 py-0.5 rounded-md font-bold transition flex items-center gap-1 bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/35 border border-cyan-500/40 cursor-pointer shadow-sm text-[10px]"
                  title="Open full cycle-by-cycle token audit ledger and monthly cloud billing in My Account & Plan"
                >
                  <Activity size={10} className="text-cyan-400" />
                  <span className="hidden sm:inline">Audit & Plan ↗</span>
                </button>
              </div>

              {/* Segmented Mode Switcher Controls */}
              <div className="flex items-center gap-1 bg-slate-950/90 p-0.5 rounded-lg border border-cyan-500/20 text-[9px] font-mono">
                <button
                  type="button"
                  onClick={() => handleSetTelemetryViewMode('financial')}
                  className={`px-2.5 py-0.5 rounded-md font-bold transition flex items-center gap-1 cursor-pointer ${
                    telemetryViewMode === 'financial'
                      ? 'bg-gradient-to-r from-amber-500 to-emerald-500 text-slate-950 font-black shadow-sm'
                      : 'text-slate-400 hover:text-amber-300 hover:bg-slate-900/60'
                  }`}
                  title="Display 4 AI Token and Cost Cards (1 Single Row)"
                >
                  <DollarSign size={10} />
                  <span>AI Cost (4)</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSetTelemetryViewMode('activity')}
                  className={`px-2.5 py-0.5 rounded-md font-bold transition flex items-center gap-1 cursor-pointer ${
                    telemetryViewMode === 'activity'
                      ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-black shadow-sm'
                      : 'text-slate-400 hover:text-purple-300 hover:bg-slate-900/60'
                  }`}
                  title="Display 3 Traffic and Interaction Cards (1 Single Row)"
                >
                  <Activity size={10} />
                  <span>Traffic (3)</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSetTelemetryViewMode('all')}
                  className={`px-2.5 py-0.5 rounded-md font-bold transition flex items-center gap-1 cursor-pointer ${
                    telemetryViewMode === 'all'
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 font-black shadow-sm'
                      : 'text-slate-400 hover:text-cyan-300 hover:bg-slate-900/60'
                  }`}
                  title="Display All 7 Cards in Compact 2-Tier Grid"
                >
                  <Zap size={10} />
                  <span>All (7)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedModule('neural_cache')}
                  className="px-2 py-0.5 rounded-md font-bold transition flex items-center gap-1 bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/35 border border-cyan-500/40 cursor-pointer shadow-sm active:scale-95 text-[10px]"
                  title="Open Luna Neural Context Cache Studio, token breakdown & connected nodes summary"
                >
                  <Zap size={10} className="text-cyan-400" />
                  <span>Neural Cache</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm("⚠️ Are you sure you want to purge all WhatsApp chat logs? This will permanently delete WhatsApp conversations from the database and reset Luna AI's active memory context.")) {
                      fetch('/api/tenet/live-chats/delete-all', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          ...(token ? { Authorization: `Bearer ${token}` } : {})
                        },
                        body: JSON.stringify({ channel: 'whatsapp' })
                      }).then(r => r.json()).then(d => {
                        if (d.success) alert(`✅ Successfully deleted ${d.deletedCount || 0} chats. Luna AI context reset!`);
                        else alert('Failed: ' + (d.error || 'Unknown error'));
                      }).catch(err => alert('Failed to purge chats: ' + err.message));
                    }
                  }}
                  className="px-2 py-0.5 rounded-md font-bold transition flex items-center gap-1 bg-rose-500/15 text-rose-300 hover:bg-rose-500/30 border border-rose-500/30 cursor-pointer shadow-sm active:scale-95 text-[10px]"
                  title="Purge all WhatsApp customer conversation logs and reset Luna memory context"
                >
                  <Trash2 size={10} className="text-rose-400" />
                  <span>Purge WA Chats</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSetTelemetryViewMode(telemetryViewMode === 'collapsed' ? 'financial' : 'collapsed')}
                  className={`px-2 py-0.5 rounded-md font-bold transition flex items-center gap-0.5 cursor-pointer ${
                    telemetryViewMode === 'collapsed'
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                      : 'text-slate-400 hover:text-white hover:bg-slate-900/60'
                  }`}
                  title={telemetryViewMode === 'collapsed' ? 'Expand Telemetry' : 'Minimize to 28px Strip for Max Chat Space'}
                >
                  {telemetryViewMode === 'collapsed' ? <ChevronDown size={10} /> : <ChevronUp size={10} />}
                  <span className="hidden sm:inline">{telemetryViewMode === 'collapsed' ? 'Expand' : 'Min'}</span>
                </button>
              </div>
            </div>

            {/* Collapsed View: Ultra-Thin 28px Strip */}
            {telemetryViewMode === 'collapsed' ? (
              <div className="px-4 py-1.5 bg-[#080D1A] flex items-center justify-between gap-3 text-[10px] font-mono overflow-x-auto">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center gap-1 text-amber-300 font-bold">
                    <Zap size={11} className="text-amber-400" />
                    <span>Total: {stats.totalTokensUsed.toLocaleString()} Tokens</span>
                    {stats.totalCostLkr !== undefined && (
                      <span className="bg-amber-500/15 border border-amber-500/30 text-amber-300 px-1 py-0.2 rounded text-[9px]">
                        💰 LKR {Number(stats.totalCostLkr || 0).toFixed(2)}
                      </span>
                    )}
                  </div>
                  {Boolean(stats.totalSavingsLkr) && (
                    <span className="text-emerald-400 font-bold bg-emerald-500/10 border border-emerald-500/20 px-1 py-0.2 rounded text-[9px]">
                      🎉 Saved LKR {Number(stats.totalSavingsLkr).toFixed(2)}
                    </span>
                  )}
                  <span className="text-slate-600">•</span>
                  <span className="text-cyan-300">{stats.totalConversations} Interactions</span>
                  <span className="text-slate-600">•</span>
                  <span className="text-purple-300">{stats.totalCalls ?? voiceConversations.length} Calls</span>
                  <span className="text-slate-600">•</span>
                  <span className="text-purple-200">{leadsList.length || stats.totalLeads || 0} Leads</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleSetTelemetryViewMode('financial')}
                  className="text-cyan-400 hover:text-cyan-200 text-[9px] font-bold underline cursor-pointer shrink-0"
                >
                  Expand ▾
                </button>
              </div>
            ) : (
              /* Compact Cards Container (Height ~56px) */
              <div className="px-4 py-2 space-y-2">
                {/* TIER 1: AI FINANCIAL ENGINE */}
                {(telemetryViewMode === 'all' || telemetryViewMode === 'financial') && (
                  <div className="space-y-2">
                    {/* 🌟 SOVEREIGN KEY POOL BILLING: YOUR FREE TOKENS VS PAID MASTER TOKENS */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {/* 1. YOUR FREE TOKENS */}
                      <div className="px-3.5 py-2.5 rounded-xl bg-gradient-to-br from-[#051c14]/95 via-[#03150e]/95 to-[#020b08]/95 border border-emerald-500/40 shadow-sm flex items-center justify-between hover:border-emerald-400/60 transition-all">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="p-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 shrink-0">
                            <Sparkles size={14} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono font-black text-emerald-300 uppercase tracking-wide">
                                Your Free Tokens
                              </span>
                              <span className="text-[7.5px] font-mono font-bold text-emerald-400 bg-emerald-500/20 px-1.5 py-0.2 rounded-full border border-emerald-500/40 shrink-0">
                                100% FREE
                              </span>
                              <span className="text-[7.5px] font-mono font-bold text-cyan-300 bg-cyan-500/20 px-1.5 py-0.2 rounded-full border border-cyan-500/40 shrink-0">
                                {tokenScope === 'cycle' ? 'CURRENT CYCLE' : 'ALL-TIME'}
                              </span>
                            </div>
                            <div className="flex items-baseline gap-2 mt-0.5">
                              <span className="text-lg font-black text-white font-mono tracking-tight leading-none">
                                {(tokenScope === 'cycle'
                                  ? (billingCycle?.activeCycle?.tokens?.freeTokens ?? billingCycle?.activeCycle?.tokenUsage?.freeTokens ?? stats.currentCycleTokens ?? stats.freeTokens?.total ?? 0)
                                  : (billingCycle?.allTimeUsage?.freeTokens ?? billingCycle?.allTimeUsage?.tokens?.freeTokens ?? billingCycle?.allTime?.tokenUsage?.freeTokens ?? stats.allTimeTokens ?? stats.keySourceBreakdown?.freeTokens?.total ?? 11093689)
                                ).toLocaleString()}
                              </span>
                              <span className="text-[9px] font-mono text-emerald-400 font-bold">
                                Cost: LKR 0.00 ($0.00)
                              </span>
                            </div>
                            <div className="text-[8.5px] font-mono text-slate-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                              <span>Dedicated Key Pool</span>
                              <span>•</span>
                              <span className="text-emerald-300 font-bold">
                                🎉 Saved LKR {Number(tokenScope === 'cycle'
                                  ? (billingCycle?.activeCycle?.tokens?.freeSavedLkr ?? billingCycle?.activeCycle?.amounts?.freeTokensSavedLkr ?? stats.totalSavingsLkr ?? 0)
                                  : (billingCycle?.allTimeUsage?.freeSavedLkr ?? billingCycle?.allTime?.amounts?.freeTokensSavedLkr ?? stats.totalSavingsLkr ?? 0)
                                ).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8.5px] font-mono font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                            <CheckCircle2 size={10} />
                            <span>{stats.keySourceBreakdown?.poolSize ? `${stats.keySourceBreakdown.poolSize} Keys Active` : 'Pool Active'}</span>
                          </span>
                        </div>
                      </div>

                      {/* 2. YOUR PAID TOKENS FROM MASTER AI */}
                      <div className="px-3.5 py-2.5 rounded-xl bg-gradient-to-br from-[#1b1405]/95 via-[#130e02]/95 to-[#0b0801]/95 border border-amber-500/40 shadow-sm flex items-center justify-between hover:border-amber-400/60 transition-all">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="p-1.5 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/40 shrink-0">
                            <Zap size={14} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono font-black text-amber-300 uppercase tracking-wide">
                                Your Paid Tokens from Master AI
                              </span>
                              <span className={`text-[7.5px] font-mono font-bold px-1.5 py-0.2 rounded-full border shrink-0 ${
                                Number(tokenScope === 'cycle'
                                  ? (billingCycle?.activeCycle?.tokens?.paidTokens ?? billingCycle?.activeCycle?.tokenUsage?.paidMasterTokens ?? 0)
                                  : (billingCycle?.allTimeUsage?.paidTokens ?? billingCycle?.allTime?.tokenUsage?.paidMasterTokens ?? 0)
                                ) > 0
                                  ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
                                  : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                              }`}>
                                {Number(tokenScope === 'cycle'
                                  ? (billingCycle?.activeCycle?.tokens?.paidTokens ?? billingCycle?.activeCycle?.tokenUsage?.paidMasterTokens ?? 0)
                                  : (billingCycle?.allTimeUsage?.paidTokens ?? billingCycle?.allTime?.tokenUsage?.paidMasterTokens ?? 0)
                                ) > 0 ? 'BILLABLE' : 'ZERO OVERAGE'}
                              </span>
                            </div>
                            <div className="flex items-baseline gap-2 mt-0.5">
                              <span className="text-lg font-black text-white font-mono tracking-tight leading-none">
                                {Number(tokenScope === 'cycle'
                                  ? (billingCycle?.activeCycle?.tokens?.paidTokens ?? billingCycle?.activeCycle?.tokenUsage?.paidMasterTokens ?? 0)
                                  : (billingCycle?.allTimeUsage?.paidTokens ?? billingCycle?.allTime?.tokenUsage?.paidMasterTokens ?? 0)
                                ).toLocaleString()}
                              </span>
                              <span className={`text-[9px] font-mono font-bold ${
                                Number(tokenScope === 'cycle'
                                  ? (billingCycle?.activeCycle?.tokens?.billableCostLkr ?? billingCycle?.activeCycle?.amounts?.totalCostLkr ?? 0)
                                  : (billingCycle?.allTimeUsage?.billableCostLkr ?? billingCycle?.allTime?.amounts?.totalCostLkr ?? 0)
                                ) > 0
                                  ? 'text-amber-300'
                                  : 'text-emerald-400'
                              }`}>
                                Billable: LKR {Number(tokenScope === 'cycle'
                                  ? (billingCycle?.activeCycle?.tokens?.billableCostLkr ?? billingCycle?.activeCycle?.amounts?.totalCostLkr ?? 0)
                                  : (billingCycle?.allTimeUsage?.billableCostLkr ?? billingCycle?.allTime?.amounts?.totalCostLkr ?? 0)
                                ).toFixed(2)} (${Number(tokenScope === 'cycle'
                                  ? (billingCycle?.activeCycle?.tokens?.billableCostUsd ?? billingCycle?.activeCycle?.amounts?.totalCostUsd ?? 0)
                                  : (billingCycle?.allTimeUsage?.billableCostUsd ?? billingCycle?.allTime?.amounts?.totalCostUsd ?? 0)
                                ).toFixed(4)})
                              </span>
                            </div>
                            <div className="text-[8.5px] font-mono text-slate-400 mt-0.5">
                              {Number(tokenScope === 'cycle'
                                ? (billingCycle?.activeCycle?.tokens?.paidTokens ?? billingCycle?.activeCycle?.tokenUsage?.paidMasterTokens ?? 0)
                                : (billingCycle?.allTimeUsage?.paidTokens ?? billingCycle?.allTime?.tokenUsage?.paidMasterTokens ?? 0)
                              ) > 0
                                ? 'Failover platform quota incurred during pool quota limits'
                                : 'No master overages incurred • 100% powered by your dedicated keys'}
                            </div>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8.5px] font-mono font-bold border ${
                            Number(tokenScope === 'cycle'
                              ? (billingCycle?.activeCycle?.tokens?.paidTokens ?? billingCycle?.activeCycle?.tokenUsage?.paidMasterTokens ?? 0)
                              : (billingCycle?.allTimeUsage?.paidTokens ?? billingCycle?.allTime?.tokenUsage?.paidMasterTokens ?? 0)
                            ) > 0
                              ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                              : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                          }`}>
                            <ShieldCheck size={10} />
                            <span>{Number(tokenScope === 'cycle'
                              ? (billingCycle?.activeCycle?.tokens?.paidTokens ?? billingCycle?.activeCycle?.tokenUsage?.paidMasterTokens ?? 0)
                              : (billingCycle?.allTimeUsage?.paidTokens ?? billingCycle?.allTime?.tokenUsage?.paidMasterTokens ?? 0)
                            ) > 0 ? 'Payable' : 'Protected'}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Channel Sub-Breakdown (4 COMPACT CARDS) */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                      {/* 1. Luna Token Usage (Text Chat Tokens) */}
                      <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-[#0c1829]/95 via-[#081220]/95 to-[#050d18]/95 border border-cyan-500/25 shadow-sm flex flex-col justify-between hover:border-cyan-400/40 transition-all">
                        <div className="flex items-center justify-between gap-1.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div className="p-1 rounded-md bg-cyan-500/15 text-cyan-300 shrink-0">
                              <Bot size={11} />
                            </div>
                            <span className="text-[9px] uppercase font-mono font-bold text-cyan-200 truncate">
                              Luna Core Brain
                            </span>
                            <span className="text-[7.5px] font-mono font-bold text-cyan-300 bg-cyan-500/15 px-1 rounded border border-cyan-500/30 shrink-0">
                              TEXT
                            </span>
                          </div>
                          {Boolean(stats.lunaTokenUsage?.costLkr !== undefined) && (
                            <span className="text-[9px] font-mono font-black text-emerald-300 bg-emerald-500/15 px-1.5 py-0.2 rounded-full border border-emerald-500/30 shrink-0">
                              💰 LKR {Number(stats.lunaTokenUsage?.costLkr || 0).toFixed(2)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-baseline justify-between gap-2 mt-1">
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-base font-black text-cyan-100 font-mono tracking-tight leading-none">
                              {(tokenScope === 'cycle'
                                ? (billingCycle?.activeCycle?.tokens?.totalTokens ?? billingCycle?.activeCycle?.tokenUsage?.totalTokens ?? stats.currentCycleTokens ?? stats.lunaTokenUsage?.total ?? stats.totalTokensUsed ?? 0)
                                : (billingCycle?.allTimeUsage?.totalTokens ?? billingCycle?.allTime?.tokenUsage?.totalTokens ?? stats.allTimeTokens ?? stats.totalTokensUsed ?? 11093689)
                              ).toLocaleString()}
                            </span>
                            {Boolean(stats.lunaTokenUsage?.costUsd !== undefined) && (
                              <span className="text-[8px] font-mono text-slate-400 leading-none">
                                (${Number(stats.lunaTokenUsage?.costUsd || 0).toFixed(4)})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-[8px] font-mono text-slate-400">
                            <span>Fresh: <strong className="text-cyan-300">{(stats.lunaTokenUsage?.freshInput ?? Math.max(0, (stats.lunaTokenUsage?.input || 0) - (stats.lunaTokenUsage?.cached || 0))).toLocaleString()}</strong></span>
                            <span>•</span>
                            <span className="text-emerald-400 font-bold">💾 Cached: {(stats.lunaTokenUsage?.cached || 0).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* 2. Luna Call Token Usage (Calling Tokens) */}
                      <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-[#1b0d2e]/95 via-[#130822]/95 to-[#0a0314]/95 border border-purple-500/25 shadow-sm flex flex-col justify-between hover:border-purple-400/40 transition-all">
                        <div className="flex items-center justify-between gap-1.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div className="p-1 rounded-md bg-purple-500/15 text-purple-300 shrink-0">
                              <PhoneCall size={11} />
                            </div>
                            <span className="text-[9px] uppercase font-mono font-bold text-purple-200 truncate">
                              Luna SIP Voice
                            </span>
                            <span className="text-[7.5px] font-mono font-bold text-purple-300 bg-purple-500/15 px-1 rounded border border-purple-500/30 shrink-0">
                              CALLS
                            </span>
                          </div>
                          {Boolean(stats.lunaCallTokenUsage?.costLkr !== undefined) && (
                            <span className="text-[9px] font-mono font-black text-emerald-300 bg-emerald-500/15 px-1.5 py-0.2 rounded-full border border-emerald-500/30 shrink-0">
                              💰 LKR {Number(stats.lunaCallTokenUsage?.costLkr || 0).toFixed(2)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-baseline justify-between gap-2 mt-1">
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-base font-black text-purple-100 font-mono tracking-tight leading-none">
                              {(stats.lunaCallTokenUsage?.total || 0).toLocaleString()}
                            </span>
                            {Boolean(stats.lunaCallTokenUsage?.costUsd !== undefined) && (
                              <span className="text-[8px] font-mono text-slate-400 leading-none">
                                (${Number(stats.lunaCallTokenUsage?.costUsd || 0).toFixed(4)})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-[8px] font-mono text-slate-400">
                            <span>In: <strong className="text-purple-300">{(stats.lunaCallTokenUsage?.input || 0).toLocaleString()}</strong></span>
                            <span>•</span>
                            <span>Out: <strong className="text-purple-400">{(stats.lunaCallTokenUsage?.output || 0).toLocaleString()}</strong></span>
                          </div>
                        </div>
                      </div>

                      {/* 3. Luna Training Knowledge Usage (Training & Vault Tokens) */}
                      <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-[#072018]/95 via-[#041611]/95 to-[#020b08]/95 border border-emerald-500/25 shadow-sm flex flex-col justify-between hover:border-emerald-400/40 transition-all">
                        <div className="flex items-center justify-between gap-1.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div className="p-1 rounded-md bg-emerald-500/15 text-emerald-300 shrink-0">
                              <BookOpen size={11} />
                            </div>
                            <span className="text-[9px] uppercase font-mono font-bold text-emerald-200 truncate">
                              Training & Vault
                            </span>
                            <span className="text-[7.5px] font-mono font-bold text-emerald-300 bg-emerald-500/15 px-1 rounded border border-emerald-500/30 shrink-0">
                              VAULT
                            </span>
                          </div>
                          {Boolean(stats.lunaTrainingKnowledgeUsage?.costLkr !== undefined) && (
                            <span className="text-[9px] font-mono font-black text-emerald-300 bg-emerald-500/15 px-1.5 py-0.2 rounded-full border border-emerald-500/30 shrink-0">
                              💰 LKR {Number(stats.lunaTrainingKnowledgeUsage?.costLkr || 0).toFixed(2)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-baseline justify-between gap-2 mt-1">
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-base font-black text-emerald-100 font-mono tracking-tight leading-none">
                              {(stats.lunaTrainingKnowledgeUsage?.total || 0).toLocaleString()}
                            </span>
                            {Boolean(stats.lunaTrainingKnowledgeUsage?.costUsd !== undefined) && (
                              <span className="text-[8px] font-mono text-slate-400 leading-none">
                                (${Number(stats.lunaTrainingKnowledgeUsage?.costUsd || 0).toFixed(4)})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 text-[8px] font-mono text-slate-400">
                            <span>In: <strong className="text-emerald-300">{(stats.lunaTrainingKnowledgeUsage?.input || 0).toLocaleString()}</strong></span>
                            <span>•</span>
                            <span>Out: <strong className="text-emerald-400">{(stats.lunaTrainingKnowledgeUsage?.output || 0).toLocaleString()}</strong></span>
                          </div>
                        </div>
                      </div>

                      {/* 4. Total Billable AI Expense */}
                      <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-[#1f1707]/95 via-[#160f03]/95 to-[#0e0a02]/95 border border-amber-500/35 shadow-sm flex flex-col justify-between hover:border-amber-400/50 transition-all">
                        <div className="flex items-center justify-between gap-1.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <div className="p-1 rounded-md bg-amber-500/15 text-amber-400 shrink-0">
                              <Zap size={11} />
                            </div>
                            <span className="text-[9px] uppercase font-mono font-bold text-amber-300 truncate">
                              Billable Amount Due
                            </span>
                            <span className="text-[7.5px] font-mono font-bold text-amber-400 bg-amber-500/15 px-1 rounded border border-amber-500/30 shrink-0">
                              PAYABLE
                            </span>
                          </div>
                          <span className="text-[9px] font-mono font-black text-amber-300 bg-amber-500/20 px-1.5 py-0.2 rounded-full border border-amber-500/40 shrink-0">
                            💰 LKR {Number(stats.keySourceBreakdown?.billableCostLkr ?? stats.billableCostLkr ?? stats.keySourceBreakdown?.paidMasterTokens?.costLkr ?? 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between gap-2 mt-1">
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-base font-black text-amber-300 font-mono tracking-tight leading-none">
                              LKR {Number(stats.keySourceBreakdown?.billableCostLkr ?? stats.billableCostLkr ?? stats.keySourceBreakdown?.paidMasterTokens?.costLkr ?? 0).toFixed(2)}
                            </span>
                            <span className="text-[8px] font-mono text-slate-400 leading-none">
                              (${Number(stats.keySourceBreakdown?.billableCostUsd ?? stats.billableCostUsd ?? stats.keySourceBreakdown?.paidMasterTokens?.costUsd ?? 0).toFixed(4)})
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-[8px] font-mono text-slate-400">
                            <span>Total: <strong className="text-amber-200">{stats.totalTokensUsed.toLocaleString()}</strong></span>
                            <span>•</span>
                            <span className="text-emerald-300 font-bold hidden sm:inline">🎉 Free Saved: LKR {Number(stats.keySourceBreakdown?.totalSavedLkr ?? stats.totalSavedLkr ?? stats.totalSavingsLkr ?? 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TIER 2: TRAFFIC & OPERATIONS (3 COMPACT CARDS) */}
                {(telemetryViewMode === 'all' || telemetryViewMode === 'activity') && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    {/* Total Interactions */}
                    <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-[#12192C] to-[#0D1322] border border-cyan-500/20 shadow-sm flex items-center justify-between hover:border-cyan-400/40 transition-all">
                      <div>
                        <span className="text-[8.5px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                          Total Interactions
                        </span>
                        <div className="flex items-baseline gap-1.5 mt-0.5">
                          <span className="text-lg font-black text-white font-sans">{stats.totalConversations}</span>
                          <span className="text-[8px] font-mono font-bold text-emerald-400 bg-emerald-500/10 px-1 py-0.2 rounded border border-emerald-500/20">
                            ACTIVE
                          </span>
                          <span className="text-[9px] font-mono text-slate-400">
                            • {stats.totalMessages || 0} turns processed
                          </span>
                        </div>
                      </div>
                      <div className="p-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 shrink-0">
                        <MessageSquare size={14} />
                      </div>
                    </div>

                    {/* Live Voice Calls KPI */}
                    <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-[#200F36] to-[#120822] border border-purple-500/25 shadow-sm flex items-center justify-between hover:border-purple-400/40 transition-all">
                      <div>
                        <span className="text-[8.5px] uppercase font-mono font-bold text-purple-300 block tracking-wider">
                          Live Voice Calls
                        </span>
                        <div className="flex items-baseline gap-1.5 mt-0.5">
                          <span className="text-lg font-black text-purple-200 font-sans">
                            {stats.totalCalls !== undefined ? stats.totalCalls : voiceConversations.length}
                          </span>
                          <span className="text-[8px] font-mono font-bold text-purple-400 bg-purple-500/15 px-1 py-0.2 rounded border border-purple-500/30">
                            {stats.completedCalls !== undefined ? `${stats.completedCalls} OK` : 'SIP'}
                          </span>
                          <span className="text-[9px] font-mono text-slate-400">
                            • {stats.missedCalls ? `${stats.missedCalls} Missed` : '0 Missed'}
                          </span>
                        </div>
                      </div>
                      <div className="p-1.5 rounded-lg bg-purple-500/20 border border-purple-500/40 text-purple-300 shrink-0">
                        <PhoneCall size={14} className="animate-pulse" />
                      </div>
                    </div>

                    {/* Captured Leads */}
                    <div className="px-3 py-2 rounded-xl bg-gradient-to-br from-[#1E1136] to-[#120B22] border border-purple-500/20 shadow-sm flex items-center justify-between hover:border-purple-400/40 transition-all">
                      <div>
                        <span className="text-[8.5px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                          Captured Leads
                        </span>
                        <div className="flex items-baseline gap-1.5 mt-0.5">
                          <span className="text-lg font-black text-purple-300 font-sans">
                            {leadsList.length || stats.totalLeads || 0}
                          </span>
                          <span className="text-[8px] font-mono font-bold text-purple-400 bg-purple-500/15 px-1 py-0.2 rounded border border-purple-500/30">
                            {stats.newLeads ? `${stats.newLeads} NEW` : 'SYNCED'}
                          </span>
                          <span className="text-[9px] font-mono text-slate-400">
                            • {stats.whatsappLeads || 0} WhatsApp • {stats.webLeads || 0} Web
                          </span>
                        </div>
                      </div>
                      <div className="p-1.5 rounded-lg bg-purple-500/15 border border-purple-500/30 text-purple-400 shrink-0">
                        <Users size={14} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Luna Core Hub Chat Split Area */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left: Chat List */}
            <div className={`w-full md:w-80 lg:w-96 border-r border-cyan-500/15 bg-[#0B101E]/95 flex flex-col shrink-0 ${activeSession ? 'hidden md:flex' : 'flex'}`}>
              <div className="p-3.5 border-b border-slate-800/80 space-y-2.5 shrink-0 bg-[#0E1528]">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by Phone #, name, voice transcript..."
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-950/80 border border-cyan-500/20 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-cyan-400 font-sans"
                  />
                </div>

                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-[10px] font-mono">
                  <button
                    type="button"
                    onClick={() => setChannelFilter('all')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition cursor-pointer ${
                      channelFilter === 'all'
                        ? 'bg-cyan-500 text-slate-950 font-black'
                        : 'bg-slate-900 text-slate-400 hover:text-white'
                    }`}
                  >
                    All ({conversations.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setChannelFilter('orders')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition flex items-center gap-1 cursor-pointer ${
                      channelFilter === 'orders'
                        ? 'bg-emerald-400 text-slate-950 font-black shadow-md shadow-emerald-500/30'
                        : 'bg-slate-900 text-emerald-400 hover:text-emerald-200'
                    }`}
                  >
                    <ShoppingBag size={10} /> Orders ({orderConversations.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setChannelFilter('voice')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition flex items-center gap-1 cursor-pointer ${
                      channelFilter === 'voice'
                        ? 'bg-purple-500 text-white font-black shadow-md shadow-purple-900/40'
                        : 'bg-slate-900 text-purple-300 hover:text-purple-100'
                    }`}
                  >
                    <PhoneCall size={10} /> Calls ({voiceConversations.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setChannelFilter('whatsapp')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition flex items-center gap-1 cursor-pointer ${
                      channelFilter === 'whatsapp'
                        ? 'bg-emerald-500 text-slate-950 font-black'
                        : 'bg-slate-900 text-slate-400 hover:text-emerald-300'
                    }`}
                  >
                    <Smartphone size={10} /> WhatsApp ({whatsAppOnlyConversations.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setChannelFilter('web_widget')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition flex items-center gap-1 cursor-pointer ${
                      channelFilter === 'web_widget'
                        ? 'bg-blue-500 text-slate-950 font-black'
                        : 'bg-slate-900 text-slate-400 hover:text-blue-300'
                    }`}
                  >
                    <Globe size={10} /> Web
                  </button>
                  <button
                    type="button"
                    onClick={() => setOnlyShowLeadsInChats((prev) => !prev)}
                    className={`px-2.5 py-1 rounded-lg font-bold transition flex items-center gap-1 cursor-pointer border ${
                      onlyShowLeadsInChats
                        ? 'bg-purple-500 text-slate-950 border-purple-400 font-black'
                        : 'bg-slate-900 border-slate-800 text-purple-300 hover:text-purple-100 hover:border-purple-500/40'
                    }`}
                    title="Toggle leads filter"
                  >
                    <Users size={10} /> Leads
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
                {filteredConversations.length === 0 ? (
                  <div className="p-8 text-center text-slate-500 space-y-2">
                    <MessageSquare size={28} className="mx-auto opacity-40 text-cyan-400" />
                    <p className="text-xs font-medium">No customer interactions found.</p>
                  </div>
                ) : (
                  filteredConversations.map((chat) => {
                    const isSelected = chat.sessionId === activeSession?.sessionId;
                    const lastMsg = chat.messages?.[chat.messages.length - 1];
                    const isVoice = chat.isVoiceCall || chat.channel === 'sip_voice' || chat.sessionId.startsWith('call_');
                    const isMissed = isVoice && (chat.callStatus === 'missed' || (chat.callDuration || 0) === 0);
                    const primaryNode = chat.usedNodes?.[0] || (isVoice ? 'Luna Live SIP Voice' : 'Luna Core Hub');
                    const isOrder = Boolean((chat as any).hasOrder || (chat as any).latestOrder || chat.messages?.some((m) => m.toolsUsed?.some((t) => t.includes('order'))));
                    const isLead = Boolean((chat as any).hasLead || (chat as any).latestLead || chat.sessionId.startsWith('lead_') ||
                      chat.usedNodes?.some((n) => n.toLowerCase().includes('lead') || n.toLowerCase().includes('action')) ||
                      chat.messages?.some((m) => m.toolsUsed?.some((t) => t.includes('lead') || t.includes('form'))));

                    return (
                      <div
                        key={chat.sessionId}
                        onClick={() => setSelectedSessionId(chat.sessionId)}
                        className={`p-3 rounded-2xl border transition-all cursor-pointer relative overflow-hidden group ${
                          isSelected
                            ? isOrder
                              ? 'bg-gradient-to-r from-emerald-950/90 via-slate-900/90 to-teal-950/80 border-emerald-400/90 shadow-[0_0_20px_rgba(16,185,129,0.35)]'
                              : isVoice
                              ? 'bg-gradient-to-r from-purple-950/90 via-slate-900/90 to-indigo-950/80 border-purple-400/90 shadow-[0_0_20px_rgba(168,85,247,0.35)]'
                              : isLead
                              ? 'bg-gradient-to-r from-sky-950/90 via-slate-900/90 to-indigo-950/80 border-sky-400/90 shadow-[0_0_20px_rgba(56,189,248,0.35)]'
                              : 'bg-gradient-to-r from-cyan-950/90 via-slate-900/90 to-blue-950/80 border-cyan-400/90 shadow-[0_0_20px_rgba(6,182,212,0.35)]'
                            : isOrder
                            ? 'bg-emerald-950/20 border-emerald-500/30 hover:border-emerald-400/60 hover:bg-emerald-900/30'
                            : isLead
                            ? 'bg-sky-950/20 border-sky-500/30 hover:border-sky-400/60 hover:bg-sky-900/30'
                            : 'bg-slate-900/60 border-slate-800/80 hover:border-cyan-500/40 hover:bg-slate-900'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-1.5 truncate">
                            {isOrder ? (
                              <span className="p-1 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm">
                                <ShoppingBag size={11} />
                              </span>
                            ) : isVoice ? (
                              <span className={`p-1 rounded-md border ${
                                isMissed
                                  ? 'bg-rose-500/20 text-rose-400 border-rose-500/40'
                                  : 'bg-purple-500/20 text-purple-300 border-purple-500/40'
                              }`}>
                                <PhoneCall size={11} />
                              </span>
                            ) : chat.channel === 'whatsapp' ? (
                              <span className="p-1 rounded-md bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                                <Smartphone size={11} />
                              </span>
                            ) : (
                              <span className="p-1 rounded-md bg-blue-500/20 text-blue-400 border border-blue-500/40">
                                <Globe size={11} />
                              </span>
                            )}
                            <div className="flex flex-col min-w-0 flex-1">
                              <span className="font-bold text-slate-200 text-xs truncate">
                                {chat.phone ? `+${chat.phone}` : chat.visitorName}
                              </span>
                              {Boolean(chat.phone && getDisplayCustomerName(chat)) && (
                                <span className="text-[10px] text-cyan-300/80 font-medium truncate flex items-center gap-1">
                                  <span>👤</span>
                                  <span>{getDisplayCustomerName(chat)}</span>
                                </span>
                              )}
                            </div>
                            {isOrder && (
                              <span className="text-[8px] font-mono font-black px-1.5 py-0.2 rounded bg-emerald-500/25 text-emerald-200 border border-emerald-400/60 shadow-sm">
                                🛍️ ORDER
                              </span>
                            )}
                            {isLead && !isOrder && (
                              <span className="text-[8px] font-mono font-black px-1.5 py-0.2 rounded bg-sky-500/25 text-sky-200 border border-sky-400/60 shadow-sm">
                                🎯 LEAD
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {isVoice && (
                              <span className={`text-[8px] font-mono font-black px-1.5 py-0.5 rounded border flex items-center gap-0.5 ${
                                isMissed
                                  ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
                                  : 'bg-purple-500/20 text-purple-200 border-purple-400/40'
                              }`}>
                                {isMissed ? '🔴 MISSED' : `⏱️ ${Math.floor((chat.callDuration || 0) / 60)}m ${(chat.callDuration || 0) % 60}s`}
                              </span>
                            )}
                            {isOrder && (
                              <span className="text-[8px] font-mono font-black px-1.5 py-0.5 rounded bg-emerald-500/25 text-emerald-200 border border-emerald-400/60 shadow-[0_0_8px_rgba(16,185,129,0.4)] flex items-center gap-0.5">
                                <ShoppingBag size={8} /> {(chat as any).latestOrder?.orderNumber || 'ORDER'}
                              </span>
                            )}
                            {isLead && !isOrder && (
                              <span className="text-[8px] font-mono font-black px-1.5 py-0.5 rounded bg-sky-500/25 text-sky-200 border border-sky-400/50 shadow-[0_0_8px_rgba(56,189,248,0.4)] flex items-center gap-0.5">
                                <Users size={8} /> LEAD
                              </span>
                            )}
                            {chat.humanRequested ? (
                              <span className="text-[8px] font-mono font-black px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/40 animate-pulse flex items-center gap-0.5">
                                <AlertTriangle size={8} /> REQ
                              </span>
                            ) : chat.isHumanTakeover ? (
                              <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 flex items-center gap-0.5">
                                {chat.takeoverMode === 'permanent' ? <Lock size={8} /> : <Clock size={8} />} {chat.takeoverMode === 'permanent' ? 'PERM' : '30M'}
                              </span>
                            ) : !isVoice ? (
                              <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 flex items-center gap-0.5">
                                <Bot size={8} /> AI
                              </span>
                            ) : null}
                            <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-slate-950 ${isVoice ? 'text-purple-300 border border-purple-500/30' : 'text-amber-300 border border-amber-500/20'} flex items-center gap-0.5 shrink-0`}>
                              {isVoice ? <PhoneCall size={9} /> : <Zap size={9} />} {chat.totalTokens.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <p className="text-[11px] text-slate-300 line-clamp-2 leading-relaxed font-sans pl-1">
                          {lastMsg?.text || (isVoice ? (isMissed ? 'Missed incoming voice call' : 'Voice call recording processed') : 'New customer conversation started.')}
                        </p>
                        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-800/60 text-[10px] font-mono">
                          <span className={`${isVoice ? 'text-purple-400' : 'text-cyan-400'} font-bold truncate max-w-[170px] flex items-center gap-1`}>
                            {isVoice ? <PhoneCall size={10} className="shrink-0" /> : <Cpu size={10} className="shrink-0" />}
                            <span className="truncate">{primaryNode}</span>
                          </span>
                          <span className="text-slate-500 text-[9px] shrink-0">
                            {lastMsg?.time || 'Recent'}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right: Message Inspector */}
            <div className={`flex-1 flex flex-col bg-[#090D16] overflow-hidden ${!activeSession ? 'hidden md:flex' : 'flex'}`}>
              {activeSession ? (
                <>
                  {(() => {
                    const isVoice = activeSession.isVoiceCall || activeSession.channel === 'sip_voice' || activeSession.sessionId.startsWith('call_');
                    const isMissed = isVoice && (activeSession.callStatus === 'missed' || (activeSession.callDuration || 0) === 0);

                    return (
                      <>
                        <div className="p-3 sm:p-4 border-b border-cyan-500/20 bg-[#0E1528] flex flex-wrap items-center justify-between gap-2.5 shrink-0">
                          <div className="flex items-center gap-2.5 sm:gap-3">
                            <button
                              type="button"
                              onClick={() => setSelectedSessionId(null)}
                              className="p-2 -ml-1 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white border border-white/10 md:hidden flex items-center justify-center shrink-0 cursor-pointer"
                              title="Back to conversation list"
                            >
                              <ArrowLeft size={16} />
                            </button>
                            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-2xl border flex items-center justify-center shadow-[0_0_15px_rgba(6,182,212,0.4)] ${
                              isVoice
                                ? 'bg-purple-500/20 border-purple-400/50 text-purple-300'
                                : activeSession.channel === 'whatsapp'
                                ? 'bg-emerald-500/15 border-emerald-400/50 text-emerald-400'
                                : 'bg-cyan-500/15 border-cyan-400/50 text-cyan-300'
                            }`}>
                              {isVoice ? (
                                <PhoneCall size={20} className={activeSession.status === 'in_progress' ? 'animate-bounce text-purple-300' : 'text-purple-300'} />
                              ) : activeSession.channel === 'whatsapp' ? (
                                <Smartphone size={20} className="text-emerald-400" />
                              ) : (
                                <Globe size={20} className="text-blue-400" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h2 className="text-sm font-black text-white font-sans">
                                  {activeSession.phone ? `+${activeSession.phone}` : (getDisplayCustomerName(activeSession) || activeSession.visitorName)}
                                </h2>
                                {activeSession.phone && getDisplayCustomerName(activeSession) && (
                                  <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 text-[10px] font-medium flex items-center gap-1">
                                    <span>👤</span> {getDisplayCustomerName(activeSession)}
                                  </span>
                                )}
                                {activeSession.phone && (
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(activeSession.phone || '', 'Phone Number')}
                                    className="text-[10px] text-cyan-400 hover:text-cyan-300 font-mono font-bold flex items-center gap-1 transition cursor-pointer"
                                  >
                                    {copiedText === 'Phone Number' ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                                    <span>{copiedText === 'Phone Number' ? 'COPIED' : 'COPY #'}</span>
                                  </button>
                                )}
                                <span className={`px-2 py-0.5 rounded-full text-[9px] font-mono font-bold border ${
                                  isVoice
                                    ? isMissed
                                      ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
                                      : 'bg-purple-500/20 text-purple-300 border-purple-500/40'
                                    : activeSession.channel === 'whatsapp'
                                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                                    : 'bg-blue-500/20 text-blue-300 border-blue-500/40'
                                }`}>
                                  {isVoice ? (isMissed ? 'MISSED CALL' : 'LIVE SIP VOICE CALL') : activeSession.channel.toUpperCase()}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 text-[10px] text-slate-400 font-mono mt-0.5 flex-wrap">
                                <span>Session: <span className="text-cyan-300">{activeSession.sessionId}</span></span>
                                <span>•</span>
                                <span className="flex items-center gap-1.5">
                                  <span>Tokens: <strong className="text-amber-300 font-bold">{activeSession.totalTokens.toLocaleString()}</strong></span>
                                  <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-950/90 border border-slate-800 text-slate-400">
                                    <span className="text-cyan-300">In: {(activeSession.promptTokens || Math.round(activeSession.totalTokens * 0.88)).toLocaleString()}</span>
                                    <span className="mx-1 text-slate-600">|</span>
                                    <span className="text-purple-300">Out: {(activeSession.completionTokens || Math.max(0, activeSession.totalTokens - Math.round(activeSession.totalTokens * 0.88))).toLocaleString()}</span>
                                  </span>
                                </span>
                                {isVoice && (
                                  <>
                                    <span>•</span>
                                    <span>Duration: <strong className="text-white">{activeSession.callDuration || 0}s ({Math.floor((activeSession.callDuration || 0) / 60)}m {(activeSession.callDuration || 0) % 60}s)</strong></span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Human Takeover & Mode Controls */}
                          <div className="flex items-center gap-2">
                            {!isVoice && (
                              <>
                                {activeSession.isHumanTakeover ? (
                                  <div className="flex items-center gap-2 bg-slate-950/90 p-1.5 rounded-2xl border border-amber-500/50 shadow-[0_0_20px_rgba(245,158,11,0.25)]">
                                    {/* Status & Live Countdown Badge */}
                                    <div className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-amber-500/25 via-rose-500/20 to-amber-500/25 border border-amber-400/50 text-amber-300 text-xs font-mono font-black flex items-center gap-2">
                                      <UserCheck size={14} className="text-amber-400 shrink-0" />
                                      {activeSession.takeoverMode === 'permanent' ? (
                                        <span className="flex items-center gap-1.5 text-rose-300">
                                          <Lock size={12} className="text-rose-400" />
                                          <span>PERMANENT TAKEOVER (AI MUTED)</span>
                                        </span>
                                      ) : (
                                        <span className="flex items-center gap-1.5">
                                          <Clock size={12} className="text-amber-400 animate-pulse" />
                                          <span>
                                            AUTO-RESUME IN:{' '}
                                            <strong className="text-white text-sm tracking-wider font-bold">
                                              {takeoverRemainingSeconds !== null ? formatCountdown(takeoverRemainingSeconds) : '30:00'}
                                            </strong>
                                          </span>
                                        </span>
                                      )}
                                    </div>

                                    {/* Mode Switcher Toggle (30m vs Permanent) */}
                                    <div className="flex items-center bg-slate-900 rounded-lg p-0.5 border border-slate-800 text-[10px] font-mono font-bold">
                                      <button
                                        type="button"
                                        disabled={togglingTakeover}
                                        onClick={() => handleToggleTakeover(activeSession.sessionId, false, 'timed_30m', 30)}
                                        className={`px-2.5 py-1 rounded-md transition cursor-pointer flex items-center gap-1 ${
                                          activeSession.takeoverMode !== 'permanent'
                                            ? 'bg-amber-500 text-slate-950 font-black shadow'
                                            : 'text-slate-400 hover:text-white'
                                        }`}
                                        title="Auto-resume Luna AI in 30 minutes"
                                      >
                                        <Clock size={10} />
                                        <span>30m Timer</span>
                                      </button>
                                      <button
                                        type="button"
                                        disabled={togglingTakeover}
                                        onClick={() => handleToggleTakeover(activeSession.sessionId, false, 'permanent')}
                                        className={`px-2.5 py-1 rounded-md transition cursor-pointer flex items-center gap-1 ${
                                          activeSession.takeoverMode === 'permanent'
                                            ? 'bg-rose-500 text-white font-black shadow'
                                            : 'text-slate-400 hover:text-white'
                                        }`}
                                        title="Permanent takeover - Luna AI will not auto-resume until manually clicked"
                                      >
                                        <Lock size={10} />
                                        <span>Permanent</span>
                                      </button>
                                    </div>

                                    {/* Resume Luna AI Button */}
                                    <button
                                      type="button"
                                      disabled={togglingTakeover}
                                      onClick={() => handleToggleTakeover(activeSession.sessionId, true)}
                                      className="px-3 py-1.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-xs font-mono font-black transition flex items-center gap-1.5 cursor-pointer shadow-[0_0_15px_rgba(6,182,212,0.4)]"
                                      title="Resume Luna AI auto-replies immediately"
                                    >
                                      <Bot size={13} />
                                      <span>RESUME LUNA AI</span>
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      disabled={togglingTakeover}
                                      onClick={() => handleToggleTakeover(activeSession.sessionId, false, 'timed_30m', 30)}
                                      className="px-3.5 py-2 rounded-xl bg-slate-900/90 border border-cyan-500/40 text-cyan-300 hover:border-cyan-300 hover:bg-cyan-500/10 font-mono text-xs font-black transition-all flex items-center gap-2 cursor-pointer shadow-lg"
                                      title="Mute Luna AI and take over for 30 minutes"
                                    >
                                      <Bot size={14} className="text-cyan-400" />
                                      <span>🤖 LUNA AI ACTIVE (TAKE OVER 30M)</span>
                                    </button>
                                    <button
                                      type="button"
                                      disabled={togglingTakeover}
                                      onClick={() => handleToggleTakeover(activeSession.sessionId, false, 'permanent')}
                                      className="px-2.5 py-2 rounded-xl bg-slate-900/90 border border-slate-700 text-slate-400 hover:border-rose-500/50 hover:text-rose-300 font-mono text-[10px] font-bold transition flex items-center gap-1 cursor-pointer"
                                      title="Take over permanently (No 30m auto-resume)"
                                    >
                                      <Lock size={11} />
                                      <span>PERMANENT</span>
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Customer Requested Human Agent Alert Banner */}
                        {activeSession.humanRequested && (
                          <div className="px-4 py-2.5 bg-gradient-to-r from-rose-950/90 via-amber-950/80 to-rose-950/90 border-b border-rose-500/40 flex items-center justify-between shrink-0 animate-pulse">
                            <div className="flex items-center gap-2 text-rose-200 text-xs font-bold font-sans">
                              <AlertTriangle size={15} className="text-rose-400 shrink-0" />
                              <span>⚠️ Customer requested to speak with a Human Agent / Real Representative!</span>
                            </div>
                            {!activeSession.isHumanTakeover && (
                              <button
                                type="button"
                                onClick={() => handleToggleTakeover(activeSession.sessionId, false)}
                                className="px-3 py-1 rounded-lg bg-rose-500 hover:bg-rose-400 text-slate-950 font-mono text-[10px] font-black uppercase tracking-wider transition shadow cursor-pointer"
                              >
                                Take Over Now
                              </button>
                            )}
                          </div>
                        )}

                        {/* Call Telemetry Card for Voice Calls */}
                        {isVoice && (
                          <div className="px-6 pt-4 pb-1">
                            <div className="p-3.5 rounded-2xl bg-gradient-to-r from-[#1A0D2E] via-[#120822] to-[#0A0414] border border-purple-500/30 flex items-center justify-between shadow-lg">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-purple-500/20 border border-purple-400/40 text-purple-300">
                                  <PhoneCall size={18} />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs font-black text-white font-sans">SIP Voice Live Stream Session</span>
                                    <span className="text-[9px] font-mono font-bold text-cyan-300 bg-cyan-950/80 px-2 py-0.5 rounded border border-cyan-500/30">
                                      Luna Ultra Voice Engine
                                    </span>
                                  </div>
                                  <p className="text-[10px] text-slate-400 font-mono">
                                    Real-time Bidirectional RTP Audio & Instant Spoken Sinhala/English Synthesis
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3 text-[10px] font-mono">
                                <div className="text-right">
                                  <div className="text-slate-400">Call Duration:</div>
                                  <div className="text-purple-300 font-bold">{activeSession.callDuration || 0} seconds</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-slate-400">Total Turns:</div>
                                  <div className="text-cyan-300 font-bold">{activeSession.messages.length}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div
                          ref={chatScrollContainerRef}
                          className="flex-1 overflow-y-auto p-6 space-y-5 bg-gradient-to-b from-[#090D16] to-[#0B0F1C] scroll-smooth"
                        >
                          {activeSession.messages.length === 0 ? (
                            <div className="p-12 text-center text-slate-500 space-y-2">
                              <Radio size={28} className="mx-auto opacity-40 text-purple-400" />
                              <p className="text-xs font-medium">
                                {isVoice ? (isMissed ? 'Incoming call was not answered (Missed call logged).' : 'Call initiated; audio streaming.') : 'No conversation messages found.'}
                              </p>
                            </div>
                          ) : (
                            activeSession.messages.map((msg, index) => {
                              const isLuna = msg.sender === 'luna' || msg.role === 'luna' || msg.role === 'model';
                              const isHumanAdmin = msg.sender === 'human_admin';
                              const isCustomer = !isLuna && !isHumanAdmin;

                              return (
                                <div
                                  key={index}
                                  className={`flex flex-col ${isCustomer ? 'items-start' : 'items-end'} animate-in fade-in`}
                                >
                                  <div className={`flex items-center gap-2 mb-1.5 px-1 text-[10px] font-mono ${
                                    isLuna ? 'text-cyan-300' : isHumanAdmin ? 'text-emerald-400' : 'text-slate-400'
                                  }`}>
                                    {isCustomer ? (
                                      <span className="font-bold text-slate-300 flex items-center gap-1">
                                        {isVoice ? <PhoneCall size={11} className="text-purple-400" /> : <Smartphone size={11} className="text-emerald-400" />}
                                        {getDisplayCustomerName(activeSession) || (isVoice ? `Caller (+${activeSession.phone || 'Unknown'})` : (activeSession.phone ? `+${activeSession.phone}` : 'Customer'))}
                                      </span>
                                    ) : isLuna ? (
                                      <>
                                        <span className="font-bold flex items-center gap-1 text-cyan-300">
                                          {isVoice ? <Radio size={11} className="text-purple-400 animate-pulse" /> : <Sparkles size={11} className="text-cyan-400" />}
                                          {isVoice ? 'Luna AI (Live Voice Stream)' : 'Luna AI Response'}
                                        </span>
                                        {msg.nodeUsed && (
                                          <span className="px-2 py-0.5 rounded-full bg-cyan-500/15 border border-cyan-400/40 text-cyan-200 text-[9px] font-black">
                                            NODE: {msg.nodeUsed}
                                          </span>
                                        )}
                                        {msg.tokens && Number(msg.tokens.totalTokens || 0) > 0 && (
                                          <span className="px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[9px] font-bold">
                                            ⚡ {(msg.tokens.totalTokens || 0).toLocaleString()} Tokens (In: {(msg.tokens.promptTokens || 0).toLocaleString()} | Out: {(msg.tokens.completionTokens || 0).toLocaleString()}{msg.tokens.cachedTokens ? ` | 💾 Cached: ${(msg.tokens.cachedTokens || 0).toLocaleString()}` : ""})
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="font-bold text-emerald-400 flex items-center gap-1">
                                        <Shield size={11} /> Human Admin (Takeover)
                                      </span>
                                    )}
                                    <span className="text-slate-500 text-[9px]">{msg.time || 'Recent'}</span>
                                  </div>

                                  <div
                                    className={`max-w-2xl rounded-2xl p-4 text-xs leading-relaxed font-sans shadow-lg ${
                                      isCustomer
                                        ? isVoice
                                          ? 'bg-gradient-to-r from-[#1A122A] to-[#120B1E] border border-purple-500/40 text-white rounded-tl-sm shadow-[0_4px_15px_rgba(168,85,247,0.2)]'
                                          : 'bg-gradient-to-r from-[#111726] to-[#0E1422] border border-slate-700/80 text-white rounded-tl-sm shadow-[0_4px_15px_rgba(0,0,0,0.4)]'
                                        : isLuna
                                        ? isVoice
                                          ? 'bg-gradient-to-r from-[#121026] via-[#101828] to-[#0C1220] border border-purple-500/50 text-slate-100 rounded-tr-sm shadow-[0_4px_25px_rgba(168,85,247,0.25)]'
                                          : 'bg-[#101828] border border-cyan-500/40 text-slate-100 rounded-tr-sm shadow-[0_4px_25px_rgba(6,182,212,0.25)]'
                                        : 'bg-gradient-to-r from-emerald-950 via-teal-950 to-[#0A1E1E] border border-emerald-500/50 text-white rounded-tr-sm shadow-[0_4px_25px_rgba(16,185,129,0.25)]'
                                    }`}
                                  >
                                    {/* Dispatched Photos & Media Previews */}
                                    {(msg.mediaUrl || (msg.mediaUrls && msg.mediaUrls.length > 0) || (msg.dispatchedMedia && msg.dispatchedMedia.length > 0)) && (
                                      <div className="mb-3 space-y-2">
                                        {((msg.mediaUrls && msg.mediaUrls.length > 0) ? msg.mediaUrls : (msg.dispatchedMedia && msg.dispatchedMedia.length > 0) ? msg.dispatchedMedia : [msg.mediaUrl!]).map((url: string, imgIdx: number) => {
                                          if (!url) return null;
                                          const fullMediaUrl = url.startsWith('http') ? url : `${typeof window !== 'undefined' ? window.location.origin : ''}${url}`;
                                          const isPdf = url.toLowerCase().endsWith('.pdf');
                                          if (isPdf) {
                                            return (
                                              <a
                                                key={imgIdx}
                                                href={fullMediaUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-950 border border-cyan-500/40 text-cyan-200 text-xs font-mono hover:border-cyan-300 transition"
                                              >
                                                <FileText size={18} className="text-rose-400 shrink-0" />
                                                <span className="truncate flex-1 font-bold">{url.split('/').pop()}</span>
                                                <ExternalLink size={12} className="text-slate-400" />
                                              </a>
                                            );
                                          }
                                          return (
                                            <div key={imgIdx} className="relative group rounded-xl overflow-hidden border border-cyan-500/40 shadow-lg bg-black/60 max-w-sm">
                                              <img
                                                src={fullMediaUrl}
                                                alt="Dispatched Media Asset"
                                                className="max-h-60 w-full object-cover rounded-xl cursor-pointer hover:opacity-95 transition"
                                                onClick={() => window.open(fullMediaUrl, '_blank')}
                                                onError={(e) => {
                                                  (e.target as HTMLElement).style.display = 'none';
                                                }}
                                              />
                                              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-black/75 backdrop-blur-sm text-[9px] font-mono text-cyan-300 border border-cyan-500/40 flex items-center gap-1 shadow-sm">
                                                <span>📷 Dispatched Media Asset</span>
                                              </div>
                                              <div
                                                onClick={() => window.open(fullMediaUrl, '_blank')}
                                                className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/75 backdrop-blur-sm text-[9px] font-mono text-white flex items-center gap-1 opacity-0 group-hover:opacity-100 transition cursor-pointer"
                                              >
                                                <span>View Full</span>
                                                <ExternalLink size={10} />
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    <MessageVisualContent
                                      text={msg.text}
                                      isIncoming={isCustomer}
                                      messageKey={`telemetry_msg_${index}`}
                                      hasImages={Boolean(msg.mediaUrl || (msg.mediaUrls && msg.mediaUrls.length > 0) || (msg.dispatchedMedia && msg.dispatchedMedia.length > 0))}
                                      theme="telemetry"
                                    />
                                    {msg.toolsUsed && msg.toolsUsed.length > 0 && (
                                      <div className="mt-2.5 pt-2 border-t border-cyan-500/20 flex items-center gap-1.5 flex-wrap text-[10px] font-mono">
                                        <span className="text-slate-400">Tools Executed:</span>
                                        {msg.toolsUsed.map((tool, tIdx) => (
                                          <span key={tIdx} className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-400/40 font-bold">
                                            ⚡ {tool}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                          {/* Anchor element to automatically snap scroll to latest message */}
                          <div ref={chatMessagesEndRef} className="h-1 shrink-0" />
                        </div>
                      </>
                    );
                  })()}

                  <form
                    onSubmit={handleSendReply}
                    className="p-3.5 border-t border-cyan-500/20 bg-[#0E1528] flex items-center gap-3 shrink-0"
                  >
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={
                        activeSession.channel === 'sip_voice' || activeSession.isVoiceCall
                          ? `Send follow-up WhatsApp message to caller (+${activeSession.phone || 'Caller'})...`
                          : `Send direct WhatsApp reply to +${activeSession.phone || 'Customer'}...`
                      }
                      disabled={sendingReply}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-slate-950 border border-cyan-500/30 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 font-sans shadow-inner"
                    />
                    <button
                      type="submit"
                      disabled={sendingReply || !replyText.trim()}
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(16,185,129,0.4)] disabled:opacity-50 cursor-pointer flex items-center gap-2 shrink-0 active:scale-95"
                    >
                      {sendingReply ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                      <span>{activeSession.isVoiceCall || activeSession.channel === 'sip_voice' ? 'Send Follow-up WA' : 'Send WhatsApp'}</span>
                    </button>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center p-8 text-center text-slate-500">
                  <div className="space-y-3 max-w-sm">
                    <Database size={36} className="mx-auto text-cyan-400 opacity-40 animate-pulse" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                      Select a Conversation
                    </h3>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : selectedModule === 'neural_cache' ? (
        /* ═══════════════════════════════════════════════════════════════ */
        /* VIEW 2.5: LUNA NEURAL CONTEXT CACHE & TOKEN ENGINE STUDIO       */
        /* ═══════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col overflow-hidden bg-[#070B14]">
          {/* Neural Cache Studio Top KPI Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4 border-b border-cyan-500/20 bg-[#0A0E1A]/90 shrink-0">
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#0A0E1A] border border-cyan-500/20 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Compiled Token Load
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-white font-sans">
                    {Math.round(cacheStudioData?.estimatedTokens || 0).toLocaleString()}
                  </span>
                  <span className="text-[10px] font-mono text-cyan-400 bg-cyan-500/10 px-1.5 py-0.5 rounded border border-cyan-500/20">
                    TOKENS
                  </span>
                </div>
                <span className="text-[9px] text-cyan-300 font-mono">
                  {cacheStudioData?.isNativeCached ? '⚡ Tier-2 Active (90% OFF)' : '⚡ In-Memory RAM Cache'}
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
                <Cpu size={20} className="animate-spin text-cyan-300 [animation-duration:12s]" />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#0A0E1A] border border-emerald-500/20 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Graph Read Latency
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-emerald-300 font-sans">
                    &lt; 0.05
                  </span>
                  <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                    MS
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Zero DB queries on messages
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                <Activity size={20} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#0A0E1A] border border-indigo-500/20 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Ingested Sensory Nodes
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-indigo-300 font-sans">
                    {cacheStudioData?.nodesSummary?.length || 0}
                  </span>
                  <span className="text-[10px] font-mono text-indigo-400 bg-indigo-500/10 px-1.5 py-0.5 rounded border border-indigo-500/20">
                    NODES
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Compiled into neural prompt
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-indigo-500/10 border border-indigo-500/30 text-indigo-400">
                <Layers size={20} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#0A0E1A] border border-purple-500/20 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Executable Action Tools
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-purple-300 font-sans">
                    {cacheStudioData?.tools?.length || 0}
                  </span>
                  <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded border border-purple-500/20">
                    TOOLS
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Autonomous function calls
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-400">
                <Code size={20} />
              </div>
            </div>
          </div>

          {/* Cache Studio Controls & Navigation Toolbar */}
          <div className="px-6 py-3 border-b border-cyan-500/20 bg-[#0B101D] flex flex-wrap items-center justify-between gap-3 shrink-0">
            <div className="flex items-center gap-1.5 bg-slate-950 p-1 rounded-xl border border-white/10">
              <button
                type="button"
                onClick={() => setCacheStudioActiveTab('overview')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  cacheStudioActiveTab === 'overview'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 shadow-md font-black'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Zap size={13} />
                <span>Neural Architecture</span>
              </button>

              <button
                type="button"
                onClick={() => setCacheStudioActiveTab('nodes')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  cacheStudioActiveTab === 'nodes'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 shadow-md font-black'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Layers size={13} />
                <span>Connected Nodes ({cacheStudioData?.nodesSummary?.length || 0})</span>
              </button>

              <button
                type="button"
                onClick={() => setCacheStudioActiveTab('prompt')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  cacheStudioActiveTab === 'prompt'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 shadow-md font-black'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <FileText size={13} />
                <span>Compiled Prompt ({Math.round(cacheStudioData?.estimatedTokens || 0).toLocaleString()} Tokens)</span>
              </button>

              <button
                type="button"
                onClick={() => setCacheStudioActiveTab('tools')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  cacheStudioActiveTab === 'tools'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-slate-950 shadow-md font-black'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Code size={13} />
                <span>Registered Tools ({cacheStudioData?.tools?.length || 0})</span>
              </button>
            </div>

            <div className="flex items-center gap-2">
              {/* Search Bar for Prompt / Tools tabs */}
              {(cacheStudioActiveTab === 'prompt' || cacheStudioActiveTab === 'tools') && (
                <div className="relative">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={cacheStudioSearchPrompt}
                    onChange={(e) => setCacheStudioSearchPrompt(e.target.value)}
                    placeholder={cacheStudioActiveTab === 'prompt' ? 'Search compiled prompt...' : 'Filter tools...'}
                    className="pl-8 pr-3 py-1.5 text-xs bg-slate-950/80 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 w-48 transition-all"
                  />
                  {cacheStudioSearchPrompt && (
                    <button
                      type="button"
                      onClick={() => setCacheStudioSearchPrompt('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              )}

              {/* Copy Prompt Button */}
              {cacheStudioActiveTab === 'prompt' && (
                <button
                  type="button"
                  onClick={handleCopyCacheStudioPrompt}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 border border-white/10 text-white text-xs font-bold transition cursor-pointer shadow-sm active:scale-95"
                  title="Copy full compiled prompt to clipboard"
                >
                  {copiedCacheStudioPrompt ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} className="text-cyan-400" />}
                  <span>{copiedCacheStudioPrompt ? 'Copied!' : 'Copy Prompt'}</span>
                </button>
              )}

              {/* Neural Cache Mode Toggle Button (AGENTS.md Rule 16 Parity) */}
              <button
                type="button"
                onClick={handleToggleNeuralCache}
                disabled={isTogglingNeuralCache}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl border text-xs font-bold transition-all shadow-sm active:scale-95 cursor-pointer disabled:opacity-50 ${
                  cacheStudioData?.isNeuralCacheEnabled !== false
                    ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200 hover:bg-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.25)]'
                    : 'bg-amber-500/20 border-amber-400 text-amber-200 hover:bg-amber-500/30 shadow-[0_0_15px_rgba(245,158,11,0.25)]'
                }`}
                title={
                  cacheStudioData?.isNeuralCacheEnabled !== false
                    ? 'Neural Cache is Active (<0.05ms memory speed). Click to disable and freshly traverse all connected nodes on every message.'
                    : 'Neural Cache is Disabled (Luna live traverses all nodes fresh per message). Click to enable instant Neural Cache.'
                }
              >
                <Zap size={13} className={isTogglingNeuralCache ? 'animate-spin text-cyan-300' : cacheStudioData?.isNeuralCacheEnabled !== false ? 'text-cyan-400' : 'text-amber-400'} />
                <span>
                  {cacheStudioData?.isNeuralCacheEnabled !== false ? 'CACHE: ENABLED (FAST)' : 'CACHE: DISABLED (LIVE NODES)'}
                </span>
              </button>

              {/* Purge & Re-index Button */}
              <button
                type="button"
                onClick={handlePurgeCacheStudio}
                disabled={isPurgingCacheStudio}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-cyan-600/40 to-emerald-600/40 border border-cyan-500/40 hover:border-cyan-400 text-cyan-200 hover:text-white text-xs font-bold transition-all shadow-[0_0_15px_rgba(6,182,212,0.2)] active:scale-95 disabled:opacity-50 cursor-pointer"
                title="Flush cached graph and re-index all active nodes immediately"
              >
                <RefreshCw size={13} className={isPurgingCacheStudio ? 'animate-spin text-cyan-300' : 'text-cyan-400'} />
                <span>{isPurgingCacheStudio ? 'Re-indexing...' : 'Purge & Re-index'}</span>
              </button>

              <button
                type="button"
                onClick={fetchCacheStudioData}
                disabled={isLoadingCacheStudio}
                className="p-1.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer border border-white/5"
                title="Refresh Cache Telemetry"
              >
                <RefreshCw size={14} className={isLoadingCacheStudio ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          {/* Success Notification Banner */}
          {cacheStudioPurgeSuccess && (
            <div className="px-6 py-2 bg-emerald-950/80 border-b border-emerald-500/40 text-emerald-300 text-xs font-mono font-bold flex items-center justify-between animate-fadeIn shrink-0">
              <div className="flex items-center gap-2">
                <Check size={14} className="text-emerald-400" />
                <span>{cacheStudioPurgeSuccess}</span>
              </div>
              <button
                type="button"
                onClick={() => setCacheStudioPurgeSuccess(null)}
                className="text-emerald-400 hover:text-white"
              >
                <X size={13} />
              </button>
            </div>
          )}

          {/* Cache Studio Main Body Content */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6 tenet-mobile-scroll">
            {isLoadingCacheStudio ? (
              <div className="flex flex-col items-center justify-center py-24 text-slate-400 space-y-3">
                <RefreshCw size={32} className="animate-spin text-cyan-400" />
                <p className="text-xs font-mono">Synchronizing neural graph cache telemetry...</p>
              </div>
            ) : (
              <>
                {/* TAB 1: OVERVIEW & ARCHITECTURE */}
                {cacheStudioActiveTab === 'overview' && (
                  <div className="space-y-6 max-w-6xl mx-auto animate-fadeIn">
                    {/* Architecture Dual-Tier Banner */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="p-5 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#0B1120] border border-cyan-500/30 shadow-xl space-y-3 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl pointer-events-none" />
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black uppercase tracking-wider text-cyan-300 flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping inline-block" />
                            Tier 1: Kangavena In-Memory Graph Cache
                          </span>
                          <span className="text-[10px] font-mono px-2 py-0.5 rounded-md bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold">
                            ACTIVE &amp; LIVE
                          </span>
                        </div>
                        <p className="text-xs text-slate-300 leading-relaxed">
                          The active visual flow graph, upstream knowledge base, product catalogs, media vault schemas, and function tools are compiled once into high-speed memory and served instantaneously. Customer greetings ("Hi", "Kiyada?", "Products") execute with <b>sub-0.05ms speed</b> and zero database read latency.
                        </p>
                        <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[10px] font-mono text-slate-400">
                          <span>Graph Read Latency: <b className="text-cyan-300">&lt; 0.05 ms</b></span>
                          <span>Storage: <b className="text-slate-200">Server RAM</b></span>
                        </div>
                      </div>

                      <div className="p-5 rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#0B1120] border border-emerald-500/30 shadow-xl space-y-3 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none" />
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-black uppercase tracking-wider text-emerald-300 flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full ${cacheStudioData?.isNativeCached ? 'bg-emerald-400 animate-pulse' : 'bg-slate-600'} inline-block`} />
                            Tier 2: Native Context Cache (90% Cost Reduction)
                          </span>
                          <span className={`text-[10px] font-mono px-2 py-0.5 rounded-md font-bold ${
                            cacheStudioData?.isNativeCached
                              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                              : 'bg-slate-800 text-slate-400 border border-white/10'
                          }`}>
                            {cacheStudioData?.isNativeCached ? 'ACTIVE (90% OFF)' : 'STANDBY (REQUIRES 32K)'}
                          </span>
                        </div>
                        <p className="text-xs text-slate-300 leading-relaxed">
                          {cacheStudioData?.isNativeCached
                            ? `Active! Flow tokens (${Math.round(cacheStudioData?.estimatedTokens || 0).toLocaleString()}) meet the 32,768 token threshold. All repeat prompt reads receive the 90% discount ($0.075 / 1M tokens) on live calls and chat.`
                            : `Standby. Google Cloud context caching automatically unlocks a 90% token expense reduction when the compiled system prompt reaches 32,768 tokens (current: ${Math.round(cacheStudioData?.estimatedTokens || 0).toLocaleString()} tokens).`}
                        </p>
                        <div className="space-y-1 pt-1">
                          <div className="flex items-center justify-between text-[10px] font-mono">
                            <span className="text-slate-400">Context Window Progress</span>
                            <span className="text-cyan-300 font-bold">
                              {Math.round(cacheStudioData?.estimatedTokens || 0).toLocaleString()} / 32,768 tokens ({Math.min(100, Math.round(((cacheStudioData?.estimatedTokens || 0) / 32768) * 100))}%)
                            </span>
                          </div>
                          <div className="w-full bg-slate-950 h-1.5 rounded-full overflow-hidden border border-white/10">
                            <div
                              className="bg-gradient-to-r from-cyan-500 to-emerald-400 h-full rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(100, Math.round(((cacheStudioData?.estimatedTokens || 0) / 32768) * 100))}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Pre-Compiled Knowledge & Sensory Feeds Section */}
                    <div className="p-5 rounded-2xl bg-[#0C1222]/80 border border-white/10 space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-black text-white uppercase tracking-wider font-sans">
                            Ingested Upstream Nodes &amp; Sensory Memory
                          </h4>
                          <p className="text-xs text-slate-400 font-mono mt-0.5">
                            Visual graph nodes connected to Luna Core Hub, pre-compiled into persistent memory
                          </p>
                        </div>
                        <span className="text-xs font-mono text-cyan-300 font-bold px-2.5 py-1 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                          {cacheStudioData?.nodesSummary?.length || 0} Connected Nodes
                        </span>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {(cacheStudioData?.nodesSummary || []).map((node: any, idx: number) => (
                          <div
                            key={idx}
                            className="p-4 rounded-xl bg-slate-900/90 border border-white/5 hover:border-cyan-500/40 transition-all flex flex-col justify-between space-y-2 group shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 shrink-0 group-hover:scale-105 transition-transform">
                                  {node.type === 'ProductCatalogNode' ? <ShoppingBag size={15} /> :
                                   node.type === 'KnowledgeNode' ? <BookOpen size={15} /> :
                                   node.type === 'MediaVaultNode' ? <ImageIcon size={15} /> :
                                   node.type === 'LunaMemoryNode' || node.type === 'CustomerMemoryNode' ? <Brain size={15} /> :
                                   node.type === 'SecurityFilterNode' ? <ShieldCheck size={15} /> :
                                   node.type === 'ScraperNode' ? <Globe size={15} /> :
                                   node.type === 'ActionNode' || node.type === 'FormGeneratorNode' ? <Layers size={15} /> :
                                   <Zap size={15} />}
                                </div>
                                <div className="min-w-0">
                                  <p className="text-xs font-bold text-white truncate font-sans">
                                    {node.title || node.name || node.type}
                                  </p>
                                  <span className="text-[10px] font-mono text-cyan-400/80 block">
                                    {node.type}
                                  </span>
                                </div>
                              </div>
                              <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
                                INGESTED
                              </span>
                            </div>

                            <div className="pt-2 border-t border-white/5 flex items-center justify-between text-[10px] font-mono text-slate-400">
                              <span>Estimated Tokens:</span>
                              <span className="text-cyan-300 font-bold">
                                ~{Math.round(node.estimatedTokens || 120).toLocaleString()}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Real-time Sequential Execution Trace */}
                    <div className="p-5 rounded-2xl bg-[#0C1222]/80 border border-white/10 space-y-3">
                      <h4 className="text-sm font-black text-white uppercase tracking-wider font-sans flex items-center gap-2">
                        <Activity size={16} className="text-cyan-400" />
                        Real-Time Execution Pipeline (Sequential Flow)
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-2">
                        <div className="p-3.5 rounded-xl bg-slate-900 border border-white/5 space-y-1">
                          <span className="text-[10px] font-mono font-bold text-cyan-400 block">STEP 1: INBOUND TRIGGER</span>
                          <p className="text-xs font-bold text-white">Customer Message / Call</p>
                          <p className="text-[10px] text-slate-400">WhatsApp text, voice audio, or live SIP incoming phone call received.</p>
                        </div>
                        <div className="p-3.5 rounded-xl bg-slate-900 border border-cyan-500/30 space-y-1">
                          <span className="text-[10px] font-mono font-bold text-emerald-400 block">STEP 2: RAM CACHE LOOKUP</span>
                          <p className="text-xs font-bold text-emerald-300">&lt; 0.05ms Zero DB Delay</p>
                          <p className="text-[10px] text-slate-400">Active flow state and prompt pre-compiled in memory, skipping DB disk read.</p>
                        </div>
                        <div className="p-3.5 rounded-xl bg-slate-900 border border-white/5 space-y-1">
                          <span className="text-[10px] font-mono font-bold text-indigo-400 block">STEP 3: CORE HUB INGESTION</span>
                          <p className="text-xs font-bold text-indigo-300">Multimodal Decision Core</p>
                          <p className="text-[10px] text-slate-400">Sovereign brain evaluates user intent and executes reasoning across graph tools.</p>
                        </div>
                        <div className="p-3.5 rounded-xl bg-slate-900 border border-white/5 space-y-1">
                          <span className="text-[10px] font-mono font-bold text-purple-400 block">STEP 4: ACTION DISPATCH</span>
                          <p className="text-xs font-bold text-purple-300">Autonomous Execution</p>
                          <p className="text-[10px] text-slate-400">Media Vault images sent, catalog items ordered, or audio streamed back instantly.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 2: CONNECTED NODES */}
                {cacheStudioActiveTab === 'nodes' && (
                  <div className="space-y-4 max-w-6xl mx-auto animate-fadeIn">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-mono text-slate-400">
                        Showing all {cacheStudioData?.nodesSummary?.length || 0} graph nodes connected upstream and downstream to Luna Core Hub.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(cacheStudioData?.nodesSummary || []).map((node: any, idx: number) => (
                        <div key={idx} className="p-4 rounded-2xl bg-slate-900 border border-white/10 space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                              <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-300 border border-cyan-500/30">
                                <Layers size={14} />
                              </div>
                              <div>
                                <h5 className="text-xs font-black text-white">{node.title || node.name || node.type}</h5>
                                <span className="text-[10px] font-mono text-cyan-400">{node.type}</span>
                              </div>
                            </div>
                            <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                              ACTIVE
                            </span>
                          </div>
                          {node.summary && (
                            <p className="text-xs text-slate-300 bg-slate-950/60 p-2.5 rounded-xl border border-white/5 font-mono text-[11px] leading-relaxed">
                              {node.summary}
                            </p>
                          )}
                          <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 pt-1">
                            <span>Node ID: <b className="text-slate-300">{node.id}</b></span>
                            <span>Token Weight: <b className="text-cyan-300">~{Math.round(node.estimatedTokens || 120).toLocaleString()}</b></span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* TAB 3: COMPILED SYSTEM PROMPT */}
                {cacheStudioActiveTab === 'prompt' && (
                  <div className="space-y-3 max-w-6xl mx-auto animate-fadeIn">
                    <div className="flex items-center justify-between text-xs font-mono text-slate-400">
                      <span>Total Prompt Weight: <b className="text-cyan-300">{Math.round(cacheStudioData?.estimatedTokens || 0).toLocaleString()} Tokens</b></span>
                      <span>Lines: <b className="text-slate-200">{(cacheStudioData?.fullSystemPrompt || cacheStudioData?.systemPromptPreview || '').split('\n').length}</b></span>
                    </div>

                    <div className="rounded-2xl bg-[#080C16] border border-white/10 overflow-hidden shadow-2xl">
                      <div className="px-4 py-2.5 bg-slate-950 border-b border-white/10 flex items-center justify-between text-xs font-mono">
                        <span className="text-cyan-300 font-bold flex items-center gap-2">
                          <FileText size={13} />
                          authoritative_luna_system_prompt.md
                        </span>
                        <span className="text-slate-500 text-[10px]">
                          Synthesized by lunaFlowInterpreter.ts
                        </span>
                      </div>
                      <pre className="p-5 text-xs font-mono text-slate-300 leading-relaxed overflow-x-auto max-h-[60vh] select-text whitespace-pre-wrap">
                        {(() => {
                          const full = cacheStudioData?.fullSystemPrompt || cacheStudioData?.systemPromptPreview || 'No compiled system prompt found for this flow.';
                          if (!cacheStudioSearchPrompt.trim()) return full;
                          return full
                            .split('\n')
                            .filter((line: string) => line.toLowerCase().includes(cacheStudioSearchPrompt.toLowerCase()))
                            .join('\n');
                        })()}
                      </pre>
                    </div>
                  </div>
                )}

                {/* TAB 4: REGISTERED TOOL SCHEMAS */}
                {cacheStudioActiveTab === 'tools' && (
                  <div className="space-y-4 max-w-6xl mx-auto animate-fadeIn">
                    <p className="text-xs font-mono text-slate-400">
                      Luna AI autonomously invokes these function tools during live multimodal sessions.
                    </p>

                    <div className="space-y-3">
                      {(cacheStudioData?.tools || [])
                        .filter((t: any) => {
                          if (!cacheStudioSearchPrompt.trim()) return true;
                          const q = cacheStudioSearchPrompt.toLowerCase();
                          return (t.name || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q);
                        })
                        .map((tool: any, idx: number) => (
                          <div key={idx} className="p-4 rounded-2xl bg-slate-900 border border-white/10 space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Code size={14} className="text-purple-400" />
                                <span className="text-xs font-black text-white font-mono">{tool.name}</span>
                              </div>
                              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-purple-500/10 text-purple-300 border border-purple-500/30">
                                Luna Action Tool
                              </span>
                            </div>
                            <p className="text-xs text-slate-300 leading-relaxed">
                              {tool.description}
                            </p>
                            {tool.parameters && (
                              <div className="bg-slate-950 p-3 rounded-xl border border-white/5 overflow-x-auto">
                                <pre className="text-[11px] font-mono text-slate-400">
                                  {JSON.stringify(tool.parameters, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : selectedModule === 'knowledge' ? (
        /* ═══════════════════════════════════════════════════════════════ */
        /* VIEW 3: KNOWLEDGE & CONTEXT BASE FULL-SCREEN STUDIO             */
        /* ═══════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col overflow-hidden bg-[#0A0E1A]">
          
          {/* Knowledge Studio Top KPI Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 py-4 border-b border-emerald-500/15 bg-[#0B1220]/90 shrink-0">
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#101A2E] to-[#0B1220] border border-emerald-500/20 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Knowledge Volume
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-white font-sans">
                    {knowledgeText.trim().split(/\s+/).filter(Boolean).length.toLocaleString()}
                  </span>
                  <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.2 rounded">
                    WORDS
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  {knowledgeText.length.toLocaleString()} characters
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                <FileText size={18} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#101A2E] to-[#0B1220] border border-emerald-500/20 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Structured Sections
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-emerald-300 font-sans">
                    {parsedSections.length}
                  </span>
                  <span className="text-[10px] font-mono text-teal-400 bg-teal-500/10 px-1.5 py-0.2 rounded">
                    SECTIONS
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  {allExtractedFAQs.length} FAQs extracted
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-teal-500/10 border border-teal-500/30 text-teal-400">
                <Layers size={18} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#101A2E] to-[#0B1220] border border-emerald-500/20 shadow-lg flex items-center justify-between">
              <div className="min-w-0 pr-2">
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Source Document Digest
                </span>
                <div className="flex items-center gap-1.5 mt-1 truncate">
                  <FileUp size={14} className="text-cyan-400 shrink-0" />
                  <span className="text-xs font-black text-cyan-300 truncate font-mono">
                    {knowledgeData?.lastTrainedFileName || 'Direct Markdown'}
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono block mt-0.5 truncate">
                  {knowledgeData?.lastTrainedFileSize
                    ? `${(knowledgeData.lastTrainedFileSize / 1024).toFixed(1)} KB Ingested`
                    : 'Neural Synced'}
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 shrink-0">
                <Brain size={18} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#101A2E] to-[#0B1220] border border-emerald-500/20 shadow-lg flex items-center justify-between">
              <div className="min-w-0 pr-2">
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  AI Context Keywords
                </span>
                <div className="flex items-center gap-1.5 mt-1 truncate">
                  <Tag size={13} className="text-amber-400 shrink-0" />
                  <span className="text-xs font-black text-amber-300 truncate font-mono">
                    {knowledgeData?.autoKeywords || 'pricing, products, support'}
                  </span>
                </div>
                <span className="text-[9px] text-emerald-400 font-mono block mt-0.5">
                  RAG Auto-Indexed
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 shrink-0">
                <Sparkles size={18} />
              </div>
            </div>
          </div>

          {/* Knowledge Controls & View Mode Toolbar */}
          <div className="px-6 py-3 border-b border-emerald-500/20 bg-[#0E1626] flex flex-wrap items-center justify-between gap-3 shrink-0">
            <div className="flex items-center gap-1.5 bg-slate-950 p-1 rounded-xl border border-emerald-500/25">
              <button
                type="button"
                onClick={() => setKnowledgeViewMode('explorer')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  knowledgeViewMode === 'explorer'
                    ? 'bg-emerald-500 text-slate-950 shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Layers size={13} />
                <span>Structured Explorer</span>
              </button>

              <button
                type="button"
                onClick={() => setKnowledgeViewMode('editor')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  knowledgeViewMode === 'editor'
                    ? 'bg-emerald-500 text-slate-950 shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <Edit3 size={13} />
                <span>Raw Markdown Editor</span>
              </button>

              <button
                type="button"
                onClick={() => setKnowledgeViewMode('faq')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  knowledgeViewMode === 'faq'
                    ? 'bg-emerald-500 text-slate-950 shadow-md'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                <HelpCircle size={13} />
                <span>FAQs Matrix ({allExtractedFAQs.length})</span>
              </button>

              <button
                type="button"
                onClick={() => setKnowledgeViewMode('train')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  knowledgeViewMode === 'train'
                    ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-slate-950 shadow-md'
                    : 'text-cyan-300 hover:text-white'
                }`}
              >
                <UploadCloud size={13} />
                <span>Train Document</span>
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleSaveKnowledge()}
                disabled={isSavingKnowledge}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(16,185,129,0.4)] cursor-pointer disabled:opacity-50"
              >
                {isSavingKnowledge ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                <span>Save Changes</span>
              </button>

              <button
                type="button"
                onClick={() => copyToClipboard(knowledgeText, 'Full Knowledge Base')}
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 hover:border-emerald-500/40 text-slate-300 hover:text-white text-xs font-bold transition cursor-pointer"
              >
                {copiedText === 'Full Knowledge Base' ? <Check size={13} className="text-emerald-400" /> : <Copy size={13} />}
                <span>Copy</span>
              </button>

              <button
                type="button"
                onClick={handleDownloadMarkdown}
                className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 hover:border-emerald-500/40 text-slate-300 hover:text-white text-xs font-bold transition cursor-pointer"
                title="Download as .md file"
              >
                <Download size={13} />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* Sub-View 1: Structured Section Explorer */}
          {knowledgeViewMode === 'explorer' && (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="px-6 py-2.5 border-b border-slate-800/80 bg-[#0B101E] flex flex-wrap items-center justify-between gap-3 shrink-0">
                <div className="flex items-center gap-1.5 overflow-x-auto text-[11px] font-mono">
                  {KNOWLEDGE_CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => setSelectedKnowledgeCategory(cat.id)}
                      className={`px-3 py-1 rounded-lg font-bold transition cursor-pointer flex items-center gap-1.5 ${
                        selectedKnowledgeCategory === cat.id
                          ? `${cat.bg} ${cat.color} border ${cat.border}`
                          : 'bg-slate-900/80 text-slate-400 hover:text-white border border-slate-800'
                      }`}
                    >
                      <cat.icon size={12} />
                      <span>{cat.label}</span>
                    </button>
                  ))}
                </div>

                <div className="relative w-64">
                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={knowledgeSearchQuery}
                    onChange={(e) => setKnowledgeSearchQuery(e.target.value)}
                    placeholder="Search sections & keywords..."
                    className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-slate-950 border border-emerald-500/20 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-400 font-sans"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 tenet-mobile-scroll">
                {filteredSections.length === 0 ? (
                  <div className="p-12 text-center text-slate-500 space-y-3 max-w-md mx-auto">
                    <BookOpen size={36} className="mx-auto text-emerald-400 opacity-40 animate-pulse" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                      No Knowledge Sections Found
                    </h3>
                    <button
                      type="button"
                      onClick={() => setKnowledgeViewMode('train')}
                      className="px-4 py-2 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-xs font-bold hover:bg-emerald-500/30 transition cursor-pointer"
                    >
                      Upload & Train Document Now
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {filteredSections.map((sec) => {
                      const catObj = KNOWLEDGE_CATEGORIES.find((c) => c.id === sec.category) || KNOWLEDGE_CATEGORIES[0];
                      const isEditingThis = editingSectionId === sec.id;

                      return (
                        <div
                          key={sec.id}
                          className="rounded-2xl bg-[#0D1424] border border-emerald-500/20 hover:border-emerald-500/40 p-4 transition-all shadow-md flex flex-col justify-between"
                        >
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2 truncate">
                                <span className={`p-1.5 rounded-lg ${catObj.bg} ${catObj.color} border ${catObj.border}`}>
                                  <catObj.icon size={13} />
                                </span>
                                <h3 className="text-xs font-black text-white truncate font-sans">
                                  {sec.title}
                                </h3>
                              </div>
                              <span className="text-[9px] font-mono text-slate-400 px-2 py-0.5 rounded bg-slate-900 border border-slate-800">
                                {sec.wordCount} words
                              </span>
                            </div>

                            {isEditingThis ? (
                              <textarea
                                value={sectionEditText}
                                onChange={(e) => setSectionEditText(e.target.value)}
                                rows={8}
                                className="w-full p-3 rounded-xl bg-slate-950 border border-emerald-500/40 text-xs text-slate-100 font-mono leading-relaxed focus:outline-none"
                              />
                            ) : (
                              <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800/80 text-xs text-slate-300 leading-relaxed font-sans max-h-56 overflow-y-auto whitespace-pre-wrap">
                                {sec.content}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-800/80 text-[11px]">
                            <span className="text-[10px] font-mono text-emerald-400 font-bold uppercase">
                              {catObj.label}
                            </span>
                            <div className="flex items-center gap-2">
                              {isEditingThis ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const updated = knowledgeText.replace(sec.content, sectionEditText);
                                      setKnowledgeText(updated);
                                      handleSaveKnowledge(updated);
                                      setEditingSectionId(null);
                                    }}
                                    className="px-3 py-1 rounded-lg bg-emerald-500 text-slate-950 font-bold text-xs cursor-pointer"
                                  >
                                    Apply
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setEditingSectionId(null)}
                                    className="px-2 py-1 text-slate-400 hover:text-white text-xs cursor-pointer"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditingSectionId(sec.id);
                                      setSectionEditText(sec.content);
                                    }}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-300 hover:bg-slate-900 transition cursor-pointer"
                                    title="Edit section"
                                  >
                                    <Edit3 size={13} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => copyToClipboard(sec.content, `Section: ${sec.title}`)}
                                    className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-300 hover:bg-slate-900 transition cursor-pointer"
                                    title="Copy section text"
                                  >
                                    <Copy size={13} />
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Sub-View 2: Raw Markdown Editor */}
          {knowledgeViewMode === 'editor' && (
            <div className="flex-1 flex flex-col p-6 overflow-hidden">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-xs text-slate-400 font-mono">
                  <FileText size={14} className="text-emerald-400" />
                  <span>Full Markdown Editor — Live updates AI prompt context</span>
                </div>
                <span className="text-[11px] font-mono text-emerald-300">
                  {knowledgeText.length.toLocaleString()} characters
                </span>
              </div>
              <textarea
                value={knowledgeText}
                onChange={(e) => setKnowledgeText(e.target.value)}
                placeholder="# Who We Are\nDetails here...\n\n# Products & Pricing\nList items and prices..."
                className="flex-1 w-full p-4 rounded-2xl bg-slate-950 border border-emerald-500/30 text-xs text-slate-100 font-mono leading-relaxed focus:outline-none focus:border-emerald-400 resize-none shadow-inner"
              />
            </div>
          )}

          {/* Sub-View 3: FAQ Matrix */}
          {knowledgeViewMode === 'faq' && (
            <div className="flex-1 flex flex-col p-3 sm:p-6 overflow-y-auto space-y-3 tenet-mobile-scroll">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-black uppercase tracking-wider text-white flex items-center gap-2">
                  <HelpCircle size={14} className="text-amber-400" />
                  <span>Synthesized Q&A Matrix ({filteredFAQs.length})</span>
                </h3>
              </div>

              {filteredFAQs.length === 0 ? (
                <div className="p-12 text-center text-slate-500 space-y-2">
                  <HelpCircle size={32} className="mx-auto opacity-40 text-amber-400" />
                  <p className="text-xs font-bold">No FAQ items detected.</p>
                </div>
              ) : (
                filteredFAQs.map((faq) => (
                  <div
                    key={faq.id}
                    className="p-4 rounded-2xl bg-[#0D1424] border border-amber-500/20 space-y-2 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="text-xs font-black text-amber-300 font-sans flex items-start gap-2">
                        <span className="text-amber-400 font-mono">Q:</span>
                        <span>{faq.question}</span>
                      </h4>
                      <span className="text-[9px] font-mono px-2 py-0.5 rounded bg-slate-900 text-slate-400 border border-slate-800 shrink-0">
                        {faq.sectionTitle}
                      </span>
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed font-sans pl-5 border-l-2 border-amber-500/30 whitespace-pre-wrap">
                      {faq.answer}
                    </p>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Sub-View 4: Drag & Drop Document & Text Ingestion Hub */}
          {knowledgeViewMode === 'train' && (
            <div className="flex-1 flex flex-col items-center justify-center p-3 sm:p-8 overflow-y-auto tenet-mobile-scroll">
              <div className="max-w-xl w-full bg-[#0D1424] border-2 border-emerald-500/30 rounded-3xl p-8 space-y-6 shadow-2xl">
                <div className="text-center space-y-2">
                  <div className="w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-400/50 flex items-center justify-center mx-auto text-emerald-400 shadow-[0_0_25px_rgba(16,185,129,0.3)]">
                    <Brain size={32} />
                  </div>
                  <h3 className="text-base font-black text-white uppercase tracking-wider">
                    Autonomous Knowledge Synthesis
                  </h3>
                  <p className="text-xs text-slate-300 font-sans leading-relaxed">
                    Upload documents (PDF, DOCX, TXT, CSV) or paste raw text. Luna Neural Core will extract, categorize, and synthesize facts into the live RAG knowledge base.
                  </p>
                </div>

                {/* Merge vs Replace Mode */}
                <div className="grid grid-cols-2 gap-3 p-1.5 rounded-2xl bg-slate-950 border border-emerald-500/20">
                  <button
                    type="button"
                    onClick={() => setIngestMode('merge')}
                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                      ingestMode === 'merge'
                        ? 'bg-emerald-500 text-slate-950 shadow-md'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    ✨ Merge with Existing
                  </button>
                  <button
                    type="button"
                    onClick={() => setIngestMode('replace')}
                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                      ingestMode === 'replace'
                        ? 'bg-rose-500 text-slate-950 shadow-md'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    🔄 Replace Entire Base
                  </button>
                </div>

                {/* Training Tab Switcher: Upload File vs Paste Text */}
                <div className="flex items-center gap-1.5 p-1.5 rounded-2xl bg-slate-950 border border-emerald-500/20">
                  <button
                    type="button"
                    disabled={isTraining}
                    onClick={() => setKnowledgeTrainingTab('upload')}
                    className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                      knowledgeTrainingTab === 'upload'
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 shadow-md'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    <UploadCloud size={14} />
                    <span>Upload Document</span>
                  </button>
                  <button
                    type="button"
                    disabled={isTraining}
                    onClick={() => setKnowledgeTrainingTab('text')}
                    className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                      knowledgeTrainingTab === 'text'
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 shadow-md'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    <Type size={14} />
                    <span>Paste Text</span>
                  </button>
                  <button
                    type="button"
                    disabled={isTraining}
                    onClick={() => setKnowledgeTrainingTab('scrape')}
                    className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                      knowledgeTrainingTab === 'scrape'
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 shadow-md'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    <Globe size={14} />
                    <span>Web Scrape</span>
                  </button>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.md,.csv,.json,image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileDrop(file);
                  }}
                />

                {isTraining ? (
                  <div className="p-6 rounded-2xl bg-emerald-950/40 border border-emerald-500/50 text-center space-y-3">
                    <Brain size={28} className="mx-auto text-emerald-400 animate-pulse" />
                    <h4 className="text-xs font-black text-emerald-300">
                      {trainingPhase === 'uploading'
                        ? 'Fetching & Crawling Target Website URL...'
                        : 'Synthesizing with Luna Neural Core...'}
                    </h4>
                    <p className="text-[11px] text-slate-400 font-mono">
                      {trainingPhase === 'uploading'
                        ? 'Extracting clean content & metadata from domain...'
                        : 'Auto-organizing facts, categories & resolving conflicts...'}
                    </p>
                    <div className="w-full bg-slate-900 rounded-full h-2 overflow-hidden">
                      <div className="bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-400 h-full w-full animate-pulse" />
                    </div>
                  </div>
                ) : knowledgeTrainingTab === 'upload' ? (
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) handleFileDrop(file);
                    }}
                    onClick={() => fileInputRef.current?.click()}
                    className={`cursor-pointer p-8 rounded-2xl border-2 border-dashed transition-all flex flex-col items-center justify-center gap-3 text-center ${
                      isDragging
                        ? 'border-emerald-400 bg-emerald-950/50 shadow-[0_0_30px_rgba(16,185,129,0.4)]'
                        : 'border-emerald-500/40 hover:border-emerald-400 bg-slate-950/60 hover:bg-slate-950'
                    }`}
                  >
                    <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                      <UploadCloud size={28} />
                    </div>
                    <div>
                      <span className="text-xs font-black text-white block">
                        Drag & Drop or Click to Upload Document
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono mt-1 block">
                        PDF, DOCX, TXT, CSV, Markdown, Product Images
                      </span>
                    </div>
                  </div>
                ) : knowledgeTrainingTab === 'text' ? (
                  /* ── Paste Text Training Card ── */
                  <div className="space-y-3 p-4 rounded-2xl bg-slate-950/80 border border-emerald-500/30">
                    <div>
                      <label className="text-[11px] font-bold text-slate-300 mb-1 block">
                        Document / Topic Title (Optional):
                      </label>
                      <input
                        type="text"
                        disabled={isTraining}
                        value={rawTextTitle}
                        onChange={(e) => setRawTextTitle(e.target.value)}
                        placeholder="e.g. Course Syllabus, Return Policies, Price Sheet..."
                        className="w-full bg-slate-900 border border-white/10 rounded-xl px-3 py-2 text-slate-200 text-xs placeholder:text-slate-600 focus:outline-none focus:border-emerald-400"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] font-bold text-slate-300 mb-1 block">
                        Knowledge Content (Sinhala, English, Tamil):
                      </label>
                      <textarea
                        rows={6}
                        disabled={isTraining}
                        value={rawTextInput}
                        onChange={(e) => setRawTextInput(e.target.value)}
                        placeholder="Paste text here to train AI (e.g. course outlines, rules, FAQs, price lists, product info in Sinhala or English)..."
                        className="w-full bg-slate-900 border border-white/10 rounded-xl p-3 text-slate-200 text-xs placeholder:text-slate-600 focus:outline-none focus:border-emerald-400 resize-none font-mono leading-relaxed"
                      />
                    </div>
                    <div className="flex items-center justify-between pt-1">
                      <span className="text-[10px] font-mono text-slate-400">
                        {rawTextInput.length} characters ({rawTextInput.trim() ? rawTextInput.trim().split(/\s+/).length : 0} words)
                      </span>
                      <div className="flex items-center gap-2">
                        {rawTextInput.length > 0 && (
                          <button
                            type="button"
                            onClick={() => {
                              setRawTextInput('');
                              setRawTextTitle('');
                            }}
                            className="px-3 py-1.5 rounded-lg text-xs font-mono text-slate-400 hover:text-white transition cursor-pointer"
                          >
                            Clear
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={!rawTextInput.trim() || isTraining}
                          onClick={handleRawTextTraining}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(16,185,129,0.4)] disabled:opacity-50 cursor-pointer"
                        >
                          <Brain size={14} />
                          <span>Synthesize & Train AI</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* ── Web Scrape & URL Ingestion Card ── */
                  <div className="space-y-4 p-5 rounded-2xl bg-slate-950/80 border border-emerald-500/30">
                    <div>
                      <label className="text-xs font-bold text-slate-200 mb-1.5 flex items-center gap-2">
                        <Globe size={14} className="text-emerald-400" /> Target Website Domain / URL:
                      </label>
                      <input
                        type="url"
                        disabled={isTraining}
                        value={targetWebsiteUrl}
                        onChange={(e) => setTargetWebsiteUrl(e.target.value)}
                        placeholder="https://example.com or https://kangavena.com"
                        className="w-full bg-slate-900 border border-white/10 rounded-xl px-3.5 py-2.5 text-slate-200 text-xs placeholder:text-slate-600 focus:outline-none focus:border-emerald-400 font-mono"
                      />
                      <span className="text-[10px] text-slate-500 font-mono mt-1 block">
                        Enter any public website or domain URL. Luna AI will scrape facts, products, policies, and FAQs automatically.
                      </span>
                    </div>

                    <div className="flex items-center justify-between gap-3 pt-1">
                      <div className="flex items-center gap-1.5 bg-slate-900 p-1 rounded-xl border border-white/5">
                        <button
                          type="button"
                          disabled={isTraining}
                          onClick={() => setUrlCrawlDepth('single')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                            urlCrawlDepth === 'single'
                              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shadow-sm'
                              : 'text-slate-500 hover:text-slate-300'
                          }`}
                        >
                          Single Page
                        </button>
                        <button
                          type="button"
                          disabled={isTraining}
                          onClick={() => setUrlCrawlDepth('deep')}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                            urlCrawlDepth === 'deep'
                              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shadow-sm'
                              : 'text-slate-500 hover:text-slate-300'
                          }`}
                        >
                          Domain Crawl
                        </button>
                      </div>

                      <button
                        type="button"
                        disabled={!targetWebsiteUrl.trim() || isTraining}
                        onClick={handleWebsiteScrapeTraining}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(16,185,129,0.4)] disabled:opacity-50 cursor-pointer"
                      >
                        <Sparkles size={14} />
                        <span>Scrape & Ingest to AI</span>
                      </button>
                    </div>
                  </div>
                )}

                {trainingError && (
                  <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 flex items-start gap-2 text-xs text-rose-300">
                    <AlertCircle size={14} className="shrink-0 mt-0.5 text-rose-400" />
                    <span>{trainingError}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ) : selectedModule === 'media_vault' ? (
        /* ═══════════════════════════════════════════════════════════════ */
        /* VIEW 4: MEDIA VAULT FULL-SCREEN ASSET STUDIO                    */
        /* ═══════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col overflow-hidden bg-[#0A0B16]">
          
          {/* Media Vault Top KPI Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 py-4 border-b border-purple-500/15 bg-[#0E0C20]/90 shrink-0">
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#16122C] to-[#0F0C20] border border-purple-500/20 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Total Media Files
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-white font-sans">{totalFilesCount}</span>
                  <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 px-1.5 py-0.2 rounded">
                    {totalCardsCount} CARD{totalCardsCount !== 1 ? 'S' : ''}
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Across {distinctMediaVaults.length || 1} Category Vault(s)
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-400">
                <FolderOpen size={18} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#16122C] to-[#0F0C20] border border-purple-500/20 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Product Images
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-sky-300 font-sans">{imageCount}</span>
                  <span className="text-[10px] font-mono text-sky-400 bg-sky-500/10 px-1.5 py-0.2 rounded">
                    PHOTOS
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Catalog & Multi-Angle Photos
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-sky-500/10 border border-sky-500/30 text-sky-400">
                <ImageIcon size={18} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#16122C] to-[#0F0C20] border border-purple-500/20 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  PDF Brochures & Docs
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-rose-300 font-sans">{pdfCount}</span>
                  <span className="text-[10px] font-mono text-rose-400 bg-rose-500/10 px-1.5 py-0.2 rounded">
                    DOCUMENTS
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Price lists & Manuals
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-400">
                <FileText size={18} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#16122C] to-[#0F0C20] border border-purple-500/20 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Video Demonstrations
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-violet-300 font-sans">{videoCount}</span>
                  <span className="text-[10px] font-mono text-violet-400 bg-violet-500/10 px-1.5 py-0.2 rounded">
                    VIDEOS
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Promo & Demo Clips
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-violet-500/10 border border-violet-500/30 text-violet-400">
                <Film size={18} />
              </div>
            </div>
          </div>

          {/* Media Vault Toolbar */}
          <div className="px-6 py-3 border-b border-purple-500/20 bg-[#100D24] flex flex-wrap items-center justify-between gap-3 shrink-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5 bg-slate-950 p-1 rounded-xl border border-purple-500/25">
                <button
                  type="button"
                  onClick={() => setMediaFilterType('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    mediaFilterType === 'all'
                      ? 'bg-purple-500 text-white shadow-md'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  All Files ({totalFilesCount})
                </button>
                <button
                  type="button"
                  onClick={() => setMediaFilterType('image')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                    mediaFilterType === 'image'
                      ? 'bg-sky-500 text-slate-950 shadow-md font-bold'
                      : 'text-slate-400 hover:text-sky-300'
                  }`}
                >
                  <ImageIcon size={13} />
                  <span>Images ({imageCount})</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMediaFilterType('pdf')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                    mediaFilterType === 'pdf'
                      ? 'bg-rose-500 text-white shadow-md'
                      : 'text-slate-400 hover:text-rose-300'
                  }`}
                >
                  <FileText size={13} />
                  <span>PDFs ({pdfCount})</span>
                </button>
                <button
                  type="button"
                  onClick={() => setMediaFilterType('video')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                    mediaFilterType === 'video'
                      ? 'bg-violet-500 text-white shadow-md'
                      : 'text-slate-400 hover:text-violet-300'
                  }`}
                >
                  <Film size={13} />
                  <span>Videos ({videoCount})</span>
                </button>
              </div>

              {/* Distinct Vault Category Tabs */}
              {distinctMediaVaults.length > 0 && (
                <div className="flex items-center gap-1 bg-slate-950/80 p-1 rounded-xl border border-purple-500/20 overflow-x-auto text-[10px] font-mono">
                  <button
                    type="button"
                    onClick={() => setSelectedVaultCategory('all')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition cursor-pointer ${
                      selectedVaultCategory === 'all'
                        ? 'bg-purple-500 text-white'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    All Categories
                  </button>
                  {distinctMediaVaults.map((vault) => (
                    <button
                      key={vault}
                      type="button"
                      onClick={() => setSelectedVaultCategory(vault)}
                      className={`px-2.5 py-1 rounded-lg font-bold transition cursor-pointer flex items-center gap-1 ${
                        selectedVaultCategory === vault
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'text-purple-300 hover:text-white'
                      }`}
                    >
                      <FolderOpen size={10} />
                      <span>{vault}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative w-56">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={mediaSearchQuery}
                onChange={(e) => setMediaSearchQuery(e.target.value)}
                placeholder="Search assets by title, tags..."
                className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-slate-950 border border-purple-500/20 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-400 font-sans"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleAddMediaAsset}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-purple-500/20 border border-purple-500/40 text-purple-200 text-xs font-bold hover:bg-purple-500/30 transition cursor-pointer shadow-sm"
              >
                <Plus size={13} />
                <span>+ Add Card</span>
              </button>

              <button
                type="button"
                onClick={() => handleSaveMediaAssets()}
                disabled={isSavingMedia}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white font-black text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(168,85,247,0.4)] cursor-pointer disabled:opacity-50"
              >
                {isSavingMedia ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                <span>Save Vault</span>
              </button>
            </div>
          </div>

          {/* Media Assets Gallery Grid */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 tenet-mobile-scroll">
            {filteredMediaAssets.length === 0 ? (
              <div className="p-12 text-center text-slate-500 space-y-3 max-w-md mx-auto">
                <FolderOpen size={36} className="mx-auto text-purple-400 opacity-40 animate-pulse" />
                <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                  No Media Assets Found
                </h3>
                <p className="text-xs text-slate-400 font-sans">
                  Add product media cards with multiple photos, video demonstrations, or PDF catalogs for Luna AI to dispatch to WhatsApp customers.
                </p>
                <button
                  type="button"
                  onClick={handleAddMediaAsset}
                  className="px-4 py-2 rounded-xl bg-purple-500/20 text-purple-300 border border-purple-500/40 text-xs font-bold hover:bg-purple-500/30 transition cursor-pointer"
                >
                  + Add First Media Card
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filteredMediaAssets.map((asset) => {
                  const isUploadingThis = uploadingAssetId === asset.id;
                  const cardUrls = Array.from(
                    new Set(
                      [
                        ...(Array.isArray(asset.galleryUrls) ? asset.galleryUrls : []),
                        ...(asset.secureUrl ? [asset.secureUrl] : []),
                      ].filter(Boolean)
                    )
                  );

                  return (
                    <div
                      key={asset.id}
                      className="rounded-3xl bg-gradient-to-b from-[#110D26] to-[#0A0717] border border-purple-500/25 hover:border-purple-500/50 p-4 transition-all duration-300 shadow-[0_8px_30px_rgba(0,0,0,0.4)] flex flex-col justify-between space-y-3.5 group/card"
                    >
                      {/* Card Header */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <div className="flex items-center gap-1.5 px-3 py-1 rounded-xl bg-purple-950/80 border border-purple-500/30 text-purple-300 text-xs font-mono font-bold">
                            <Layers size={13} className="text-purple-400" />
                            <span>Images, Videos & Docs</span>
                          </div>
                          {asset.vaultName && (
                            <span className="text-[10px] font-mono text-purple-300 bg-purple-950/70 border border-purple-500/30 px-2 py-0.5 rounded-lg truncate max-w-[110px]">
                              {asset.vaultName}
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono text-slate-400 font-bold bg-slate-950/80 px-2 py-0.5 rounded-md border border-slate-800">
                            {cardUrls.length} file{cardUrls.length !== 1 ? 's' : ''}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleDeleteMediaAsset(asset.id)}
                            className="p-1.5 rounded-xl text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition cursor-pointer"
                            title="Delete card"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Multi-Media Gallery Studio */}
                      <div className="space-y-2.5 p-3 rounded-2xl bg-slate-950/80 border border-purple-500/15">
                        {/* Multi-File Upload Dropzone */}
                        <label className="w-full flex items-center justify-center gap-2 p-2.5 rounded-2xl bg-purple-950/40 hover:bg-purple-900/50 border border-dashed border-purple-500/40 hover:border-purple-400 text-purple-200 text-xs font-bold transition cursor-pointer shadow-sm group">
                          {isUploadingThis || isBatchUploadingMedia ? (
                            <RefreshCw size={14} className="animate-spin text-purple-300" />
                          ) : (
                            <UploadCloud size={14} className="text-purple-400 group-hover:scale-110 transition-transform" />
                          )}
                          <span>+ Upload Media (Images, Videos, Docs)</span>
                          <input
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files && e.target.files.length > 0) {
                                handleMultiFileUploadForAsset(asset.id, e.target.files);
                              }
                            }}
                          />
                        </label>

                        {/* Interactive Gallery Grid */}
                        {cardUrls.length > 0 ? (
                          <div className="grid grid-cols-3 gap-2.5">
                            {cardUrls.map((gUrl, gIdx) => {
                              const gType = detectMediaType(gUrl, 'image');

                              return (
                                <div
                                  key={gIdx}
                                  onClick={() =>
                                    setPreviewMedia({
                                      url: gUrl,
                                      title: `${asset.title || 'Media Asset'} #${gIdx + 1}`,
                                      type: gType,
                                    })
                                  }
                                  className="relative aspect-square rounded-2xl bg-slate-950 border border-purple-500/30 hover:border-purple-400 overflow-hidden group/g flex items-center justify-center cursor-pointer shadow-md transition-all hover:scale-[1.03]"
                                >
                                  {gType === 'pdf' ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center bg-rose-950/30 text-rose-400 p-2 text-center">
                                      <FileText size={24} className="text-rose-400 drop-shadow" />
                                      <span className="text-[9px] font-mono font-bold text-rose-300 mt-1 line-clamp-1">PDF DOC</span>
                                    </div>
                                  ) : gType === 'video' ? (
                                    <div className="w-full h-full relative flex flex-col items-center justify-center bg-purple-950/30 text-violet-400">
                                      <video src={gUrl} preload="metadata" className="w-full h-full object-cover opacity-60" />
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full bg-purple-600/90 text-white flex items-center justify-center shadow-lg">
                                          <Play size={14} className="fill-white ml-0.5" />
                                        </div>
                                      </div>
                                      <span className="absolute bottom-1 right-1 text-[7px] font-mono font-bold px-1 rounded bg-black/80 text-purple-300">VID</span>
                                    </div>
                                  ) : (
                                    <img
                                      src={gUrl}
                                      alt={`gallery-${gIdx}`}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        (e.target as any).src =
                                          'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="60" height="60"><text x="10" y="35" fill="%23999" font-size="11">Media</text></svg>';
                                      }}
                                    />
                                  )}

                                  {/* Type Tag Badge */}
                                  <span className="absolute top-1.5 left-1.5 text-[8px] font-mono font-black px-1.5 py-0.5 rounded bg-black/80 text-purple-300 border border-purple-500/30 uppercase">
                                    {gType}
                                  </span>

                                  {/* Hover Action Controls */}
                                  <div
                                    className="absolute inset-0 bg-slate-950/85 opacity-0 group-hover/g:opacity-100 flex items-center justify-center gap-1.5 transition-opacity z-10"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setPreviewMedia({
                                          url: gUrl,
                                          title: `${asset.title || 'Media Asset'} #${gIdx + 1}`,
                                          type: gType,
                                        })
                                      }
                                      className="p-1.5 rounded-lg bg-purple-500/40 text-purple-200 hover:text-white hover:bg-purple-500 transition cursor-pointer"
                                      title="Fullscreen Preview"
                                    >
                                      <Eye size={12} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => copyToClipboard(gUrl, 'Asset URL')}
                                      className="p-1.5 rounded-lg bg-slate-800 text-slate-300 hover:text-white transition cursor-pointer"
                                      title="Copy URL"
                                    >
                                      <Copy size={12} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const remaining = cardUrls.filter((_, idx) => idx !== gIdx);
                                        const next = mediaAssets.map((a) =>
                                          a.id === asset.id
                                            ? {
                                                ...a,
                                                galleryUrls: remaining,
                                                secureUrl: remaining[0] || '',
                                              }
                                            : a
                                        );
                                        setMediaAssets(next);
                                        handleSaveMediaAssets(next);
                                      }}
                                      className="p-1.5 rounded-lg bg-rose-500/30 text-rose-300 hover:text-white hover:bg-rose-500 transition cursor-pointer"
                                      title="Remove"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="p-6 rounded-2xl bg-slate-950/40 border border-dashed border-purple-500/20 text-center text-xs text-slate-500 space-y-1">
                            <FolderOpen size={24} className="mx-auto text-purple-400/40" />
                            <p className="font-bold text-slate-400">No media uploaded yet</p>
                            <p className="text-[11px] text-slate-500">Click the upload button above to add images, videos, or PDFs</p>
                          </div>
                        )}
                      </div>

                      {/* Title, Vault & Tags Inputs */}
                      <div className="space-y-2 text-xs">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] uppercase font-mono font-bold text-slate-400 block mb-1">
                              Asset Title
                            </label>
                            <input
                              type="text"
                              value={asset.title}
                              onChange={(e) => handleUpdateMediaAsset(asset.id, 'title', e.target.value)}
                              placeholder="e.g. 2026 Model X"
                              className="w-full px-2.5 py-1.5 rounded-xl bg-slate-950 border border-slate-800 focus:border-purple-400 text-white font-sans text-xs"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase font-mono font-bold text-slate-400 block mb-1">
                              Vault / Category
                            </label>
                            <input
                              type="text"
                              value={asset.vaultName || ''}
                              onChange={(e) => handleUpdateMediaAsset(asset.id, 'vaultName', e.target.value)}
                              placeholder="e.g. Vehicles"
                              className="w-full px-2.5 py-1.5 rounded-xl bg-slate-950 border border-slate-800 focus:border-purple-400 text-purple-300 font-sans text-xs"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-[10px] uppercase font-mono font-bold text-slate-400 block mb-1">
                            Trigger Tags (Comma Separated)
                          </label>
                          <input
                            type="text"
                            value={asset.tags}
                            onChange={(e) => handleUpdateMediaAsset(asset.id, 'tags', e.target.value)}
                            placeholder="pricing, brochure, catalog, offer"
                            className="w-full px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 focus:border-purple-400 text-purple-300 font-mono text-[11px]"
                          />
                        </div>

                        <div>
                          <label className="text-[10px] uppercase font-mono font-bold text-slate-400 block mb-1">
                            Asset Description / Marketing Pitch
                          </label>
                          <textarea
                            rows={3}
                            value={asset.description || ''}
                            onChange={(e) => handleUpdateMediaAsset(asset.id, 'description', e.target.value)}
                            placeholder="Full promotional description, certifications (ISO, GMP, FSSC), ingredients, dosage, benefits, and sales copy to quote or send to customers..."
                            className="w-full px-3 py-1.5 rounded-xl bg-slate-950 border border-slate-800 focus:border-purple-400 text-slate-200 font-sans text-xs resize-y"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : selectedModule === 'customer_memory' ? (
        /* ═══════════════════════════════════════════════════════════════ */
        /* VIEW 4B: CUSTOMER EPISODIC MEMORY VAULT STUDIO (PHASE 26)       */
        /* ═══════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col overflow-hidden bg-[#0A0714]">
          {/* Top KPI Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 px-6 py-4 border-b border-purple-500/20 bg-[#0E0A1E]/90 shrink-0">
            {/* Total Clients */}
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1C0E30] to-[#120822] border border-purple-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Remembered Clients
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-white font-sans">{totalMemoriesCount || memoriesList.length}</span>
                  <span className="text-[10px] font-mono text-purple-300 bg-purple-500/20 px-1.5 py-0.2 rounded border border-purple-500/30">
                    EPISODIC
                  </span>
                </div>
                <span className="text-[9px] text-purple-300/80 font-mono">
                  Cross-session recall
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-purple-500/20 border border-purple-500/40 text-purple-300">
                <Brain size={18} className="animate-pulse" />
              </div>
            </div>

            {/* VIP Clients */}
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1C0E30] to-[#120822] border border-purple-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  VIP Clients
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-amber-300 font-sans">
                    {memoriesList.filter((m) => m.isVip).length}
                  </span>
                  <span className="text-[10px] font-mono text-amber-400 bg-amber-500/15 px-1.5 py-0.2 rounded border border-amber-500/30">
                    ⭐ PRIORITY
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Recognized high-value
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-400">
                <Star size={18} />
              </div>
            </div>

            {/* Token Shield Status */}
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1C0E30] to-[#120822] border border-purple-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Token Shield
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-emerald-300 font-sans">~95%</span>
                  <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/15 px-1.5 py-0.2 rounded border border-emerald-500/30">
                    SAVINGS
                  </span>
                </div>
                <span className="text-[9px] text-emerald-400/80 font-mono">
                  Distilled facts only
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400">
                <Zap size={18} />
              </div>
            </div>

            {/* Rule 15 Engine */}
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1C0E30] to-[#120822] border border-purple-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Memory Synthesis
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-sm font-black text-purple-200 font-mono">ACTIVE</span>
                  <span className="text-[10px] font-mono text-cyan-400 bg-cyan-500/15 px-1.5 py-0.2 rounded border border-cyan-500/30">
                    RULE 15
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Luna Neural Synthesis Engine
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-cyan-500/20 border border-cyan-500/40 text-cyan-400">
                <ShieldCheck size={18} />
              </div>
            </div>
          </div>

          {/* Main 2-Column Studio Workspace */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left Column: Customer Memory Directory */}
            <div className={`w-full md:w-80 lg:w-96 border-r border-purple-500/20 bg-[#0E0A1E]/80 flex flex-col shrink-0 ${selectedMemoryId !== null ? 'hidden md:flex' : 'flex'}`}>
              {/* Search Bar */}
              <div className="p-4 border-b border-purple-500/20 space-y-2">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400" />
                  <input
                    type="text"
                    value={memoriesSearchQuery}
                    onChange={(e) => {
                      setMemoriesSearchQuery(e.target.value);
                      fetchMemories(e.target.value);
                    }}
                    placeholder="Search phone, name or topic..."
                    className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-950/80 border border-purple-500/30 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-400 font-sans"
                  />
                </div>
                <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
                  <span>{totalMemoriesCount || memoriesList.length} Customer Profiles</span>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setShowAddMemoryModal(true)}
                      className="px-2 py-0.5 rounded-lg bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border border-purple-500/40 flex items-center gap-1 cursor-pointer font-bold transition"
                      title="Manually create or pre-seed a customer memory profile"
                    >
                      <Plus size={10} />
                      <span>Add Profile</span>
                    </button>
                    <button
                      onClick={() => fetchMemories()}
                      className="text-purple-400 hover:text-purple-300 flex items-center gap-1 cursor-pointer"
                    >
                      <RefreshCw size={10} className={memoriesLoading ? 'animate-spin' : ''} />
                      <span>Refresh</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Customers List */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2 tenet-mobile-scroll">
                {memoriesLoading ? (
                  <div className="py-12 text-center text-slate-500 space-y-2">
                    <Loader2 size={24} className="animate-spin mx-auto text-purple-400" />
                    <p className="text-xs">Accessing Neural Memory Vault...</p>
                  </div>
                ) : memoriesList.length === 0 ? (
                  <div className="py-12 text-center text-slate-500 space-y-2 border border-dashed border-purple-500/20 rounded-2xl p-6">
                    <Brain size={28} className="mx-auto text-purple-400/40" />
                    <p className="text-xs font-bold text-slate-300">No customer memories yet</p>
                    <p className="text-[11px] text-slate-500">
                      When customers call or message on WhatsApp, Luna will automatically distill and save their preferences here.
                    </p>
                    <button
                      type="button"
                      onClick={() => setShowAddMemoryModal(true)}
                      className="mt-2 px-3 py-1.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold flex items-center gap-1.5 mx-auto transition cursor-pointer shadow-lg shadow-purple-900/40"
                    >
                      <Plus size={13} />
                      <span>Create First Memory Profile</span>
                    </button>
                  </div>
                ) : (
                  memoriesList.map((m) => {
                    const isSelected = selectedMemoryId === m.id;
                    const name = m.customerName || 'Returning Customer';
                    const lastDate = m.lastContactAt
                      ? formatDate(m.lastContactAt)
                      : 'Recent';

                    return (
                      <div
                        key={m.id}
                        onClick={() => setSelectedMemoryId(m.id)}
                        className={`p-3 rounded-xl border transition-all cursor-pointer space-y-1.5 ${
                          isSelected
                            ? 'bg-purple-950/60 border-purple-400 shadow-md shadow-purple-900/30'
                            : 'bg-slate-950/50 border-purple-500/20 hover:border-purple-500/50 hover:bg-purple-950/30'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-purple-500/20 border border-purple-400/30 flex items-center justify-center text-purple-300 font-black text-xs">
                              {name.slice(0, 1).toUpperCase()}
                            </div>
                            <div>
                              <h4 className="text-xs font-bold text-white leading-tight flex items-center gap-1.5">
                                <span>{name}</span>
                                {m.isVip && <Star size={11} className="text-amber-400 fill-amber-400" />}
                              </h4>
                              <span className="text-[10px] font-mono text-purple-300/80">+{m.customerIdentifier}</span>
                            </div>
                          </div>
                          <span className="text-[9px] font-mono text-slate-500">{lastDate}</span>
                        </div>

                        {m.distilledMemory && (
                          <p className="text-[10px] text-slate-400 line-clamp-2 leading-relaxed font-sans pl-1 border-l-2 border-purple-500/30">
                            {m.distilledMemory.replace(/^[•\-*]\s*/gm, '')}
                          </p>
                        )}

                        <div className="flex items-center justify-between pt-1 border-t border-purple-500/10 text-[9px] font-mono">
                          <span className="text-purple-400">⚡ {m.totalInteractions || 1} Interactions</span>
                          <span className={`px-1.5 py-0.2 rounded uppercase font-bold text-[8px] ${
                            m.sentimentTrend === 'positive'
                              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                              : m.sentimentTrend === 'demanding'
                              ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                              : 'bg-slate-800 text-slate-400'
                          }`}>
                            {m.sentimentTrend || 'Neutral'}
                          </span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right Column: Customer Episodic Memory Inspector & Simulator */}
            <div className={`flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6 tenet-mobile-scroll ${selectedMemoryId === null ? 'hidden md:flex' : 'flex flex-col'}`}>
              {(() => {
                const activeMemory = selectedMemoryId !== null
                  ? memoriesList.find((m) => m.id === selectedMemoryId)
                  : memoriesList[0];

                if (!activeMemory) {
                  return (
                    <div className="h-full flex items-center justify-center text-center p-12 text-slate-500 space-y-2">
                      <div>
                        <Brain size={36} className="mx-auto opacity-30 text-purple-400 mb-2" />
                        <p className="text-sm font-bold text-slate-300">Select a customer profile on the left</p>
                        <p className="text-xs text-slate-500">Inspect distilled facts, edit preferences, or preview Luna Live prompt injection.</p>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="space-y-5 max-w-4xl">
                    {/* Header Card */}
                    <div className="p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-[#1C0E30] via-[#140B24] to-[#10081C] border border-purple-500/30 shadow-xl flex items-center justify-between">
                      <div className="flex items-center gap-2.5 sm:gap-3.5">
                        <button
                          type="button"
                          onClick={() => setSelectedMemoryId(null)}
                          className="p-2 -ml-1 rounded-xl bg-purple-950/60 hover:bg-purple-900/80 text-purple-300 hover:text-white border border-purple-500/30 md:hidden flex items-center justify-center shrink-0 cursor-pointer"
                          title="Back to customer list"
                        >
                          <ArrowLeft size={16} />
                        </button>
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-purple-500/20 border border-purple-400/40 flex items-center justify-center text-purple-300 font-black text-base sm:text-lg shadow-inner shrink-0">
                          {(activeMemory.customerName || 'C').slice(0, 1).toUpperCase()}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h2 className="text-base sm:text-lg font-black text-white font-sans">
                              {activeMemory.customerName || 'Returning Customer'}
                            </h2>
                            {activeMemory.isVip ? (
                              <span className="px-2 py-0.5 rounded-md text-[9px] font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 flex items-center gap-1">
                                <Star size={10} className="fill-amber-400" />
                                VIP CLIENT
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-md text-[9px] font-mono text-slate-400 bg-slate-900 border border-slate-800">
                                STANDARD
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-400 font-mono mt-0.5">
                            <span className="text-purple-300 font-bold">+{activeMemory.customerIdentifier}</span>
                            <span>•</span>
                            <span>{activeMemory.totalInteractions || 1} Total Interactions</span>
                            <span>•</span>
                            <span>Last spoke: {activeMemory.lastContactAt ? formatDateTime(activeMemory.lastContactAt) : 'N/A'}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingMemory(activeMemory)}
                          className="px-3 py-1.5 rounded-xl bg-purple-600/30 border border-purple-400/40 hover:bg-purple-600/50 text-purple-200 text-xs font-bold flex items-center gap-1.5 transition cursor-pointer"
                        >
                          <Edit3 size={13} />
                          <span>Edit Facts</span>
                        </button>
                        <button
                          onClick={() => handleDeleteMemory(activeMemory.id)}
                          disabled={isDeletingMemory}
                          className="px-3 py-1.5 rounded-xl bg-rose-500/20 border border-rose-500/30 hover:bg-rose-500/40 text-rose-300 text-xs font-bold flex items-center gap-1.5 transition cursor-pointer"
                          title="GDPR Forget Customer"
                        >
                          <Trash2 size={13} />
                          <span>Forget Client</span>
                        </button>
                      </div>
                    </div>

                    {/* Distilled Episodic Memory Card */}
                    <div className="p-5 rounded-2xl bg-slate-950/80 border border-purple-500/30 space-y-3 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Brain size={16} className="text-purple-400" />
                          <h3 className="text-sm font-bold text-white">Distilled Episodic Memory Facts</h3>
                        </div>
                        <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                          Auto-Synthesized by Luna Neural Engine
                        </span>
                      </div>
                      <div className="p-4 rounded-xl bg-[#0F0A1C] border border-purple-500/20 text-xs text-purple-100 font-sans leading-relaxed whitespace-pre-wrap">
                        {activeMemory.distilledMemory || 'No specific notes recorded yet.'}
                      </div>
                    </div>

                    {/* Recorded Customer Preferences & Key Attributes */}
                    <div className="p-5 rounded-2xl bg-slate-950/80 border border-purple-500/30 space-y-3 shadow-lg">
                      <div className="flex items-center gap-2">
                        <Sliders size={16} className="text-purple-400" />
                        <h3 className="text-sm font-bold text-white">Extracted Key Preferences & Attributes</h3>
                      </div>
                      {(() => {
                        const { timeline, ...cleanPrefs } = activeMemory.keyPreferences || {};
                        const entries = Object.entries(cleanPrefs);
                        if (entries.length === 0) {
                          return <p className="text-xs text-slate-500 italic">No specific structured preferences recorded.</p>;
                        }
                        return (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                            {entries.map(([k, v]) => (
                              <div key={k} className="p-3 rounded-xl bg-[#0F0A1C] border border-purple-500/20">
                                <span className="text-[10px] uppercase font-mono text-purple-400 font-bold block">{k}</span>
                                <span className="text-xs font-semibold text-white mt-0.5 block">{String(v)}</span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Chronological Interaction History & Conversation Timeline */}
                    <div className="p-5 rounded-2xl bg-slate-950/80 border border-purple-500/30 space-y-4 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock size={16} className="text-purple-400" />
                          <h3 className="text-sm font-bold text-white">
                            Chronological Conversation History & Topic Timeline
                          </h3>
                        </div>
                        <span className="text-[10px] font-mono text-purple-300 bg-purple-500/10 px-2.5 py-0.5 rounded-full border border-purple-500/30">
                          {Array.isArray(activeMemory.keyPreferences?.timeline) ? activeMemory.keyPreferences.timeline.length : 1} Sessions Recorded
                        </span>
                      </div>

                      {Array.isArray(activeMemory.keyPreferences?.timeline) && activeMemory.keyPreferences.timeline.length > 0 ? (
                        <div className="space-y-3">
                          {activeMemory.keyPreferences.timeline.map((item: any, idx: number) => (
                            <div
                              key={idx}
                              className="p-3.5 rounded-xl bg-gradient-to-r from-[#180E28] via-[#10081C] to-slate-950 border border-purple-500/25 space-y-1.5 hover:border-purple-400/50 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="px-2 py-0.5 rounded text-[9px] font-mono font-bold uppercase bg-purple-500/20 text-purple-300 border border-purple-500/40">
                                    {item.channel || 'Interaction'}
                                  </span>
                                  <h4 className="text-xs font-bold text-white">
                                    {item.topic || 'Discussion'}
                                  </h4>
                                </div>
                                <span className="text-[10px] font-mono text-slate-400 flex items-center gap-1">
                                  <Clock size={11} className="text-purple-400" />
                                  <span>{item.timestamp || 'Recent'}</span>
                                </span>
                              </div>
                              <p className="text-xs text-slate-300 font-sans leading-relaxed pl-2 border-l-2 border-purple-500/40">
                                {item.summary}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-3.5 rounded-xl bg-[#0F0A1C] border border-purple-500/20 space-y-1">
                          <div className="flex items-center justify-between text-[10px] font-mono text-purple-300">
                            <span className="font-bold">Latest Interaction</span>
                            <span>{activeMemory.lastContactAt ? formatDateTime(activeMemory.lastContactAt) : 'Recently'}</span>
                          </div>
                          <p className="text-xs text-slate-300 font-sans leading-relaxed">
                            {activeMemory.lastSummary || activeMemory.distilledMemory || 'First session recorded.'}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Live Context Prompt Simulation */}
                    <div className="p-5 rounded-2xl bg-slate-950/80 border border-purple-500/30 space-y-3 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sparkles size={16} className="text-cyan-400" />
                          <h3 className="text-sm font-bold text-white">Live Prompt Injection Preview</h3>
                        </div>
                        <span className="text-[10px] font-mono text-cyan-400">Injected into Luna Live Session</span>
                      </div>
                      <pre className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 text-[11px] font-mono text-cyan-200 overflow-x-auto whitespace-pre-wrap leading-relaxed">
{`======================================================================
### ACCREDITED RETURNING CUSTOMER EPISODIC MEMORY CARD ###
======================================================================
- Client Identifier / Phone: +${activeMemory.customerIdentifier}
- Client Name: ${activeMemory.customerName || 'Returning Client'}
- Total Past Interactions: ${activeMemory.totalInteractions || 1}
- VIP Status: ${activeMemory.isVip ? '⭐ VIP CLIENT' : 'Standard'}
- Past Sentiment: ${(activeMemory.sentimentTrend || 'neutral').toUpperCase()}

[DISTILLED EPISODIC FACTS & PREFERENCES]:
${activeMemory.distilledMemory || 'Prior inquiries recorded.'}

[CONVERSATIONAL MEMORY DIRECTIVES]:
- Warmly acknowledge and welcome back ${activeMemory.customerName || 'our client'}. Reference past context naturally.
======================================================================`}
                      </pre>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Edit Memory Modal */}
          {editingMemory && (
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="w-full max-w-lg bg-[#120B22] border-2 border-purple-500/40 rounded-3xl p-6 space-y-4 shadow-2xl">
                <div className="flex items-center justify-between pb-3 border-b border-purple-500/20">
                  <div className="flex items-center gap-2">
                    <Brain size={20} className="text-purple-400" />
                    <h3 className="text-sm font-black text-white uppercase font-sans">
                      Edit Customer Memory Profile
                    </h3>
                  </div>
                  <button
                    onClick={() => setEditingMemory(null)}
                    className="p-1 rounded-lg text-slate-400 hover:text-white"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="space-y-3.5 text-xs">
                  <div>
                    <label className="font-bold text-slate-300 block mb-1">Customer Full Name</label>
                    <input
                      type="text"
                      value={editingMemory.customerName || ''}
                      onChange={(e) => setEditingMemory({ ...editingMemory, customerName: e.target.value })}
                      placeholder="e.g. Kasun Perera"
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-purple-500/30 text-white font-sans focus:border-purple-400 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="font-bold text-slate-300 block mb-1">Distilled Episodic Facts (Bullet Points)</label>
                    <textarea
                      rows={5}
                      value={editingMemory.distilledMemory || ''}
                      onChange={(e) => setEditingMemory({ ...editingMemory, distilledMemory: e.target.value })}
                      placeholder="• Inquired about AC installation in Kandy&#10;• Budget: Rs. 180,000&#10;• Preferred brand: Panasonic"
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-purple-500/30 text-white font-sans focus:border-purple-400 focus:outline-none resize-none leading-relaxed"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950/80 border border-purple-500/20">
                    <div className="flex items-center gap-2">
                      <Star size={16} className="text-amber-400" />
                      <div>
                        <div className="font-bold text-white text-xs">VIP Customer Tier</div>
                        <div className="text-[10px] text-slate-400">Grants priority conversational attention</div>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={Boolean(editingMemory.isVip)}
                      onChange={(e) => setEditingMemory({ ...editingMemory, isVip: e.target.checked })}
                      className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-3 border-t border-purple-500/20">
                  <button
                    type="button"
                    onClick={() => setEditingMemory(null)}
                    className="px-4 py-2 rounded-xl bg-slate-900 text-slate-300 hover:text-white text-xs font-bold cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSaveMemory(editingMemory.id, {
                      customerName: editingMemory.customerName,
                      distilledMemory: editingMemory.distilledMemory,
                      isVip: editingMemory.isVip,
                    })}
                    disabled={isSavingMemory}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-black flex items-center gap-1.5 shadow-lg shadow-purple-900/40 cursor-pointer"
                  >
                    {isSavingMemory ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                    <span>Save Changes</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Modal: Add New Customer Memory Profile */}
          {showAddMemoryModal && (
            <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-[#0E0A1E] border border-purple-500/40 rounded-2xl w-full max-w-lg shadow-2xl p-6 space-y-5 animate-in fade-in zoom-in-95 duration-200">
                <div className="flex items-center justify-between border-b border-purple-500/20 pb-3">
                  <div className="flex items-center gap-2">
                    <Brain size={20} className="text-purple-400" />
                    <h3 className="text-sm font-black text-white uppercase font-sans">
                      Add Customer Memory Profile
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowAddMemoryModal(false)}
                    className="p-1 rounded-lg text-slate-400 hover:text-white"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="space-y-3.5 text-xs">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="font-bold text-slate-300 block mb-1">
                        Phone / Identifier <span className="text-rose-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={newMemoryPhone}
                        onChange={(e) => setNewMemoryPhone(e.target.value)}
                        placeholder="e.g. 0771234567"
                        className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-purple-500/30 text-white font-mono focus:border-purple-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="font-bold text-slate-300 block mb-1">Customer Name</label>
                      <input
                        type="text"
                        value={newMemoryName}
                        onChange={(e) => setNewMemoryName(e.target.value)}
                        placeholder="e.g. Kasun Perera"
                        className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-purple-500/30 text-white font-sans focus:border-purple-400 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-bold text-slate-300 block mb-1">Distilled Episodic Facts / Notes</label>
                    <textarea
                      rows={3}
                      value={newMemoryDistilled}
                      onChange={(e) => setNewMemoryDistilled(e.target.value)}
                      placeholder="• Inquired about luxury watches&#10;• Preferred brand: Naviforce / Curren&#10;• Ready to order next week"
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-purple-500/30 text-white font-sans focus:border-purple-400 focus:outline-none resize-none leading-relaxed"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 block mb-1">Budget</label>
                      <input
                        type="text"
                        value={newMemoryPrefBudget}
                        onChange={(e) => setNewMemoryPrefBudget(e.target.value)}
                        placeholder="Rs. 25,000"
                        className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-purple-500/30 text-white text-[11px] font-sans focus:border-purple-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 block mb-1">Product Choice</label>
                      <input
                        type="text"
                        value={newMemoryPrefProduct}
                        onChange={(e) => setNewMemoryPrefProduct(e.target.value)}
                        placeholder="Curren Chrono"
                        className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-purple-500/30 text-white text-[11px] font-sans focus:border-purple-400 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 block mb-1">Location</label>
                      <input
                        type="text"
                        value={newMemoryPrefLocation}
                        onChange={(e) => setNewMemoryPrefLocation(e.target.value)}
                        placeholder="Colombo"
                        className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-purple-500/30 text-white text-[11px] font-sans focus:border-purple-400 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950/80 border border-purple-500/20">
                    <div className="flex items-center gap-2">
                      <Star size={16} className="text-amber-400" />
                      <div>
                        <div className="font-bold text-white text-xs">VIP Customer Tier</div>
                        <div className="text-[10px] text-slate-400">Grants priority conversational attention</div>
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      checked={newMemoryIsVip}
                      onChange={(e) => setNewMemoryIsVip(e.target.checked)}
                      className="w-4 h-4 rounded bg-slate-900 border-slate-700 text-purple-600 focus:ring-purple-500 cursor-pointer"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-3 border-t border-purple-500/20">
                  <button
                    type="button"
                    onClick={() => setShowAddMemoryModal(false)}
                    className="px-4 py-2 rounded-xl bg-slate-900 text-slate-300 hover:text-white text-xs font-bold cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCreateMemory}
                    disabled={isCreatingMemory}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-black flex items-center gap-1.5 shadow-lg shadow-purple-900/40 cursor-pointer disabled:opacity-50"
                  >
                    {isCreatingMemory ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />}
                    <span>Create Profile</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : selectedModule === 'action_leads' ? (
        /* ═══════════════════════════════════════════════════════════════ */
        /* VIEW 5: ACTION INTEGRATION & CAPTURED LEADS STUDIO              */
        /* ═══════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col overflow-hidden bg-[#0A0B16]">
          
          {/* Action Leads Top KPI Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5 px-6 py-4 border-b border-purple-500/20 bg-[#0E0C20]/90 shrink-0">
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1A0F2E] to-[#120B22] border border-purple-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Total Captured Leads
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-white font-sans">{leadsList.length}</span>
                  <span className="text-[10px] font-mono text-purple-400 bg-purple-500/15 px-1.5 py-0.2 rounded border border-purple-500/30">
                    LUNA CRM
                  </span>
                </div>
                <span className="text-[9px] text-purple-300/80 font-mono">
                  All channels combined
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-purple-500/20 border border-purple-500/40 text-purple-300">
                <Users size={18} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1A0F2E] to-[#120B22] border border-purple-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  WhatsApp Leads
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-emerald-300 font-sans">
                    {stats.whatsappLeads || leadsList.filter((l) => l.formType === 'whatsapp_lead' || l.formType === 'whatsapp').length}
                  </span>
                  <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/15 px-1.5 py-0.2 rounded border border-emerald-500/30">
                    VERIFIED
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Direct phone captures
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400">
                <Smartphone size={18} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1A0F2E] to-[#120B22] border border-purple-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Web Form Leads
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-blue-300 font-sans">
                    {stats.webLeads || leadsList.filter((l) => l.formType !== 'whatsapp_lead' && l.formType !== 'whatsapp').length}
                  </span>
                  <span className="text-[10px] font-mono text-blue-400 bg-blue-500/15 px-1.5 py-0.2 rounded border border-blue-500/30">
                    WIDGET
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Online site inquiries
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-blue-500/20 border border-blue-500/40 text-blue-400">
                <Globe size={18} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1A0F2E] to-[#120B22] border border-purple-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  New / Pending
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-amber-300 font-sans">
                    {leadsList.filter((l) => l.status === 'new').length}
                  </span>
                  <span className="text-[10px] font-mono text-amber-400 bg-amber-500/15 px-1.5 py-0.2 rounded border border-amber-500/30">
                    ACTION REQ
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Awaiting follow-up
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-400">
                <AlertCircle size={18} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1A0F2E] to-[#120B22] border border-purple-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Converted
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-xl font-black text-teal-300 font-sans">
                    {leadsList.filter((l) => l.status === 'converted').length}
                  </span>
                  <span className="text-[10px] font-mono text-teal-400 bg-teal-500/15 px-1.5 py-0.2 rounded border border-teal-500/30">
                    WON
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Successfully closed
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-teal-500/20 border border-teal-500/40 text-teal-400">
                <CheckCircle2 size={18} />
              </div>
            </div>
          </div>

          {/* Action Leads Controls Toolbar */}
          <div className="px-6 py-3 border-b border-purple-500/20 bg-[#100D24] flex flex-wrap items-center justify-between gap-3 shrink-0">
            <div className="flex flex-wrap items-center gap-1.5 bg-slate-950 p-1 rounded-xl border border-purple-500/25">
              <button
                type="button"
                onClick={() => setLeadsFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  leadsFilter === 'all' ? 'bg-purple-500 text-white shadow-md' : 'text-slate-400 hover:text-white'
                }`}
              >
                All ({leadsList.length})
              </button>
              <button
                type="button"
                onClick={() => setLeadsFilter('new')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                  leadsFilter === 'new' ? 'bg-amber-500 text-slate-950 shadow-md font-black' : 'text-slate-400 hover:text-amber-300'
                }`}
              >
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                <span>New ({leadsList.filter((l) => l.status === 'new').length})</span>
              </button>
              <button
                type="button"
                onClick={() => setLeadsFilter('contacted')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  leadsFilter === 'contacted' ? 'bg-blue-500 text-white shadow-md' : 'text-slate-400 hover:text-blue-300'
                }`}
              >
                Contacted ({leadsList.filter((l) => l.status === 'contacted').length})
              </button>
              <button
                type="button"
                onClick={() => setLeadsFilter('converted')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  leadsFilter === 'converted' ? 'bg-emerald-500 text-slate-950 shadow-md font-black' : 'text-slate-400 hover:text-emerald-300'
                }`}
              >
                Converted ({leadsList.filter((l) => l.status === 'converted').length})
              </button>
              <button
                type="button"
                onClick={() => setLeadsFilter('whatsapp')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1 ${
                  leadsFilter === 'whatsapp' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-400 hover:text-emerald-300'
                }`}
              >
                <Smartphone size={11} />
                <span>WhatsApp ({leadsList.filter((l) => l.formType === 'whatsapp_lead' || l.formType === 'whatsapp').length})</span>
              </button>
            </div>

            <div className="relative w-64">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={leadsSearchQuery}
                onChange={(e) => setLeadsSearchQuery(e.target.value)}
                placeholder="Search leads by name, phone, notes..."
                className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-slate-950 border border-purple-500/20 text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-400 font-sans"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleExportLeadsCSV}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-slate-900 border border-purple-500/40 hover:border-purple-400 text-purple-200 text-xs font-bold transition cursor-pointer"
                title="Export leads to CSV"
              >
                <Download size={13} />
                <span>Export CSV</span>
              </button>

              <button
                type="button"
                onClick={() => fetchPortalData()}
                disabled={refreshing}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-purple-500/20 border border-purple-500/40 hover:bg-purple-500/30 text-purple-300 text-xs font-bold transition cursor-pointer disabled:opacity-50"
              >
                <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
                <span>Sync Leads</span>
              </button>
            </div>
          </div>

          {/* Action Leads Studio Main Split Layout */}
          <div className="flex-1 flex overflow-hidden">
            {/* Left: Leads Table / Cards */}
            <div className={`w-full lg:w-3/5 border-r border-purple-500/20 bg-[#0B0A1A]/95 flex flex-col overflow-hidden ${selectedLead ? 'hidden lg:flex' : 'flex'}`}>
              <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
                {filteredLeads.length === 0 ? (
                  <div className="p-12 text-center text-slate-500 space-y-3 max-w-md mx-auto">
                    <Users size={36} className="mx-auto text-purple-400 opacity-40 animate-pulse" />
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider">
                      No Leads Match Selected Filter
                    </h3>
                    <p className="text-xs text-slate-400 font-sans">
                      As Luna AI interacts with customers on WhatsApp and Web, inquiries and contact details will automatically register here.
                    </p>
                  </div>
                ) : (
                  filteredLeads.map((lead) => {
                    const isSelected = selectedLead?.id === lead.id;
                    const isWhatsApp = lead.formType === 'whatsapp_lead' || lead.formType === 'whatsapp';

                    return (
                      <div
                        key={lead.id}
                        onClick={() => setSelectedLeadId(lead.id)}
                        className={`p-4 rounded-2xl border transition-all cursor-pointer relative overflow-hidden group ${
                          isSelected
                            ? 'bg-gradient-to-r from-purple-950/90 via-slate-900/90 to-indigo-950/80 border-purple-400/90 shadow-[0_0_20px_rgba(168,85,247,0.35)]'
                            : 'bg-slate-900/60 border-purple-500/20 hover:border-purple-500/50 hover:bg-slate-900'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-purple-500/15 border border-purple-500/30 text-purple-300 group-hover:scale-105 transition-transform shrink-0">
                              {isWhatsApp ? <Smartphone size={16} className="text-emerald-400" /> : <Globe size={16} className="text-blue-400" />}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-black text-white font-sans">
                                  {lead.visitorName || 'Anonymous Visitor'}
                                </h4>
                                <span className={`text-[8px] font-mono font-black px-2 py-0.5 rounded-full border uppercase ${
                                  lead.status === 'new'
                                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 animate-pulse'
                                    : lead.status === 'contacted'
                                    ? 'bg-blue-500/20 text-blue-300 border-blue-500/40'
                                    : lead.status === 'converted'
                                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                                    : 'bg-slate-800 text-slate-400 border-slate-700'
                                }`}>
                                  {lead.status}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-[11px] font-mono text-slate-400">
                                {lead.visitorPhone && (
                                  <a
                                    href={`https://wa.me/${lead.visitorPhone.replace(/\+/g, '').trim()}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-emerald-400 hover:text-emerald-300 font-bold flex items-center gap-1 underline underline-offset-2"
                                  >
                                    <Smartphone size={11} />
                                    <span>{lead.visitorPhone}</span>
                                  </a>
                                )}
                                {lead.visitorEmail && (
                                  <span className="text-slate-300 truncate max-w-[160px]">
                                    {lead.visitorEmail}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="text-right shrink-0">
                            <span className="text-[9px] font-mono text-slate-500 block">
                              {formatDateTime(lead.createdAt)}
                            </span>
                            <span className="text-[10px] font-mono text-purple-300/80 font-bold uppercase block mt-1">
                              {lead.formType}
                            </span>
                          </div>
                        </div>

                        {/* Clean Inquiry Summary Snippet */}
                        {(() => {
                          const parsed = parseLeadDialogue(lead);
                          return parsed.cleanSummary ? (
                            <p className="mt-2 text-xs text-slate-300 line-clamp-2 leading-relaxed font-sans pl-1 border-l-2 border-purple-500/30">
                              {parsed.cleanSummary}
                            </p>
                          ) : null;
                        })()}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Right: Selected Lead Inspector & Action Node Destination Settings */}
            <div className={`w-full lg:w-2/5 flex flex-col bg-[#070712] overflow-y-auto p-3 sm:p-5 space-y-5 ${!selectedLead ? 'hidden lg:flex' : 'flex'}`}>
              {selectedLead ? (
                (() => {
                  const parsedSelectedLead = parseLeadDialogue(selectedLead);
                  return (
                    <div className="space-y-4">
                      {/* Lead Inspector Header */}
                      <div className="p-4 rounded-2xl bg-gradient-to-r from-[#180E2B] to-[#110B1E] border border-purple-500/30 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => setSelectedLeadId(null)}
                              className="p-1.5 -ml-1 rounded-xl bg-purple-950/60 hover:bg-purple-900/80 text-purple-300 hover:text-white border border-purple-500/30 lg:hidden flex items-center justify-center shrink-0 cursor-pointer"
                              title="Back to leads list"
                            >
                              <ArrowLeft size={14} />
                            </button>
                            <span className="text-[10px] font-mono uppercase text-purple-400 font-black tracking-wider">
                              Lead Dossier #{selectedLead.id}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            {selectedLead.visitorPhone && (
                              <a
                                href={`https://wa.me/${selectedLead.visitorPhone.replace(/\+/g, '').trim()}`}
                                target="_blank"
                                rel="noreferrer"
                                className="px-2.5 py-1 rounded-xl bg-[#00A884] hover:bg-[#008f6f] text-slate-950 text-[11px] font-black flex items-center gap-1 transition shadow-sm"
                              >
                                <Smartphone size={11} />
                                <span>WhatsApp Chat</span>
                              </a>
                            )}
                          </div>
                        </div>

                        <div className="space-y-1">
                          <h3 className="text-base font-black text-white font-sans">
                            {selectedLead.visitorName || 'Anonymous Visitor'}
                          </h3>
                          <p className="text-xs font-mono text-slate-400">
                            Channel: <span className="text-purple-300 font-bold uppercase">{selectedLead.formType}</span> • Captured on {formatDateTime(selectedLead.createdAt)}
                          </p>
                        </div>

                        {/* Status Switcher Buttons */}
                        <div className="pt-2 border-t border-purple-500/20 flex items-center gap-2">
                          <span className="text-[10px] font-mono text-slate-400 font-bold uppercase">Status:</span>
                          {(['new', 'contacted', 'converted', 'archived'] as const).map((st) => (
                            <button
                              key={st}
                              type="button"
                              onClick={() => handleUpdateLeadStatus(selectedLead.id, st)}
                              className={`px-2.5 py-1 rounded-lg text-[10px] font-mono font-bold uppercase transition cursor-pointer border ${
                                selectedLead.status === st
                                  ? st === 'converted'
                                    ? 'bg-emerald-500 text-slate-950 border-emerald-400'
                                    : st === 'contacted'
                                    ? 'bg-blue-500 text-white border-blue-400'
                                    : st === 'new'
                                    ? 'bg-amber-500 text-slate-950 border-amber-400'
                                    : 'bg-slate-700 text-white border-slate-600'
                                  : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
                              }`}
                            >
                              {st}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Clean Customer Inquiry Summary */}
                      {parsedSelectedLead.cleanSummary && (
                        <div className="p-4 rounded-2xl bg-slate-950/90 border border-purple-500/30 space-y-1.5 shadow-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-mono uppercase text-purple-400 font-bold flex items-center gap-1.5">
                              <AlertCircle size={12} />
                              <span>Customer Inquiry / Order Summary</span>
                            </span>
                          </div>
                          <p className="text-xs text-slate-200 leading-relaxed font-sans whitespace-pre-wrap">
                            {parsedSelectedLead.cleanSummary}
                          </p>
                        </div>
                      )}

                      {/* Authentic WhatsApp Real Chat Preview Window */}
                      <div className="rounded-2xl border border-emerald-500/30 overflow-hidden shadow-2xl bg-[#0B141A] flex flex-col">
                        {/* WhatsApp Top Header Bar */}
                        <div className="px-4 py-3 bg-[#202C33] border-b border-[#2A3942] flex items-center justify-between shrink-0">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <div className="w-9 h-9 rounded-full bg-[#00A884]/20 border border-[#00A884]/40 flex items-center justify-center text-[#00A884] font-bold">
                                <Smartphone size={18} />
                              </div>
                              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-[#00A884] border-2 border-[#202C33]" />
                            </div>
                            <div>
                              <div className="flex items-center gap-1.5">
                                <h4 className="text-xs font-bold text-[#E9EDEF] font-sans">
                                  {selectedLead.visitorName || (selectedLead.visitorPhone ? `+${selectedLead.visitorPhone}` : 'Customer')}
                                </h4>
                                <span className="text-[8px] font-mono text-[#00A884] bg-[#00A884]/15 px-1.5 py-0.2 rounded border border-[#00A884]/30 font-bold">
                                  WHATSAPP
                                </span>
                              </div>
                              <p className="text-[10px] text-[#8696A0] font-sans flex items-center gap-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#00A884] animate-pulse" />
                                <span>online • Captured on {formatDate(selectedLead.createdAt)}</span>
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5">
                            {selectedLead.visitorPhone && (
                              <a
                                href={`https://wa.me/${selectedLead.visitorPhone.replace(/\+/g, '').trim()}`}
                                target="_blank"
                                rel="noreferrer"
                                className="px-2.5 py-1 rounded-xl bg-[#00A884] hover:bg-[#008f6f] text-slate-950 text-[11px] font-bold flex items-center gap-1 transition shadow-md"
                              >
                                <Smartphone size={11} />
                                <span>Chat</span>
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={() => copyToClipboard(selectedLead.lunaVoiceTranscript || parsedSelectedLead.turns.map(t => `${t.speaker}: ${t.text}`).join('\n'), 'Chat Transcript')}
                              className="p-1.5 rounded-xl bg-[#111B21] text-[#8696A0] hover:text-[#E9EDEF] hover:bg-[#2A3942] transition cursor-pointer"
                              title="Copy Full Chat Transcript"
                            >
                              <Copy size={13} />
                            </button>
                          </div>
                        </div>

                        {/* WhatsApp Chat Canvas */}
                        <div 
                          className="p-4 space-y-3.5 overflow-y-auto min-h-[360px] max-h-[560px] flex-1 bg-[#0B141A]"
                          style={{
                            backgroundImage: `radial-gradient(circle at 50% 50%, rgba(0, 168, 132, 0.04) 0%, transparent 80%)`,
                          }}
                        >
                          {/* Date Pill Badge */}
                          <div className="flex justify-center my-1">
                            <span className="px-3 py-1 rounded-lg bg-[#182229] border border-[#2A3942]/60 text-[#8696A0] text-[10px] font-mono uppercase tracking-wider shadow-sm">
                              {formatDate(selectedLead.createdAt)} • Captured Lead Dialogue Context
                            </span>
                          </div>

                          {parsedSelectedLead.turns.length === 0 ? (
                            <div className="p-8 text-center text-[#8696A0] space-y-2">
                              <Smartphone size={28} className="mx-auto opacity-40 text-[#00A884]" />
                              <p className="text-xs font-sans">No multi-turn chat history recorded for this lead.</p>
                              <p className="text-[11px] text-slate-400 font-sans">{parsedSelectedLead.cleanSummary}</p>
                            </div>
                          ) : (
                            parsedSelectedLead.turns.map((turn, tIdx) => {
                              const isUser = turn.sender === 'user';

                              return (
                                <div
                                  key={tIdx}
                                  className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}
                                >
                                  <div
                                    className={`max-w-[88%] sm:max-w-[82%] rounded-2xl p-3 shadow-md relative group transition-all ${
                                      isUser
                                        ? 'bg-[#005C4B] text-[#E9EDEF] rounded-tr-none border border-[#005C4B]/60'
                                        : 'bg-[#202C33] text-[#E9EDEF] rounded-tl-none border border-[#2A3942]'
                                    }`}
                                  >
                                    {/* Speaker Header */}
                                    <div className="flex items-center justify-between gap-3 mb-1.5 pb-1 border-b border-white/10 text-[10px] font-mono">
                                      <span className={`font-bold flex items-center gap-1 ${isUser ? 'text-[#7fe3c5]' : 'text-[#53bdeb]'}`}>
                                        {isUser ? <Smartphone size={11} /> : <Bot size={11} />}
                                        <span>{isUser ? (selectedLead.visitorName || 'Customer') : 'Luna AI'}</span>
                                      </span>
                                      {turn.time && (
                                        <span className="text-[#8696A0] text-[9px]">{turn.time}</span>
                                      )}
                                    </div>

                                    {/* Message Text */}
                                    {renderFormattedWhatsAppText(turn.text)}

                                    {/* Footer: Timestamp & Blue Double Checkmarks */}
                                    <div className="flex items-center justify-end gap-1 mt-1.5 text-[9px] text-[#8696A0] font-mono">
                                      <span>{turn.time || formatTime(selectedLead.createdAt)}</span>
                                      {isUser && (
                                        <CheckCheck size={13} className="text-[#53bdeb]" />
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>

                      {/* Captured Form Payload */}
                      {selectedLead.formData && Object.keys(selectedLead.formData).filter((k) => k !== 'recentDialogue' && k !== 'recentTurns').length > 0 && (
                        <div className="p-4 rounded-2xl bg-slate-950/80 border border-purple-500/20 space-y-2">
                          <span className="text-[10px] font-mono uppercase text-slate-400 font-bold block">
                            Captured Form Payload
                          </span>
                          <div className="space-y-1 text-xs font-mono">
                            {Object.entries(selectedLead.formData)
                              .filter(([k]) => k !== 'recentDialogue' && k !== 'recentTurns')
                              .map(([k, v]) => (
                                <div key={k} className="flex items-start justify-between py-1 border-b border-slate-900">
                                  <span className="text-purple-300 font-bold">{k}:</span>
                                  <span className="text-slate-300 text-right max-w-[200px] truncate">{String(v)}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : (
                <div className="p-8 text-center text-slate-500 space-y-2 border border-dashed border-purple-500/20 rounded-2xl">
                  <Users size={28} className="mx-auto opacity-40 text-purple-400" />
                  <p className="text-xs font-medium">Select a lead on the left to inspect customer details and update pipeline status.</p>
                </div>
              )}

              {/* Action Node Routing & Auto-Execution Settings */}
              <div className="rounded-2xl bg-gradient-to-br from-[#120B22] to-[#0A0714] border border-purple-500/30 p-5 space-y-4 shadow-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sliders size={16} className="text-purple-400" />
                    <h3 className="text-sm font-black text-white font-sans">
                      Action Node Pipeline Router
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSaveActionNodeSettings(actionData?.targetDestination || '', actionData?.actionType || 'lead_capture')}
                    disabled={isSavingActionNode}
                    className="px-3 py-1.5 rounded-xl bg-purple-500 hover:bg-purple-400 text-slate-950 font-black text-xs flex items-center gap-1.5 transition cursor-pointer shadow-md"
                  >
                    {isSavingActionNode ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                    <span>Save Route</span>
                  </button>
                </div>

                <div className="space-y-3 text-xs">
                  <div>
                    <label className="font-bold text-slate-300 block mb-1">
                      Action Execution Type
                    </label>
                    <select
                      value={actionData?.actionType || 'whatsapp_lead'}
                      onChange={(e) => setActionData({ ...actionData, actionType: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 focus:border-purple-400 font-sans"
                    >
                      <option value="whatsapp_lead">🎯 Capture & Send WhatsApp Lead to Manager</option>
                      <option value="quick_sale">⚡ Execute POS Sale & Print Thermal Receipt</option>
                      <option value="stock_check">📦 Real-Time Inventory & Barcode Lookup</option>
                      <option value="hotel_checkin">🏨 Voice Hotel Check-in / Room Status</option>
                      <option value="webhook">🌐 Custom Webhook / Zapier HTTP POST</option>
                    </select>
                  </div>

                  <div>
                    <label className="font-bold text-slate-300 block mb-1">
                      Target WhatsApp / Staff Alert Number
                    </label>
                    <input
                      type="text"
                      value={actionData?.targetDestination || ''}
                      onChange={(e) => setActionData({ ...actionData, targetDestination: e.target.value })}
                      placeholder="e.g. +94771234567 or sales@company.com"
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-purple-300 focus:border-purple-400 font-mono text-xs"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-950 border border-purple-500/20">
                    <div>
                      <span className="font-bold text-white block">Auto-Sync to Portal CRM</span>
                      <span className="text-[10px] text-slate-400">All captured forms appear in this studio</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={actionData?.autoSync !== false}
                      onChange={(e) => setActionData({ ...actionData, autoSync: e.target.checked })}
                      className="w-4 h-4 rounded bg-slate-950 border-slate-700 text-purple-500 focus:ring-purple-400/30 cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : selectedModule === 'intent_alerts' ? (
        /* ═══════════════════════════════════════════════════════════════ */
        /* VIEW 8: SMART INTENT & VIP ALERTS FULL-SCREEN STUDIO            */
        /* ═══════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col overflow-hidden bg-[#0A070B]">
          {/* Top KPI Summary Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 px-6 py-4 border-b border-rose-500/20 bg-[#140A10]/90 shrink-0">
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#250F18] to-[#1A0A10] border border-rose-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Monitored Intent Rules
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-black text-rose-300 font-sans">
                    {intentRules.length}
                  </span>
                  <span className="text-[10px] font-mono text-rose-400 bg-rose-500/20 px-1.5 py-0.5 rounded border border-rose-500/30">
                    {intentRules.filter(r => r.isEnabled !== false).length} ACTIVE
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Autonomous watcher triggers
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-rose-500/20 border border-rose-500/40 text-rose-400">
                <BellRing size={20} className="animate-pulse" />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#250F18] to-[#1A0A10] border border-rose-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Delivery Gateway
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-base font-black text-white font-mono truncate max-w-[140px]">
                    Action & WhatsApp Hub
                  </span>
                </div>
                <span className="text-[9px] text-emerald-400 font-mono">
                  Autonomous downstream dispatch
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400">
                <Zap size={20} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#250F18] to-[#1A0A10] border border-rose-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Execution Protocol
                </span>
                <div className="flex items-center gap-2 mt-1">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
                  <span className="text-base font-black text-rose-200 font-sans">
                    SILENT MODE
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Zero client conversation friction
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-pink-500/20 border border-pink-500/40 text-pink-400">
                <ShieldCheck size={20} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#250F18] to-[#1A0A10] border border-rose-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Live Action Dispatches
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-black text-pink-300 font-sans">
                    {stats.totalLeads || 0}
                  </span>
                  <span className="text-[10px] font-mono text-pink-400 bg-pink-500/20 px-1.5 py-0.5 rounded border border-pink-500/30">
                    DISPATCHED
                  </span>
                </div>
                <span className="text-[9px] text-slate-400 font-mono">
                  Management notifications
                </span>
              </div>
              <div className="p-2.5 rounded-xl bg-rose-500/20 border border-rose-500/40 text-rose-400">
                <Zap size={20} />
              </div>
            </div>
          </div>

          {/* Main Body: 2 Column Layout (Left: Intent Rule Cards, Right: Live Alert History / Simulator) */}
          <div className="flex-1 overflow-hidden flex flex-col lg:flex-row divide-y lg:divide-y-0 lg:divide-x divide-rose-500/20">
            {/* Left: Rules Management */}
            <div className="w-full lg:w-1/2 flex flex-col overflow-hidden p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <Sparkles className="text-rose-400" size={18} />
                    <span>Monitored Intent Rules</span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    Configure customer interests, keywords, and buying signals to watch for.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddingPortalRule(!isAddingPortalRule)}
                  className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white font-bold text-xs flex items-center gap-1.5 shadow-lg shadow-rose-500/20 transition cursor-pointer"
                >
                  <Plus size={14} />
                  <span>Add Intent Rule</span>
                </button>
              </div>

              {/* Add Rule Inline Card */}
              {isAddingPortalRule && (
                <div className="p-4 rounded-2xl bg-gradient-to-br from-[#250F18] to-[#150A10] border border-rose-500/40 space-y-3 shadow-xl animate-fade-in">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-rose-300">New Intent Rule</span>
                    <button
                      type="button"
                      onClick={() => setIsAddingPortalRule(false)}
                      className="text-slate-400 hover:text-slate-200 text-xs"
                    >
                      Cancel
                    </button>
                  </div>
                  <input
                    type="text"
                    value={newPortalIntentTitle}
                    onChange={(e) => setNewPortalIntentTitle(e.target.value)}
                    placeholder="Intent Name (e.g. 🌹 Red Rose Lovers, 💎 VIP Budget)"
                    className="w-full bg-slate-950 border border-slate-700 focus:border-rose-400 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none"
                  />
                  <textarea
                    rows={2}
                    value={newPortalIntentKeywords}
                    onChange={(e) => setNewPortalIntentKeywords(e.target.value)}
                    placeholder="Trigger Keywords / Criteria (e.g. asking for red roses, wants red flower bouquet, price of 50 roses)"
                    className="w-full bg-slate-950 border border-slate-700 focus:border-rose-400 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none resize-none"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={newPortalIntentChannel}
                      onChange={(e) => setNewPortalIntentChannel(e.target.value as any)}
                      className="bg-slate-950 border border-slate-700 focus:border-rose-400 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none"
                    >
                      <option value="whatsapp">📱 WhatsApp Alert</option>
                      <option value="sms">💬 SMS Alert</option>
                      <option value="email">✉️ Email</option>
                      <option value="webhook">🌐 Webhook</option>
                    </select>
                    <select
                      value={newPortalIntentUrgency}
                      onChange={(e) => setNewPortalIntentUrgency(e.target.value as any)}
                      className="bg-slate-950 border border-slate-700 focus:border-rose-400 rounded-xl px-3 py-2 text-xs text-slate-200 focus:outline-none"
                    >
                      <option value="high">🚨 High Priority</option>
                      <option value="medium">⚡ Medium</option>
                      <option value="low">📋 Low</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleCreatePortalRule}
                    disabled={!newPortalIntentTitle.trim() || !newPortalIntentKeywords.trim()}
                    className="w-full py-2 rounded-xl bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white font-bold text-xs disabled:opacity-50 transition cursor-pointer"
                  >
                    Save Intent Trigger
                  </button>
                </div>
              )}

              {/* List of Configured Rules */}
              <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                {intentRules.length === 0 ? (
                  <div className="p-8 text-center bg-slate-950/60 border border-slate-800/80 rounded-2xl">
                    <BellRing size={32} className="mx-auto text-slate-600 mb-2 opacity-60" />
                    <p className="text-xs text-slate-400">No intent rules configured yet.</p>
                    <p className="text-[11px] text-slate-500 mt-1">Click &quot;Add Intent Rule&quot; to start watching for customer interests.</p>
                  </div>
                ) : (
                  intentRules.map((rule) => (
                    <div
                      key={rule.id}
                      className={`p-4 rounded-2xl border transition-all ${
                        rule.isEnabled !== false
                          ? 'bg-slate-950/80 border-rose-500/30 hover:border-rose-500/60 shadow-lg'
                          : 'bg-slate-950/40 border-slate-800/60 opacity-60'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`w-3 h-3 rounded-full ${
                              rule.urgency === 'high'
                                ? 'bg-rose-400 shadow-[0_0_8px_rgba(244,63,94,0.8)]'
                                : rule.urgency === 'medium'
                                ? 'bg-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.8)]'
                                : 'bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.8)]'
                            }`}
                          />
                          <div>
                            <h4 className="font-bold text-sm text-white">{rule.intentTitle}</h4>
                            <span className="text-[10px] text-slate-400 font-mono">
                              Channel: {rule.notificationChannel?.toUpperCase() || 'WHATSAPP'} • Urgency: {rule.urgency?.toUpperCase() || 'HIGH'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <label className="flex items-center gap-1.5 text-[11px] text-slate-300 font-mono cursor-pointer">
                            <span>{rule.isEnabled !== false ? 'Active' : 'Paused'}</span>
                            <input
                              type="checkbox"
                              checked={rule.isEnabled !== false}
                              onChange={(e) => handleToggleRule(rule.id, e.target.checked)}
                              className="w-4 h-4 accent-rose-500 cursor-pointer"
                            />
                          </label>
                          <button
                            type="button"
                            onClick={() => handleDeletePortalRule(rule.id)}
                            className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition"
                            title="Delete Rule"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 p-2.5 rounded-xl bg-slate-900/90 border border-slate-800/80">
                        <span className="text-[10px] text-rose-300 font-mono block">Matched Keywords / Signals:</span>
                        <p className="text-xs text-slate-200 mt-0.5">{rule.matchConditions}</p>
                      </div>

                      <div className="mt-3 pt-2 border-t border-slate-800/60 flex items-center justify-between">
                        <span className="text-[10px] text-slate-400 font-mono flex items-center gap-1">
                          <Zap size={11} className="text-rose-400" />
                          <span>Routes via Action & Integration Node</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => handleTestIntentAlert(rule)}
                          disabled={isTestingIntent}
                          className="text-[10px] font-bold text-rose-300 hover:text-white bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 px-2.5 py-1 rounded-lg flex items-center gap-1 transition cursor-pointer"
                        >
                          <Send size={11} />
                          <span>Test Simulation</span>
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Right: Live Alert History & Simulation Feed */}
            <div className="w-full lg:w-1/2 flex flex-col overflow-hidden p-6 space-y-4 bg-slate-950/40">
              <div>
                <h3 className="text-base font-bold text-white flex items-center gap-2">
                  <Activity className="text-rose-400" size={18} />
                  <span>Real-Time Alert Dispatch Feed</span>
                </h3>
                <p className="text-xs text-slate-400">
                  Audit log of autonomous VIP alerts sent to management.
                </p>
              </div>

              {testIntentResult && (
                <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-950/60 to-slate-950/80 border border-emerald-500/40 space-y-2 animate-fade-in shadow-xl">
                  <div className="flex items-center gap-2 text-emerald-300 text-xs font-bold">
                    <CheckCircle2 size={16} className="text-emerald-400" />
                    <span>Test Notification Successfully Dispatched!</span>
                  </div>
                  <pre className="text-[11px] font-mono text-emerald-200 bg-slate-950 p-3 rounded-xl border border-emerald-500/20 whitespace-pre-wrap">
                    {testIntentResult.alertSummary}
                  </pre>
                </div>
              )}

              <div className="flex-1 overflow-y-auto space-y-3 pr-2">
                {conversations.slice(0, 8).map((c: any, idx: number) => {
                  const firstMsg = c.messages?.[0]?.text || 'Customer inquiry';
                  return (
                    <div
                      key={c.sessionId || idx}
                      className="p-3.5 rounded-2xl bg-slate-950/80 border border-slate-800/80 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-white">{c.visitorName || 'Customer'}</span>
                          {c.phone && <span className="text-[10px] text-emerald-400 font-mono">+{c.phone}</span>}
                        </div>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {c.lastActiveAt ? formatTime(c.lastActiveAt) : 'Recent'}
                        </span>
                      </div>
                      <p className="text-xs text-slate-300 bg-slate-900/80 p-2 rounded-xl border border-slate-800 font-sans">
                        &quot;{firstMsg}&quot;
                      </p>
                      <div className="flex items-center justify-between text-[10px] text-slate-400 font-mono">
                        <span className="flex items-center gap-1 text-rose-300">
                          <Zap size={11} className="text-rose-400" />
                          Watcher Active
                        </span>
                        <span className="text-slate-500">Channel: {c.channel?.toUpperCase() || 'WHATSAPP'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : selectedModule === 'product_catalog' ? (
        /* ═══════════════════════════════════════════════════════════════ */
        /* VIEW 9: PRODUCT CATALOG & LIVE MENU FULL-SCREEN STUDIO          */
        /* ═══════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col overflow-hidden bg-[#070E0B]">
          {/* Top Sub-Tab Switcher: Products vs Orders & Courier Tracking */}
          <div className="px-6 pt-3 pb-2 border-b border-emerald-500/20 bg-[#0B1712] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCatalogStudioTab('products')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
                  catalogStudioTab === 'products'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 shadow-md shadow-emerald-500/20 font-black'
                    : 'bg-[#0E2018] text-slate-300 hover:text-white border border-emerald-500/20'
                }`}
              >
                <ShoppingBag size={14} />
                <span>Product Catalog & Stock</span>
                <span className={`px-1.5 py-0.2 rounded text-[10px] font-mono ${
                  catalogStudioTab === 'products' ? 'bg-slate-950/20 text-slate-950' : 'bg-emerald-950 text-emerald-300'
                }`}>
                  {catalogProducts.length}
                </span>
              </button>

              {(hasBookingCapability || catalogStudioTab === 'bookings') && (
                <button
                  type="button"
                  onClick={() => {
                    setCatalogStudioTab('bookings');
                    fetchTenantOrders();
                  }}
                  className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
                    catalogStudioTab === 'bookings'
                      ? 'bg-gradient-to-r from-teal-400 to-emerald-400 text-slate-950 shadow-md shadow-teal-500/25 font-black'
                      : 'bg-[#0E2018] text-slate-300 hover:text-white border border-teal-500/30'
                  }`}
                >
                  <Car size={14} className={catalogStudioTab === 'bookings' ? 'text-slate-950' : 'text-teal-400'} />
                  <span>Bookings & Reservations</span>
                  <span className={`px-1.5 py-0.2 rounded text-[10px] font-mono font-bold ${
                    catalogStudioTab === 'bookings' ? 'bg-slate-950/20 text-slate-950' : 'bg-teal-950 text-teal-300'
                  }`}>
                    {bookingsList.length}
                  </span>
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setCatalogStudioTab('orders');
                  fetchTenantOrders();
                }}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
                  catalogStudioTab === 'orders'
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 shadow-md shadow-emerald-500/20 font-black'
                    : 'bg-[#0E2018] text-slate-300 hover:text-white border border-emerald-500/20'
                }`}
              >
                <Truck size={14} />
                <span>Orders & Delivery</span>
                <span className={`px-1.5 py-0.2 rounded text-[10px] font-mono ${
                  catalogStudioTab === 'orders' ? 'bg-slate-950/20 text-slate-950' : 'bg-teal-950 text-teal-300'
                }`}>
                  {physicalOrdersList.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setCatalogStudioTab('recycle_bin')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
                  catalogStudioTab === 'recycle_bin'
                    ? 'bg-gradient-to-r from-rose-500 to-rose-600 text-white shadow-md shadow-rose-500/20 font-black'
                    : 'bg-[#0E2018] text-slate-300 hover:text-white border border-rose-500/20'
                }`}
              >
                <Trash2 size={14} className={((productCatalogData?.recycleBin || []).length > 0) ? 'text-rose-400' : ''} />
                <span>Recycle Bin</span>
                <span className={`px-1.5 py-0.2 rounded text-[10px] font-mono ${
                  catalogStudioTab === 'recycle_bin' ? 'bg-black/30 text-white' : 'bg-rose-950 text-rose-300'
                }`}>
                  {(productCatalogData?.recycleBin || []).length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setCatalogStudioTab('snapshots')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
                  catalogStudioTab === 'snapshots'
                    ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-md shadow-indigo-500/20 font-black'
                    : 'bg-[#0E2018] text-slate-300 hover:text-white border border-indigo-500/20'
                }`}
              >
                <History size={14} className={((productCatalogData?.snapshots || []).length > 0) ? 'text-indigo-400' : ''} />
                <span>Snapshots & Backups</span>
                <span className={`px-1.5 py-0.2 rounded text-[10px] font-mono ${
                  catalogStudioTab === 'snapshots' ? 'bg-black/30 text-white' : 'bg-indigo-950 text-indigo-300'
                }`}>
                  {(productCatalogData?.snapshots || []).length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setCatalogStudioTab('order_templates')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
                  catalogStudioTab === 'order_templates'
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 shadow-md shadow-amber-500/20 font-black'
                    : 'bg-[#0E2018] text-slate-300 hover:text-white border border-amber-500/20'
                }`}
              >
                <FileText size={14} className={catalogStudioTab === 'order_templates' ? 'text-slate-950' : 'text-amber-400'} />
                <span>Order Form & Courier Templates</span>
                <span className={`px-1.5 py-0.2 rounded text-[10px] font-mono ${
                  catalogStudioTab === 'order_templates' ? 'bg-black/20 text-slate-950 font-bold' : 'bg-amber-950 text-amber-300'
                }`}>
                  Config
                </span>
              </button>

              <button
                type="button"
                onClick={() => setCatalogStudioTab('pre_filled_templates')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
                  catalogStudioTab === 'pre_filled_templates'
                    ? 'bg-gradient-to-r from-teal-500 to-emerald-500 text-slate-950 shadow-md shadow-teal-500/20 font-black'
                    : 'bg-[#0E2018] text-slate-300 hover:text-white border border-teal-500/20'
                }`}
              >
                <Zap size={14} className={catalogStudioTab === 'pre_filled_templates' ? 'text-slate-950' : 'text-teal-400'} />
                <span>Zero-Token Quick Replies & Templates</span>
                <span className={`px-1.5 py-0.2 rounded text-[10px] font-mono ${
                  catalogStudioTab === 'pre_filled_templates' ? 'bg-black/20 text-slate-950 font-bold' : 'bg-teal-950 text-teal-300'
                }`}>
                  {currentPreFilledTemplates.length}
                </span>
              </button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg border border-emerald-500/20 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
                <span>Autonomous Orders & Tracking Ready</span>
              </span>
            </div>
          </div>

          {catalogStudioTab === 'products' ? (
            <>
          {/* Top KPI Summary Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3.5 px-3 sm:px-6 py-3 sm:py-4 border-b border-emerald-500/20 bg-[#0B1712]/90 shrink-0">
            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F261C] to-[#0A1A13] border border-emerald-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Total Catalog Items
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-black text-emerald-300 font-sans">
                    {catalogProducts.length}
                  </span>
                  <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/20 px-1.5 py-0.5 rounded border border-emerald-500/30">
                    ITEMS
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                <ShoppingBag size={20} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F261C] to-[#0A1A13] border border-emerald-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  In-Stock Available
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-black text-teal-300 font-sans">
                    {catalogProducts.filter(p => p.isAvailable !== false).length}
                  </span>
                  <span className="text-[10px] font-mono text-teal-400 bg-teal-500/20 px-1.5 py-0.5 rounded border border-teal-500/30">
                    READY TO SELL
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-teal-500/10 border border-teal-500/20 text-teal-400">
                <CheckCircle2 size={20} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F261C] to-[#0A1A13] border border-emerald-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Store Currency
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-2xl font-black text-white font-mono">
                    {productCatalogData?.defaultCurrency || 'LKR'}
                  </span>
                  <span className="text-[10px] font-mono text-slate-400">
                    PRICING
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                <DollarSign size={20} />
              </div>
            </div>

            <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F261C] to-[#0A1A13] border border-emerald-500/30 shadow-lg flex items-center justify-between">
              <div>
                <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                  Luna AI Brain Sync
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-lg font-black text-emerald-300 font-sans flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping inline-block" />
                    LIVE SYNCED
                  </span>
                </div>
              </div>
              <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                <Zap size={20} />
              </div>
            </div>
          </div>

          {/* Main Content Body */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 scrollbar-thin scrollbar-thumb-emerald-950 tenet-mobile-scroll">
            {/* Left Column (7 cols): Catalog Items List */}
            <div className="lg:col-span-7 space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-[#0E1F18] p-3.5 rounded-2xl border border-emerald-500/30">
                <div className="flex items-center gap-2.5 flex-1 w-full">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={catalogSearchQuery}
                      onChange={(e) => setCatalogSearchQuery(e.target.value)}
                      placeholder="Search products by name, category, or description..."
                      className="w-full pl-9 pr-3 py-1.5 bg-[#08120E] border border-emerald-500/30 focus:border-emerald-400 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none transition"
                    />
                  </div>

                  <select
                    value={selectedCatalogCategory}
                    onChange={(e) => setSelectedCatalogCategory(e.target.value)}
                    className="bg-[#08120E] border border-emerald-500/30 focus:border-emerald-400 text-xs font-mono text-slate-200 rounded-xl px-3 py-1.5 focus:outline-none"
                  >
                    <option value="all">All Categories</option>
                    {allCatalogCategories.map((cat: string) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  onClick={() => setIsAddingCatalogProduct(!isAddingCatalogProduct)}
                  className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider transition flex items-center gap-1.5 cursor-pointer shadow-lg shadow-emerald-500/20 shrink-0"
                >
                  <Plus size={14} />
                  <span>Add Product</span>
                </button>
              </div>

              {/* Category Pills & Navigation Bar */}
              <div className="p-3 bg-[#0E1F18] border border-emerald-500/30 rounded-2xl flex flex-wrap items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-1.5 overflow-x-auto py-0.5 scrollbar-none flex-1 max-w-full">
                  <button
                    type="button"
                    onClick={() => setSelectedCatalogCategory('all')}
                    className={`px-3 py-1.5 rounded-xl font-mono text-[11px] font-bold transition flex items-center gap-1.5 shrink-0 cursor-pointer ${
                      selectedCatalogCategory === 'all'
                        ? 'bg-emerald-500 text-slate-950 shadow-sm shadow-emerald-500/30 font-black'
                        : 'bg-[#08120E] text-slate-300 hover:text-white border border-emerald-500/20'
                    }`}
                  >
                    <span>All Items</span>
                    <span className={`px-1.5 py-0.2 rounded text-[10px] ${
                      selectedCatalogCategory === 'all' ? 'bg-slate-950/30 text-slate-950 font-bold' : 'bg-slate-800 text-slate-400'
                    }`}>
                      {catalogProducts.length}
                    </span>
                  </button>

                  {allCatalogCategories.map((cat: string) => {
                    const catCount = catalogProducts.filter(p => getProductCategories(p).some(c => c.toLowerCase() === cat.toLowerCase())).length;
                    const isCatActive = selectedCatalogCategory.toLowerCase() === cat.toLowerCase();
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setSelectedCatalogCategory(cat)}
                        className={`px-3 py-1.5 rounded-xl font-mono text-[11px] font-bold transition flex items-center gap-1.5 shrink-0 cursor-pointer ${
                          isCatActive
                            ? 'bg-emerald-500 text-slate-950 shadow-sm shadow-emerald-500/30 font-black'
                            : 'bg-[#08120E] text-slate-300 hover:text-white border border-emerald-500/20'
                        }`}
                      >
                        <Folder size={12} className={isCatActive ? 'text-slate-950' : 'text-emerald-400'} />
                        <span>{cat}</span>
                        <span className={`px-1.5 py-0.2 rounded text-[10px] ${
                          isCatActive ? 'bg-slate-950/30 text-slate-950 font-bold' : 'bg-slate-800 text-slate-400'
                        }`}>
                          {catCount}
                        </span>
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => setShowPortalCategoryManager(!showPortalCategoryManager)}
                    className="px-2.5 py-1.5 rounded-xl bg-emerald-950/80 hover:bg-emerald-900/80 text-emerald-300 border border-emerald-500/40 text-[11px] font-mono font-bold flex items-center gap-1.5 shrink-0 cursor-pointer transition active:scale-95 shadow-sm"
                    title="Manage Categories (Create, Rename, Delete)"
                  >
                    <FolderPlus size={13} className="text-emerald-400" />
                    <span>+ Category</span>
                  </button>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => setPortalCatalogViewMode(portalCatalogViewMode === 'flat' ? 'grouped' : 'flat')}
                    className={`px-3 py-1.5 rounded-xl text-[11px] font-mono font-bold transition flex items-center gap-1.5 cursor-pointer ${
                      portalCatalogViewMode === 'grouped'
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm'
                        : 'bg-[#08120E] text-slate-400 border border-slate-800 hover:text-slate-200'
                    }`}
                    title="Toggle Grouped by Category vs Flat List View"
                  >
                    <Layers size={13} />
                    <span>{portalCatalogViewMode === 'grouped' ? 'Grouped' : 'List'}</span>
                  </button>
                </div>
              </div>

              {/* Category Management Drawer */}
              {showPortalCategoryManager && (
                <div className="p-4 bg-gradient-to-br from-[#0F261C] to-[#0A1A13] border border-emerald-500/40 rounded-2xl space-y-3 shadow-xl animate-fade-in">
                  <div className="flex items-center justify-between pb-2 border-b border-emerald-500/20">
                    <span className="text-xs uppercase font-mono font-bold text-emerald-400 flex items-center gap-2">
                      <FolderPlus size={14} /> Product Categories Management
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowPortalCategoryManager(false)}
                      className="text-slate-400 hover:text-white text-xs cursor-pointer p-1"
                    >
                      ✕ Close
                    </button>
                  </div>

                  {/* Tab Switcher: Active Categories vs Category Recycle Bin */}
                  <div className="flex items-center gap-2 pb-1 border-b border-emerald-500/20">
                    <button
                      type="button"
                      onClick={() => setPortalCategoryTab('active')}
                      className={`px-3 py-1 rounded-xl text-xs font-mono font-bold transition cursor-pointer flex items-center gap-1.5 ${
                        portalCategoryTab === 'active'
                          ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20 font-black'
                          : 'bg-[#08120E] text-slate-400 hover:text-white border border-emerald-500/20'
                      }`}
                    >
                      <Folder size={12} />
                      <span>Active Categories ({allCatalogCategories.length})</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPortalCategoryTab('recycle')}
                      className={`px-3 py-1 rounded-xl text-xs font-mono font-bold transition cursor-pointer flex items-center gap-1.5 ${
                        portalCategoryTab === 'recycle'
                          ? 'bg-rose-500 text-white shadow-md shadow-rose-500/20 font-black'
                          : 'bg-[#08120E] text-slate-400 hover:text-slate-200 border border-slate-800'
                      }`}
                    >
                      <Trash2 size={12} className={explicitCategoryRecycleBin.length > 0 ? 'text-rose-400' : ''} />
                      <span>Category Recycle Bin ({explicitCategoryRecycleBin.length})</span>
                    </button>
                  </div>

                  {portalCategoryTab === 'active' ? (
                    <>
                      <div className="p-3 bg-[#08120E] rounded-xl border border-emerald-500/30 space-y-2">
                        <div className="text-[11px] font-bold text-emerald-400 flex items-center gap-1.5">
                          <FolderPlus size={13} />
                          <span>Add New Product Category</span>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            type="text"
                            value={newPortalCategoryInput}
                            onChange={(e) => setNewPortalCategoryInput(e.target.value)}
                            placeholder="Category Name (e.g. Mini Car, Vans, Bouquets)*"
                            className="flex-1 bg-[#050C0A] border border-emerald-500/30 focus:border-emerald-400 rounded-lg px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAddPortalCategory(newPortalCategoryInput, newPortalCategoryRuleInput);
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleAddPortalCategory(newPortalCategoryInput, newPortalCategoryRuleInput)}
                            className="px-4 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-bold text-xs rounded-lg transition shrink-0 cursor-pointer shadow-md"
                          >
                            + Create Category
                          </button>
                        </div>
                        <div>
                          <input
                            type="text"
                            value={newPortalCategoryRuleInput}
                            onChange={(e) => setNewPortalCategoryRuleInput(e.target.value)}
                            placeholder="Category Description & Rules (e.g. 1 km Rs 40 extra charge, refundable deposit Rs. 15,000, 5% fleet discount)"
                            className="w-full bg-[#050C0A] border border-cyan-500/30 focus:border-cyan-400 rounded-lg px-3 py-1.5 text-xs text-cyan-200 placeholder-slate-500 focus:outline-none"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAddPortalCategory(newPortalCategoryInput, newPortalCategoryRuleInput);
                              }
                            }}
                          />
                          <span className="text-[10px] text-cyan-400/80 mt-1 block">
                            💡 Luna AI proactively uses this category description & pricing rules when answering customer questions or quotes!
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2 max-h-56 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-emerald-950">
                        {allCatalogCategories.map((cat: string) => {
                          const count = catalogProducts.filter(p => (p.category || 'General').trim().toLowerCase() === cat.toLowerCase()).length;
                          const isEditingThisName = renamingPortalCategory?.oldName === cat;
                          const isEditingThisRule = editingCategoryRule?.catName === cat;
                          const currentRule = (productCatalogData?.categoryRules || {})[cat] || '';

                          return (
                            <div key={cat} className="p-2.5 bg-[#08120E] rounded-xl border border-emerald-500/20 text-xs space-y-2">
                              <div className="flex items-center justify-between">
                                {isEditingThisName ? (
                                  <div className="flex items-center gap-2 flex-1 mr-2">
                                    <input
                                      type="text"
                                      value={renamingPortalCategory.newName}
                                      onChange={(e) => setRenamingPortalCategory({ ...renamingPortalCategory, newName: e.target.value })}
                                      className="bg-[#050C0A] border border-emerald-400 rounded-lg px-2 py-1 text-xs text-white flex-1"
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          handleRenamePortalCategory(renamingPortalCategory.oldName, renamingPortalCategory.newName);
                                        }
                                      }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleRenamePortalCategory(renamingPortalCategory.oldName, renamingPortalCategory.newName)}
                                      className="text-[11px] font-bold text-emerald-400 hover:text-emerald-300 px-2 py-1 bg-emerald-950 rounded-lg border border-emerald-500/40 cursor-pointer"
                                    >
                                      Save
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setRenamingPortalCategory(null)}
                                      className="text-[11px] text-slate-400 hover:text-white px-1.5 cursor-pointer"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <Folder size={14} className="text-emerald-400 shrink-0" />
                                    <span className="font-mono font-bold text-slate-200">{cat}</span>
                                    <span className="text-[10px] font-mono text-slate-400 bg-slate-900 px-2 py-0.5 rounded-full border border-slate-800">
                                      {count} {count === 1 ? 'item' : 'items'}
                                    </span>
                                  </div>
                                )}

                                {!isEditingThisName && (
                                  <div className="flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      onClick={() => setRenamingPortalCategory({ oldName: cat, newName: cat })}
                                      className="p-1 text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded-lg transition cursor-pointer"
                                      title="Rename Category"
                                    >
                                      <Edit2 size={13} />
                                    </button>
                                    {cat.toLowerCase() !== 'general' && (
                                      <button
                                        type="button"
                                        onClick={() => handleDeletePortalCategory(cat)}
                                        className="p-1 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition cursor-pointer"
                                        title="Delete Category"
                                      >
                                        <Trash2 size={13} />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Category Description & Rules Section */}
                              {isEditingThisRule ? (
                                <div className="p-2 bg-[#050C0A] rounded-lg border border-cyan-500/50 space-y-1.5">
                                  <div className="text-[10px] text-cyan-400 font-semibold flex items-center gap-1">
                                    <FileText size={11} />
                                    <span>Category Description & Pricing Rules for "{cat}"</span>
                                  </div>
                                  <textarea
                                    rows={2}
                                    value={editingCategoryRule.rule}
                                    onChange={(e) => setEditingCategoryRule({ ...editingCategoryRule, rule: e.target.value })}
                                    className="w-full bg-slate-950 border border-cyan-500/40 rounded-lg p-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 resize-none"
                                    placeholder="e.g. 1 km extra charge Rs 40, refundable deposit Rs. 15,000, 5% fleet discount..."
                                    autoFocus
                                  />
                                  <div className="flex items-center justify-end gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleSaveCategoryRule(editingCategoryRule.catName, editingCategoryRule.rule)}
                                      className="text-[11px] font-bold text-slate-950 bg-cyan-400 hover:bg-cyan-300 px-3 py-1 rounded-lg cursor-pointer transition shadow-sm"
                                    >
                                      Save Description
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingCategoryRule(null)}
                                      className="text-[11px] text-slate-400 hover:text-white px-2 py-1 cursor-pointer"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="p-1.5 bg-[#050C0A] rounded-lg border border-slate-800/80 flex items-start justify-between gap-2">
                                  <div className="flex items-start gap-1.5 flex-1 min-w-0">
                                    <FileText size={12} className={currentRule ? "text-cyan-400 shrink-0 mt-0.5" : "text-slate-600 shrink-0 mt-0.5"} />
                                    {currentRule ? (
                                      <p className="text-[11px] text-cyan-300/90 leading-relaxed break-words">{currentRule}</p>
                                    ) : (
                                      <span className="text-[11px] text-slate-500 italic">No description or rules defined yet.</span>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setEditingCategoryRule({ catName: cat, rule: currentRule })}
                                    className="text-[10px] font-semibold text-cyan-400 hover:text-cyan-300 hover:underline px-1.5 py-0.5 bg-cyan-950/40 rounded border border-cyan-500/20 shrink-0 cursor-pointer"
                                  >
                                    {currentRule ? 'Edit' : '+ Add Description'}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    /* ── Category Recycle Bin Tab ── */
                    <div className="space-y-2 max-h-56 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-rose-950">
                      {explicitCategoryRecycleBin.length === 0 ? (
                        <div className="py-6 text-center text-slate-500 text-xs font-mono">
                          <Trash2 size={20} className="mx-auto mb-1.5 text-slate-600 opacity-60" />
                          <span>Category Recycle Bin is empty. No deleted categories.</span>
                        </div>
                      ) : (
                        explicitCategoryRecycleBin.map((recItem: any) => (
                          <div
                            key={recItem.id}
                            className="p-2.5 rounded-xl bg-[#08120E] border border-rose-500/30 flex items-center justify-between gap-3 hover:border-rose-500/50 transition"
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <Folder size={14} className="text-rose-400 shrink-0" />
                                <span className="font-mono font-bold text-white text-xs truncate">
                                  📁 {recItem.name}
                                </span>
                              </div>
                              <div className="text-[10px] text-slate-400 font-mono mt-0.5 flex items-center gap-2">
                                <span>{recItem.productCount || 0} items re-routed</span>
                                <span>•</span>
                                <span>{recItem.deletedAt ? new Date(recItem.deletedAt).toLocaleDateString() : 'Recent'}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRestorePortalCategory(recItem.id)}
                              className="px-3 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-[11px] font-mono font-bold rounded-lg transition flex items-center gap-1.5 cursor-pointer shrink-0 active:scale-95"
                              title={`Restore category "${recItem.name}"`}
                            >
                              <RotateCcw size={12} />
                              <span>Restore</span>
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Datalist for Category Auto-Complete */}
              <datalist id="portal-catalog-categories-list">
                {allCatalogCategories.map((c: string) => (
                  <option key={c} value={c} />
                ))}
              </datalist>

              {/* Active Category Header & Rules / Description Banner */}
              {selectedCatalogCategory !== 'all' && (
                <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-r from-[#0C241B] via-[#091B13] to-[#071610] p-4 shadow-xl space-y-3 relative overflow-hidden group">
                  {/* Ambient Glow */}
                  <div className="absolute top-0 right-0 w-64 h-32 bg-emerald-500/10 blur-3xl rounded-full pointer-events-none -mr-16 -mt-10" />

                  <div className="flex flex-wrap items-center justify-between gap-3 relative z-10">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 rounded-xl bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 shadow-sm">
                        <Folder size={18} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-black text-white font-mono tracking-tight">
                            {selectedCatalogCategory}
                          </h3>
                          <span className="text-[10px] font-mono text-emerald-300 bg-emerald-950/80 px-2 py-0.5 rounded-full border border-emerald-500/40 font-bold">
                            {catalogProducts.filter(p => getProductCategories(p).some(c => c.toLowerCase() === selectedCatalogCategory.toLowerCase())).length} Items
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 font-sans">
                          Category policy & pricing rules used autonomously by Luna AI
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const currentRule = (productCatalogData?.categoryRules || {})[selectedCatalogCategory] || '';
                          setEditingCategoryRule({ catName: selectedCatalogCategory, rule: currentRule });
                        }}
                        className="text-xs font-mono font-bold text-cyan-300 hover:text-white bg-cyan-950/60 hover:bg-cyan-900/80 px-3 py-1.5 rounded-xl border border-cyan-500/40 hover:border-cyan-400 transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                      >
                        <Edit2 size={13} className="text-cyan-400" />
                        <span>{(productCatalogData?.categoryRules || {})[selectedCatalogCategory] ? 'Edit Description & Rules' : '+ Add Description & Rules'}</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setNewCatalogProdCategories([selectedCatalogCategory]);
                          setNewCatalogProdCategory(selectedCatalogCategory);
                          setIsAddingCatalogProduct(true);
                        }}
                        className="text-xs font-mono font-bold text-slate-950 bg-emerald-400 hover:bg-emerald-300 px-3 py-1.5 rounded-xl transition flex items-center gap-1.5 shadow-sm cursor-pointer"
                      >
                        <Plus size={13} />
                        <span>Add Product</span>
                      </button>
                    </div>
                  </div>

                  {/* Description / Rules Display or Inline Editor */}
                  {editingCategoryRule?.catName === selectedCatalogCategory ? (
                    <div className="mt-2 p-3 bg-[#06120D] border border-cyan-500/40 rounded-xl space-y-2 relative z-10 animate-fade-in">
                      <div className="flex items-center justify-between">
                        <label className="text-[11px] font-mono font-bold text-cyan-300 flex items-center gap-1.5">
                          <FileText size={13} className="text-cyan-400" />
                          <span>Edit Description, Rates & Rules for &quot;{selectedCatalogCategory}&quot;:</span>
                        </label>
                        <span className="text-[10px] text-slate-400 font-sans">
                          Luna AI quotes these rates and rules to WhatsApp customers
                        </span>
                      </div>
                      <textarea
                        rows={3}
                        value={editingCategoryRule.rule}
                        onChange={(e) => setEditingCategoryRule({ ...editingCategoryRule, rule: e.target.value })}
                        placeholder="e.g. 100km: Rs. 5,000 | 120km: Rs. 5,500 | Extra km: Rs. 40 | Refundable deposit: Rs. 15,000"
                        className="w-full bg-[#030906] border border-cyan-500/30 focus:border-cyan-400 rounded-lg p-2 text-xs text-white placeholder-slate-500 font-sans focus:outline-none"
                      />
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingCategoryRule(null)}
                          className="px-3 py-1 rounded-lg text-xs font-mono text-slate-400 hover:text-white bg-slate-900 border border-slate-700 cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveCategoryRule(editingCategoryRule.catName, editingCategoryRule.rule)}
                          className="px-3.5 py-1 rounded-lg text-xs font-mono font-bold text-slate-950 bg-cyan-400 hover:bg-cyan-300 transition cursor-pointer flex items-center gap-1 shadow-sm shadow-cyan-500/30"
                        >
                          <Check size={12} />
                          <span>Save Rules</span>
                        </button>
                      </div>
                    </div>
                  ) : (productCatalogData?.categoryRules || {})[selectedCatalogCategory] ? (
                    <div className="mt-1 p-3 bg-[#06120D]/90 border border-emerald-500/20 rounded-xl flex items-start gap-2.5 relative z-10">
                      <FileText size={15} className="text-cyan-400 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-mono font-bold text-cyan-300 uppercase tracking-wider mb-0.5">
                          Category Description & Pricing Policy:
                        </div>
                        <p className="text-xs text-cyan-100 font-sans leading-relaxed whitespace-pre-wrap">
                          {(productCatalogData?.categoryRules || {})[selectedCatalogCategory]}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-1 p-2.5 bg-[#06120D]/60 border border-dashed border-emerald-500/30 rounded-xl flex items-center justify-between gap-2 relative z-10">
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <AlertCircle size={14} className="text-amber-400 shrink-0" />
                        <span>No rules or description defined for <strong>{selectedCatalogCategory}</strong> yet.</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingCategoryRule({ catName: selectedCatalogCategory, rule: '' });
                        }}
                        className="text-[11px] font-mono text-emerald-400 hover:text-emerald-300 hover:underline cursor-pointer"
                      >
                        + Add Description & Rates
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Products List Feed */}
              {portalCatalogViewMode === 'grouped' ? (
                <div className="space-y-4">
                  {allCatalogCategories
                    .filter(cat => selectedCatalogCategory === 'all' || selectedCatalogCategory.toLowerCase() === cat.toLowerCase())
                    .map(cat => {
                      const catProds = catalogProducts.filter(p => {
                        const matchesCat = getProductCategories(p).some(c => c.toLowerCase() === cat.toLowerCase());
                        const matchesSearch = !catalogSearchQuery.trim() ||
                          p.name?.toLowerCase().includes(catalogSearchQuery.toLowerCase()) ||
                          p.description?.toLowerCase().includes(catalogSearchQuery.toLowerCase()) ||
                          p.sku?.toLowerCase().includes(catalogSearchQuery.toLowerCase()) ||
                          (Array.isArray(p.searchKeywords) && p.searchKeywords.some((k: string) => k.toLowerCase().includes(catalogSearchQuery.toLowerCase())));
                        return matchesCat && matchesSearch;
                      });

                      return (
                        <div key={cat} className="rounded-2xl border border-emerald-500/30 bg-[#0A1A13]/80 overflow-hidden shadow-lg">
                          <div className="px-4 py-2.5 bg-[#0E2018] border-b border-emerald-500/30 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Folder size={14} className="text-emerald-400" />
                              <span className="text-xs font-bold text-emerald-300 font-mono">
                                {cat}
                              </span>
                              <span className="text-[10px] font-mono text-slate-400 bg-slate-900 px-2 py-0.5 rounded-full border border-slate-800">
                                {catProds.length} {catProds.length === 1 ? 'item' : 'items'}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setNewCatalogProdCategories([cat]);
                                setNewCatalogProdCategory(cat);
                                setSelectedCatalogCategory(cat);
                                setIsAddingCatalogProduct(true);
                              }}
                              className="text-[11px] font-bold text-emerald-300 hover:text-white flex items-center gap-1.5 bg-emerald-950/60 hover:bg-emerald-900/60 px-2.5 py-1 rounded-xl border border-emerald-500/30 transition cursor-pointer"
                            >
                              <Plus size={12} />
                              <span>Add to {cat}</span>
                            </button>
                          </div>
                          {(productCatalogData?.categoryRules || {})[cat] && (
                            <div className="px-4 py-2 bg-[#06120D] border-b border-emerald-500/20 text-xs text-cyan-300 flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText size={13} className="text-cyan-400 shrink-0" />
                                <span className="text-[11px] text-slate-400 shrink-0 font-medium">Description & Rates:</span>
                                <span className="text-[11px] text-cyan-200 truncate">{(productCatalogData?.categoryRules || {})[cat]}</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowPortalCategoryManager(true);
                                  setEditingCategoryRule({ catName: cat, rule: (productCatalogData?.categoryRules || {})[cat] || '' });
                                }}
                                className="text-[10px] font-bold text-cyan-400 hover:underline shrink-0"
                              >
                                Edit Rule
                              </button>
                            </div>
                          )}
                          <div className="p-3 space-y-3">
                            {catProds.length === 0 ? (
                              <div className="text-center py-4 text-xs text-slate-500 italic">
                                No items in this category yet. Click &quot;Add to {cat}&quot; above!
                              </div>
                            ) : (
                              catProds.map(renderPortalProductCard)
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="space-y-3">
                  {catalogProducts
                    .filter((p) => {
                      const matchesSearch = !catalogSearchQuery.trim() ||
                        p.name?.toLowerCase().includes(catalogSearchQuery.toLowerCase()) ||
                        p.description?.toLowerCase().includes(catalogSearchQuery.toLowerCase()) ||
                        p.sku?.toLowerCase().includes(catalogSearchQuery.toLowerCase()) ||
                        (Array.isArray(p.searchKeywords) && p.searchKeywords.some((k: string) => k.toLowerCase().includes(catalogSearchQuery.toLowerCase())));
                      const matchesCat = selectedCatalogCategory === 'all' || getProductCategories(p).some(c => c.toLowerCase() === selectedCatalogCategory.toLowerCase());
                      return matchesSearch && matchesCat;
                    })
                    .map(renderPortalProductCard)}
                </div>
              )}
            </div>

            {/* Right Column (5 cols): Add / Quick Editor & Live AI Preview */}
            <div className="lg:col-span-5 space-y-4">
              {/* Add New Product Form Card */}
              <div className="p-5 rounded-2xl bg-gradient-to-br from-[#0F261C] to-[#0A1A13] border border-emerald-500/40 shadow-xl space-y-3">
                <div className="flex items-center justify-between border-b border-emerald-500/20 pb-3">
                  <h3 className="text-sm font-black text-white flex items-center gap-2 font-sans">
                    <Sparkles size={16} className="text-emerald-400" />
                    <span>Add New E-Commerce Product</span>
                  </h3>
                  {isSavingCatalogProducts && (
                    <span className="text-[10px] font-mono text-emerald-400 animate-pulse">
                      Saving to AI...
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] font-mono font-bold text-slate-300 uppercase block mb-1">
                      Product Name *
                    </label>
                    <input
                      type="text"
                      value={newCatalogProdName}
                      onChange={(e) => setNewCatalogProdName(e.target.value)}
                      placeholder="e.g. 🌹 Royal Red Rose Bouquet"
                      className="w-full bg-[#08120E] border border-emerald-500/30 focus:border-emerald-400 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none transition"
                    />
                  </div>

                  {/* Multi-Category Selector & Tag Cloud */}
                  <div className="space-y-1.5 p-3 rounded-xl bg-[#08120E]/90 border border-emerald-500/30">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-mono font-bold text-emerald-300 uppercase flex items-center gap-1.5">
                        <Folder size={12} className="text-emerald-400" />
                        <span>Assigned Categories (Multiple Allowed)</span>
                      </label>
                      <span className="text-[10px] font-mono text-slate-400">
                        {newCatalogProdCategories.length} selected
                      </span>
                    </div>

                    {/* Selected category chips */}
                    <div className="flex flex-wrap gap-1.5 min-h-[34px] p-1.5 rounded-lg bg-[#050D09] border border-emerald-500/20 items-center">
                      {newCatalogProdCategories.map((cat) => (
                        <span
                          key={cat}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                        >
                          <Folder size={10} className="text-emerald-400" />
                          <span>{cat}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const remaining = newCatalogProdCategories.filter((c) => c !== cat);
                              setNewCatalogProdCategories(remaining.length > 0 ? remaining : ['General']);
                              setNewCatalogProdCategory(remaining[0] || 'General');
                            }}
                            className="text-emerald-400 hover:text-rose-400 ml-1 cursor-pointer font-black text-xs"
                            title="Remove category"
                          >
                            ×
                          </button>
                        </span>
                      ))}

                      {/* Inline Input to add/create category */}
                      <input
                        type="text"
                        list="portal-catalog-categories-list"
                        value={newCategoryTagInput}
                        onChange={(e) => setNewCategoryTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = newCategoryTagInput.trim();
                            if (val && !newCatalogProdCategories.includes(val)) {
                              setNewCatalogProdCategories([...newCatalogProdCategories.filter((c) => c !== 'General'), val]);
                              setNewCatalogProdCategory(val);
                              setNewCategoryTagInput('');
                            }
                          }
                        }}
                        placeholder="+ Add or type category..."
                        className="bg-transparent border-none text-[11px] text-white placeholder-slate-500 focus:outline-none font-mono flex-1 min-w-[140px] px-1"
                      />
                    </div>

                    {/* Quick-add available store categories pill bar */}
                    {allCatalogCategories.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap pt-1">
                        <span className="text-[9px] font-mono text-slate-500 uppercase">Quick Add:</span>
                        {allCatalogCategories
                          .filter((c) => !newCatalogProdCategories.includes(c))
                          .slice(0, 8)
                          .map((cat) => (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => {
                                setNewCatalogProdCategories([...newCatalogProdCategories.filter((c) => c !== 'General'), cat]);
                                setNewCatalogProdCategory(cat);
                              }}
                              className="text-[10px] font-mono text-slate-300 hover:text-emerald-300 bg-[#0E2018] hover:bg-emerald-950/60 px-2 py-0.5 rounded-md border border-emerald-500/20 hover:border-emerald-500/40 transition cursor-pointer"
                            >
                              + {cat}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <label className="text-[10px] font-mono font-bold text-slate-300 uppercase block mb-1">
                        Price *
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={newCatalogProdPrice}
                        onChange={(e) => setNewCatalogProdPrice(e.target.value)}
                        placeholder="4500"
                        className="w-full bg-[#08120E] border border-emerald-500/30 focus:border-emerald-400 rounded-xl px-2 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none font-mono"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-mono font-bold text-slate-400 uppercase block mb-1">
                        Original Price
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={newCatalogProdOriginalPrice}
                        onChange={(e) => setNewCatalogProdOriginalPrice(e.target.value)}
                        placeholder="5500"
                        className="w-full bg-[#08120E] border border-emerald-500/30 focus:border-emerald-400 rounded-xl px-2 py-1.5 text-xs text-slate-300 placeholder-slate-600 focus:outline-none font-mono"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-mono font-bold text-emerald-400 uppercase block mb-1">
                        Stock Units
                      </label>
                      <input
                        type="number"
                        value={newCatalogProdStockQty}
                        onChange={(e) => setNewCatalogProdStockQty(e.target.value)}
                        placeholder="50"
                        className="w-full bg-[#08120E] border border-emerald-500/30 focus:border-emerald-400 rounded-xl px-2 py-1.5 text-xs text-emerald-300 placeholder-slate-600 focus:outline-none font-mono"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-mono font-bold text-slate-300 uppercase block mb-1">
                        Unit
                      </label>
                      <input
                        type="text"
                        value={newCatalogProdUnit}
                        onChange={(e) => setNewCatalogProdUnit(e.target.value)}
                        placeholder="Pieces"
                        className="w-full bg-[#08120E] border border-emerald-500/30 focus:border-emerald-400 rounded-xl px-2 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-mono font-bold text-slate-300 uppercase block mb-1">
                      Highlights / Features (Helps AI describe naturally)
                    </label>
                    <textarea
                      rows={2}
                      value={newCatalogProdDesc}
                      onChange={(e) => setNewCatalogProdDesc(e.target.value)}
                      placeholder="Fresh 24 premium imported red roses with greeting card..."
                      className="w-full bg-[#08120E] border border-emerald-500/30 focus:border-emerald-400 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none transition resize-none"
                    />
                  </div>

                  {/* ── Multi-Media, Document Upload & Luna AI Ingestion ──────── */}
                  <div className="p-3 rounded-2xl bg-[#08120E] border border-emerald-500/30 space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-mono font-bold text-emerald-300 uppercase flex items-center gap-1">
                        <Paperclip size={12} />
                        <span>Product Media & Documents ({newCatalogProdMedia.length})</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => handleAiImproveCatalogProduct(false)}
                        disabled={isAiEnrichingNewCatalog}
                        className="px-3 py-1 rounded-xl bg-gradient-to-r from-emerald-500/30 to-teal-500/30 hover:from-emerald-500/40 hover:to-teal-500/40 border border-emerald-400/60 text-emerald-200 hover:text-white font-bold text-[11px] flex items-center gap-1.5 transition shadow-[0_0_15px_rgba(16,185,129,0.3)] disabled:opacity-50 cursor-pointer"
                        title="Luna AI analyzes uploaded images, videos & documents to auto-enrich description, category, and keywords"
                      >
                        {isAiEnrichingNewCatalog ? (
                          <>
                            <Loader2 size={12} className="animate-spin text-emerald-400" />
                            <span>Luna Analyzing...</span>
                          </>
                        ) : (
                          <>
                            <Wand2 size={12} className="text-emerald-300 animate-pulse" />
                            <span>✨ Improve with Luna AI</span>
                          </>
                        )}
                      </button>
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newCatalogProdMediaUrl}
                        onChange={(e) => setNewCatalogProdMediaUrl(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && newCatalogProdMediaUrl.trim()) {
                            e.preventDefault();
                            const url = newCatalogProdMediaUrl.trim();
                            const ext = url.split('.').pop()?.toLowerCase() || '';
                            let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
                            if (['mp4', 'mov', 'webm'].includes(ext)) mType = 'video';
                            else if (['pdf'].includes(ext)) mType = 'pdf';
                            else if (['doc', 'docx', 'txt'].includes(ext)) mType = 'document';
                            setNewCatalogProdMedia(prev => [...prev, {
                              id: `med_${Date.now()}`,
                              type: mType,
                              url,
                              name: `Asset ${prev.length + 1}`
                            }]);
                            setNewCatalogProdMediaUrl('');
                          }
                        }}
                        placeholder="Paste URL + Enter, or select multiple files from device →"
                        className="flex-1 bg-[#050C0A] border border-emerald-500/30 focus:border-emerald-400 rounded-xl px-2.5 py-1.5 text-[11px] text-white placeholder-slate-500 focus:outline-none font-mono"
                      />
                      <input
                        type="file"
                        ref={newCatalogFileInputRef}
                        multiple
                        onChange={(e) => {
                          if (e.target.files && e.target.files.length > 0) {
                            handleCatalogMultiFileUpload(e.target.files, false);
                          }
                        }}
                        accept="image/*,video/*,application/pdf,.doc,.docx,.txt"
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => newCatalogFileInputRef.current?.click()}
                        disabled={isUploadingCatalogMedia}
                        className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 rounded-xl text-[10px] font-bold font-mono flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50 shrink-0"
                        title="Upload multiple photos, videos, or PDF spec sheets"
                      >
                        {isUploadingCatalogMedia ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Upload size={12} />
                        )}
                        <span>+ Upload Files</span>
                      </button>
                    </div>

                    {/* Attached Media Chips */}
                    {newCatalogProdMedia.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1 max-h-28 overflow-y-auto pr-1">
                        {newCatalogProdMedia.map((m, idx) => (
                          <div
                            key={m.id || idx}
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-emerald-950/60 border border-emerald-500/40 text-[10px] font-mono text-emerald-200 shadow-sm"
                          >
                            {m.type === 'image' && (
                              <img
                                src={m.url}
                                alt=""
                                className="w-5 h-5 rounded-lg object-cover border border-emerald-400/40"
                                onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                              />
                            )}
                            {m.type === 'video' && <Film size={13} className="text-purple-400" />}
                            {m.type === 'pdf' && <FileText size={13} className="text-rose-400" />}
                            {m.type === 'document' && <FileText size={13} className="text-amber-400" />}
                            <span className="truncate max-w-[130px]" title={m.name || m.url}>
                              {m.name || m.type.toUpperCase()}
                            </span>
                            <button
                              type="button"
                              onClick={() => setSelectedProductMediaPreview({ title: m.name || 'Preview', url: m.url, type: m.type })}
                              className="text-slate-400 hover:text-emerald-300 ml-1 cursor-pointer"
                              title="Preview Media"
                            >
                              <Eye size={12} />
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewCatalogProdMedia(prev => prev.filter((_, i) => i !== idx))}
                              className="text-slate-400 hover:text-rose-400 ml-1 cursor-pointer"
                              title="Remove"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Optional Raw Text / ChatGPT Paste */}
                    <div className="pt-2 border-t border-emerald-500/20">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-mono text-slate-400 flex items-center gap-1.5">
                          <FileText size={11} className="text-emerald-400" />
                          <span>Paste ChatGPT / Raw Specs (Optional)</span>
                        </span>
                        {newCatalogProdRawText && (
                          <button
                            type="button"
                            onClick={() => setNewCatalogProdRawText('')}
                            className="text-[10px] text-slate-500 hover:text-rose-400 font-mono cursor-pointer transition"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <textarea
                        rows={2}
                        value={newCatalogProdRawText}
                        onChange={(e) => setNewCatalogProdRawText(e.target.value)}
                        placeholder="Paste ChatGPT output, full descriptions, raw supplier specs, or price lists here... Luna AI will read and auto-fill details!"
                        className="w-full bg-[#050C0A] border border-emerald-500/20 focus:border-emerald-400/60 rounded-xl px-2.5 py-1.5 text-[11px] text-slate-200 placeholder-slate-600 focus:outline-none resize-none font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-mono font-bold text-slate-300 uppercase block mb-1">
                      Customer Query Keywords (Auto-generated by Luna AI)
                    </label>
                    <input
                      type="text"
                      value={newCatalogProdKeywords}
                      onChange={(e) => setNewCatalogProdKeywords(e.target.value)}
                      placeholder="red rose, rose bouquet, rathu mal, fresh flowers"
                      className="w-full bg-[#08120E] border border-emerald-500/30 focus:border-emerald-400 rounded-xl px-2.5 py-1.5 text-[11px] text-white placeholder-slate-500 focus:outline-none font-mono"
                    />
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <label className="flex items-center gap-2 text-xs text-slate-300 font-mono cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newCatalogProdAvailable}
                        onChange={(e) => setNewCatalogProdAvailable(e.target.checked)}
                        className="w-4 h-4 accent-emerald-500 rounded"
                      />
                      <span>In Stock / Ready to Sell</span>
                    </label>

                    <button
                      type="button"
                      onClick={handleCreateCatalogProduct}
                      disabled={!newCatalogProdName.trim() || !newCatalogProdPrice.trim() || isSavingCatalogProducts}
                      className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider transition cursor-pointer shadow-lg disabled:opacity-50"
                    >
                      Save Product
                    </button>
                  </div>
                </div>
              </div>

              {/* Live AI Knowledge Preview Card */}
              <div className="p-4 rounded-2xl bg-[#091510] border border-emerald-500/20 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono font-bold uppercase text-emerald-400 flex items-center gap-1.5">
                    <Bot size={13} />
                    <span>How Luna AI Uses This Catalog</span>
                  </span>
                  <span className="text-[9px] font-mono text-slate-400">Autonomous Multimedia Dispatch</span>
                </div>
                <p className="text-[11px] text-slate-300 leading-relaxed">
                  When a customer asks for item prices, photos, video demos, or PDF brochures on WhatsApp or live calls, Luna AI quotes these exact prices and autonomously dispatches attached photos/videos/documents directly to the customer without developer intervention.
                </p>
              </div>
            </div>

            {/* ── Edit Product Modal ── */}
            {editingCatalogProduct && (
              <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
                <div className="bg-[#0A1A13] border border-sky-500/40 rounded-3xl p-6 max-w-lg w-full shadow-2xl space-y-4 animate-in fade-in zoom-in-95">
                  <div className="flex items-center justify-between border-b border-sky-500/20 pb-3">
                    <h3 className="text-sm font-black text-white flex items-center gap-2 font-sans">
                      <Edit3 size={16} className="text-sky-400" />
                      <span>Edit Product & Stock: {editingCatalogProduct.name}</span>
                    </h3>
                    <button
                      type="button"
                      onClick={() => setEditingCatalogProduct(null)}
                      className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition cursor-pointer"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] font-mono font-bold text-slate-300 uppercase block mb-1">
                        Product Name *
                      </label>
                      <input
                        type="text"
                        value={editCatalogProdName}
                        onChange={(e) => setEditCatalogProdName(e.target.value)}
                        className="w-full bg-[#08120E] border border-sky-500/30 focus:border-sky-400 rounded-xl px-3 py-2 text-xs text-white focus:outline-none transition"
                      />
                    </div>

                  {/* Multi-Category Selector & Tag Cloud */}
                  <div className="space-y-1.5 p-3 rounded-xl bg-[#08120E]/90 border border-sky-500/30">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-mono font-bold text-sky-300 uppercase flex items-center gap-1.5">
                        <Folder size={12} className="text-sky-400" />
                        <span>Assigned Categories (Multiple Allowed)</span>
                      </label>
                      <span className="text-[10px] font-mono text-slate-400">
                        {editCatalogProdCategories.length} selected
                      </span>
                    </div>

                    {/* Selected category chips */}
                    <div className="flex flex-wrap gap-1.5 min-h-[34px] p-1.5 rounded-lg bg-[#050D09] border border-sky-500/20 items-center">
                      {editCatalogProdCategories.map((cat) => (
                        <span
                          key={cat}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono font-bold bg-sky-500/20 text-sky-300 border border-sky-500/40"
                        >
                          <Folder size={10} className="text-sky-400" />
                          <span>{cat}</span>
                          <button
                            type="button"
                            onClick={() => {
                              const remaining = editCatalogProdCategories.filter((c) => c !== cat);
                              setEditCatalogProdCategories(remaining.length > 0 ? remaining : ['General']);
                              setEditCatalogProdCategory(remaining[0] || 'General');
                            }}
                            className="text-sky-400 hover:text-rose-400 ml-1 cursor-pointer font-black text-xs"
                            title="Remove category"
                          >
                            ×
                          </button>
                        </span>
                      ))}

                      {/* Inline Input to add/assign category */}
                      <input
                        type="text"
                        list="portal-catalog-categories-list"
                        value={editCategoryTagInput}
                        onChange={(e) => setEditCategoryTagInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const val = editCategoryTagInput.trim();
                            if (val && !editCatalogProdCategories.includes(val)) {
                              setEditCatalogProdCategories([...editCatalogProdCategories.filter((c) => c !== 'General'), val]);
                              setEditCatalogProdCategory(val);
                              setEditCategoryTagInput('');
                            }
                          }
                        }}
                        placeholder="+ Add or type category..."
                        className="bg-transparent border-none text-[11px] text-white placeholder-slate-500 focus:outline-none font-mono flex-1 min-w-[140px] px-1"
                      />
                    </div>

                    {/* Quick-add available store categories pill bar */}
                    {allCatalogCategories.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap pt-1">
                        <span className="text-[9px] font-mono text-slate-500 uppercase">Quick Add:</span>
                        {allCatalogCategories
                          .filter((c) => !editCatalogProdCategories.includes(c))
                          .slice(0, 8)
                          .map((cat) => (
                            <button
                              key={cat}
                              type="button"
                              onClick={() => {
                                setEditCatalogProdCategories([...editCatalogProdCategories.filter((c) => c !== 'General'), cat]);
                                setEditCatalogProdCategory(cat);
                              }}
                              className="text-[10px] font-mono text-slate-300 hover:text-sky-300 bg-[#0E2018] hover:bg-sky-950/60 px-2 py-0.5 rounded-md border border-sky-500/20 hover:border-sky-500/40 transition cursor-pointer"
                            >
                              + {cat}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <label className="text-[10px] font-mono font-bold text-slate-300 uppercase block mb-1">
                        Price *
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={editCatalogProdPrice}
                        onChange={(e) => setEditCatalogProdPrice(e.target.value)}
                        className="w-full bg-[#08120E] border border-sky-500/30 focus:border-sky-400 rounded-xl px-2 py-1.5 text-xs text-white font-mono focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-mono font-bold text-slate-400 uppercase block mb-1">
                        Original Price
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={editCatalogProdOriginalPrice}
                        onChange={(e) => setEditCatalogProdOriginalPrice(e.target.value)}
                        className="w-full bg-[#08120E] border border-sky-500/30 focus:border-sky-400 rounded-xl px-2 py-1.5 text-xs text-slate-300 font-mono focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-mono font-bold text-sky-300 uppercase block mb-1">
                        Stock Units
                      </label>
                      <input
                        type="number"
                        value={editCatalogProdStockQty}
                        onChange={(e) => setEditCatalogProdStockQty(e.target.value)}
                        className="w-full bg-[#08120E] border border-sky-500/30 focus:border-sky-400 rounded-xl px-2 py-1.5 text-xs text-sky-300 font-mono focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] font-mono font-bold text-slate-300 uppercase block mb-1">
                        Unit
                      </label>
                      <input
                        type="text"
                        value={editCatalogProdUnit}
                        onChange={(e) => setEditCatalogProdUnit(e.target.value)}
                        className="w-full bg-[#08120E] border border-sky-500/30 focus:border-sky-400 rounded-xl px-2 py-1.5 text-xs text-white focus:outline-none"
                      />
                    </div>
                  </div>

                    <div>
                      <label className="text-[10px] font-mono font-bold text-slate-300 uppercase block mb-1">
                        Highlights / Features
                      </label>
                      <textarea
                        rows={2}
                        value={editCatalogProdDesc}
                        onChange={(e) => setEditCatalogProdDesc(e.target.value)}
                        className="w-full bg-[#08120E] border border-sky-500/30 focus:border-sky-400 rounded-xl px-3 py-2 text-xs text-white focus:outline-none transition resize-none"
                      />
                    </div>

                    {/* ── Multi-Media, Document Upload & Luna AI Ingestion (Edit) ─ */}
                    <div className="p-3 rounded-2xl bg-[#08120E] border border-sky-500/30 space-y-2.5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-mono font-bold text-sky-300 uppercase flex items-center gap-1">
                          <Paperclip size={12} />
                          <span>Product Media & Documents ({editCatalogProdMedia.length})</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => handleAiImproveCatalogProduct(true)}
                          disabled={isAiEnrichingEditCatalog}
                          className="px-3 py-1 rounded-xl bg-gradient-to-r from-sky-500/30 to-indigo-500/30 hover:from-sky-500/40 hover:to-indigo-500/40 border border-sky-400/60 text-sky-200 hover:text-white font-bold text-[11px] flex items-center gap-1.5 transition shadow-[0_0_15px_rgba(56,189,248,0.3)] disabled:opacity-50 cursor-pointer"
                          title="Luna AI analyzes uploaded images, videos & documents to auto-enrich description, category, and keywords"
                        >
                          {isAiEnrichingEditCatalog ? (
                            <>
                              <Loader2 size={12} className="animate-spin text-sky-400" />
                              <span>Luna Analyzing...</span>
                            </>
                          ) : (
                            <>
                              <Wand2 size={12} className="text-sky-300 animate-pulse" />
                              <span>✨ Improve with Luna AI</span>
                            </>
                          )}
                        </button>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editCatalogProdMediaUrl}
                          onChange={(e) => setEditCatalogProdMediaUrl(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && editCatalogProdMediaUrl.trim()) {
                              e.preventDefault();
                              const url = editCatalogProdMediaUrl.trim();
                              const ext = url.split('.').pop()?.toLowerCase() || '';
                              let mType: 'image' | 'video' | 'pdf' | 'document' = 'image';
                              if (['mp4', 'mov', 'webm'].includes(ext)) mType = 'video';
                              else if (['pdf'].includes(ext)) mType = 'pdf';
                              else if (['doc', 'docx', 'txt'].includes(ext)) mType = 'document';
                              setEditCatalogProdMedia(prev => [...prev, {
                                id: `med_${Date.now()}`,
                                type: mType,
                                url,
                                name: `Asset ${prev.length + 1}`
                              }]);
                              setEditCatalogProdMediaUrl('');
                            }
                          }}
                          placeholder="Paste URL + Enter, or select multiple files from device →"
                          className="flex-1 bg-[#050C0A] border border-sky-500/30 focus:border-sky-400 rounded-xl px-2.5 py-1.5 text-[11px] text-white placeholder-slate-500 focus:outline-none font-mono"
                        />
                        <input
                          type="file"
                          ref={editCatalogFileInputRef}
                          multiple
                          onChange={(e) => {
                            if (e.target.files && e.target.files.length > 0) {
                              handleCatalogMultiFileUpload(e.target.files, true);
                            }
                          }}
                          accept="image/*,video/*,application/pdf,.doc,.docx,.txt"
                          className="hidden"
                        />
                        <button
                          type="button"
                          onClick={() => editCatalogFileInputRef.current?.click()}
                          disabled={isEditUploadingCatalogMedia}
                          className="px-3 py-1.5 bg-sky-500/20 hover:bg-sky-500/30 text-sky-300 border border-sky-500/40 rounded-xl text-[10px] font-bold font-mono flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50 shrink-0"
                          title="Upload multiple photos, videos, or PDF spec sheets"
                        >
                          {isEditUploadingCatalogMedia ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <Upload size={12} />
                          )}
                          <span>+ Upload Files</span>
                        </button>
                      </div>

                      {/* Attached Media Chips */}
                      {editCatalogProdMedia.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-1 max-h-28 overflow-y-auto pr-1">
                          {editCatalogProdMedia.map((m, idx) => (
                            <div
                              key={m.id || idx}
                              className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-sky-950/60 border border-sky-500/40 text-[10px] font-mono text-sky-200 shadow-sm"
                            >
                              {m.type === 'image' && (
                                <img
                                  src={m.url}
                                  alt=""
                                  className="w-5 h-5 rounded-lg object-cover border border-sky-400/40"
                                  onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }}
                                />
                              )}
                              {m.type === 'video' && <Film size={13} className="text-purple-400" />}
                              {m.type === 'pdf' && <FileText size={13} className="text-rose-400" />}
                              {m.type === 'document' && <FileText size={13} className="text-amber-400" />}
                              <span className="truncate max-w-[130px]" title={m.name || m.url}>
                                {m.name || m.type.toUpperCase()}
                              </span>
                              <button
                                type="button"
                                onClick={() => setSelectedProductMediaPreview({ title: m.name || 'Preview', url: m.url, type: m.type })}
                                className="text-slate-400 hover:text-sky-300 ml-1 cursor-pointer"
                                title="Preview Media"
                              >
                                <Eye size={12} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditCatalogProdMedia(prev => prev.filter((_, i) => i !== idx))}
                                className="text-slate-400 hover:text-rose-400 ml-1 cursor-pointer"
                                title="Remove"
                              >
                                <X size={12} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Optional Raw Text / ChatGPT Paste (Edit) */}
                      <div className="pt-2 border-t border-sky-500/20">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] font-mono text-slate-400 flex items-center gap-1.5">
                            <FileText size={11} className="text-sky-400" />
                            <span>Paste ChatGPT / Raw Specs (Optional)</span>
                          </span>
                          {editCatalogProdRawText && (
                            <button
                              type="button"
                              onClick={() => setEditCatalogProdRawText('')}
                              className="text-[10px] text-slate-500 hover:text-rose-400 font-mono cursor-pointer transition"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        <textarea
                          rows={2}
                          value={editCatalogProdRawText}
                          onChange={(e) => setEditCatalogProdRawText(e.target.value)}
                          placeholder="Paste ChatGPT output, full descriptions, raw supplier specs, or price lists here... Luna AI will read and auto-fill details!"
                          className="w-full bg-[#050C0A] border border-sky-500/20 focus:border-sky-400/60 rounded-xl px-2.5 py-1.5 text-[11px] text-slate-200 placeholder-slate-600 focus:outline-none resize-none font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-[10px] font-mono font-bold text-slate-300 uppercase block mb-1">
                        Customer Query Keywords (Auto-generated by Luna AI)
                      </label>
                      <input
                        type="text"
                        value={editCatalogProdKeywords}
                        onChange={(e) => setEditCatalogProdKeywords(e.target.value)}
                        placeholder="red rose, rose bouquet, rathu mal, fresh flowers"
                        className="w-full bg-[#08120E] border border-sky-500/30 focus:border-sky-400 rounded-xl px-2.5 py-1.5 text-[11px] text-white focus:outline-none font-mono"
                      />
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-800">
                      <label className="flex items-center gap-2 text-xs text-slate-300 font-mono cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editCatalogProdAvailable}
                          onChange={(e) => setEditCatalogProdAvailable(e.target.checked)}
                          className="w-4 h-4 accent-sky-500 rounded"
                        />
                        <span>In Stock / Ready to Sell</span>
                      </label>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setEditingCatalogProduct(null)}
                          className="px-3 py-1.5 rounded-xl border border-slate-700 text-slate-300 text-xs hover:bg-slate-800 transition cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveEditCatalogProduct}
                          disabled={!editCatalogProdName.trim() || !editCatalogProdPrice.trim() || isSavingCatalogProducts}
                          className="px-4 py-1.5 rounded-xl bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider transition cursor-pointer shadow-lg disabled:opacity-50"
                        >
                          Update Product
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Product Media Preview Modal */}
          {selectedProductMediaPreview && (
            <div
              className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
              onClick={() => setSelectedProductMediaPreview(null)}
            >
              <div
                className="bg-[#0D1E16] border border-emerald-500/40 rounded-3xl p-5 max-w-lg w-full shadow-2xl space-y-4 animate-scale-up"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between border-b border-emerald-500/20 pb-3">
                  <h3 className="text-sm font-bold text-white truncate font-sans">
                    {selectedProductMediaPreview.title || 'Product Media Asset'}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setSelectedProductMediaPreview(null)}
                    className="p-1 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="rounded-2xl overflow-hidden bg-black flex items-center justify-center max-h-[350px]">
                  {selectedProductMediaPreview.type === 'video' ? (
                    <video
                      src={selectedProductMediaPreview.url}
                      controls
                      autoPlay
                      className="w-full max-h-[350px] object-contain"
                    />
                  ) : selectedProductMediaPreview.type === 'pdf' ? (
                    <div className="p-8 text-center space-y-3">
                      <FileText size={48} className="mx-auto text-amber-400" />
                      <p className="text-xs text-white font-mono">{selectedProductMediaPreview.url}</p>
                      <a
                        href={selectedProductMediaPreview.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-500 text-slate-950 font-bold text-xs"
                      >
                        <span>Open PDF in New Tab</span>
                        <ExternalLink size={13} />
                      </a>
                    </div>
                  ) : (
                    <img
                      src={selectedProductMediaPreview.url}
                      alt={selectedProductMediaPreview.title}
                      className="w-full max-h-[350px] object-contain"
                    />
                  )}
                </div>

                <div className="flex items-center justify-between text-[11px] font-mono text-slate-400">
                  <span className="truncate max-w-[300px]">{selectedProductMediaPreview.url}</span>
                  <a
                    href={selectedProductMediaPreview.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-emerald-400 hover:underline flex items-center gap-1"
                  >
                    <span>Direct Link</span>
                    <ExternalLink size={11} />
                  </a>
                </div>
              </div>
            </div>
          )}
          </>
        ) : catalogStudioTab === 'recycle_bin' ? (
          /* ═══════════════════════════════════════════════════════════════ */
          /* VIEW 9C: PRODUCT CATALOG RECYCLE BIN                           */
          /* ═══════════════════════════════════════════════════════════════ */
          <div className="flex-1 flex flex-col overflow-hidden bg-[#070E0B]">
            {/* Top Toolbar */}
            <div className="px-6 py-4 border-b border-rose-500/20 bg-[#12080A]/90 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0">
              <div>
                <h3 className="text-sm font-black text-rose-300 flex items-center gap-2">
                  <Trash2 size={16} className="text-rose-400" />
                  <span>Product Recycle Bin</span>
                  <span className="text-[10px] font-mono font-bold bg-rose-500/20 text-rose-300 border border-rose-500/30 px-2 py-0.5 rounded-full">
                    {(productCatalogData?.recycleBin || []).length} DELETED ITEMS
                  </span>
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Items removed manually or by Luna AI are preserved here with instant 1-click restore.
                </p>
              </div>

              {(productCatalogData?.recycleBin || []).length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleRestoreAllPortalRecycle}
                    className="px-3.5 py-1.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-xs font-mono font-bold transition flex items-center gap-1.5 cursor-pointer"
                  >
                    <RotateCcw size={13} />
                    <span>Restore All Items</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('Permanently delete all items from the recycle bin? This action cannot be undone.')) {
                        handleEmptyPortalRecycleBin();
                      }
                    }}
                    className="px-3.5 py-1.5 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 text-xs font-mono font-bold transition cursor-pointer"
                  >
                    Empty Recycle Bin
                  </button>
                </div>
              )}
            </div>

            {/* Recycle Bin Feed */}
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-rose-950">
              {(productCatalogData?.recycleBin || []).length === 0 ? (
                <div className="py-20 text-center text-slate-500 font-mono">
                  <Archive size={40} className="mx-auto text-slate-700 mb-3" />
                  <p className="text-sm font-bold text-slate-400">Recycle Bin is Empty</p>
                  <p className="text-xs text-slate-600 mt-1">
                    When you or Luna AI delete products from the catalog, they will appear here safely.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(productCatalogData?.recycleBin || []).map((item: any) => {
                    const prod = item.product || item;
                    const thumb = prod.imageUrl || (prod.media && prod.media[0]?.url);
                    return (
                      <div
                        key={item.id}
                        className="p-4 rounded-2xl bg-[#140B0E] border border-rose-500/25 hover:border-rose-500/50 transition-all flex flex-col justify-between shadow-lg"
                      >
                        <div>
                          <div className="flex items-start gap-3">
                            {thumb ? (
                              <img src={thumb} alt={prod.name} className="w-12 h-12 rounded-xl object-cover border border-rose-500/20 shrink-0" />
                            ) : (
                              <div className="w-12 h-12 rounded-xl bg-slate-900 border border-rose-500/20 flex items-center justify-center text-slate-500 shrink-0">
                                <Package size={20} />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <h4 className="text-xs font-bold text-white truncate">{prod.name}</h4>
                              <div className="text-[10px] text-slate-400 font-mono mt-0.5 flex items-center gap-1.5">
                                <span className="text-emerald-400 font-bold">{prod.currency || productCatalogData?.defaultCurrency || 'LKR'} {Number(prod.price || 0).toLocaleString()}</span>
                                <span>•</span>
                                <span>{prod.category || 'General'}</span>
                              </div>
                              {prod.sku && (
                                <div className="text-[9px] font-mono text-slate-500 mt-0.5">SKU: {prod.sku}</div>
                              )}
                            </div>
                          </div>

                          <div className="mt-3 pt-2.5 border-t border-rose-500/15 text-[10px] font-mono text-slate-400 flex items-center justify-between">
                            <span>Deleted: {item.deletedAt ? new Date(item.deletedAt).toLocaleDateString() : 'Recently'}</span>
                            <span className={item.deletedBy === 'luna_ai' ? 'text-cyan-400 font-bold' : 'text-slate-400'}>
                              {item.deletedBy === 'luna_ai' ? '🤖 By Luna AI' : '👤 Manual'}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-rose-500/20">
                          <button
                            type="button"
                            onClick={() => handleRestorePortalRecycle(item.id)}
                            className="px-3 py-1.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-xs font-mono font-bold transition flex items-center gap-1 cursor-pointer active:scale-95"
                            title="Restore back into active catalog"
                          >
                            <RotateCcw size={12} />
                            <span>Restore Product</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handlePermanentDeletePortalRecycle(item.id)}
                            className="p-1.5 rounded-xl text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition cursor-pointer"
                            title="Permanently remove forever"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        ) : catalogStudioTab === 'snapshots' ? (
          /* ═══════════════════════════════════════════════════════════════ */
          /* VIEW 9D: PRODUCT CATALOG SNAPSHOTS & ROLLBACK                   */
          /* ═══════════════════════════════════════════════════════════════ */
          <div className="flex-1 flex flex-col overflow-hidden bg-[#070E0B]">
            {/* Top Toolbar */}
            <div className="px-6 py-4 border-b border-indigo-500/20 bg-[#0A0D18]/90 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0">
              <div>
                <h3 className="text-sm font-black text-indigo-300 flex items-center gap-2">
                  <History size={16} className="text-indigo-400" />
                  <span>Catalog Snapshots & Rollback Backups</span>
                  <span className="text-[10px] font-mono font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 px-2 py-0.5 rounded-full">
                    {(productCatalogData?.snapshots || []).length} SNAPSHOTS
                  </span>
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Point-in-time catalog checkpoints. Auto-created before deletions or manually saved before major inventory updates.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsCreatingPortalSnapshot(!isCreatingPortalSnapshot)}
                  className="px-3.5 py-1.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-slate-950 font-bold text-xs font-mono transition flex items-center gap-1.5 cursor-pointer shadow-md shadow-indigo-500/20"
                >
                  <Camera size={13} />
                  <span>Create Snapshot Now</span>
                </button>
              </div>
            </div>

            {isCreatingPortalSnapshot && (
              <form onSubmit={handleCreatePortalSnapshot} className="px-6 py-3 bg-indigo-950/40 border-b border-indigo-500/30 flex items-center gap-3 animate-fade-in">
                <input
                  type="text"
                  value={portalSnapshotLabel}
                  onChange={(e) => setPortalSnapshotLabel(e.target.value)}
                  placeholder="Snapshot Label (e.g. Before Festive Season Price Update, Restock Backup...)"
                  className="flex-1 bg-slate-900 border border-indigo-500/40 focus:border-indigo-400 rounded-xl px-3.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none"
                />
                <button
                  type="submit"
                  className="px-4 py-1.5 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-slate-950 font-black text-xs uppercase tracking-wider transition cursor-pointer"
                >
                  Save Snapshot
                </button>
                <button
                  type="button"
                  onClick={() => setIsCreatingPortalSnapshot(false)}
                  className="text-slate-400 hover:text-slate-200 text-xs px-2"
                >
                  Cancel
                </button>
              </form>
            )}

            {/* Snapshots Feed */}
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-indigo-950">
              {(productCatalogData?.snapshots || []).length === 0 ? (
                <div className="py-20 text-center text-slate-500 font-mono">
                  <History size={40} className="mx-auto text-slate-700 mb-3" />
                  <p className="text-sm font-bold text-slate-400">No Snapshots Saved Yet</p>
                  <p className="text-xs text-slate-600 mt-1">
                    Snapshots are automatically created before deletions, or you can take a manual snapshot anytime.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(productCatalogData?.snapshots || []).map((snap: any) => (
                    <div
                      key={snap.id}
                      className="p-4 rounded-2xl bg-[#0D101C] border border-indigo-500/25 hover:border-indigo-500/50 transition-all flex flex-col justify-between shadow-lg"
                    >
                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-xs font-bold text-white truncate">{snap.label}</h4>
                          <span className={`text-[8px] font-mono font-bold px-1.5 py-0.5 rounded border uppercase shrink-0 ${
                            snap.trigger === 'pre_delete'
                              ? 'bg-rose-500/20 text-rose-300 border-rose-500/30'
                              : snap.trigger === 'restore'
                                ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                                : 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30'
                          }`}>
                            {snap.trigger || 'MANUAL'}
                          </span>
                        </div>

                        <div className="text-[10px] text-slate-400 font-mono mt-2 flex items-center gap-2">
                          <span className="text-indigo-300 font-bold">📦 {snap.productCount || snap.products?.length || 0} Products</span>
                          <span>•</span>
                          <span>{snap.timestamp ? new Date(snap.timestamp).toLocaleString() : 'N/A'}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-indigo-500/20">
                        <button
                          type="button"
                          onClick={() => {
                            if (confirm(`Rollback entire catalog to "${snap.label}" (${snap.productCount || snap.products?.length || 0} products)? A pre-rollback snapshot of current catalog will be created automatically.`)) {
                              handleRestorePortalSnapshot(snap.id);
                            }
                          }}
                          className="px-3 py-1.5 rounded-xl bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-200 border border-indigo-500/40 text-xs font-mono font-bold transition flex items-center gap-1 cursor-pointer active:scale-95"
                          title="Rollback catalog to this version"
                        >
                          <RotateCcw size={12} />
                          <span>Rollback Catalog</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeletePortalSnapshot(snap.id)}
                          className="p-1.5 rounded-xl text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition cursor-pointer"
                          title="Delete snapshot"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : catalogStudioTab === 'bookings' ? (
          /* ═══════════════════════════════════════════════════════════════ */
          /* VIEW 9B-1: DEDICATED VEHICLE & CAB BOOKINGS STUDIO              */
          /* ═══════════════════════════════════════════════════════════════ */
          <div className="flex-1 flex flex-col overflow-hidden bg-[#070E0B]">
            {/* Bookings KPI Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 px-6 py-4 border-b border-teal-500/20 bg-[#0B1712]/90 shrink-0">
              <div className="p-3 rounded-2xl bg-[#092019] border border-teal-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Total Bookings</span>
                  <span className="text-xl font-black text-white font-mono mt-0.5 block">{bookingsList.length}</span>
                </div>
                <Car size={18} className="text-teal-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#092019] border border-sky-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Confirmed</span>
                  <span className="text-xl font-black text-sky-300 font-mono mt-0.5 block">
                    {bookingsList.filter((b) => (orderEdits[b.id]?.status || b.status) === 'confirmed').length}
                  </span>
                </div>
                <CheckCircle2 size={18} className="text-sky-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#092019] border border-amber-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Active / On Rent</span>
                  <span className="text-xl font-black text-amber-300 font-mono mt-0.5 block">
                    {bookingsList.filter((b) => ['processing', 'dispatched', 'out_for_delivery'].includes(orderEdits[b.id]?.status || b.status)).length}
                  </span>
                </div>
                <Clock size={18} className="text-amber-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#092019] border border-emerald-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Completed / Returned</span>
                  <span className="text-xl font-black text-emerald-300 font-mono mt-0.5 block">
                    {bookingsList.filter((b) => (orderEdits[b.id]?.status || b.status) === 'delivered').length}
                  </span>
                </div>
                <CheckCheck size={18} className="text-emerald-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#092019] border border-rose-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Cancelled</span>
                  <span className="text-xl font-black text-rose-300 font-mono mt-0.5 block">
                    {bookingsList.filter((b) => (orderEdits[b.id]?.status || b.status) === 'cancelled').length}
                  </span>
                </div>
                <XCircle size={18} className="text-rose-400" />
              </div>
            </div>

            {/* Filter & Search Bar for Bookings */}
            <div className="px-6 py-3 border-b border-teal-500/20 bg-[#0B1712]/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-1.5 overflow-x-auto max-w-full pb-1 sm:pb-0">
                {[
                  { id: 'all', label: 'All Bookings' },
                  { id: 'confirmed', label: 'Confirmed' },
                  { id: 'active', label: 'Active Rental / Trip' },
                  { id: 'delivered', label: 'Completed / Returned' },
                  { id: 'cancelled', label: 'Cancelled' },
                ].map((st) => (
                  <button
                    key={st.id}
                    type="button"
                    onClick={() => setBookingsFilterStatus(st.id)}
                    className={`px-3 py-1 rounded-xl text-[11px] font-mono transition cursor-pointer shrink-0 ${
                      bookingsFilterStatus === st.id
                        ? 'bg-teal-500 text-slate-950 font-bold shadow-sm shadow-teal-500/30'
                        : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                    }`}
                  >
                    {st.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-72">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={bookingsSearchQuery}
                    onChange={(e) => setBookingsSearchQuery(e.target.value)}
                    placeholder="Search vehicle, booking#, customer, branch..."
                    className="w-full pl-9 pr-3 py-1.5 bg-[#08120E] border border-teal-500/30 focus:border-teal-400 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none font-mono"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => fetchTenantOrders()}
                  className="p-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 transition cursor-pointer shrink-0"
                  title="Refresh Bookings"
                >
                  <RefreshCw size={14} className={isLoadingOrders ? 'animate-spin text-teal-400' : ''} />
                </button>
              </div>
            </div>

            {/* Bookings List Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-thin scrollbar-thumb-teal-950">
              {isLoadingOrders && bookingsList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400 space-y-3">
                  <Loader2 size={32} className="animate-spin text-teal-400" />
                  <span className="text-xs font-mono">Loading Vehicle & Cab Bookings...</span>
                </div>
              ) : filteredBookings.length === 0 ? (
                <div className="text-center py-20 border border-dashed border-teal-500/20 rounded-3xl bg-[#0B1712]/40 max-w-2xl mx-auto p-8 space-y-3">
                  <Car size={48} className="mx-auto text-teal-500/40" />
                  <h3 className="text-base font-bold text-white">No Vehicle / Service Bookings Yet</h3>
                  <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
                    When customers book vehicle rentals, cab rides, or appointment services via WhatsApp, Live SIP Calls, or Web Avatar Chat, Luna AI autonomously confirms their schedule, calculates rates, and records their reservation here.
                  </p>
                  <div className="pt-2">
                    <span className="text-[10px] font-mono text-teal-400 bg-teal-500/10 px-3 py-1 rounded-lg border border-teal-500/30">
                      ⚡ Autonomous Fleet & Booking Engine Ready & Listening
                    </span>
                  </div>
                </div>
              ) : (
                filteredBookings.map((order: any) => {
                  const edit = orderEdits[order.id] || {};
                  const currentStatus = edit.status || order.status;
                  const orderItems = Array.isArray(order.items) ? order.items : [];

                  const statusBadgeColors: Record<string, string> = {
                    confirmed: 'bg-sky-500/15 border-sky-500/30 text-sky-300',
                    processing: 'bg-amber-500/15 border-amber-500/30 text-amber-300',
                    dispatched: 'bg-teal-500/15 border-teal-500/30 text-teal-300',
                    out_for_delivery: 'bg-teal-500/15 border-teal-500/30 text-teal-300',
                    delivered: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300',
                    cancelled: 'bg-rose-500/15 border-rose-500/30 text-rose-300',
                  };

                  const channelIcon = order.channel === 'sip_call' ? <PhoneCall size={12} className="text-purple-400" /> : order.channel === 'web_avatar' ? <Bot size={12} className="text-cyan-400" /> : <Smartphone size={12} className="text-emerald-400" />;

                  return (
                    <div
                      key={order.id}
                      className="p-5 rounded-2xl bg-[#0E2018]/90 border border-teal-500/30 shadow-xl space-y-4 hover:border-teal-400/60 transition"
                    >
                      {/* Booking Header */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 pb-3 border-b border-teal-500/20">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-sm font-black text-teal-300 font-mono tracking-wider flex items-center gap-1.5">
                            <Car size={16} className="text-teal-400" />
                            {order.orderNumber}
                          </span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(order.orderNumber, 'Booking Number')}
                            className="p-1 rounded bg-slate-900/60 hover:bg-slate-800 text-slate-400 hover:text-white transition cursor-pointer"
                            title="Copy Booking ID"
                          >
                            <Copy size={11} />
                          </button>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-black border uppercase bg-teal-500/20 border-teal-500/40 text-teal-300 flex items-center gap-1 shadow-sm shadow-teal-500/10">
                            🚗 CAB / VEHICLE BOOKING
                          </span>

                          {/* Quick Status Dropdown */}
                          <div className="relative inline-block">
                            <select
                              value={currentStatus}
                              onChange={(e) => handleUpdateOrderTracking(order.id, { status: e.target.value })}
                              className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold border uppercase cursor-pointer focus:outline-none ${statusBadgeColors[currentStatus] || 'bg-slate-800 text-slate-300'}`}
                            >
                              <option value="confirmed" className="bg-[#0B1712] text-sky-300">CONFIRMED</option>
                              <option value="processing" className="bg-[#0B1712] text-amber-300">ACTIVE RENTAL</option>
                              <option value="delivered" className="bg-[#0B1712] text-emerald-300">COMPLETED / RETURNED</option>
                              <option value="cancelled" className="bg-[#0B1712] text-rose-300">CANCELLED</option>
                            </select>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 text-[11px] font-mono text-slate-400">
                          <span className="flex items-center gap-1">
                            {channelIcon}
                            <span>{order.channel?.toUpperCase() || 'WHATSAPP'}</span>
                          </span>
                          <span>•</span>
                          <span>{new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>

                      {/* Booking Body Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                        {/* Reserved Vehicle & Rental Schedule (7 cols) */}
                        <div className="md:col-span-7 p-3.5 rounded-xl bg-[#08120E] border border-teal-500/20 space-y-2.5">
                          <span className="text-[10px] uppercase font-mono font-bold text-teal-400 block tracking-wider flex items-center gap-1.5">
                            <Car size={12} />
                            <span>Reserved Vehicle & Schedule</span>
                          </span>

                          {/* Vehicle items */}
                          <div className="space-y-1.5">
                            {orderItems.map((it: any, idx: number) => (
                              <div key={idx} className="p-2.5 rounded-xl bg-[#0B1712] border border-slate-800 flex items-center justify-between">
                                <div>
                                  <div className="text-xs font-bold text-white flex items-center gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                                    <span>{it.product_name}</span>
                                  </div>
                                  {it.options && (
                                    <div className="text-[11px] text-teal-300/80 font-mono mt-0.5">
                                      {it.options}
                                    </div>
                                  )}
                                </div>
                                <span className="text-xs font-mono font-bold text-emerald-400">
                                  {order.currency || 'LKR'} {Number(it.subtotal || it.unit_price).toLocaleString()}
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Highlight Schedule Pill Box */}
                          <div className="p-3 rounded-xl bg-[#050D0A] border border-teal-500/30 space-y-2">
                            {order.metadata?.bookingDates && (
                              <div className="text-xs text-white font-mono font-bold flex items-center gap-2">
                                <span className="px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 text-[10px] uppercase">🗓️ Dates:</span>
                                <span className="text-teal-200">{order.metadata.bookingDates}</span>
                              </div>
                            )}

                            <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono">
                              {order.metadata?.rentalDuration && (
                                <span className="px-2.5 py-1 rounded-lg bg-slate-900 border border-slate-700 text-slate-300 flex items-center gap-1">
                                  <span>⏱️ Duration:</span>
                                  <strong className="text-white">{order.metadata.rentalDuration}</strong>
                                </span>
                              )}
                              {order.metadata?.packageType && (
                                <span className="px-2.5 py-1 rounded-lg bg-indigo-950/50 border border-indigo-500/30 text-indigo-300 flex items-center gap-1">
                                  <span>📦 Package:</span>
                                  <strong className="text-white">{order.metadata.packageType}</strong>
                                </span>
                              )}
                              {(order.metadata?.pickupLocation || order.deliveryAddress) && (
                                <span className="px-2.5 py-1 rounded-lg bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 flex items-center gap-1">
                                  <MapPin size={11} className="text-rose-400" />
                                  <span>Pickup:</span>
                                  <strong className="text-white">{order.metadata?.pickupLocation || order.deliveryAddress}</strong>
                                </span>
                              )}
                            </div>

                            {order.notes && (
                              <div className="text-[11px] text-amber-300/90 bg-amber-500/10 p-2 rounded-lg border border-amber-500/20 leading-relaxed">
                                📝 <strong>Terms & Notes:</strong> {order.notes}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Customer & Settlement (5 cols) */}
                        <div className="md:col-span-5 p-3.5 rounded-xl bg-[#08120E] border border-teal-500/20 flex flex-col justify-between space-y-3">
                          <div className="space-y-2">
                            <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                              Customer & Settlement
                            </span>
                            <div className="text-sm font-bold text-white">{order.customerName}</div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-teal-400">+{order.customerPhone}</span>
                              <a
                                href={`https://wa.me/${order.customerPhone}`}
                                target="_blank"
                                rel="noreferrer"
                                className="px-2.5 py-1 rounded-lg bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-[11px] font-mono flex items-center gap-1.5 border border-emerald-500/30"
                              >
                                <Smartphone size={12} />
                                <span>WhatsApp Direct Chat</span>
                              </a>
                            </div>

                            <div className="pt-2 border-t border-slate-800/80">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-400 font-mono">Estimated Total:</span>
                                <span className="text-base font-black font-mono text-emerald-400">
                                  {order.currency || 'LKR'} {Number(order.totalAmount).toLocaleString()}
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-[11px] font-mono text-slate-400 pt-1">
                                <span>Payment: <strong className="text-white uppercase">{order.paymentMethod?.replace(/_/g, ' ')}</strong></span>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${order.paymentStatus === 'paid' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                                  {order.paymentStatus?.toUpperCase() || 'PENDING'}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="pt-2 border-t border-slate-800/80 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleSendCustomerTrackingWhatsApp(order.id)}
                              disabled={isNotifyingCustomer === order.id}
                              className="w-full py-1.5 px-3 rounded-xl bg-teal-500/10 hover:bg-teal-500/20 text-teal-300 hover:text-white border border-teal-500/30 text-xs font-mono font-bold transition flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                            >
                              {isNotifyingCustomer === order.id ? (
                                <Loader2 size={13} className="animate-spin text-teal-400" />
                              ) : (
                                <Send size={13} className="text-teal-400" />
                              )}
                              <span>Send WhatsApp Confirmation</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ) : catalogStudioTab === 'order_templates' ? (
          /* ═══════════════════════════════════════════════════════════════ */
          /* VIEW 9B-3: ORDER INTAKE FORM & COURIER FORWARDING STUDIO        */
          /* ═══════════════════════════════════════════════════════════════ */
          <div className="flex-1 flex flex-col overflow-hidden bg-[#070E0B]">
            {/* Top KPI & Action Bar */}
            <div className="px-6 py-4 border-b border-amber-500/20 bg-[#0B1712]/90 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0">
              <div>
                <h3 className="text-sm font-black text-white flex items-center gap-2">
                  <FileText size={16} className="text-amber-400" />
                  <span>Order Intake Form & 1-Tap Courier Forwarding Studio</span>
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Customize the exact details Luna collects from customers & format owner alert messages for instant forwarding to couriers.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg border border-emerald-500/20 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
                  <span>Smart WhatsApp Fallback Active</span>
                </span>
              </div>
            </div>

            {/* Sub-Tabs: Intake Fields, Courier Template, Customer Receipt, Live Simulation */}
            <div className="px-6 pt-3 pb-2 border-b border-white/5 bg-[#08120E] flex items-center gap-2 shrink-0 overflow-x-auto">
              <button
                type="button"
                onClick={() => setPortalOrderTemplateSubTab('fields')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition cursor-pointer ${
                  portalOrderTemplateSubTab === 'fields'
                    ? 'bg-amber-500 text-slate-950 font-black shadow-md shadow-amber-500/20'
                    : 'bg-[#0E2018] text-slate-300 hover:text-white border border-amber-500/20'
                }`}
              >
                <ClipboardCheck size={13} />
                <span>1. Intake Form Fields</span>
              </button>

              <button
                type="button"
                onClick={() => setPortalOrderTemplateSubTab('owner_alert')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition cursor-pointer ${
                  portalOrderTemplateSubTab === 'owner_alert'
                    ? 'bg-amber-500 text-slate-950 font-black shadow-md shadow-amber-500/20'
                    : 'bg-[#0E2018] text-slate-300 hover:text-white border border-amber-500/20'
                }`}
              >
                <Bell size={13} />
                <span>2. Owner Order Alert Template</span>
              </button>

              <button
                type="button"
                onClick={() => setPortalOrderTemplateSubTab('courier')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition cursor-pointer ${
                  portalOrderTemplateSubTab === 'courier'
                    ? 'bg-amber-500 text-slate-950 font-black shadow-md shadow-amber-500/20'
                    : 'bg-[#0E2018] text-slate-300 hover:text-white border border-amber-500/20'
                }`}
              >
                <Truck size={13} />
                <span>3. Courier Forwarding Template</span>
              </button>

              <button
                type="button"
                onClick={() => setPortalOrderTemplateSubTab('customer')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition cursor-pointer ${
                  portalOrderTemplateSubTab === 'customer'
                    ? 'bg-amber-500 text-slate-950 font-black shadow-md shadow-amber-500/20'
                    : 'bg-[#0E2018] text-slate-300 hover:text-white border border-amber-500/20'
                }`}
              >
                <Smartphone size={13} />
                <span>4. Customer Receipt Template</span>
              </button>

              <button
                type="button"
                onClick={() => setPortalOrderTemplateSubTab('preview')}
                className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-1.5 transition cursor-pointer ${
                  portalOrderTemplateSubTab === 'preview'
                    ? 'bg-amber-500 text-slate-950 font-black shadow-md shadow-amber-500/20'
                    : 'bg-[#0E2018] text-slate-300 hover:text-white border border-amber-500/20'
                }`}
              >
                <Eye size={13} />
                <span>5. Live WhatsApp Simulation</span>
              </button>
            </div>

            {/* Sub-Tab Body */}
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-amber-950">
              {/* SUB-TAB 1: Intake Form Fields */}
              {portalOrderTemplateSubTab === 'fields' && (
                <div className="max-w-4xl space-y-6 animate-fade-in">
                  <div className="p-4 rounded-2xl bg-[#0E2018] border border-amber-500/20 text-xs text-slate-300 leading-relaxed">
                    <strong className="text-amber-300">Luna Autonomous Order Collection Protocol:</strong> When a customer expresses purchase intent, Luna gathers the fields selected below before executing the official order confirmation.
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                    {/* Full Name */}
                    <div className="p-4 rounded-2xl bg-[#0E2018] border border-slate-800 flex items-center justify-between">
                      <div>
                        <div className="font-bold text-white text-sm">1. Customer Full Name</div>
                        <div className="text-xs text-slate-400 mt-0.5">Primary recipient name for courier parcel</div>
                      </div>
                      <span className="text-[10px] px-2 py-0.5 rounded-lg bg-emerald-950 text-emerald-300 border border-emerald-500/30 font-mono">
                        Mandatory
                      </span>
                    </div>

                    {/* Delivery Address */}
                    <div className="p-4 rounded-2xl bg-[#0E2018] border border-slate-800 flex items-center justify-between">
                      <div>
                        <div className="font-bold text-white text-sm">2. Delivery Street Address</div>
                        <div className="text-xs text-slate-400 mt-0.5">Doorstep street address / house number</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={currentOrderTemplateConfig.fields?.deliveryAddress !== false}
                        onChange={(e) => {
                          const updated = {
                            ...currentOrderTemplateConfig,
                            fields: { ...currentOrderTemplateConfig.fields, deliveryAddress: e.target.checked }
                          };
                          handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
                        }}
                        className="rounded accent-amber-500 cursor-pointer w-5 h-5"
                      />
                    </div>

                    {/* City / Nearest Town */}
                    <div className="p-4 rounded-2xl bg-[#0E2018] border border-slate-800 flex items-center justify-between">
                      <div>
                        <div className="font-bold text-white text-sm">3. City / Nearest Town</div>
                        <div className="text-xs text-slate-400 mt-0.5">Essential for courier delivery routing</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={currentOrderTemplateConfig.fields?.city !== false}
                        onChange={(e) => {
                          const updated = {
                            ...currentOrderTemplateConfig,
                            fields: { ...currentOrderTemplateConfig.fields, city: e.target.checked }
                          };
                          handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
                        }}
                        className="rounded accent-amber-500 cursor-pointer w-5 h-5"
                      />
                    </div>

                    {/* Primary Phone */}
                    <div className="p-4 rounded-2xl bg-[#0E2018] border border-slate-800 flex items-center justify-between">
                      <div>
                        <div className="font-bold text-white text-sm">4. Primary Contact Phone</div>
                        <div className="text-xs text-slate-400 mt-0.5">Main customer phone number</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={currentOrderTemplateConfig.fields?.primaryPhone !== false}
                        onChange={(e) => {
                          const updated = {
                            ...currentOrderTemplateConfig,
                            fields: { ...currentOrderTemplateConfig.fields, primaryPhone: e.target.checked }
                          };
                          handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
                        }}
                        className="rounded accent-amber-500 cursor-pointer w-5 h-5"
                      />
                    </div>

                    {/* Secondary Phone (Courier Backup) */}
                    <div className="p-4 rounded-2xl bg-amber-950/25 border border-amber-500/40 flex items-center justify-between shadow-lg">
                      <div>
                        <div className="font-bold text-amber-300 text-sm flex items-center gap-1.5">
                          <span>5. 2nd Phone Number (Courier Backup)</span>
                        </div>
                        <div className="text-xs text-slate-300 mt-0.5">
                          Delivery rider calls backup number if primary is unreachable
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={currentOrderTemplateConfig.fields?.secondaryPhone !== false}
                        onChange={(e) => {
                          const updated = {
                            ...currentOrderTemplateConfig,
                            fields: { ...currentOrderTemplateConfig.fields, secondaryPhone: e.target.checked }
                          };
                          handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
                        }}
                        className="rounded accent-amber-500 cursor-pointer w-5 h-5"
                      />
                    </div>

                    {/* Working WhatsApp Number (Smart Fallback) */}
                    <div className="p-4 rounded-2xl bg-emerald-950/25 border border-emerald-500/40 flex items-center justify-between shadow-lg">
                      <div>
                        <div className="font-bold text-emerald-300 text-sm flex items-center gap-1.5">
                          <span>6. Working WhatsApp Number</span>
                        </div>
                        <div className="text-xs text-emerald-300/80 mt-0.5">
                          ⚡ Smart Fallback: Grabs current chatting WhatsApp if user does not provide another
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={currentOrderTemplateConfig.fields?.whatsappNumber !== false}
                        onChange={(e) => {
                          const updated = {
                            ...currentOrderTemplateConfig,
                            fields: { ...currentOrderTemplateConfig.fields, whatsappNumber: e.target.checked }
                          };
                          handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
                        }}
                        className="rounded accent-emerald-500 cursor-pointer w-5 h-5"
                      />
                    </div>

                    {/* Landmark / Notes */}
                    <div className="p-4 rounded-2xl bg-[#0E2018] border border-slate-800 flex items-center justify-between">
                      <div>
                        <div className="font-bold text-white text-sm">7. Landmark / Nearest Place</div>
                        <div className="text-xs text-slate-400 mt-0.5">e.g. Opposite temple, near clock tower</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={currentOrderTemplateConfig.fields?.landmark === true}
                        onChange={(e) => {
                          const updated = {
                            ...currentOrderTemplateConfig,
                            fields: { ...currentOrderTemplateConfig.fields, landmark: e.target.checked }
                          };
                          handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
                        }}
                        className="rounded accent-amber-500 cursor-pointer w-5 h-5"
                      />
                    </div>

                    {/* Delivery Notes */}
                    <div className="p-4 rounded-2xl bg-[#0E2018] border border-slate-800 flex items-center justify-between">
                      <div>
                        <div className="font-bold text-white text-sm">8. Special Delivery Instructions</div>
                        <div className="text-xs text-slate-400 mt-0.5">Customer time preferences or requests</div>
                      </div>
                      <input
                        type="checkbox"
                        checked={currentOrderTemplateConfig.fields?.notes !== false}
                        onChange={(e) => {
                          const updated = {
                            ...currentOrderTemplateConfig,
                            fields: { ...currentOrderTemplateConfig.fields, notes: e.target.checked }
                          };
                          handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
                        }}
                        className="rounded accent-amber-500 cursor-pointer w-5 h-5"
                      />
                    </div>
                  </div>

                  {/* Custom Fields Manager */}
                  <div className="p-5 rounded-2xl bg-[#0E2018] border border-slate-800 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-bold text-white">Custom Business Fields</h4>
                        <p className="text-xs text-slate-400">Add bespoke fields (e.g. NIC Number, Size, Color preference) that Luna will gather.</p>
                      </div>
                      <span className="text-xs font-mono text-amber-400 font-bold">
                        {(currentOrderTemplateConfig.fields?.customFields || []).length} Custom Fields
                      </span>
                    </div>

                    {(currentOrderTemplateConfig.fields?.customFields || []).map((cf: any, idx: number) => (
                      <div key={idx} className="p-3 bg-[#08120E] rounded-xl border border-slate-700 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-amber-400 font-bold">{cf.key}</span>
                          <span className="text-white font-bold">— {cf.label}</span>
                          {cf.required && (
                            <span className="text-[9px] bg-rose-950 text-rose-300 px-2 py-0.5 rounded border border-rose-500/30">
                              Required
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const nextFields = (currentOrderTemplateConfig.fields?.customFields || []).filter((_: any, i: number) => i !== idx);
                            const updated = {
                              ...currentOrderTemplateConfig,
                              fields: { ...currentOrderTemplateConfig.fields, customFields: nextFields }
                            };
                            handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
                          }}
                          className="text-slate-400 hover:text-rose-400 p-1 transition cursor-pointer"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}

                    <div className="flex flex-wrap items-center gap-2.5 pt-2">
                      <input
                        type="text"
                        placeholder="Field key (e.g. nic_number)"
                        value={portalNewCustomFieldKey}
                        onChange={(e) => setPortalNewCustomFieldKey(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                        className="flex-1 min-w-[150px] bg-[#08120E] border border-slate-700 focus:border-amber-400 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 font-mono focus:outline-none"
                      />
                      <input
                        type="text"
                        placeholder="Display label (e.g. NIC Number)"
                        value={portalNewCustomFieldLabel}
                        onChange={(e) => setPortalNewCustomFieldLabel(e.target.value)}
                        className="flex-1 min-w-[180px] bg-[#08120E] border border-slate-700 focus:border-amber-400 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none"
                      />
                      <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer bg-[#08120E] px-3 py-2 rounded-xl border border-slate-700">
                        <input
                          type="checkbox"
                          checked={portalNewCustomFieldRequired}
                          onChange={(e) => setPortalNewCustomFieldRequired(e.target.checked)}
                          className="rounded accent-amber-500 w-4 h-4"
                        />
                        <span>Required</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          if (!portalNewCustomFieldKey.trim() || !portalNewCustomFieldLabel.trim()) return;
                          const nextFields = [
                            ...(currentOrderTemplateConfig.fields?.customFields || []),
                            { key: portalNewCustomFieldKey.trim(), label: portalNewCustomFieldLabel.trim(), required: portalNewCustomFieldRequired }
                          ];
                          const updated = {
                            ...currentOrderTemplateConfig,
                            fields: { ...currentOrderTemplateConfig.fields, customFields: nextFields }
                          };
                          handleSaveCatalogProducts(undefined, undefined, undefined, undefined, undefined, undefined, updated);
                          setPortalNewCustomFieldKey('');
                          setPortalNewCustomFieldLabel('');
                          setPortalNewCustomFieldRequired(false);
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold rounded-xl text-xs flex items-center gap-1.5 transition cursor-pointer shadow-md shadow-amber-500/20"
                      >
                        <Plus size={13} /> Add Custom Field
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* SUB-TAB 2: Owner Order Alert Template */}
              {portalOrderTemplateSubTab === 'owner_alert' && (
                <div className="max-w-4xl space-y-4 animate-fade-in">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 rounded-2xl bg-[#0E2018] border border-amber-500/20 text-xs">
                    <p className="text-slate-300 leading-relaxed">
                      <strong className="text-amber-300">Owner Order Alert Format:</strong> This is the initial comprehensive confirmation sent to the owner's WhatsApp containing the full order ID, ordered items breakdown, customer address, phone numbers, and active chatting WhatsApp account.
                    </p>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setOwnerAlertTemplateDraft('');
                          ownerAlertDraftDirtyRef.current = true;
                        }}
                        className="px-3 py-1.5 rounded-xl bg-rose-950/40 hover:bg-rose-900/60 border border-rose-500/30 text-rose-300 text-xs font-mono flex items-center gap-1.5 transition cursor-pointer"
                        title="Erase all text so you can create a completely new template"
                      >
                        <Trash2 size={12} /> Clear / Empty Form
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setOwnerAlertTemplateDraft(DEFAULT_PORTAL_OWNER_CONFIRM_TEMPLATE);
                          ownerAlertDraftDirtyRef.current = true;
                        }}
                        className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-mono flex items-center gap-1.5 transition cursor-pointer"
                      >
                        <RotateCcw size={12} /> Reset to Default
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-mono font-bold text-slate-400 uppercase">
                        Click to Insert Variable Tag:
                      </span>
                      <div className="flex items-center gap-2">
                        {templateSaveFeedback && (
                          <span className="text-xs font-bold text-emerald-400 animate-pulse">
                            {templateSaveFeedback}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleSaveOwnerAlertTemplate(ownerAlertTemplateDraft)}
                          disabled={isSavingTemplate}
                          className="px-3.5 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs flex items-center gap-1.5 transition cursor-pointer shadow-md shadow-amber-500/20"
                        >
                          <Save size={12} /> {isSavingTemplate ? 'Saving...' : 'Save Template'}
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {[
                        'orderNumber', 'storeName', 'orderType', 'customerName', 'deliveryAddress', 'city',
                        'customerPhone', 'secondaryPhone', 'whatsappNumber', 'itemsList',
                        'totalAmount', 'currency', 'paymentMethod', 'notes', 'orderTime'
                      ].map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => handleInsertOwnerAlertTag(v)}
                          className="px-2 py-1 bg-[#08120E] hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-lg text-xs font-mono transition cursor-pointer shadow-sm"
                        >
                          {`{{${v}}}`}
                        </button>
                      ))}
                    </div>
                  </div>

                  <textarea
                    ref={ownerAlertTextareaRef}
                    rows={12}
                    value={ownerAlertTemplateDraft}
                    placeholder="Type your owner alert template here or click Clear to start from blank..."
                    onChange={(e) => {
                      const next = e.target.value;
                      ownerAlertDraftDirtyRef.current = true;
                      isTypingTemplateRef.current = true;
                      setOwnerAlertTemplateDraft(next);
                      ownerAlertCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? next.length,
                        end: e.currentTarget.selectionEnd ?? next.length,
                      };
                    }}
                    onSelect={(e) => {
                      ownerAlertCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? ownerAlertTemplateDraft.length,
                        end: e.currentTarget.selectionEnd ?? ownerAlertTemplateDraft.length,
                      };
                    }}
                    onKeyUp={(e) => {
                      ownerAlertCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? ownerAlertTemplateDraft.length,
                        end: e.currentTarget.selectionEnd ?? ownerAlertTemplateDraft.length,
                      };
                    }}
                    onClick={(e) => {
                      ownerAlertCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? ownerAlertTemplateDraft.length,
                        end: e.currentTarget.selectionEnd ?? ownerAlertTemplateDraft.length,
                      };
                    }}
                    onBlur={() => {
                      isTypingTemplateRef.current = false;
                    }}
                    className="w-full bg-[#08120E] border border-slate-700 focus:border-amber-400 rounded-2xl p-4 font-mono text-xs text-white leading-relaxed focus:outline-none resize-y shadow-inner"
                  />
                </div>
              )}

              {/* SUB-TAB 3: Courier Forwarding Template */}
              {portalOrderTemplateSubTab === 'courier' && (
                <div className="max-w-4xl space-y-4 animate-fade-in">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 rounded-2xl bg-[#0E2018] border border-amber-500/20 text-xs">
                    <p className="text-slate-300 leading-relaxed">
                      <strong className="text-amber-300">1-Tap Courier Forwarding Format:</strong> This message is sent to your registered WhatsApp as soon as an order is placed. Tap <strong>Forward</strong> on WhatsApp and select Prompt, Koombiyo, Fardar, Domex, or your courier rider!
                    </p>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setCourierTemplateDraft('');
                          courierDraftDirtyRef.current = true;
                        }}
                        className="px-3 py-1.5 rounded-xl bg-rose-950/40 hover:bg-rose-900/60 border border-rose-500/30 text-rose-300 text-xs font-mono flex items-center gap-1.5 transition cursor-pointer"
                        title="Erase all text so you can create a completely new template"
                      >
                        <Trash2 size={12} /> Clear / Empty Form
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setCourierTemplateDraft(DEFAULT_PORTAL_COURIER_TEMPLATE);
                          courierDraftDirtyRef.current = true;
                        }}
                        className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-mono flex items-center gap-1.5 transition cursor-pointer"
                      >
                        <RotateCcw size={12} /> Reset to Default
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-mono font-bold text-slate-400 uppercase">
                        Click to Insert Variable Tag:
                      </span>
                      <div className="flex items-center gap-2">
                        {templateSaveFeedback && (
                          <span className="text-xs font-bold text-emerald-400 animate-pulse">
                            {templateSaveFeedback}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleSaveCourierTemplate(courierTemplateDraft)}
                          disabled={isSavingTemplate}
                          className="px-3.5 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs flex items-center gap-1.5 transition cursor-pointer shadow-md shadow-amber-500/20"
                        >
                          <Save size={12} /> {isSavingTemplate ? 'Saving...' : 'Save Template'}
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {[
                        'orderNumber', 'storeName', 'customerName', 'deliveryAddress', 'city',
                        'customerPhone', 'secondaryPhone', 'whatsappNumber', 'itemsList',
                        'totalAmount', 'currency', 'paymentMethod', 'notes', 'orderTime'
                      ].map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => handleInsertCourierTag(v)}
                          className="px-2 py-1 bg-[#08120E] hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-lg text-xs font-mono transition cursor-pointer shadow-sm"
                        >
                          {`{{${v}}}`}
                        </button>
                      ))}
                    </div>
                  </div>

                  <textarea
                    ref={courierTextareaRef}
                    rows={14}
                    value={courierTemplateDraft}
                    placeholder="Write your custom courier forwarding template here or click variable tags above (or clear all to start fresh)..."
                    onChange={(e) => {
                      const next = e.target.value;
                      courierCursorRef.current = {
                        start: e.target.selectionStart ?? next.length,
                        end: e.target.selectionEnd ?? next.length,
                      };
                      setCourierTemplateDraft(next);
                      courierDraftDirtyRef.current = true;
                    }}
                    onFocus={(e) => {
                      isTypingTemplateRef.current = true;
                      courierCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? courierTemplateDraft.length,
                        end: e.currentTarget.selectionEnd ?? courierTemplateDraft.length,
                      };
                    }}
                    onSelect={(e) => {
                      courierCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? courierTemplateDraft.length,
                        end: e.currentTarget.selectionEnd ?? courierTemplateDraft.length,
                      };
                    }}
                    onKeyUp={(e) => {
                      courierCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? courierTemplateDraft.length,
                        end: e.currentTarget.selectionEnd ?? courierTemplateDraft.length,
                      };
                    }}
                    onClick={(e) => {
                      courierCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? courierTemplateDraft.length,
                        end: e.currentTarget.selectionEnd ?? courierTemplateDraft.length,
                      };
                    }}
                    onBlur={() => {
                      isTypingTemplateRef.current = false;
                    }}
                    className="w-full bg-[#08120E] border border-slate-700 focus:border-amber-400 rounded-2xl p-4 font-mono text-xs text-white leading-relaxed focus:outline-none resize-y shadow-inner"
                  />
                </div>
              )}

              {/* SUB-TAB 3: Customer Receipt Template */}
              {portalOrderTemplateSubTab === 'customer' && (
                <div className="max-w-4xl space-y-4 animate-fade-in">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 rounded-2xl bg-[#0E2018] border border-teal-500/20 text-xs">
                    <p className="text-slate-300 leading-relaxed">
                      <strong className="text-teal-300">Customer Confirmation Receipt:</strong> This confirmation message is delivered to the customer on WhatsApp once Luna successfully captures their order.
                    </p>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => {
                          setCustomerTemplateDraft('');
                          customerDraftDirtyRef.current = true;
                        }}
                        className="px-3 py-1.5 rounded-xl bg-rose-950/40 hover:bg-rose-900/60 border border-rose-500/30 text-rose-300 text-xs font-mono flex items-center gap-1.5 transition cursor-pointer"
                        title="Erase all text so you can create a completely new customer receipt"
                      >
                        <Trash2 size={12} /> Clear / Empty Form
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setCustomerTemplateDraft(DEFAULT_PORTAL_CUSTOMER_RECEIPT_TEMPLATE);
                          customerDraftDirtyRef.current = true;
                        }}
                        className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-mono flex items-center gap-1.5 transition cursor-pointer"
                      >
                        <RotateCcw size={12} /> Reset to Default
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[11px] font-mono font-bold text-slate-400 uppercase">
                        Click to Insert Variable Tag:
                      </span>
                      <div className="flex items-center gap-2">
                        {templateSaveFeedback && (
                          <span className="text-xs font-bold text-emerald-400 animate-pulse">
                            {templateSaveFeedback}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleSaveCustomerTemplate(customerTemplateDraft)}
                          disabled={isSavingTemplate}
                          className="px-3.5 py-1.5 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-black text-xs flex items-center gap-1.5 transition cursor-pointer shadow-md shadow-teal-500/20"
                        >
                          <Save size={12} /> {isSavingTemplate ? 'Saving...' : 'Save Template'}
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {[
                        'orderNumber', 'storeName', 'customerName', 'deliveryAddress', 'city',
                        'customerPhone', 'secondaryPhone', 'whatsappNumber', 'itemsList',
                        'totalAmount', 'currency', 'paymentMethod', 'notes', 'orderTime'
                      ].map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => handleInsertCustomerTag(v)}
                          className="px-2 py-1 bg-[#08120E] hover:bg-teal-500/20 text-teal-300 border border-teal-500/30 rounded-lg text-xs font-mono transition cursor-pointer shadow-sm"
                        >
                          {`{{${v}}}`}
                        </button>
                      ))}
                    </div>
                  </div>

                  <textarea
                    ref={customerTextareaRef}
                    rows={14}
                    value={customerTemplateDraft}
                    placeholder="Write your custom customer confirmation receipt template here or click variable tags above (or clear all to start fresh)..."
                    onChange={(e) => {
                      const next = e.target.value;
                      customerCursorRef.current = {
                        start: e.target.selectionStart ?? next.length,
                        end: e.target.selectionEnd ?? next.length,
                      };
                      setCustomerTemplateDraft(next);
                      customerDraftDirtyRef.current = true;
                    }}
                    onFocus={(e) => {
                      isTypingTemplateRef.current = true;
                      customerCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? customerTemplateDraft.length,
                        end: e.currentTarget.selectionEnd ?? customerTemplateDraft.length,
                      };
                    }}
                    onSelect={(e) => {
                      customerCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? customerTemplateDraft.length,
                        end: e.currentTarget.selectionEnd ?? customerTemplateDraft.length,
                      };
                    }}
                    onKeyUp={(e) => {
                      customerCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? customerTemplateDraft.length,
                        end: e.currentTarget.selectionEnd ?? customerTemplateDraft.length,
                      };
                    }}
                    onClick={(e) => {
                      customerCursorRef.current = {
                        start: e.currentTarget.selectionStart ?? customerTemplateDraft.length,
                        end: e.currentTarget.selectionEnd ?? customerTemplateDraft.length,
                      };
                    }}
                    onBlur={() => {
                      isTypingTemplateRef.current = false;
                    }}
                    className="w-full bg-[#08120E] border border-slate-700 focus:border-teal-400 rounded-2xl p-4 font-mono text-xs text-white leading-relaxed focus:outline-none resize-y shadow-inner"
                  />
                </div>
              )}

              {/* SUB-TAB 4: Live Simulation */}
              {portalOrderTemplateSubTab === 'preview' && (
                <div className="max-w-3xl space-y-4 animate-fade-in">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPortalPreviewMode('owner_alert')}
                        className={`px-3.5 py-1.5 rounded-xl text-xs font-bold cursor-pointer transition ${
                          portalPreviewMode === 'owner_alert' ? 'bg-amber-500 text-slate-950 font-black shadow-md shadow-amber-500/20' : 'bg-[#0E2018] text-slate-400 hover:text-white'
                        }`}
                      >
                        Owner Order Alert
                      </button>
                      <button
                        type="button"
                        onClick={() => setPortalPreviewMode('courier')}
                        className={`px-3.5 py-1.5 rounded-xl text-xs font-bold cursor-pointer transition ${
                          portalPreviewMode === 'courier' ? 'bg-amber-500 text-slate-950 font-black shadow-md shadow-amber-500/20' : 'bg-[#0E2018] text-slate-400 hover:text-white'
                        }`}
                      >
                        Courier Forwarding Message
                      </button>
                      <button
                        type="button"
                        onClick={() => setPortalPreviewMode('customer')}
                        className={`px-3.5 py-1.5 rounded-xl text-xs font-bold cursor-pointer transition ${
                          portalPreviewMode === 'customer' ? 'bg-teal-500 text-slate-950 font-black shadow-md shadow-teal-500/20' : 'bg-[#0E2018] text-slate-400 hover:text-white'
                        }`}
                      >
                        Customer Confirmation Receipt
                      </button>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        const rawOwnerAlert = ownerAlertTemplateDraft !== undefined ? ownerAlertTemplateDraft : (currentOrderTemplateConfig.ownerConfirmTemplate || DEFAULT_PORTAL_OWNER_CONFIRM_TEMPLATE);
                        const rawCourier = courierTemplateDraft !== undefined ? courierTemplateDraft : (currentOrderTemplateConfig.ownerCourierTemplate || DEFAULT_PORTAL_COURIER_TEMPLATE);
                        const rawCustomer = customerTemplateDraft !== undefined ? customerTemplateDraft : (currentOrderTemplateConfig.customerConfirmationTemplate || DEFAULT_PORTAL_CUSTOMER_RECEIPT_TEMPLATE);
                        const sampleText = portalPreviewMode === 'owner_alert'
                          ? rawOwnerAlert
                              .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                              .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                              .replace(/\{\{\s*orderType\s*\}\}/gi, 'Delivery Order')
                              .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                              .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                              .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                              .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                              .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                              .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                              .replace(/\{\{\s*itemsList\s*\}\}/gi, '  1. *Rose Bouquet* x 1 = LKR 4,500\n  2. *Chocolates Box* x 1 = LKR 2,200')
                              .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                              .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                              .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                              .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM. Landmark: Near Clock Tower.')
                              .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM')
                          : portalPreviewMode === 'courier'
                          ? rawCourier
                              .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                              .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                              .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                              .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                              .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                              .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                              .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                              .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                              .replace(/\{\{\s*itemsList\s*\}\}/gi, '  1. *Rose Bouquet* x 1 = LKR 4,500\n  2. *Chocolates Box* x 1 = LKR 2,200')
                              .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                              .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                              .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                              .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM. Landmark: Near Clock Tower.')
                              .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM')
                          : rawCustomer
                              .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                              .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                              .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                              .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                              .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                              .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                              .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                              .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                              .replace(/\{\{\s*itemsList\s*\}\}/gi, '1x Rose Bouquet (LKR 4,500), 1x Chocolates Box (LKR 2,200)')
                              .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                              .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                              .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                              .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM')
                              .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM');
                        copyToClipboard(sampleText, 'Sample Message');
                        setPortalCopiedPreview(true);
                        setTimeout(() => setPortalCopiedPreview(false), 2000);
                      }}
                      className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs flex items-center gap-1.5 cursor-pointer shadow-sm"
                    >
                      {portalCopiedPreview ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                      <span>{portalCopiedPreview ? 'Copied!' : 'Copy Sample Text'}</span>
                    </button>
                  </div>

                  {/* WhatsApp Simulation Bubble */}
                  <div className="p-5 rounded-3xl bg-[#0b141a] border border-[#1f2c34] text-slate-100 font-sans text-xs shadow-2xl whitespace-pre-wrap leading-relaxed">
                    {portalPreviewMode === 'owner_alert'
                      ? (ownerAlertTemplateDraft !== undefined ? ownerAlertTemplateDraft : (currentOrderTemplateConfig.ownerConfirmTemplate || DEFAULT_PORTAL_OWNER_CONFIRM_TEMPLATE))
                          .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                          .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                          .replace(/\{\{\s*orderType\s*\}\}/gi, 'Delivery Order')
                          .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                          .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                          .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                          .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                          .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                          .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                          .replace(/\{\{\s*itemsList\s*\}\}/gi, '  1. *Rose Bouquet* x 1 = LKR 4,500\n  2. *Chocolates Box* x 1 = LKR 2,200')
                          .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                          .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                          .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                          .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM. Landmark: Near Clock Tower.')
                          .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM')
                      : portalPreviewMode === 'courier'
                      ? (courierTemplateDraft !== undefined ? courierTemplateDraft : (currentOrderTemplateConfig.ownerCourierTemplate || DEFAULT_PORTAL_COURIER_TEMPLATE))
                          .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                          .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                          .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                          .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                          .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                          .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                          .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                          .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                          .replace(/\{\{\s*itemsList\s*\}\}/gi, '  1. *Rose Bouquet* x 1 = LKR 4,500\n  2. *Chocolates Box* x 1 = LKR 2,200')
                          .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                          .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                          .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                          .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM. Landmark: Near Clock Tower.')
                          .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM')
                      : (customerTemplateDraft !== undefined ? customerTemplateDraft : (currentOrderTemplateConfig.customerConfirmationTemplate || DEFAULT_PORTAL_CUSTOMER_RECEIPT_TEMPLATE))
                          .replace(/\{\{\s*orderNumber\s*\}\}/gi, 'ORD-849201')
                          .replace(/\{\{\s*storeName\s*\}\}/gi, 'Blossom Lifestyle Store')
                          .replace(/\{\{\s*customerName\s*\}\}/gi, 'Kamal Perera')
                          .replace(/\{\{\s*deliveryAddress\s*\}\}/gi, 'No. 45/A, Galle Road')
                          .replace(/\{\{\s*city\s*\}\}/gi, 'Maharagama')
                          .replace(/\{\{\s*customerPhone\s*\}\}/gi, '94771234567')
                          .replace(/\{\{\s*secondaryPhone\s*\}\}/gi, '+94719876543')
                          .replace(/\{\{\s*whatsappNumber\s*\}\}/gi, '+94771234567')
                          .replace(/\{\{\s*itemsList\s*\}\}/gi, '1x Rose Bouquet (LKR 4,500), 1x Chocolates Box (LKR 2,200)')
                          .replace(/\{\{\s*totalAmount\s*\}\}/gi, '6,700')
                          .replace(/\{\{\s*currency\s*\}\}/gi, 'LKR')
                          .replace(/\{\{\s*paymentMethod\s*\}\}/gi, 'CASH ON DELIVERY (COD)')
                          .replace(/\{\{\s*notes\s*\}\}/gi, 'Deliver after 4 PM')
                          .replace(/\{\{\s*orderTime\s*\}\}/gi, '9/22/2026, 3:30 PM')
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : catalogStudioTab === 'pre_filled_templates' ? (
          /* ═══════════════════════════════════════════════════════════════ */
          /* VIEW 9B-4: ZERO-TOKEN PRE-FILLED MESSAGES & GREETINGS STUDIO   */
          /* ═══════════════════════════════════════════════════════════════ */
          <div className="flex-1 flex flex-col overflow-hidden bg-[#070E0B]">
            {/* Top KPI & Action Bar */}
            <div className="px-6 py-4 border-b border-teal-500/20 bg-[#0B1712]/90 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0">
              <div>
                <h3 className="text-sm font-black text-white flex items-center gap-2">
                  <Zap size={16} className="text-teal-400" />
                  <span>Zero-Token Instant Quick-Replies & Greeting Gatekeeper Studio</span>
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Bypasses Gemini LLM on greetings (&quot;hi&quot;, &quot;hello&quot;) and common keyword inquiries to save 100% tokens. Luna smoothly takes over intelligent conversation from message #2 onwards.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-[#0F261C] border border-teal-500/30 cursor-pointer">
                  <span className="text-xs text-slate-200 font-bold">
                    {currentPreFilledEnabled ? '⚡ Gatekeeper Active' : '○ Bypassed'}
                  </span>
                  <input
                    type="checkbox"
                    checked={currentPreFilledEnabled}
                    onChange={(e) => {
                      const nextVal = e.target.checked;
                      handleSaveCatalogProducts(
                        undefined, undefined, undefined, undefined, undefined, undefined, undefined,
                        undefined, nextVal
                      );
                    }}
                    className="rounded accent-emerald-500 cursor-pointer w-4 h-4"
                  />
                </label>

                <button
                  type="button"
                  onClick={() => {
                    setPortalPreFilledEditingId('new');
                    setPortalPreFilledDraft({
                      id: 'tmpl_' + Date.now(),
                      title: '',
                      triggerType: 'keyword_match',
                      triggerKeywords: [],
                      messageText: '',
                      packMediaAlbum: true,
                      isEnabled: true,
                    });
                    setPortalPreFilledKeywordsInput('');
                  }}
                  className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-black text-xs shadow-md shadow-teal-500/20 flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus size={13} />
                  <span>New Template</span>
                </button>
              </div>
            </div>

            {/* KPI Cards Row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-6 py-3 border-b border-white/5 bg-[#08120E] shrink-0">
              <div className="p-3 rounded-2xl bg-[#0F261C] border border-teal-500/20 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Total Templates</span>
                  <span className="text-xl font-black text-white font-mono mt-0.5 block">{currentPreFilledTemplates.length}</span>
                </div>
                <Zap size={18} className="text-teal-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#0F261C] border border-emerald-500/20 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Active Gatekeepers</span>
                  <span className="text-xl font-black text-emerald-400 font-mono mt-0.5 block">
                    {currentPreFilledTemplates.filter((t: any) => t.isEnabled).length}
                  </span>
                </div>
                <CheckCircle2 size={18} className="text-emerald-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#0F261C] border border-sky-500/20 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Token Consumption</span>
                  <span className="text-xl font-black text-sky-300 font-mono mt-0.5 block">0 Tokens</span>
                </div>
                <Sparkles size={18} className="text-sky-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#0F261C] border border-purple-500/20 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Turn 2 Handover</span>
                  <span className="text-sm font-black text-purple-300 font-mono mt-1 block">Live Luna AI</span>
                </div>
                <ArrowRight size={18} className="text-purple-400" />
              </div>
            </div>

            {/* Main Content Area: Split View */}
            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
              {/* Left Pane: Template Cards List */}
              <div className="w-full lg:w-1/2 border-r border-white/5 flex flex-col overflow-hidden bg-[#070E0B]">
                {/* Search Bar & Filter */}
                <div className="p-4 border-b border-white/5 flex items-center gap-2 bg-[#091510]">
                  <div className="relative flex-1">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search templates, trigger keywords..."
                      value={portalPreFilledSearch}
                      onChange={(e) => setPortalPreFilledSearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-400"
                    />
                  </div>
                  {currentPreFilledTemplates.length === 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const sample = [
                          {
                            id: 'tmpl_welcome_greeting',
                            title: '👋 Welcome Greeting & Quick Assistance',
                            triggerType: 'first_contact_greeting',
                            triggerKeywords: ['hi', 'hello', 'hey', 'start', 'wisthara', 'details', 'help', 'info', 'ආයුබෝවන්', 'හලෝ'],
                            messageText: `👋 Hello {{customer_name}}! Welcome to *{{store_name}}*.\n\nHow can we help you today? Feel free to ask about any of our products, check prices, or place an order directly here! 🛍️✨`,
                            isEnabled: true,
                            priority: 10,
                          }
                        ];
                        handleSaveCatalogProducts(
                          undefined, undefined, undefined, undefined, undefined, undefined, undefined,
                          sample, true
                        );
                      }}
                      className="px-2.5 py-1.5 rounded-xl bg-teal-500/20 text-teal-300 text-xs border border-teal-500/30 whitespace-nowrap cursor-pointer hover:bg-teal-500/30"
                    >
                      Load Default
                    </button>
                  )}
                </div>

                {/* Templates Scrollable List */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin scrollbar-thumb-teal-950">
                  {currentPreFilledTemplates.length === 0 ? (
                    <div className="p-8 rounded-2xl bg-[#091510] border border-dashed border-teal-500/30 text-center space-y-3">
                      <Zap size={28} className="mx-auto text-teal-400" />
                      <div className="text-sm font-bold text-white">No Pre-filled Templates Yet</div>
                      <p className="text-xs text-slate-400 max-w-sm mx-auto">
                        Create your first pre-filled message template to automatically greet incoming customers and answer common questions at 0 tokens!
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setPortalPreFilledEditingId('new');
                          setPortalPreFilledDraft({
                            id: 'tmpl_' + Date.now(),
                            title: 'Welcome Greeting',
                            triggerType: 'first_contact_greeting',
                            triggerKeywords: ['hi', 'hello', 'hey', 'start', 'wisthara'],
                            messageText: '👋 Hello {{customer_name}}! Welcome to {{store_name}}. How can we assist you today?',
                            packMediaAlbum: true,
                            isEnabled: true,
                          });
                          setPortalPreFilledKeywordsInput('hi, hello, hey, start, wisthara');
                        }}
                        className="px-4 py-2 rounded-xl bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-xs shadow-md cursor-pointer inline-flex items-center gap-1.5"
                      >
                        <Plus size={13} />
                        <span>Create Welcome Greeting</span>
                      </button>
                    </div>
                  ) : (
                    currentPreFilledTemplates
                      .filter((tmpl: any) => {
                        if (!portalPreFilledSearch.trim()) return true;
                        const q = portalPreFilledSearch.toLowerCase();
                        return (
                          tmpl.title?.toLowerCase().includes(q) ||
                          tmpl.messageText?.toLowerCase().includes(q) ||
                          (tmpl.triggerKeywords || []).some((k: string) => k.toLowerCase().includes(q))
                        );
                      })
                      .map((tmpl: any) => {
                        const isSelected = portalPreFilledEditingId === tmpl.id;
                        const linkedProd = catalogProducts.find((p: any) => p.id === tmpl.attachedProductId);
                        return (
                          <div
                            key={tmpl.id}
                            className={`p-4 rounded-2xl border transition cursor-pointer ${
                              isSelected
                                ? 'bg-[#0E231B] border-teal-400 ring-2 ring-teal-400/30 shadow-lg shadow-teal-500/10'
                                : tmpl.isEnabled
                                ? 'bg-[#091510] border-slate-800 hover:border-teal-500/40'
                                : 'bg-[#060D0A] border-slate-900 opacity-60'
                            }`}
                            onClick={() => {
                              setPortalPreFilledEditingId(tmpl.id);
                              setPortalPreFilledDraft(tmpl);
                              setPortalPreFilledKeywordsInput((tmpl.triggerKeywords || []).join(', '));
                            }}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="space-y-1.5 flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <input
                                    type="checkbox"
                                    checked={tmpl.isEnabled}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      const updated = currentPreFilledTemplates.map((t: any) =>
                                        t.id === tmpl.id ? { ...t, isEnabled: e.target.checked } : t
                                      );
                                      handleSaveCatalogProducts(
                                        undefined, undefined, undefined, undefined, undefined, undefined, undefined,
                                        updated
                                      );
                                    }}
                                    className="rounded accent-emerald-500 cursor-pointer w-4 h-4"
                                  />
                                  <span className="font-bold text-white text-xs truncate">{tmpl.title}</span>
                                  {tmpl.isEnabled ? (
                                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold flex items-center gap-1">
                                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                      <span>Active</span>
                                    </span>
                                  ) : (
                                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-500/30 font-bold flex items-center gap-1">
                                      <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
                                      <span>Paused</span>
                                    </span>
                                  )}
                                  {tmpl.triggerType === 'first_contact_greeting' && (
                                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300 border border-sky-500/30">
                                      👋 1st Greeting
                                    </span>
                                  )}
                                  {tmpl.triggerType === 'keyword_match' && (
                                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                                      🔍 Keywords
                                    </span>
                                  )}
                                  {tmpl.triggerType === 'product_inquiry' && (
                                    <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                      🛍️ Product
                                    </span>
                                  )}
                                </div>

                                {/* Trigger keywords chips */}
                                {(tmpl.triggerKeywords || []).length > 0 && (
                                  <div className="flex flex-wrap gap-1 items-center">
                                    <span className="text-[9px] text-slate-500">Triggers on:</span>
                                    {tmpl.triggerKeywords.slice(0, 4).map((kw: string, i: number) => (
                                      <span key={i} className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-900 text-slate-300 border border-white/5">
                                        {kw}
                                      </span>
                                    ))}
                                    {tmpl.triggerKeywords.length > 4 && (
                                      <span className="text-[9px] text-slate-500">+{tmpl.triggerKeywords.length - 4} more</span>
                                    )}
                                  </div>
                                )}

                                {/* Attached Product / Media */}
                                <div className="flex items-center gap-2 flex-wrap text-[10px] text-slate-400">
                                  {(() => {
                                    const rawIds = Array.isArray(tmpl.attachedProductIds) && tmpl.attachedProductIds.length > 0
                                      ? tmpl.attachedProductIds
                                      : (tmpl.attachedProductId ? [tmpl.attachedProductId] : []);
                                    if (rawIds.length === 0) return null;
                                    return (
                                      <span className="text-emerald-400 flex items-center gap-1 font-medium">
                                        <Package size={11} />
                                        <span>
                                          {rawIds.length === 1
                                            ? catalogProducts.find((p: any) => p.id === rawIds[0])?.name || '1 Product'
                                            : `${rawIds.length} Products`}
                                        </span>
                                      </span>
                                    );
                                  })()}
                                  {((tmpl.attachedMediaUrls && tmpl.attachedMediaUrls.length > 0) || tmpl.attachedMediaUrl) && (
                                    <span className="text-teal-400 flex items-center gap-1">
                                      <Paperclip size={11} />
                                      <span>
                                        Media ({Array.isArray(tmpl.attachedMediaUrls) && tmpl.attachedMediaUrls.length > 1
                                          ? `${tmpl.attachedMediaUrls.length} Assets`
                                          : (tmpl.attachedMediaType || 'Asset')})
                                      </span>
                                    </span>
                                  )}
                                </div>

                                {/* Message preview snippet */}
                                <p className="text-[11px] text-slate-300 line-clamp-2 leading-relaxed font-sans">
                                  {tmpl.messageText}
                                </p>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setPortalPreFilledEditingId(tmpl.id);
                                    setPortalPreFilledDraft(tmpl);
                                    setPortalPreFilledKeywordsInput((tmpl.triggerKeywords || []).join(', '));
                                  }}
                                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-teal-500/20 hover:text-teal-300 text-slate-400 transition cursor-pointer"
                                  title="Edit Template"
                                >
                                  <Edit3 size={13} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const clone = {
                                      ...tmpl,
                                      id: 'tmpl_' + Date.now(),
                                      title: `${tmpl.title} (Copy)`,
                                    };
                                    const updated = [...currentPreFilledTemplates, clone];
                                    handleSaveCatalogProducts(
                                      undefined, undefined, undefined, undefined, undefined, undefined, undefined,
                                      updated
                                    );
                                  }}
                                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 transition cursor-pointer"
                                  title="Duplicate"
                                >
                                  <Copy size={13} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (confirm(`Delete template "${tmpl.title}"?`)) {
                                      const updated = currentPreFilledTemplates.filter((t: any) => t.id !== tmpl.id);
                                      if (portalPreFilledEditingId === tmpl.id) {
                                        setPortalPreFilledEditingId(null);
                                        setPortalPreFilledDraft({});
                                      }
                                      handleSaveCatalogProducts(
                                        undefined, undefined, undefined, undefined, undefined, undefined, undefined,
                                        updated
                                      );
                                    }
                                  }}
                                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-500/20 hover:text-rose-300 text-slate-400 transition cursor-pointer"
                                  title="Delete Template"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })
                  )}
                </div>
              </div>

              {/* Right Pane: Detailed Editor & Live WhatsApp Simulation */}
              <div className="w-full lg:w-1/2 flex flex-col overflow-y-auto p-6 bg-[#08120E] space-y-5 scrollbar-thin scrollbar-thumb-teal-950">
                {portalPreFilledEditingId ? (
                  <div className="space-y-5 max-w-2xl">
                    <div className="flex items-center justify-between pb-3 border-b border-white/5">
                      <div className="flex items-center gap-2">
                        <Edit3 size={15} className="text-teal-400" />
                        <h4 className="text-sm font-bold text-white">
                          {portalPreFilledEditingId === 'new' ? 'Create New Pre-filled Template' : 'Edit Pre-filled Template'}
                        </h4>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setPortalPreFilledEditingId(null);
                          setPortalPreFilledDraft({});
                        }}
                        className="text-xs px-3 py-1 rounded-xl bg-slate-800 text-slate-300 hover:text-white cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>

                    {/* Individual Active / Armed Toggle Switch */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-[#0E2018] border border-slate-800">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-200">Template Status:</span>
                        <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold flex items-center gap-1.5 ${
                          (portalPreFilledDraft.isEnabled !== false && portalPreFilledDraft.isEnabled !== 'false' && portalPreFilledDraft.isEnabled !== 0 && portalPreFilledDraft.isEnabled !== '0')
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            : 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                        }`}>
                          {(portalPreFilledDraft.isEnabled !== false && portalPreFilledDraft.isEnabled !== 'false' && portalPreFilledDraft.isEnabled !== 0 && portalPreFilledDraft.isEnabled !== '0') ? (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                              <span>⚡ Active (Gatekeeper Armed)</span>
                            </>
                          ) : (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-rose-400"></span>
                              <span>⏸ Paused (Bypassed)</span>
                            </>
                          )}
                        </span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={portalPreFilledDraft.isEnabled !== false}
                          onChange={(e) => setPortalPreFilledDraft({ ...portalPreFilledDraft, isEnabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-slate-800 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                      </label>
                    </div>

                    {/* Template Title */}
                    <div>
                      <label className="text-xs font-bold text-slate-200 block mb-1.5">
                        Template Title / Internal Name
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Denim Jacket Quick Overview or Welcome Greeting"
                        value={portalPreFilledDraft.title || ''}
                        onChange={(e) => setPortalPreFilledDraft({ ...portalPreFilledDraft, title: e.target.value })}
                        className="w-full bg-[#0E2018] border border-slate-700 focus:border-teal-400 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none"
                      />
                    </div>

                    {/* Trigger Condition Selector */}
                    <div>
                      <label className="text-xs font-bold text-slate-200 block mb-1.5">
                        Trigger Condition (When Should This Message Send?)
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setPortalPreFilledDraft({
                              ...portalPreFilledDraft,
                              triggerType: 'first_contact_greeting',
                              triggerKeywords: ['hi', 'hello', 'hey', 'start', 'wisthara', 'details', 'help', 'info', 'ආයුබෝවන්', 'හලෝ']
                            });
                            setPortalPreFilledKeywordsInput('hi, hello, hey, start, wisthara, details, help, info, ආයුබෝවන්, හලෝ');
                          }}
                          className={`p-3 rounded-xl border text-left transition cursor-pointer ${
                            portalPreFilledDraft.triggerType === 'first_contact_greeting'
                              ? 'bg-sky-500/20 border-sky-400 text-sky-200 shadow-sm'
                              : 'bg-[#0E2018] border-slate-800 text-slate-400 hover:border-slate-700'
                          }`}
                        >
                          <div className="font-bold text-xs flex items-center gap-1.5">
                            <span>👋</span>
                            <span>1st Greeting</span>
                          </div>
                          <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                            Initial incoming message or greetings (hi, hello, hey)
                          </div>
                        </button>

                        <button
                          type="button"
                          onClick={() => setPortalPreFilledDraft({ ...portalPreFilledDraft, triggerType: 'keyword_match' })}
                          className={`p-3 rounded-xl border text-left transition cursor-pointer ${
                            portalPreFilledDraft.triggerType === 'keyword_match'
                              ? 'bg-amber-500/20 border-amber-400 text-amber-200 shadow-sm'
                              : 'bg-[#0E2018] border-slate-800 text-slate-400 hover:border-slate-700'
                          }`}
                        >
                          <div className="font-bold text-xs flex items-center gap-1.5">
                            <span>🔍</span>
                            <span>Keywords / Questions</span>
                          </div>
                          <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                            Specific questions or keywords (e.g. &quot;denim&quot;, &quot;delivery&quot;)
                          </div>
                        </button>

                        <button
                          type="button"
                          onClick={() => setPortalPreFilledDraft({ ...portalPreFilledDraft, triggerType: 'product_inquiry' })}
                          className={`p-3 rounded-xl border text-left transition cursor-pointer ${
                            portalPreFilledDraft.triggerType === 'product_inquiry'
                              ? 'bg-emerald-500/20 border-emerald-400 text-emerald-200 shadow-sm'
                              : 'bg-[#0E2018] border-slate-800 text-slate-400 hover:border-slate-700'
                          }`}
                        >
                          <div className="font-bold text-xs flex items-center gap-1.5">
                            <span>🛍️</span>
                            <span>Product Inquiry</span>
                          </div>
                          <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                            Direct inquiry about specific catalog products
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Trigger Keywords Input & Suggestions */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-bold text-slate-200">
                          Trigger Keywords / Match Phrases (Comma-Separated)
                        </label>
                        <span className="text-[10px] text-slate-400">Case-insensitive matching</span>
                      </div>
                      <input
                        type="text"
                        placeholder="e.g. denim, denim jacket, can i get more info about denim"
                        value={portalPreFilledKeywordsInput}
                        onChange={(e) => {
                          const text = e.target.value;
                          setPortalPreFilledKeywordsInput(text);
                          const parsed = text.split(',').map((s: string) => s.trim().toLowerCase()).filter(Boolean);
                          setPortalPreFilledDraft({ ...portalPreFilledDraft, triggerKeywords: parsed });
                        }}
                        className="w-full bg-[#0E2018] border border-slate-700 focus:border-teal-400 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none font-mono"
                      />
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className="text-[10px] text-slate-500 self-center">Suggestions:</span>
                        <button
                          type="button"
                          onClick={() => {
                            const add = ['hi', 'hello', 'hey', 'start', 'wisthara'];
                            const current = portalPreFilledDraft.triggerKeywords || [];
                            const combined = Array.from(new Set([...current, ...add]));
                            setPortalPreFilledKeywordsInput(combined.join(', '));
                            setPortalPreFilledDraft({ ...portalPreFilledDraft, triggerKeywords: combined });
                          }}
                          className="text-[10px] px-2 py-0.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 cursor-pointer"
                        >
                          + Greetings
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const add = ['price', 'cost', 'gana', 'keeyada'];
                            const current = portalPreFilledDraft.triggerKeywords || [];
                            const combined = Array.from(new Set([...current, ...add]));
                            setPortalPreFilledKeywordsInput(combined.join(', '));
                            setPortalPreFilledDraft({ ...portalPreFilledDraft, triggerKeywords: combined });
                          }}
                          className="text-[10px] px-2 py-0.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 cursor-pointer"
                        >
                          + Pricing
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const add = ['details', 'info', 'wisthara', 'more info'];
                            const current = portalPreFilledDraft.triggerKeywords || [];
                            const combined = Array.from(new Set([...current, ...add]));
                            setPortalPreFilledKeywordsInput(combined.join(', '));
                            setPortalPreFilledDraft({ ...portalPreFilledDraft, triggerKeywords: combined });
                          }}
                          className="text-[10px] px-2 py-0.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 cursor-pointer"
                        >
                          + Details / Info
                        </button>
                      </div>
                    </div>

                    {/* 🛍️ MULTI-PRODUCT SELECTION & MULTI-PHOTO GALLERY PER PRODUCT */}
                    {(() => {
                      const rawProductIds: string[] = Array.isArray(portalPreFilledDraft.attachedProductIds) && portalPreFilledDraft.attachedProductIds.length > 0
                        ? portalPreFilledDraft.attachedProductIds
                        : (portalPreFilledDraft.attachedProductId ? [portalPreFilledDraft.attachedProductId] : []);

                      const handleAddProduct = (prodId: string) => {
                        if (!prodId || rawProductIds.includes(prodId)) return;
                        const nextIds = [...rawProductIds, prodId];
                        const prod = catalogProducts.find((p: any) => p.id === prodId);
                        const prodImgs: string[] = [];
                        if (prod?.imageUrl) prodImgs.push(prod.imageUrl);
                        if (Array.isArray(prod?.media)) {
                          prod.media.forEach((m: any) => {
                            const u = typeof m === 'string' ? m : m?.url;
                            if (u && !prodImgs.includes(u)) prodImgs.push(u);
                          });
                        }
                        const nextSelectedImages = {
                          ...(portalPreFilledDraft.selectedProductImages || {}),
                          [prodId]: prodImgs
                        };
                        setPortalPreFilledDraft({
                          ...portalPreFilledDraft,
                          attachedProductIds: nextIds,
                          attachedProductId: nextIds[0],
                          selectedProductImages: nextSelectedImages,
                          attachedMediaUrl: !portalPreFilledDraft.attachedMediaUrl && prod?.imageUrl ? prod.imageUrl : portalPreFilledDraft.attachedMediaUrl
                        });
                      };

                      const handleRemoveProduct = (prodId: string) => {
                        const nextIds = rawProductIds.filter(id => id !== prodId);
                        const nextSelectedImages = { ...(portalPreFilledDraft.selectedProductImages || {}) };
                        delete nextSelectedImages[prodId];
                        setPortalPreFilledDraft({
                          ...portalPreFilledDraft,
                          attachedProductIds: nextIds,
                          attachedProductId: nextIds[0] || undefined,
                          selectedProductImages: nextSelectedImages
                        });
                      };

                      const handleToggleProductImage = (prodId: string, imgUrl: string) => {
                        const currentImages = portalPreFilledDraft.selectedProductImages?.[prodId] || [];
                        let updatedImages: string[];
                        if (currentImages.includes(imgUrl)) {
                          updatedImages = currentImages.filter((u: string) => u !== imgUrl);
                        } else {
                          updatedImages = [...currentImages, imgUrl];
                        }
                        setPortalPreFilledDraft({
                          ...portalPreFilledDraft,
                          selectedProductImages: {
                            ...(portalPreFilledDraft.selectedProductImages || {}),
                            [prodId]: updatedImages
                          }
                        });
                      };

                      return (
                        <div className="space-y-3 pt-3 border-t border-white/5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-200 flex items-center gap-1.5">
                              <ShoppingBag size={14} className="text-teal-400" />
                              <span>Attach Catalog Products (Multiple Supported)</span>
                            </label>
                            <span className="text-[10px] text-teal-400 font-mono">
                              {rawProductIds.length} Product{rawProductIds.length === 1 ? '' : 's'} Selected
                            </span>
                          </div>

                          {/* Product Dropdown to Add */}
                          <div className="flex items-center gap-2">
                            <select
                              value=""
                              onChange={(e) => handleAddProduct(e.target.value)}
                              className="flex-1 bg-[#0E2018] border border-slate-700 focus:border-teal-400 rounded-xl px-3 py-2 text-xs text-white focus:outline-none cursor-pointer"
                            >
                              <option value="">-- Click to select & add a product to this template --</option>
                              {catalogProducts
                                .filter((p: any) => !rawProductIds.includes(p.id))
                                .map((p: any) => (
                                  <option key={p.id} value={p.id}>
                                    + {p.name} ({p.currency || 'LKR'} {p.price?.toLocaleString()})
                                  </option>
                                ))}
                            </select>
                          </div>

                          {/* Selected Products List & Photo Selector */}
                          {rawProductIds.length > 0 && (
                            <div className="space-y-2.5 mt-2">
                              {rawProductIds.map((pid) => {
                                const prod = catalogProducts.find((x: any) => x.id === pid);
                                if (!prod) return null;

                                // Collect all images for this product (primary + gallery)
                                const allProdImages: string[] = [];
                                if (prod.imageUrl) allProdImages.push(prod.imageUrl);
                                if (Array.isArray(prod.media)) {
                                  prod.media.forEach((m: any) => {
                                    const u = typeof m === 'string' ? m : m?.url;
                                    if (u && !allProdImages.includes(u)) allProdImages.push(u);
                                  });
                                }

                                const selectedImgs = portalPreFilledDraft.selectedProductImages?.[prod.id] || (prod.imageUrl ? [prod.imageUrl] : allProdImages);

                                return (
                                  <div key={pid} className="p-3 rounded-2xl bg-[#0B1A13] border border-teal-500/30 space-y-2.5">
                                    {/* Product Header */}
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="flex items-center gap-2.5 overflow-hidden flex-1 min-w-0">
                                        {prod.imageUrl ? (
                                          <img src={prod.imageUrl} alt={prod.name} className="w-8 h-8 rounded-lg object-cover border border-teal-500/30 shrink-0" />
                                        ) : (
                                          <Package size={18} className="text-teal-400 shrink-0" />
                                        )}
                                        <div className="truncate">
                                          <span className="text-xs font-bold text-white truncate block">{prod.name}</span>
                                          <span className="text-[10px] text-teal-400 font-mono">
                                            {prod.currency || 'LKR'} {prod.price?.toLocaleString()}
                                            {prod.category ? ` • ${prod.category}` : ''}
                                          </span>
                                        </div>
                                      </div>

                                      <button
                                        type="button"
                                        onClick={() => handleRemoveProduct(pid)}
                                        className="text-slate-400 hover:text-rose-400 p-1.5 rounded-lg hover:bg-rose-500/10 transition cursor-pointer"
                                        title="Remove Product"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>

                                    {/* Multi-Photo Checklist for this product */}
                                    {allProdImages.length > 0 && (
                                      <div className="pt-2 border-t border-white/5 space-y-2">
                                        <div className="flex items-center justify-between text-[10px]">
                                          <span className="text-slate-300 font-medium flex items-center gap-1">
                                            <Camera size={11} className="text-teal-400" />
                                            <span>Select Photos to Send ({selectedImgs.length}/{allProdImages.length}):</span>
                                          </span>
                                          <div className="flex items-center gap-2">
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setPortalPreFilledDraft({
                                                  ...portalPreFilledDraft,
                                                  selectedProductImages: {
                                                    ...(portalPreFilledDraft.selectedProductImages || {}),
                                                    [prod.id]: allProdImages
                                                  }
                                                });
                                              }}
                                              className="text-[9px] text-teal-300 hover:underline cursor-pointer"
                                            >
                                              All Photos
                                            </button>
                                            <span className="text-slate-600">•</span>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setPortalPreFilledDraft({
                                                  ...portalPreFilledDraft,
                                                  selectedProductImages: {
                                                    ...(portalPreFilledDraft.selectedProductImages || {}),
                                                    [prod.id]: prod.imageUrl ? [prod.imageUrl] : allProdImages.slice(0, 1)
                                                  }
                                                });
                                              }}
                                              className="text-[9px] text-slate-400 hover:underline cursor-pointer"
                                            >
                                              Primary Only
                                            </button>
                                          </div>
                                        </div>

                                        {/* Photo Thumbnails */}
                                        <div className="flex flex-wrap gap-2">
                                          {allProdImages.map((imgUrl, imgIdx) => {
                                            const isSelected = selectedImgs.includes(imgUrl);
                                            return (
                                              <button
                                                key={imgIdx}
                                                type="button"
                                                onClick={() => handleToggleProductImage(prod.id, imgUrl)}
                                                className={`relative rounded-xl overflow-hidden border transition cursor-pointer ${
                                                  isSelected
                                                    ? 'border-teal-400 ring-2 ring-teal-400/40'
                                                    : 'border-white/10 opacity-40 hover:opacity-80'
                                                }`}
                                              >
                                                <img src={imgUrl} alt={`View ${imgIdx + 1}`} className="w-14 h-14 object-cover" />
                                                {isSelected && (
                                                  <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-teal-500 text-slate-950 flex items-center justify-center">
                                                    <Check size={10} strokeWidth={3} />
                                                  </div>
                                                )}
                                                {imgIdx === 0 && (
                                                  <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[8px] text-white text-center font-mono py-0.5">
                                                    Primary
                                                  </span>
                                                )}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Anti-clumping Toggle */}
                          <label className="flex items-start gap-2.5 p-3 rounded-2xl bg-teal-950/20 border border-teal-500/20 cursor-pointer mt-1">
                            <input
                              type="checkbox"
                              checked={portalPreFilledDraft.sendProductsIndividually !== false}
                              onChange={(e) => setPortalPreFilledDraft({ ...portalPreFilledDraft, sendProductsIndividually: e.target.checked })}
                              className="rounded accent-emerald-500 mt-0.5 cursor-pointer w-4 h-4"
                            />
                            <div className="flex-1 text-[11px] text-slate-300">
                              <div className="font-bold text-teal-300 flex items-center gap-1.5">
                                <span>📦 Send Products Individually Without Mixing</span>
                                <span className="text-[9px] px-1.5 py-0.2 rounded bg-teal-500/20 text-teal-300 font-mono">Anti-Clump</span>
                              </div>
                              <div className="text-[10px] text-slate-400 mt-0.5 leading-relaxed">
                                Dispatches each product as its own distinct card with its title, price, description, and selected photos with sequential pacing. Never clumps products into one confusing blob!
                              </div>
                            </div>
                          </label>

                          {/* 🖼️ Pack Media Album / WhatsApp Grid Toggle */}
                          <label className="flex items-start gap-2.5 p-3 rounded-2xl bg-teal-950/30 border border-teal-400/30 cursor-pointer mt-2 shadow-sm">
                            <input
                              type="checkbox"
                              checked={portalPreFilledDraft.packMediaAlbum !== false}
                              onChange={(e) => setPortalPreFilledDraft({ ...portalPreFilledDraft, packMediaAlbum: e.target.checked })}
                              className="rounded accent-teal-400 mt-0.5 cursor-pointer w-4 h-4"
                            />
                            <div className="flex-1 text-[11px] text-slate-300">
                              <div className="font-bold text-teal-300 flex items-center gap-1.5">
                                <span>🖼️ Pack Media into WhatsApp Album (Grid)</span>
                                <span className="text-[9px] px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 font-mono">Clean Album</span>
                              </div>
                              <div className="text-[10px] text-slate-400 mt-0.5 leading-relaxed">
                                Packs all product photos together into a native WhatsApp 4-image grid / album without separate captions, and dispatches your text message cleanly underneath.
                              </div>
                            </div>
                          </label>
                        </div>
                      );
                    })()}

                    {/* 🗄️ ATTACH MEDIA ASSETS (MEDIA VAULT & CUSTOM URLS) */}
                    {(() => {
                      const attachedMediaVaultAssetIds: string[] = Array.isArray(portalPreFilledDraft.attachedMediaVaultAssetIds)
                        ? portalPreFilledDraft.attachedMediaVaultAssetIds
                        : [];

                      const attachedMediaUrls: string[] = Array.isArray(portalPreFilledDraft.attachedMediaUrls)
                        ? portalPreFilledDraft.attachedMediaUrls
                        : (portalPreFilledDraft.attachedMediaUrl ? [portalPreFilledDraft.attachedMediaUrl] : []);

                      const handleToggleMediaVaultAsset = (asset: any) => {
                        const isAttached = attachedMediaVaultAssetIds.includes(asset.id) || attachedMediaUrls.includes(asset.secureUrl);
                        let nextIds: string[];
                        let nextUrls: string[];
                        if (isAttached) {
                          nextIds = attachedMediaVaultAssetIds.filter(id => id !== asset.id);
                          nextUrls = attachedMediaUrls.filter((u: string) => u !== asset.secureUrl);
                        } else {
                          nextIds = [...attachedMediaVaultAssetIds, asset.id];
                          nextUrls = [...attachedMediaUrls, asset.secureUrl];
                        }
                        setPortalPreFilledDraft({
                          ...portalPreFilledDraft,
                          attachedMediaVaultAssetIds: nextIds,
                          attachedMediaUrls: nextUrls,
                          attachedMediaUrl: nextUrls[0] || undefined,
                          attachedMediaType: asset.mediaType || portalPreFilledDraft.attachedMediaType || 'image'
                        });
                      };

                      return (
                        <div className="space-y-3 pt-3 border-t border-white/5">
                          <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-200 flex items-center gap-1.5">
                              <Paperclip size={14} className="text-teal-400" />
                              <span>Attach Media Vault Assets & Media URLs</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => setPortalPreFilledShowMediaVault(!portalPreFilledShowMediaVault)}
                              className="text-[10px] px-2.5 py-1 rounded-xl bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/30 font-bold flex items-center gap-1.5 cursor-pointer"
                            >
                              <FolderOpen size={12} />
                              <span>{portalPreFilledShowMediaVault ? 'Close Vault ✕' : `Browse Media Vault (${mediaAssets.length}) ⚡`}</span>
                            </button>
                          </div>

                          {/* Media Vault Browser Drawer */}
                          {portalPreFilledShowMediaVault && (
                            <div className="p-3 rounded-2xl bg-[#0B1A13] border border-teal-500/40 space-y-2.5 animate-fade-in">
                              <div className="flex items-center justify-between gap-2">
                                <div className="relative flex-1">
                                  <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                  <input
                                    type="text"
                                    placeholder="Search vault assets by title or tag..."
                                    value={portalPreFilledMediaVaultSearch}
                                    onChange={(e) => setPortalPreFilledMediaVaultSearch(e.target.value)}
                                    className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-teal-400"
                                  />
                                </div>
                                <div className="flex items-center gap-1">
                                  {(['all', 'image', 'video', 'pdf'] as const).map(t => (
                                    <button
                                      key={t}
                                      type="button"
                                      onClick={() => setPortalPreFilledMediaVaultType(t)}
                                      className={`text-[9px] uppercase px-2 py-0.5 rounded-lg cursor-pointer ${
                                        portalPreFilledMediaVaultType === t ? 'bg-teal-500 text-slate-950 font-bold' : 'bg-slate-800 text-slate-400'
                                      }`}
                                    >
                                      {t}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {mediaAssets.length === 0 ? (
                                <div className="p-4 text-center text-xs text-slate-400 bg-slate-950/60 rounded-xl border border-dashed border-slate-800">
                                  No Media Vault assets found. You can upload assets in the Media Vault tab, or paste direct URLs below!
                                </div>
                              ) : (
                                <div className="max-h-48 overflow-y-auto grid grid-cols-2 sm:grid-cols-3 gap-2 scrollbar-thin">
                                  {mediaAssets
                                    .filter((a: any) => {
                                      if (portalPreFilledMediaVaultType !== 'all' && a.mediaType !== portalPreFilledMediaVaultType) return false;
                                      if (!portalPreFilledMediaVaultSearch.trim()) return true;
                                      const q = portalPreFilledMediaVaultSearch.toLowerCase();
                                      return a.title?.toLowerCase().includes(q) || a.tags?.toLowerCase().includes(q);
                                    })
                                    .map((asset: any) => {
                                      const isAttached = attachedMediaVaultAssetIds.includes(asset.id) || attachedMediaUrls.includes(asset.secureUrl);
                                      return (
                                        <button
                                          key={asset.id}
                                          type="button"
                                          onClick={() => handleToggleMediaVaultAsset(asset)}
                                          className={`p-2 rounded-xl border text-left transition flex items-center gap-2 cursor-pointer ${
                                            isAttached
                                              ? 'bg-teal-950/60 border-teal-400 ring-1 ring-teal-400/50'
                                              : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                                          }`}
                                        >
                                          {asset.mediaType === 'image' && asset.secureUrl ? (
                                            <img src={asset.secureUrl} alt={asset.title} className="w-9 h-9 rounded-lg object-cover shrink-0 border border-white/10" />
                                          ) : (
                                            <Paperclip size={16} className="text-teal-400 shrink-0" />
                                          )}
                                          <div className="truncate flex-1 min-w-0">
                                            <div className="text-[11px] font-bold text-white truncate">{asset.title}</div>
                                            <div className="text-[9px] text-slate-400 uppercase font-mono">{asset.mediaType}</div>
                                          </div>
                                          {isAttached && (
                                            <div className="w-4 h-4 rounded-full bg-teal-500 text-slate-950 flex items-center justify-center shrink-0">
                                              <Check size={10} strokeWidth={3} />
                                            </div>
                                          )}
                                        </button>
                                      );
                                    })}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Attached Media Vault Assets Chips */}
                          {attachedMediaUrls.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {attachedMediaUrls.map((url: string, uIdx: number) => (
                                <div key={uIdx} className="flex items-center gap-2 px-2.5 py-1 rounded-xl bg-teal-950/40 border border-teal-500/30 text-[10px] text-teal-200">
                                  <img src={url} alt="Media" className="w-5 h-5 rounded object-cover" onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                                  <span className="max-w-[160px] truncate font-mono">{url}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const nextUrls = attachedMediaUrls.filter((_, i) => i !== uIdx);
                                      setPortalPreFilledDraft({
                                        ...portalPreFilledDraft,
                                        attachedMediaUrls: nextUrls,
                                        attachedMediaUrl: nextUrls[0] || undefined
                                      });
                                    }}
                                    className="text-slate-400 hover:text-rose-400 ml-1 cursor-pointer"
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Direct Custom Media URL Input */}
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              placeholder="Or paste direct image/video/PDF URL: https://..."
                              value={portalPreFilledDraft.attachedMediaUrl || ''}
                              onChange={(e) => {
                                const u = e.target.value;
                                setPortalPreFilledDraft({
                                  ...portalPreFilledDraft,
                                  attachedMediaUrl: u,
                                  attachedMediaUrls: u ? [u] : []
                                });
                              }}
                              className="flex-1 bg-[#0E2018] border border-slate-700 focus:border-teal-400 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none font-mono"
                            />
                          </div>
                        </div>
                      );
                    })()}

                    {/* Message Textarea & Variables */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-bold text-slate-200 flex items-center gap-1.5">
                          <MessageSquare size={13} className="text-teal-400" />
                          <span>WhatsApp Response Text</span>
                        </label>
                        <span className="text-[10px] text-slate-400">Supports *bold*, _italic_, emoji</span>
                      </div>

                      {/* Variable Helper Chips */}
                      <div className="flex flex-wrap items-center gap-1.5 mb-2">
                        <span className="text-[10px] text-slate-500">Insert:</span>
                        {[
                          { tag: '{{store_name}}', label: 'Store Name' },
                          { tag: '{{customer_name}}', label: 'Customer Name' },
                          { tag: '{{product_name}}', label: 'Product Name' },
                          { tag: '{{price}}', label: 'Price' },
                          { tag: '{{currency}}', label: 'Currency' }
                        ].map((v) => (
                          <button
                            key={v.tag}
                            type="button"
                            onClick={() => {
                              const current = portalPreFilledDraft.messageText || '';
                              setPortalPreFilledDraft({ ...portalPreFilledDraft, messageText: current + (current ? ' ' : '') + v.tag });
                            }}
                            className="text-[10px] font-mono px-2 py-0.5 rounded-lg bg-teal-500/10 hover:bg-teal-500/20 text-teal-300 border border-teal-500/30 cursor-pointer"
                          >
                            +{v.label}
                          </button>
                        ))}
                      </div>

                      <textarea
                        rows={5}
                        value={portalPreFilledDraft.messageText || ''}
                        onChange={(e) => setPortalPreFilledDraft({ ...portalPreFilledDraft, messageText: e.target.value })}
                        placeholder="👋 Hello {{customer_name}}! Welcome to {{store_name}}. How can we assist you today?"
                        className="w-full bg-[#0E2018] border border-slate-700 focus:border-teal-400 rounded-xl p-3 text-xs text-white leading-relaxed focus:outline-none resize-y"
                      />
                    </div>

                    {/* Live WhatsApp Simulation Bubble */}
                    {(() => {
                      const rawProductIds: string[] = Array.isArray(portalPreFilledDraft.attachedProductIds) && portalPreFilledDraft.attachedProductIds.length > 0
                        ? portalPreFilledDraft.attachedProductIds
                        : (portalPreFilledDraft.attachedProductId ? [portalPreFilledDraft.attachedProductId] : []);

                      const attachedMediaUrls: string[] = Array.isArray(portalPreFilledDraft.attachedMediaUrls)
                        ? portalPreFilledDraft.attachedMediaUrls
                        : (portalPreFilledDraft.attachedMediaUrl ? [portalPreFilledDraft.attachedMediaUrl] : []);

                      return (
                        <div className="p-4 rounded-3xl bg-[#0b141a] border border-[#1f2c34] text-slate-200 font-sans text-xs space-y-3 shadow-2xl">
                          <div className="text-[10px] uppercase font-mono tracking-wider text-teal-400 font-bold flex items-center justify-between">
                            <span>📱 Live WhatsApp Simulation Preview</span>
                            <span className="text-[9px] text-emerald-400 flex items-center gap-1 font-mono">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                              <span>0 Tokens Dispatched</span>
                            </span>
                          </div>

                          {/* Bubble 1: Main Greeting Text */}
                          <div className="p-3 rounded-2xl bg-[#1f2c34] text-slate-200 text-xs shadow space-y-1">
                            <div className="whitespace-pre-wrap leading-relaxed">
                              {(() => {
                                const selProds = catalogProducts.filter((p: any) => rawProductIds.includes(p.id));
                                const raw = portalPreFilledDraft.messageText || '👋 Enter message text above to preview...';
                                return raw
                                  .replace(/\{\{\s*store_name\s*\}\}/gi, 'Blossom Lifestyle Store')
                                  .replace(/\{\{\s*customer_name\s*\}\}/gi, 'Kamal')
                                  .replace(/\{\{\s*product_name\s*\}\}/gi, selProds.length > 0 ? selProds.map((p: any) => p.name).join(', ') : 'Premium Denim Jacket')
                                  .replace(/\{\{\s*price\s*\}\}/gi, selProds[0] ? `${selProds[0].currency || 'LKR'} ${selProds[0].price?.toLocaleString() || ''}` : 'LKR 4,500')
                                  .replace(/\{\{\s*currency\s*\}\}/gi, selProds[0]?.currency || 'LKR');
                              })()}
                            </div>
                          </div>

                          {/* Attached Media Vault Assets Bubble */}
                          {attachedMediaUrls.length > 0 && (
                            <div className="p-2.5 rounded-2xl bg-[#1f2c34] text-slate-200 text-xs shadow space-y-1">
                              <div className="text-[9px] text-teal-300 font-mono">📎 Attached Media ({attachedMediaUrls.length}):</div>
                              <div className="flex gap-2 overflow-x-auto py-1">
                                {attachedMediaUrls.map((url: string, i: number) => (
                                  <img key={i} src={url} alt={`Media ${i+1}`} className="w-16 h-16 object-cover rounded-xl border border-white/10 shrink-0" onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Standalone Product Cards (Anti-Clumped) */}
                          {rawProductIds.map((pid: string, idx: number) => {
                            const p = catalogProducts.find((x: any) => x.id === pid);
                            if (!p) return null;
                            const selectedImgs = portalPreFilledDraft.selectedProductImages?.[p.id] || (p.imageUrl ? [p.imageUrl] : []);
                            const curr = p.currency || 'LKR';
                            return (
                              <div key={pid} className="p-3 rounded-2xl bg-[#1f2c34] text-slate-200 text-xs shadow space-y-2 border-l-2 border-teal-400">
                                <div className="text-[10px] font-mono text-teal-300 flex items-center justify-between">
                                  <span>Card #{idx + 1}: Standalone Product Message</span>
                                  <span className="text-[9px] text-slate-400">{selectedImgs.length} photo{selectedImgs.length === 1 ? '' : 's'}</span>
                                </div>
                                {selectedImgs.length > 0 && (
                                  <div className="rounded-xl overflow-hidden border border-white/10 bg-black/40">
                                    <img src={selectedImgs[0]} alt={p.name} className="w-full h-32 object-cover" onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                                  </div>
                                )}
                                {selectedImgs.length > 1 && (
                                  <div className="flex gap-1.5 overflow-x-auto py-0.5">
                                    {selectedImgs.slice(1).map((img: string, i: number) => (
                                      <img key={i} src={img} alt={`View ${i+2}`} className="w-11 h-11 object-cover rounded-lg border border-white/10 shrink-0" onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
                                    ))}
                                  </div>
                                )}
                                <div className="text-[11px] font-sans leading-relaxed space-y-0.5">
                                  <div className="font-bold text-white">🛍️ {p.name}</div>
                                  <div className="text-teal-300 font-bold">💵 Price: {curr} {p.price?.toLocaleString()}</div>
                                  {p.category && <div className="text-slate-300 text-[10px]">📦 Category: {p.category}</div>}
                                  {p.description && <div className="text-slate-400 text-[10px] line-clamp-2 mt-1">{p.description}</div>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Bottom Save & Cancel Bar */}
                    <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/5">
                      <button
                        type="button"
                        onClick={() => {
                          setPortalPreFilledEditingId(null);
                          setPortalPreFilledDraft({});
                        }}
                        className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!portalPreFilledDraft.title?.trim()) {
                            showToast('Please enter a template title', 'error');
                            return;
                          }
                          if (!portalPreFilledDraft.messageText?.trim()) {
                            showToast('Please enter message text', 'error');
                            return;
                          }

                          const rawProductIds: string[] = Array.isArray(portalPreFilledDraft.attachedProductIds) && portalPreFilledDraft.attachedProductIds.length > 0
                            ? portalPreFilledDraft.attachedProductIds
                            : (portalPreFilledDraft.attachedProductId ? [portalPreFilledDraft.attachedProductId] : []);

                          const newTmpl = {
                            id: portalPreFilledDraft.id || 'tmpl_' + Date.now(),
                            title: portalPreFilledDraft.title.trim(),
                            triggerType: portalPreFilledDraft.triggerType || 'keyword_match',
                            triggerKeywords: Array.isArray(portalPreFilledDraft.triggerKeywords) && portalPreFilledDraft.triggerKeywords.length > 0
                              ? portalPreFilledDraft.triggerKeywords
                              : portalPreFilledKeywordsInput.split(',').map((s: string) => s.trim().toLowerCase()).filter(Boolean),
                            messageText: portalPreFilledDraft.messageText.trim(),
                            attachedProductId: rawProductIds[0] || undefined,
                            attachedProductIds: rawProductIds.length > 0 ? rawProductIds : undefined,
                            attachedMediaUrl: portalPreFilledDraft.attachedMediaUrl?.trim() || undefined,
                            attachedMediaUrls: Array.isArray(portalPreFilledDraft.attachedMediaUrls) && portalPreFilledDraft.attachedMediaUrls.length > 0 ? portalPreFilledDraft.attachedMediaUrls : undefined,
                            attachedMediaType: portalPreFilledDraft.attachedMediaType || 'image',
                            attachedMediaVaultAssetIds: Array.isArray(portalPreFilledDraft.attachedMediaVaultAssetIds) && portalPreFilledDraft.attachedMediaVaultAssetIds.length > 0 ? portalPreFilledDraft.attachedMediaVaultAssetIds : undefined,
                            selectedProductImages: portalPreFilledDraft.selectedProductImages || undefined,
                            sendProductsIndividually: portalPreFilledDraft.sendProductsIndividually !== false,
                            isEnabled: portalPreFilledDraft.isEnabled !== false && portalPreFilledDraft.isEnabled !== 'false' && portalPreFilledDraft.isEnabled !== 0 && portalPreFilledDraft.isEnabled !== '0' && portalPreFilledDraft.isEnabled !== 'disabled' && portalPreFilledDraft.isEnabled !== 'paused',
                            priority: portalPreFilledDraft.priority || 10,
                          };

                          let updatedList: any[];
                          if (portalPreFilledEditingId === 'new') {
                            updatedList = [...currentPreFilledTemplates, newTmpl];
                          } else {
                            updatedList = currentPreFilledTemplates.map((t: any) =>
                              t.id === portalPreFilledEditingId ? newTmpl : t
                            );
                          }

                          handleSaveCatalogProducts(
                            undefined, undefined, undefined, undefined, undefined, undefined, undefined,
                            updatedList
                          );
                          setPortalPreFilledEditingId(null);
                          setPortalPreFilledDraft({});
                          showToast('Template saved successfully!', 'success');
                        }}
                        className="px-5 py-2 rounded-xl bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-black text-xs shadow-md shadow-teal-500/20 flex items-center gap-1.5 cursor-pointer"
                      >
                        <Check size={14} />
                        <span>Save Template to Active Flow</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Placeholder when no template is selected for editing */
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-8 border border-dashed border-teal-500/20 rounded-3xl bg-[#091510]/50 space-y-3">
                    <Zap size={32} className="text-teal-400" />
                    <div className="text-sm font-bold text-white">Select a Template to Edit or Create a New One</div>
                    <p className="text-xs text-slate-400 max-w-sm">
                      Choose any template from the left to edit triggers, attach products or media, and test live WhatsApp response simulations.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setPortalPreFilledEditingId('new');
                        setPortalPreFilledDraft({
                          id: 'tmpl_' + Date.now(),
                          title: '',
                          triggerType: 'keyword_match',
                          triggerKeywords: [],
                          messageText: '',
                          isEnabled: true,
                        });
                        setPortalPreFilledKeywordsInput('');
                      }}
                      className="px-4 py-2 rounded-xl bg-teal-500/20 text-teal-300 border border-teal-500/30 hover:bg-teal-500/30 text-xs font-bold flex items-center gap-1.5 cursor-pointer"
                    >
                      <Plus size={13} />
                      <span>Create New Template</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* ═══════════════════════════════════════════════════════════════ */
          /* VIEW 9B-2: ORDERS & LIVE COURIER TRACKING STUDIO                */
          /* ═══════════════════════════════════════════════════════════════ */
          <div className="flex-1 flex flex-col overflow-hidden bg-[#070E0B]">
            {/* Order KPI Bar */}
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 px-6 py-4 border-b border-emerald-500/20 bg-[#0B1712]/90 shrink-0">
              <div className="p-3 rounded-2xl bg-[#0F261C] border border-emerald-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Total Orders</span>
                  <span className="text-xl font-black text-white font-mono mt-0.5 block">{physicalOrdersList.length}</span>
                </div>
                <Package size={18} className="text-emerald-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#0F261C] border border-sky-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Confirmed</span>
                  <span className="text-xl font-black text-sky-300 font-mono mt-0.5 block">
                    {physicalOrdersList.filter((o) => (orderEdits[o.id]?.status || o.status) === 'confirmed').length}
                  </span>
                </div>
                <CheckCircle2 size={18} className="text-sky-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#0F261C] border border-amber-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Processing</span>
                  <span className="text-xl font-black text-amber-300 font-mono mt-0.5 block">
                    {physicalOrdersList.filter((o) => (orderEdits[o.id]?.status || o.status) === 'processing').length}
                  </span>
                </div>
                <Clock size={18} className="text-amber-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#0F261C] border border-purple-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Dispatched</span>
                  <span className="text-xl font-black text-purple-300 font-mono mt-0.5 block">
                    {physicalOrdersList.filter((o) => (orderEdits[o.id]?.status || o.status) === 'dispatched').length}
                  </span>
                </div>
                <Truck size={18} className="text-purple-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#0F261C] border border-teal-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Out for Delivery</span>
                  <span className="text-xl font-black text-teal-300 font-mono mt-0.5 block">
                    {physicalOrdersList.filter((o) => (orderEdits[o.id]?.status || o.status) === 'out_for_delivery').length}
                  </span>
                </div>
                <Navigation size={18} className="text-teal-400" />
              </div>

              <div className="p-3 rounded-2xl bg-[#0F261C] border border-emerald-500/30 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono font-bold text-slate-400 block">Delivered</span>
                  <span className="text-xl font-black text-emerald-300 font-mono mt-0.5 block">
                    {physicalOrdersList.filter((o) => (orderEdits[o.id]?.status || o.status) === 'delivered').length}
                  </span>
                </div>
                <CheckCheck size={18} className="text-emerald-400" />
              </div>
            </div>

            {/* Filter & Search Bar */}
            <div className="px-6 py-3 border-b border-emerald-500/20 bg-[#0B1712]/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-1.5 overflow-x-auto max-w-full pb-1 sm:pb-0">
                {['all', 'confirmed', 'processing', 'dispatched', 'out_for_delivery', 'delivered', 'cancelled'].map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => {
                      setOrdersFilterStatus(st);
                      fetchTenantOrders(st, ordersSearchQuery);
                    }}
                    className={`px-3 py-1 rounded-xl text-[11px] font-mono transition cursor-pointer shrink-0 ${
                      ordersFilterStatus === st
                        ? 'bg-emerald-500 text-slate-950 font-bold'
                        : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                    }`}
                  >
                    {st === 'all' ? 'All Orders' : st.replace(/_/g, ' ').toUpperCase()}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={ordersSearchQuery}
                    onChange={(e) => {
                      setOrdersSearchQuery(e.target.value);
                      fetchTenantOrders(ordersFilterStatus, e.target.value);
                    }}
                    placeholder="Search order#, customer, phone..."
                    className="w-full pl-9 pr-3 py-1.5 bg-[#08120E] border border-emerald-500/30 focus:border-emerald-400 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => fetchTenantOrders()}
                  className="p-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-300 transition cursor-pointer shrink-0"
                  title="Refresh Orders"
                >
                  <RefreshCw size={14} className={isLoadingOrders ? 'animate-spin text-emerald-400' : ''} />
                </button>
              </div>
            </div>

            {/* Orders List Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 scrollbar-thin scrollbar-thumb-emerald-950">
              {isLoadingOrders && physicalOrdersList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-400 space-y-3">
                  <Loader2 size={32} className="animate-spin text-emerald-400" />
                  <span className="text-xs font-mono">Loading Orders & Tracking records...</span>
                </div>
              ) : filteredPhysicalOrders.length === 0 ? (
                <div className="text-center py-20 border border-dashed border-emerald-500/20 rounded-3xl bg-[#0B1712]/40 max-w-2xl mx-auto p-8 space-y-3">
                  <Package size={48} className="mx-auto text-emerald-500/40" />
                  <h3 className="text-base font-bold text-white">No Physical Delivery Orders Yet</h3>
                  <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
                    When customers ask to buy physical products with delivery addresses via WhatsApp, Live SIP Calls, or Web Avatar Chat, Luna AI autonomously records their order and address here.
                  </p>
                  <div className="pt-2">
                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-lg border border-emerald-500/30">
                      ⚡ Autonomous Order Engine Ready & Listening
                    </span>
                  </div>
                </div>
              ) : (
                filteredPhysicalOrders.map((order: any) => {
                  const edit = orderEdits[order.id] || {};
                  const currentStatus = edit.status || order.status;
                  const courierComp = edit.courierCompany !== undefined ? edit.courierCompany : (order.courierCompany || '');
                  const trackingNum = edit.courierTrackingNumber !== undefined ? edit.courierTrackingNumber : (order.courierTrackingNumber || '');
                  const trackingUrl = edit.courierTrackingUrl !== undefined ? edit.courierTrackingUrl : (order.courierTrackingUrl || '');
                  const estDelivery = edit.estimatedDelivery !== undefined ? edit.estimatedDelivery : (order.estimatedDelivery || '');
                  const orderItems = Array.isArray(order.items) ? order.items : [];

                  const statusBadgeColors: Record<string, string> = {
                    confirmed: 'bg-sky-500/15 border-sky-500/30 text-sky-300',
                    processing: 'bg-amber-500/15 border-amber-500/30 text-amber-300',
                    dispatched: 'bg-purple-500/15 border-purple-500/30 text-purple-300',
                    out_for_delivery: 'bg-teal-500/15 border-teal-500/30 text-teal-300',
                    delivered: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300',
                    cancelled: 'bg-rose-500/15 border-rose-500/30 text-rose-300',
                  };

                  const channelIcon = order.channel === 'sip_call' ? <PhoneCall size={12} className="text-purple-400" /> : order.channel === 'web_avatar' ? <Bot size={12} className="text-cyan-400" /> : <Smartphone size={12} className="text-emerald-400" />;

                  return (
                    <div
                      key={order.id}
                      className="p-5 rounded-2xl bg-[#0E2018]/90 border border-emerald-500/30 shadow-xl space-y-4 hover:border-emerald-400/60 transition"
                    >
                      {/* Order Header */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5 pb-3 border-b border-emerald-500/20">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-sm font-black text-emerald-300 font-mono tracking-wider flex items-center gap-1.5">
                            <Package size={16} className="text-emerald-400" />
                            {order.orderNumber}
                          </span>
                          <button
                            type="button"
                            onClick={() => copyToClipboard(order.orderNumber, 'Order Number')}
                            className="p-1 rounded bg-slate-900/60 hover:bg-slate-800 text-slate-400 hover:text-white transition cursor-pointer"
                            title="Copy Order ID"
                          >
                            <Copy size={11} />
                          </button>
                          {(order.metadata?.orderType === 'booking' || order.metadata?.orderType === 'rental' || order.orderNumber?.startsWith('BKG-')) ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-black border uppercase bg-teal-500/20 border-teal-500/40 text-teal-300 flex items-center gap-1 shadow-sm shadow-teal-500/10">
                              🚗 CAB / VEHICLE BOOKING
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold border uppercase bg-emerald-500/10 border-emerald-500/20 text-emerald-400">
                              🛍️ E-COMMERCE ORDER
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold border uppercase ${statusBadgeColors[currentStatus] || 'bg-slate-800 text-slate-300'}`}>
                            {currentStatus?.replace(/_/g, ' ')}
                          </span>
                        </div>

                        <div className="flex items-center gap-3 text-[11px] font-mono text-slate-400">
                          <span className="flex items-center gap-1">
                            {channelIcon}
                            <span>{order.channel?.toUpperCase() || 'WHATSAPP'}</span>
                          </span>
                          <span>•</span>
                          <span>{new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>

                      {/* Customer & Items Split Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                        {/* Customer & Address / Booking Info (5 cols) */}
                        <div className="md:col-span-5 p-3.5 rounded-xl bg-[#08120E] border border-slate-800 space-y-2">
                          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                            {(order.metadata?.orderType === 'booking' || order.metadata?.orderType === 'rental' || order.orderNumber?.startsWith('BKG-'))
                              ? '🚗 Customer & Booking Schedule'
                              : 'Customer & Delivery Details'}
                          </span>
                          <div className="text-xs font-bold text-white">{order.customerName}</div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-emerald-400">+{order.customerPhone}</span>
                            <a
                              href={`https://wa.me/${order.customerPhone}`}
                              target="_blank"
                              rel="noreferrer"
                              className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 text-[10px] font-mono flex items-center gap-1 border border-emerald-500/30"
                            >
                              <Smartphone size={10} />
                              <span>WhatsApp</span>
                            </a>
                          </div>

                          {/* Specific Booking Schedule Pills */}
                          {order.metadata?.bookingDates && (
                            <div className="text-xs text-teal-300 font-mono font-bold bg-teal-950/40 p-2 rounded-lg border border-teal-500/30 flex items-center gap-1.5">
                              <span>🗓️ Dates:</span>
                              <span className="text-white">{order.metadata.bookingDates}</span>
                            </div>
                          )}

                          {(order.metadata?.rentalDuration || order.metadata?.packageType) && (
                            <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono">
                              {order.metadata?.rentalDuration && (
                                <span className="px-2 py-0.5 rounded bg-slate-900 border border-slate-700 text-slate-300">
                                  ⏱️ {order.metadata.rentalDuration}
                                </span>
                              )}
                              {order.metadata?.packageType && (
                                <span className="px-2 py-0.5 rounded bg-indigo-950/40 border border-indigo-500/30 text-indigo-300">
                                  📦 {order.metadata.packageType}
                                </span>
                              )}
                            </div>
                          )}

                          <div className="text-xs text-slate-300 flex items-start gap-1.5 pt-1">
                            <MapPin size={13} className="text-rose-400 shrink-0 mt-0.5" />
                            <span>{order.deliveryAddress}{order.city ? `, ${order.city}` : ''}</span>
                          </div>
                          {order.notes && (
                            <div className="text-[11px] text-amber-300/80 bg-amber-500/10 p-2 rounded-lg border border-amber-500/20">
                              📝 Notes: {order.notes}
                            </div>
                          )}
                        </div>

                        {/* Ordered Items (7 cols) */}
                        <div className="md:col-span-7 p-3.5 rounded-xl bg-[#08120E] border border-slate-800 flex flex-col justify-between">
                          <div>
                            <div className="flex items-center justify-between pb-2 border-b border-slate-800/80">
                              <span className="text-[10px] uppercase font-mono font-bold text-slate-400 tracking-wider">
                                Items ({orderItems.length})
                              </span>
                              <span className="text-xs font-mono font-black text-emerald-400">
                                Total: {order.currency || 'LKR'} {Number(order.totalAmount).toLocaleString()}
                              </span>
                            </div>

                            <div className="space-y-1.5 pt-2 max-h-28 overflow-y-auto scrollbar-thin">
                              {orderItems.map((it: any, idx: number) => (
                                <div key={idx} className="flex items-center justify-between text-xs text-slate-300">
                                  <span>
                                    <span className="font-bold text-emerald-300">{it.quantity}x</span> {it.product_name}
                                  </span>
                                  <span className="font-mono text-slate-400">
                                    {order.currency || 'LKR'} {Number(it.subtotal || (it.unit_price * it.quantity)).toLocaleString()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="flex items-center justify-between pt-2 border-t border-slate-800/80 mt-2 text-[11px] font-mono text-slate-400">
                            <span>Payment: <strong className="text-white uppercase">{order.paymentMethod?.replace(/_/g, ' ')}</strong></span>
                            <span className={`px-2 py-0.5 rounded text-[10px] ${order.paymentStatus === 'paid' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                              {order.paymentStatus?.toUpperCase() || 'PENDING'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Courier Partner & Live Tracking Cyber-Panel */}
                      <div className="p-4 rounded-xl bg-[#0A1A13] border border-emerald-500/25 space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-emerald-300 flex items-center gap-1.5 font-mono">
                            <Truck size={14} className="text-emerald-400" />
                            Courier Partner & Live Tracking Configuration
                          </span>
                          <span className="text-[10px] font-mono text-slate-400">
                            Client tracking viewable over WhatsApp & Web
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2.5">
                          {/* Status Selector */}
                          <div>
                            <label className="text-[9px] uppercase font-mono font-bold text-slate-400 block mb-1">
                              Order Status
                            </label>
                            <select
                              value={currentStatus}
                              onChange={(e) => {
                                const val = e.target.value;
                                setOrderEdits(prev => ({ ...prev, [order.id]: { ...(prev[order.id] || {}), status: val } }));
                              }}
                              className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none font-mono"
                            >
                              <option value="confirmed">Confirmed (New)</option>
                              <option value="processing">Processing in Warehouse</option>
                              <option value="dispatched">Dispatched to Courier</option>
                              <option value="out_for_delivery">Out for Delivery</option>
                              <option value="delivered">Delivered</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>

                          {/* Courier Partner */}
                          <div>
                            <label className="text-[9px] uppercase font-mono font-bold text-slate-400 block mb-1">
                              Courier Partner
                            </label>
                            <input
                              type="text"
                              list={`courier-list-${order.id}`}
                              value={courierComp}
                              onChange={(e) => {
                                const val = e.target.value;
                                setOrderEdits(prev => ({ ...prev, [order.id]: { ...(prev[order.id] || {}), courierCompany: val } }));
                              }}
                              placeholder="e.g. Domex / Prompt"
                              className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none font-mono"
                            />
                            <datalist id={`courier-list-${order.id}`}>
                              <option value="Domex" />
                              <option value="Prompt Xpress" />
                              <option value="Koombiyo" />
                              <option value="Pronto" />
                              <option value="DHL" />
                              <option value="Certis Lanka" />
                              <option value="Fardar" />
                              <option value="Citypak" />
                            </datalist>
                          </div>

                          {/* Tracking Number */}
                          <div>
                            <label className="text-[9px] uppercase font-mono font-bold text-slate-400 block mb-1">
                              Tracking / Waybill #
                            </label>
                            <input
                              type="text"
                              value={trackingNum}
                              onChange={(e) => {
                                const val = e.target.value;
                                setOrderEdits(prev => ({ ...prev, [order.id]: { ...(prev[order.id] || {}), courierTrackingNumber: val } }));
                              }}
                              placeholder="e.g. DOM-948210"
                              className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none font-mono"
                            />
                          </div>

                          {/* Estimated Delivery */}
                          <div>
                            <label className="text-[9px] uppercase font-mono font-bold text-slate-400 block mb-1">
                              Estimated Delivery
                            </label>
                            <input
                              type="text"
                              value={estDelivery}
                              onChange={(e) => {
                                const val = e.target.value;
                                setOrderEdits(prev => ({ ...prev, [order.id]: { ...(prev[order.id] || {}), estimatedDelivery: val } }));
                              }}
                              placeholder="e.g. Tomorrow by 5 PM"
                              className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none font-mono"
                            />
                          </div>
                        </div>

                        {/* Tracking URL input */}
                        <div>
                          <label className="text-[9px] uppercase font-mono font-bold text-slate-400 block mb-1">
                            Courier Tracking URL (Optional Web Link)
                          </label>
                          <input
                            type="text"
                            value={trackingUrl}
                            onChange={(e) => {
                              const val = e.target.value;
                              setOrderEdits(prev => ({ ...prev, [order.id]: { ...(prev[order.id] || {}), courierTrackingUrl: val } }));
                            }}
                            placeholder="https://track.domex.lk?ref=DOM-948210"
                            className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-400 rounded-lg px-2.5 py-1 text-xs text-white focus:outline-none font-mono"
                          />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-800/80">
                          <button
                            type="button"
                            disabled={isUpdatingTracking}
                            onClick={() => handleUpdateOrderTracking(order.id, {
                              status: currentStatus,
                              courierCompany: courierComp,
                              courierTrackingNumber: trackingNum,
                              courierTrackingUrl: trackingUrl,
                              estimatedDelivery: estDelivery,
                            })}
                            className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 transition cursor-pointer shadow-md shadow-emerald-500/20 active:scale-95"
                          >
                            <Save size={13} />
                            <span>{isUpdatingTracking ? 'Saving...' : 'Save Tracking'}</span>
                          </button>

                          <button
                            type="button"
                            disabled={isNotifyingCustomer === order.id}
                            onClick={() => handleSendCustomerTrackingWhatsApp(order.id)}
                            className="px-3.5 py-1.5 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 font-bold text-xs flex items-center gap-1.5 transition cursor-pointer active:scale-95"
                            title="Send live WhatsApp tracking message to customer"
                          >
                            <Send size={13} />
                            <span>{isNotifyingCustomer === order.id ? 'Sending WhatsApp...' : '📲 Send WhatsApp to Customer'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      ) : selectedModule === 'whatsapp_web' ? (
        /* ═══════════════════════════════════════════════════════════════ */
        /* VIEW: 100% AUTHENTIC WHATSAPP WEB & MOBILE CONSOLE             */
        /* ═══════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col overflow-hidden bg-[#0c1317] p-2 md:p-4">
          <KangavenaWhatsAppWeb
            token={token}
            tenant={tenant}
            initialSessionId={selectedSessionId}
            onClose={() => setSelectedModule('whatsapp_instance')}
          />
        </div>
      ) : selectedModule === 'whatsapp_instance' ? (
        /* ═══════════════════════════════════════════════════════════════ */
        /* VIEW 4: WHATSAPP INSTANCE & GATEWAY FULL-SCREEN STUDIO          */
        /* ═══════════════════════════════════════════════════════════════ */
        <div className="flex-1 flex flex-col overflow-hidden bg-[#070D0A]">
          
          {/* WhatsApp Top KPI Strip */}
          {(() => {
            const isOfficial = whatsAppData?.apiMode === 'official';
            const isMetaConnected = Boolean(isOfficial && whatsAppData?.metaPhoneNumberId);
            const isEvoConnected = Boolean(!isOfficial && (whatsAppData?.connectionStatus === 'connected' || stats.whatsappConnected));
            const isConnected = isOfficial ? isMetaConnected : isEvoConnected;

            const effectiveTenantId = tenant?.apiKey || tenant?.tenantId || (tenant?.id ? String(tenant.id) : '');
            const baseUrl = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'https://kangavena.com';
            const universalWebhookUrl = `${baseUrl}/api/tenet/webhook/whatsapp`;
            const dedicatedWebhookUrl = `${baseUrl}/api/tenet/webhook/whatsapp/${effectiveTenantId || 'YOUR_TENANT_KEY'}`;
            const webhookUrl = dedicatedWebhookUrl;
            const defaultTenantVerifyToken = effectiveTenantId ? `kangavena_wa_${effectiveTenantId.slice(0, 16)}` : 'kangavena_wa_verify_token';
            const rawToken = String(whatsAppData?.metaVerifyToken || '').trim();
            const lowerToken = rawToken.toLowerCase();
            const isGenericToken = !rawToken || lowerToken === 'kangavena' || lowerToken === 'kangavena_wa' || lowerToken === 'kangavena_wa_verify_token' || lowerToken === 'token' || lowerToken === 'default' || lowerToken === 'kangavena_wa_template';
            
            let isAlienToken = false;
            if (rawToken.startsWith('kangavena_wa_') && effectiveTenantId) {
              const clean8 = effectiveTenantId.replace(/^TENET_/i, '').slice(0, 8);
              const idPart = tenant?.id ? String(tenant.id) : '';
              if (!rawToken.includes(effectiveTenantId.slice(0, 8)) && (clean8 && !rawToken.includes(clean8)) && (!idPart || !rawToken.includes(idPart))) {
                isAlienToken = true;
              }
            }

            const isStaleOrForeignToken = isGenericToken || isAlienToken;
            const verifyToken = isStaleOrForeignToken ? defaultTenantVerifyToken : (whatsAppData?.metaVerifyToken || defaultTenantVerifyToken);

            const isOldDummyAppId = whatsAppData?.sipAppId === '2463786290783462';
            const currentAppId = (whatsAppData?.sipAppId && !isOldDummyAppId) ? whatsAppData.sipAppId : KANGAVENA_OFFICIAL_META_APP_ID;
            const isCustomIsolatedApp = Boolean(whatsAppData?.sipAppId && whatsAppData.sipAppId !== KANGAVENA_OFFICIAL_META_APP_ID && !isOldDummyAppId);

            const sipServerHost = whatsAppData?.sipServerHost || 'wa.kangavena.com';
            const sipPort = whatsAppData?.sipPort || 5061;
            const sipParamName = whatsAppData?.sipCustomParamName || 'tenant_id';
            const sipParamValue = whatsAppData?.sipCustomParamValue || (tenant?.id ? String(tenant.id) : (effectiveTenantId || '11'));
            const enableSipCalling = whatsAppData?.enableSipCalling ?? true;

            const copyToClipboard = (text: string, type: 'url' | 'token' | 'universal' | 'appId' | 'sipHost' | 'sipPort' | 'sipParamName' | 'sipParamValue' | 'mobileLink') => {
              if (typeof navigator !== 'undefined' && navigator.clipboard) {
                navigator.clipboard.writeText(text);
                if (type === 'url') {
                  setCopiedMetaWebhookUrl(true);
                  setTimeout(() => setCopiedMetaWebhookUrl(false), 2500);
                } else if (type === 'universal') {
                  setCopiedUniversalWebhookUrl(true);
                  setTimeout(() => setCopiedUniversalWebhookUrl(false), 2500);
                } else if (type === 'token') {
                  setCopiedMetaVerifyToken(true);
                  setTimeout(() => setCopiedMetaVerifyToken(false), 2500);
                } else if (type === 'appId') {
                  setCopiedAppId(true);
                  setTimeout(() => setCopiedAppId(false), 2500);
                } else if (type === 'sipHost') {
                  setCopiedSipHost(true);
                  setTimeout(() => setCopiedSipHost(false), 2500);
                } else if (type === 'sipPort') {
                  setCopiedSipPort(true);
                  setTimeout(() => setCopiedSipPort(false), 2500);
                } else if (type === 'sipParamName') {
                  setCopiedSipParamName(true);
                  setTimeout(() => setCopiedSipParamName(false), 2500);
                } else if (type === 'sipParamValue') {
                  setCopiedSipParamValue(true);
                  setTimeout(() => setCopiedSipParamValue(false), 2500);
                } else if (type === 'mobileLink') {
                  setCopiedMobileLink(true);
                  setTimeout(() => setCopiedMobileLink(false), 2500);
                }
              }
            };

            return (
              <>
                {/* Cyber Multi-Instance Switcher Bar */}
                {whatsAppInstances.length > 1 && (
                  <div className="px-6 py-2.5 bg-[#08120D] border-b border-emerald-500/20 flex flex-wrap items-center justify-between gap-3 shrink-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 shrink-0">
                        <Smartphone size={14} className="text-emerald-400" />
                        <span>Deployed Instances ({whatsAppInstances.length}):</span>
                      </span>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {whatsAppInstances.map((inst: any, idx: number) => {
                          const isSelected = (selectedWhatsAppNodeId === inst.nodeId) || (!selectedWhatsAppNodeId && idx === 0);
                          const isPersonal = inst.isPersonalWhatsApp === true;
                          const rawP = inst.phoneNumber || '';
                          const formattedP = rawP ? (String(rawP).startsWith('+') ? String(rawP) : `+${rawP}`) : '';
                          const customNick = (inst.displayName && !inst.displayName.startsWith('tenet_wa_'))
                            ? inst.displayName
                            : (inst.instanceLabel && !inst.instanceLabel.startsWith('tenet_wa_'))
                            ? inst.instanceLabel
                            : (inst.instanceName && !inst.instanceName.startsWith('tenet_wa_'))
                            ? inst.instanceName
                            : '';
                          const displayTabLabel = formattedP
                            ? (customNick ? `${formattedP} (${customNick})` : formattedP)
                            : (customNick || (isPersonal ? 'Personal WhatsApp' : `Line ${idx + 1}`));
                          const isConn = inst.connectionStatus === 'connected' || (idx === 0 && stats.whatsappConnected);
                          const chatCount = whatsAppOnlyConversations.filter((c: any) => isChatMatchingSelectedInstance(c, inst.nodeId, inst.phoneNumber, inst.metaPhoneNumberId)).length;

                          return (
                            <button
                              key={inst.nodeId || idx}
                              type="button"
                              onClick={() => {
                                setSelectedWhatsAppNodeId(inst.nodeId);
                                setWhatsAppData(inst);
                                setWhatsAppQrCode(inst.qrCode || null);
                              }}
                              className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all flex items-center gap-2 cursor-pointer ${
                                isSelected
                                  ? 'bg-emerald-500 text-slate-950 shadow-[0_0_15px_rgba(16,185,129,0.5)] scale-105'
                                  : 'bg-slate-900/90 text-slate-300 border border-slate-700/60 hover:border-emerald-500/40 hover:text-white'
                              }`}
                            >
                              <span className={`w-2 h-2 rounded-full ${isConn ? (isSelected ? 'bg-slate-950' : 'bg-emerald-400') : (isSelected ? 'bg-amber-950' : 'bg-amber-400')}`} />
                              <span>{displayTabLabel}</span>
                              <span className={`text-[9px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                                isSelected ? 'bg-slate-950/40 text-slate-950' : 'bg-emerald-500/20 text-emerald-300'
                              }`}>
                                {chatCount}
                              </span>
                              {isPersonal && (
                                <span className={`text-[8px] px-1 py-0.2 rounded font-mono uppercase ${
                                  isSelected ? 'bg-slate-950/30 text-slate-950' : 'bg-emerald-500/20 text-emerald-300'
                                }`}>
                                  Personal
                                </span>
                              )}
                            </button>
                          );
                        })}

                        {/* All Lines Combined Tab */}
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedWhatsAppNodeId('all');
                          }}
                          className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                            selectedWhatsAppNodeId === 'all'
                              ? 'bg-emerald-500 text-slate-950 shadow-[0_0_15px_rgba(16,185,129,0.5)] scale-105'
                              : 'bg-slate-900/90 text-slate-300 border border-slate-700/60 hover:border-emerald-500/40 hover:text-white'
                          }`}
                        >
                          <span>All Lines</span>
                          <span className={`text-[9px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                            selectedWhatsAppNodeId === 'all' ? 'bg-slate-950/40 text-slate-950' : 'bg-emerald-500/20 text-emerald-300'
                          }`}>
                            {whatsAppOnlyConversations.length}
                          </span>
                        </button>
                      </div>
                    </div>
                    <div className="text-[10px] font-mono text-slate-400 flex items-center gap-2">
                      <span>Configuring:</span>
                      <span className="text-emerald-300 font-bold px-2 py-0.5 rounded bg-emerald-950/60 border border-emerald-500/30">
                        {selectedWhatsAppNodeId === 'all'
                          ? `All Lines (${whatsAppInstances.length})`
                          : (whatsAppData?.phoneNumber ? (String(whatsAppData.phoneNumber).startsWith('+') ? String(whatsAppData.phoneNumber) : `+${whatsAppData.phoneNumber}`) : (whatsAppData?.displayName || whatsAppData?.instanceName || whatsAppData?.nodeId || 'Active Instance'))}
                      </span>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5 px-6 py-4 border-b border-emerald-500/20 bg-[#0B1510]/90 shrink-0">
                  {/* Card 1: Phone Identifier */}
                  <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F2218] to-[#0A1610] border border-emerald-500/30 shadow-lg flex items-center justify-between">
                    <div>
                      <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                        {isOfficial ? 'Meta Phone ID / Number' : 'Linked Phone Number'}
                      </span>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-lg font-black text-white font-sans truncate max-w-[150px]">
                          {isOfficial 
                            ? (whatsAppData?.phoneNumber ? `+${whatsAppData.phoneNumber}` : (whatsAppData?.metaPhoneNumberId || 'Meta Cloud API'))
                            : (whatsAppData?.phoneNumber || stats.whatsappPhone ? `+${whatsAppData?.phoneNumber || stats.whatsappPhone}` : 'Not Linked')
                          }
                        </span>
                      </div>
                      <span className="text-[9px] text-emerald-400 font-mono">
                        {isConnected 
                          ? (isOfficial ? '✅ Meta WABA Official Active' : '✅ Multi-Device Active')
                          : (isOfficial ? '⚠️ Meta Setup Required' : '⚠️ Offline / Disconnected')
                        }
                      </span>
                    </div>
                    <div className={`p-2.5 rounded-xl border ${isOfficial ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-400' : 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'}`}>
                      {isOfficial ? <Building2 size={20} /> : <Smartphone size={20} />}
                    </div>
                  </div>

                  {/* Card 2: Gateway Status */}
                  <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F2218] to-[#0A1610] border border-emerald-500/30 shadow-lg flex items-center justify-between">
                    <div>
                      <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                        {isOfficial ? 'Official Meta Cloud API' : 'Evolution API Status'}
                      </span>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`w-2.5 h-2.5 rounded-full ${
                          isConnected ? 'bg-emerald-400 animate-ping' : 'bg-rose-400'
                        }`} />
                        <span className="text-sm font-black text-white font-mono">
                          {isConnected ? (isOfficial ? 'ONLINE (META TLS)' : 'ONLINE (OPEN)') : 'DISCONNECTED'}
                        </span>
                      </div>
                      <span className="text-[9px] text-slate-400 font-mono truncate max-w-[150px] block">
                        {isOfficial ? `WABA: ${whatsAppData?.metaWabaId || 'Configured'}` : (whatsAppData?.displayName || (whatsAppData?.instanceName && !whatsAppData.instanceName.startsWith('tenet_wa_') ? whatsAppData.instanceName : `Node: ${whatsAppData?.nodeId || 'primary'}`))}
                      </span>
                    </div>
                    <div className={`p-2.5 rounded-xl border ${isOfficial ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-400' : 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'}`}>
                      <Wifi size={20} />
                    </div>
                  </div>

                  {/* Card 3: Inquiries */}
                  <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F2218] to-[#0A1610] border border-emerald-500/30 shadow-lg flex items-center justify-between">
                    <div>
                      <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                        WhatsApp Inquiries
                      </span>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-xl font-black text-white font-sans">
                          {activeInstanceWhatsAppConversations.length}
                        </span>
                        <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.2 rounded">
                          SESSIONS
                        </span>
                      </div>
                      <span className="text-[9px] text-slate-400 font-mono">
                        {stats.totalTokensUsed.toLocaleString()} tokens used
                      </span>
                    </div>
                    <div className="p-2.5 rounded-xl bg-teal-500/20 border border-teal-500/40 text-teal-400">
                      <MessageSquare size={20} />
                    </div>
                  </div>

                  {/* Card 4: Leads */}
                  <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F2218] to-[#0A1610] border border-emerald-500/30 shadow-lg flex items-center justify-between">
                    <div>
                      <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                        WhatsApp Leads
                      </span>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-xl font-black text-emerald-300 font-sans">
                          {stats.whatsappLeads || leadsList.filter((l) => l.formType === 'whatsapp_lead' || l.formType === 'whatsapp').length}
                        </span>
                        <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.2 rounded">
                          CAPTURED
                        </span>
                      </div>
                      <span className="text-[9px] text-slate-400 font-mono">
                        Direct phone leads
                      </span>
                    </div>
                    <div className="p-2.5 rounded-xl bg-purple-500/20 border border-purple-500/40 text-purple-400">
                      <Users size={20} />
                    </div>
                  </div>

                  {/* Card 5: AI Auto-Reply Switch */}
                  <div className={`p-3.5 rounded-2xl border shadow-lg flex items-center justify-between transition-all ${
                    whatsAppData?.autoReply !== false
                      ? 'bg-gradient-to-br from-[#0F2218] to-[#0A1610] border-emerald-500/40 shadow-[0_0_20px_rgba(16,185,129,0.15)]'
                      : 'bg-gradient-to-br from-[#220F12] to-[#160A0C] border-rose-500/40 shadow-[0_0_20px_rgba(244,63,94,0.15)]'
                  }`}>
                    <div>
                      <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
                        AI Auto-Reply
                      </span>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-sm font-black font-mono ${whatsAppData?.autoReply !== false ? 'text-emerald-300' : 'text-rose-300'}`}>
                          {whatsAppData?.autoReply !== false ? 'ENABLED' : 'PAUSED'}
                        </span>
                        <button
                          type="button"
                          onClick={handleToggleAutoReply}
                          className={`px-2.5 py-1 rounded-lg text-[9px] font-mono font-black border transition-all cursor-pointer flex items-center gap-1.5 active:scale-95 ${
                            whatsAppData?.autoReply !== false
                              ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/30 hover:border-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.3)]'
                              : 'bg-rose-500/20 text-rose-300 border-rose-500/40 hover:bg-rose-500/30 hover:border-rose-400 shadow-[0_0_12px_rgba(244,63,94,0.3)]'
                          }`}
                          title={whatsAppData?.autoReply !== false ? 'Click to Pause Luna Auto-Reply on WhatsApp' : 'Click to Enable Luna Auto-Reply on WhatsApp'}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${whatsAppData?.autoReply !== false ? 'bg-emerald-400 animate-pulse' : 'bg-rose-400'}`} />
                          <span>{whatsAppData?.autoReply !== false ? 'PAUSE AI' : 'ENABLE AI'}</span>
                        </button>
                      </div>
                      <span className="text-[9px] text-slate-400 font-mono truncate max-w-[150px] block mt-0.5">
                        Style: {whatsAppData?.whatsappFormattingStyle || 'sales_promo'}
                      </span>
                    </div>
                    <div className={`p-2.5 rounded-xl border ${whatsAppData?.autoReply !== false ? 'bg-emerald-500/20 border-emerald-400/40 text-emerald-400' : 'bg-rose-500/20 border-rose-400/40 text-rose-400'}`}>
                      <Bot size={20} className={whatsAppData?.autoReply !== false ? 'animate-pulse' : ''} />
                    </div>
                  </div>
                </div>

                {/* WhatsApp Studio Main Layout */}
                <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                  {/* Left Column: Device Linking & Operational Configurations */}
                  <div className="w-full lg:w-1/2 border-r border-emerald-500/20 bg-[#0A140F]/95 p-3 sm:p-6 overflow-y-auto space-y-4 sm:space-y-6 tenet-mobile-scroll">
                    
                    {/* 🧪 Real-Time WhatsApp AI Simulator (Zero-Connect Testing) */}
                    <div className="p-4 rounded-2xl bg-gradient-to-r from-[#0d2826] via-[#091f1c] to-[#071714] border border-cyan-400/40 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-cyan-500/20 border border-cyan-400/40 flex items-center justify-center text-cyan-300 shadow-md shrink-0">
                          <Sparkles size={20} className="animate-pulse" />
                        </div>
                        <div>
                          <h4 className="text-xs font-black text-white tracking-wide flex items-center gap-2">
                            <span>Real-Time WhatsApp AI Simulator</span>
                            <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                              ZERO-CONNECT TEST
                            </span>
                          </h4>
                          <p className="text-[11px] text-slate-300 mt-0.5 leading-relaxed">
                            Test Luna AI in real-time without connecting a WhatsApp account. Switch between <strong className="text-amber-300">👑 Store Owner Mode</strong> and <strong className="text-emerald-300">👤 Customer Mode</strong>.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
                        <button
                          type="button"
                          onClick={() => setShowWhatsAppSimulatorModal(true)}
                          className="w-full md:w-auto px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-slate-950 font-black text-xs flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(34,211,238,0.5)] hover:scale-105 active:scale-95 transition cursor-pointer"
                        >
                          <Sparkles size={14} />
                          <span>LAUNCH SIMULATOR</span>
                        </button>
                      </div>
                    </div>

                    {/* 🚀 Launch 100% Authentic WhatsApp Web Console Banner */}
                    <div className="p-4 rounded-2xl bg-gradient-to-r from-[#0F2E22] via-[#0A1F17] to-[#0A1610] border border-emerald-400/40 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-400 shadow-md shrink-0">
                          <Smartphone size={20} />
                        </div>
                        <div>
                          <h4 className="text-xs font-black text-white tracking-wide flex items-center gap-2">
                            <span>Kangavena WhatsApp Web Console</span>
                            <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                              NATIVE WA FEEL
                            </span>
                          </h4>
                          <p className="text-[11px] text-slate-300 mt-0.5 leading-relaxed">
                            Official Meta Cloud API Web & Mobile console. Direct mobile URL: <span className="text-emerald-300 font-mono font-bold">kangavena.com/whatsapp</span>
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
                        <button
                          type="button"
                          onClick={() => copyToClipboard(`${window.location.origin}/whatsapp`, 'mobileLink')}
                          className="px-3 py-2 rounded-xl bg-[#111f19] hover:bg-[#1a3328] text-emerald-300 border border-emerald-500/40 font-mono text-xs flex items-center gap-1.5 transition cursor-pointer"
                          title="Copy Direct Mobile URL for staff & cashiers"
                        >
                          <Share2 size={13} />
                          <span>{copiedMobileLink ? 'Copied!' : 'Copy Mobile Link'}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm("⚠️ Are you sure you want to delete all WhatsApp chat logs? This will permanently remove conversation history from the database and wipe Luna AI's active memory context.")) {
                              fetch('/api/tenet/live-chats/delete-all', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                  ...(token ? { Authorization: `Bearer ${token}` } : {})
                                },
                                body: JSON.stringify({ channel: 'whatsapp' })
                              }).then(r => r.json()).then(d => {
                                if (d.success) alert(`✅ Successfully deleted ${d.deletedCount || 0} chats. Luna AI context reset!`);
                                else alert('Failed: ' + (d.error || 'Unknown error'));
                              }).catch(err => alert('Failed to delete chats: ' + err.message));
                            }
                          }}
                          className="px-3 py-2 rounded-xl bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 border border-rose-500/30 font-mono text-xs flex items-center gap-1.5 transition cursor-pointer"
                          title="Delete all WhatsApp chat history & reset Luna context"
                        >
                          <Trash2 size={13} className="text-rose-400" />
                          <span>Delete Chats</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedModule('whatsapp_web')}
                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-400 to-teal-400 hover:from-emerald-300 hover:to-teal-300 text-slate-950 font-black text-xs uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-emerald-500/30 active:scale-95 transition-all cursor-pointer"
                          title="Launch authentic WhatsApp Web Console"
                        >
                          <span>Open WA Web</span>
                          <ArrowRight size={13} className="text-slate-950" />
                        </button>
                      </div>
                    </div>

                    {/* Instance Label & Identifier (Multi-Instance support) */}
                    {(() => {
                      const currentNickname = whatsAppData?.displayName || whatsAppData?.instanceLabel || (whatsAppData?.instanceName?.startsWith('tenet_wa_') ? '' : whatsAppData?.instanceName) || '';
                      return (
                        <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0F2218] to-[#0A1610] border border-emerald-500/30 flex items-center justify-between gap-3 shadow-lg">
                          <div className="flex-1">
                            <label className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider mb-1">
                              Instance Nickname / Line Tag
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={currentNickname}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setWhatsAppData((prev: any) => ({ ...prev, instanceName: val, displayName: val, instanceLabel: val }));
                                  setWhatsAppInstances((prev) =>
                                    prev.map((inst) => inst.nodeId === (whatsAppData?.nodeId || 'whatsappNode-1') ? { ...inst, instanceName: val, displayName: val, instanceLabel: val } : inst)
                                  );
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveInstanceTitle();
                                  }
                                }}
                                placeholder={whatsAppData?.isPersonalWhatsApp ? 'Personal WhatsApp' : 'e.g. Sales Line, Branch Colombo'}
                                className="flex-1 px-3 py-1.5 rounded-xl bg-slate-950/80 border border-emerald-500/30 text-white font-mono text-xs focus:outline-none focus:border-emerald-400 placeholder-slate-600"
                              />
                              <button
                                type="button"
                                onClick={() => handleSaveInstanceTitle()}
                                disabled={isSavingInstanceTitle}
                                className={`px-3.5 py-1.5 rounded-xl text-xs font-mono font-bold flex items-center gap-1.5 transition-all cursor-pointer shrink-0 ${
                                  titleSavedSuccess
                                    ? 'bg-emerald-500 text-slate-950 shadow-[0_0_12px_rgba(16,185,129,0.5)]'
                                    : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 hover:border-emerald-400'
                                }`}
                                title="Save Nickname (Enter)"
                              >
                                {isSavingInstanceTitle ? (
                                  <>
                                    <RefreshCw size={12} className="animate-spin" />
                                    <span>Saving...</span>
                                  </>
                                ) : titleSavedSuccess ? (
                                  <>
                                    <CheckCircle2 size={12} className="text-slate-950" />
                                    <span>Saved!</span>
                                  </>
                                ) : (
                                  <>
                                    <Save size={12} />
                                    <span>Save Title</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="text-[9px] font-mono text-slate-500 block">Node ID</span>
                            <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-500/30">
                              {whatsAppData?.nodeId || 'primary'}
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Dual-Tier Engine Mode Switcher Tabs */}
                    <div className="flex p-1 bg-slate-950/90 rounded-2xl border border-white/10 w-full">
                      <button
                        type="button"
                        onClick={() => setWhatsAppData({ ...whatsAppData, apiMode: 'unofficial' })}
                        className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer ${
                          !isOfficial
                            ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm'
                            : 'text-slate-400 hover:text-slate-200'
                        }`}
                      >
                        <Smartphone size={14} className={!isOfficial ? 'text-emerald-400' : 'text-slate-500'} />
                        <span>Unofficial (Evolution API)</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setWhatsAppData({ ...whatsAppData, apiMode: 'official' })}
                        className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer ${
                          isOfficial
                            ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 text-cyan-300 border border-cyan-500/40 shadow-sm'
                            : 'text-slate-400 hover:text-slate-200'
                        }`}
                      >
                        <Building2 size={14} className={isOfficial ? 'text-cyan-400' : 'text-slate-500'} />
                        <span>Official (Meta Cloud API)</span>
                        <span className="text-[8px] font-mono font-black px-1.5 py-0.2 rounded bg-cyan-400/20 text-cyan-300 border border-cyan-400/30">WABA</span>
                      </button>
                    </div>

                    {/* TIER 1: OFFICIAL META CLOUD API CARD */}
                    {isOfficial ? (
                      <div className="rounded-2xl bg-gradient-to-br from-[#0B1522] to-[#070D18] border border-cyan-500/30 p-5 space-y-4 shadow-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Building2 size={16} className="text-cyan-400" />
                            <h3 className="text-sm font-black text-white font-sans">
                              Meta Cloud API (Official Enterprise Tier)
                            </h3>
                          </div>
                          <span className={`text-[9px] font-mono font-bold px-2.5 py-0.5 rounded-full border ${
                            isMetaConnected
                              ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40 shadow-[0_0_8px_rgba(6,182,212,0.3)]'
                              : 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                          }`}>
                            {isMetaConnected ? 'ONLINE (META TLS)' : 'CREDENTIALS REQUIRED'}
                          </span>
                        </div>

                        <p className="text-xs text-slate-300 leading-relaxed font-sans">
                          Direct enterprise integration with WhatsApp Business Account (WABA). Zero QR codes, zero phone disconnections, and 100% SLA uptime.
                        </p>

                        {/* 🟢 Live Connected Meta Cloud API Status HUD Card */}
                        {isMetaConnected ? (
                          <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-950/70 via-slate-900 to-cyan-950/50 border border-emerald-500/50 shadow-xl space-y-3 relative overflow-hidden">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="relative flex h-2.5 w-2.5">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                                </span>
                                <span className="text-xs font-black text-emerald-300 tracking-wide flex items-center gap-1.5 font-sans">
                                  <ShieldCheck size={14} className="text-emerald-400" />
                                  Meta Cloud API Connected
                                </span>
                              </div>
                              <span className="text-[9px] font-mono font-black uppercase px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                                LIVE ACTIVE
                              </span>
                            </div>

                            <div className="p-3 rounded-xl bg-slate-950/80 border border-white/5 space-y-1.5">
                              <div className="flex items-center justify-between">
                                <div className="text-xs font-black text-white flex items-center gap-1.5">
                                  <Building2 size={13} className="text-cyan-400" />
                                  <span>{whatsAppData?.displayName || tenant?.name || 'Official WhatsApp Business'}</span>
                                </div>
                                <span className="text-[9px] font-mono text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                                  Verified
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone size={14} className="text-emerald-400" />
                                <span className="text-base font-black text-emerald-300 font-mono tracking-wider">
                                  {whatsAppData?.phoneNumber || 'Connected'}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-2 pt-1 text-[9px] font-mono text-slate-400 border-t border-white/5">
                                <div>WABA: <span className="text-slate-200">{whatsAppData?.metaWabaId || 'Auto-Synced'}</span></div>
                                <div>Phone ID: <span className="text-slate-200">{whatsAppData?.metaPhoneNumberId || 'Auto-Synced'}</span></div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-0.5">
                              <button
                                type="button"
                                onClick={() => handleDiscoverPortalAccounts(true)}
                                disabled={isDiscoveringPortalAccounts}
                                className="w-full py-2 px-3 rounded-xl bg-slate-800 hover:bg-slate-700 border border-white/10 text-slate-200 font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer transition-colors active:scale-98"
                                title="Re-scan Meta Partner or switch connected business numbers"
                              >
                                <RefreshCw size={12} className={isDiscoveringPortalAccounts ? "animate-spin text-cyan-400" : "text-cyan-400"} />
                                <span>{isDiscoveringPortalAccounts ? 'Scanning...' : 'Re-sync / Switch'}</span>
                              </button>
                              <button
                                type="button"
                                onClick={handleDisconnectWhatsApp}
                                disabled={isDisconnectingWhatsApp}
                                className="w-full py-2 px-3 rounded-xl bg-rose-950/60 hover:bg-rose-900/80 border border-rose-500/40 text-rose-300 hover:text-rose-100 font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer transition-colors shadow-sm active:scale-98"
                              >
                                <PowerOff size={12} className={isDisconnectingWhatsApp ? "animate-spin text-rose-400" : "text-rose-400"} />
                                <span>{isDisconnectingWhatsApp ? 'Disconnecting...' : 'Log Out / Disconnect'}</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* 🚀 1-Click Meta Embedded Signup Quick Action Banner */
                          <div className="p-4 rounded-2xl bg-gradient-to-r from-cyan-950/60 via-blue-950/50 to-indigo-950/60 border border-cyan-500/40 space-y-2.5 shadow-lg relative overflow-hidden">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Sparkles size={14} className="text-cyan-400 animate-pulse" />
                                <span className="text-xs font-black text-white tracking-wide">
                                  Meta Cloud API 1-Click Fast Connect
                                </span>
                              </div>
                              <span className="text-[9px] font-mono font-black uppercase px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-400/30">
                                OFFICIAL PARTNER
                              </span>
                            </div>
                            <p className="text-xs text-slate-300 leading-relaxed">
                              Connect your business WhatsApp number in 30 seconds via official Meta popup dialog. Automatic WABA binding, 2FA registration, and webhook sync.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                              <button
                                type="button"
                                onClick={handleLaunchPortalMetaSignup}
                                disabled={isLaunchingPortalMetaSignup}
                                className="w-full py-2.5 px-3 rounded-xl bg-gradient-to-r from-cyan-500 hover:from-cyan-400 to-blue-600 hover:to-blue-500 text-white font-black text-xs shadow-md shadow-cyan-500/25 flex items-center justify-center gap-1.5 cursor-pointer transition-all active:scale-98 disabled:opacity-50"
                              >
                                {isLaunchingPortalMetaSignup ? (
                                  <RefreshCw size={13} className="animate-spin text-white" />
                                ) : (
                                  <Building2 size={13} className="text-cyan-100" />
                                )}
                                <span>{isLaunchingPortalMetaSignup ? 'Opening Meta...' : '🚀 1-Click Fast Connect'}</span>
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDiscoverPortalAccounts(true)}
                                disabled={isDiscoveringPortalAccounts}
                                className="w-full py-2.5 px-3 rounded-xl bg-slate-900/90 hover:bg-slate-800 border border-emerald-500/50 hover:border-emerald-400 text-emerald-300 hover:text-emerald-200 font-black text-xs shadow-md shadow-emerald-500/10 flex items-center justify-center gap-1.5 cursor-pointer transition-all active:scale-98 disabled:opacity-50"
                                title="Discover verified numbers already shared with Kangavena Meta Partner"
                              >
                                <RefreshCw size={13} className={isDiscoveringPortalAccounts ? "animate-spin text-emerald-400" : "text-emerald-400"} />
                                <span>{isDiscoveringPortalAccounts ? 'Scanning Meta...' : '⚡ Auto-Detect & Sync'}</span>
                              </button>
                            </div>
                          </div>
                        )}

                        {/* ⚡ Meta Discovered Accounts Modal */}
                        {showPortalDiscoveryModal && (
                          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-150">
                            <div className="w-full max-w-lg bg-slate-900 border border-cyan-500/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                              <div className="p-4 bg-gradient-to-r from-slate-950 via-slate-900 to-cyan-950/40 border-b border-white/10 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="p-2 rounded-lg bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                                    <Sparkles size={16} />
                                  </div>
                                  <div>
                                    <h3 className="text-sm font-black text-white">Discovered Meta WhatsApp Accounts</h3>
                                    <p className="text-[10px] text-slate-400">Select an authorized business number to connect to Luna AI</p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setShowPortalDiscoveryModal(false)}
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 cursor-pointer transition-colors"
                                >
                                  <XCircle size={18} />
                                </button>
                              </div>

                              <div className="p-4 space-y-3 overflow-y-auto flex-1">
                                {discoveredPortalAccounts.filter(a => a.phones?.length > 0).length === 0 ? (
                                  <div className="p-6 text-center text-slate-400 space-y-2">
                                    <AlertCircle size={28} className="mx-auto text-amber-400/80" />
                                    <p className="text-xs font-bold text-slate-300">No authorized numbers found yet</p>
                                    <p className="text-[11px] text-slate-500">
                                      Please use "1-Click Fast Connect" to link your WhatsApp Business account with Kangavena.
                                    </p>
                                  </div>
                                ) : (
                                  discoveredPortalAccounts.filter(a => a.phones?.length > 0).map((account) => (
                                    <div key={account.wabaId} className="p-3.5 rounded-xl bg-slate-950/70 border border-white/10 hover:border-cyan-500/40 transition-all space-y-2.5">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <h4 className="text-xs font-black text-white flex items-center gap-1.5">
                                            <Building2 size={13} className="text-cyan-400" />
                                            {account.wabaName}
                                          </h4>
                                          <span className="text-[9px] font-mono text-slate-500">WABA ID: {account.wabaId}</span>
                                        </div>
                                        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                                          Authorized
                                        </span>
                                      </div>

                                      <div className="space-y-1.5 pt-1">
                                        {account.phones.map((phone: any) => (
                                          <div key={phone.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-900/80 border border-white/5 hover:border-emerald-500/30">
                                            <div className="space-y-0.5">
                                              <div className="flex items-center gap-2">
                                                <Phone size={12} className="text-emerald-400" />
                                                <span className="text-xs font-black text-emerald-300 font-mono">{phone.displayPhone}</span>
                                              </div>
                                              <div className="text-[9px] text-slate-400 flex items-center gap-2">
                                                <span>Name: {phone.verifiedName || 'Business Phone'}</span>
                                                <span>•</span>
                                                <span className="font-mono">ID: {phone.id}</span>
                                              </div>
                                            </div>

                                            <button
                                              type="button"
                                              onClick={() => handleBindDiscoveredPortalPhone(account.wabaId, phone, account.wabaName)}
                                              className="py-1.5 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs shadow-md shadow-emerald-500/20 flex items-center gap-1.5 cursor-pointer transition-all active:scale-95"
                                            >
                                              <CheckCircle2 size={13} />
                                              <span>Connect</span>
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>

                              <div className="p-3 bg-slate-950 border-t border-white/10 flex items-center justify-between">
                                <button
                                  type="button"
                                  onClick={() => handleDiscoverPortalAccounts(true)}
                                  disabled={isDiscoveringPortalAccounts}
                                  className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1.5 cursor-pointer"
                                >
                                  <RefreshCw size={12} className={isDiscoveringPortalAccounts ? "animate-spin" : ""} />
                                  <span>Re-scan Meta Partner</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setShowPortalDiscoveryModal(false)}
                                  className="py-1.5 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold cursor-pointer"
                                >
                                  Close
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* ⚙️ Custom Developer Mode & Manual Credentials (Accordion) */}
                        <div className="rounded-xl border border-white/10 bg-slate-950/40 overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setShowPortalManualDeveloperSettings(!showPortalManualDeveloperSettings)}
                            className="w-full p-3 flex items-center justify-between hover:bg-white/[0.02] cursor-pointer transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <Sliders size={14} className={showPortalManualDeveloperSettings ? "text-cyan-400" : "text-slate-400"} />
                              <span className="text-xs font-black uppercase tracking-wider text-slate-200">
                                Manual &amp; Custom Developer Settings
                              </span>
                              {whatsAppData?.useCustomMetaToken && (
                                <span className="text-[8px] font-mono px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-bold">
                                  BYOT ACTIVE
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1.5 text-slate-400">
                              <span className="text-[10px] font-mono">
                                {showPortalManualDeveloperSettings ? 'Hide Settings' : 'Configure Custom API / IDs'}
                              </span>
                              {showPortalManualDeveloperSettings ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </div>
                          </button>

                          {showPortalManualDeveloperSettings && (
                            <div className="p-4 border-t border-white/5 space-y-3.5 text-xs animate-in fade-in slide-in-from-top-1 duration-200">
                              {/* Permanent Access Token */}
                              <div className="space-y-1.5 p-3 rounded-xl border border-slate-800/60 bg-slate-900/30">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="font-bold text-cyan-300 flex items-center gap-1.5">
                                    <Key size={13} className="text-cyan-400" />
                                    Custom Meta Access Token (BYOT)
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                      {whatsAppData?.useCustomMetaToken ? 'Enabled' : 'Disabled'}
                                    </span>
                                    <button
                                      type="button"
                                      role="switch"
                                      aria-checked={whatsAppData?.useCustomMetaToken || false}
                                      onClick={() => setWhatsAppData({ ...whatsAppData, useCustomMetaToken: !whatsAppData?.useCustomMetaToken })}
                                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-slate-900 transition-colors ${whatsAppData?.useCustomMetaToken ? 'bg-cyan-500' : 'bg-slate-700'}`}
                                    >
                                      <span className="sr-only">Use Custom Meta Token</span>
                                      <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${whatsAppData?.useCustomMetaToken ? 'translate-x-2' : '-translate-x-2'}`}
                                      />
                                    </button>
                                  </div>
                                </div>
                                
                                {!whatsAppData?.useCustomMetaToken ? (
                                  <div className="p-2.5 rounded-lg bg-emerald-950/40 border border-emerald-900/50">
                                    <p className="text-xs text-emerald-300/90 font-mono flex items-center gap-2">
                                      <ShieldCheck size={14} className="text-emerald-400 shrink-0" />
                                      <span>Kangavena Managed System Token is Active. Zero-Trust Mode engaged.</span>
                                    </p>
                                  </div>
                                ) : (
                                  <div className="space-y-1.5 mt-2 animate-in fade-in slide-in-from-top-1 duration-200">
                                    <div className="flex justify-end">
                                      <button
                                        type="button"
                                        onClick={() => setMetaTokenVisible(!metaTokenVisible)}
                                        className="text-[10px] text-slate-400 hover:text-cyan-300 flex items-center gap-1 transition cursor-pointer"
                                      >
                                        {metaTokenVisible ? <EyeOff size={11} /> : <Eye size={11} />}
                                        <span>{metaTokenVisible ? 'Hide Token' : 'Reveal Token'}</span>
                                      </button>
                                    </div>
                                    <input
                                      type={metaTokenVisible ? 'text' : 'password'}
                                      autoComplete="new-password"
                                      data-lpignore="true"
                                      name="custom_meta_token_field"
                                      value={whatsAppData?.metaAccessToken || ''}
                                      onChange={(e) => setWhatsAppData({ ...whatsAppData, metaAccessToken: e.target.value })}
                                      placeholder="Paste your custom EAA... token here"
                                      className="select-text w-full px-3 py-2 rounded-xl bg-slate-950/90 border border-cyan-800/50 text-cyan-200 font-mono text-xs focus:border-cyan-400 focus:outline-none placeholder:text-slate-500 shadow-[0_0_15px_rgba(34,211,238,0.1)]"
                                      onPaste={(e) => {
                                        e.stopPropagation();
                                        const pasted = e.clipboardData.getData('text');
                                        if (pasted) {
                                          setWhatsAppData((prev: any) => ({ ...prev, metaAccessToken: pasted.trim() }));
                                        }
                                      }}
                                    />
                                    <p className="text-[9px] text-amber-400/80 mt-1 font-sans">
                                      ⚠️ Warning: You are using an isolated custom token. Make sure it has correct permissions.
                                    </p>
                                  </div>
                                )}
                              </div>

                              {/* Phone Number ID & WABA ID */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <label className="font-bold text-slate-300 block">Phone Number ID</label>
                                  <input
                                    type="text"
                                    value={whatsAppData?.metaPhoneNumberId || ''}
                                    onChange={(e) => setWhatsAppData({ ...whatsAppData, metaPhoneNumberId: e.target.value })}
                                    placeholder="e.g. 104857291837465"
                                    className="w-full px-3 py-2 rounded-xl bg-slate-950/90 border border-slate-800 text-white font-mono text-xs focus:border-cyan-400 focus:outline-none"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <label className="font-bold text-slate-300 block">WABA Account ID</label>
                                  <input
                                    type="text"
                                    value={whatsAppData?.metaWabaId || ''}
                                    onChange={(e) => setWhatsAppData({ ...whatsAppData, metaWabaId: e.target.value })}
                                    placeholder="e.g. 102938475610293"
                                    className="w-full px-3 py-2 rounded-xl bg-slate-950/90 border border-slate-800 text-white font-mono text-xs focus:border-cyan-400 focus:outline-none"
                                  />
                                </div>
                              </div>

                              {/* Meta App ID & Registered Phone */}
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                      <label className="font-bold text-slate-300 block flex items-center gap-1">
                                        <Key size={11} className="text-cyan-400" />
                                        Meta App ID
                                      </label>
                                      {isCustomIsolatedApp ? (
                                        <span className="text-[7.5px] font-mono px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 font-bold">
                                          ISOLATED CUSTOM APP
                                        </span>
                                      ) : (
                                        <span className="text-[7.5px] font-mono px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold">
                                          AUTO DEFAULT (KANGAVENA)
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      {isCustomIsolatedApp && (
                                        <button
                                          type="button"
                                          onClick={() => setWhatsAppData({ ...whatsAppData, sipAppId: KANGAVENA_OFFICIAL_META_APP_ID })}
                                          className="text-[9px] font-mono text-amber-300 hover:text-amber-200 underline cursor-pointer"
                                        >
                                          Reset to Default
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(currentAppId, 'appId')}
                                        className="text-[9px] font-mono text-cyan-400 hover:text-cyan-300 flex items-center gap-0.5 cursor-pointer"
                                      >
                                        {copiedAppId ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
                                        <span>{copiedAppId ? 'Copied' : 'Copy'}</span>
                                      </button>
                                    </div>
                                  </div>
                                  <input
                                    type="text"
                                    value={currentAppId}
                                    onChange={(e) => setWhatsAppData({ ...whatsAppData, sipAppId: e.target.value.trim() })}
                                    placeholder={`Default: ${KANGAVENA_OFFICIAL_META_APP_ID}`}
                                    className="w-full px-3 py-2 rounded-xl bg-slate-950/90 border border-slate-800 text-cyan-200 font-mono text-xs focus:border-cyan-400 focus:outline-none"
                                  />
                                  <p className="text-[8px] text-slate-500 font-sans">
                                    {isCustomIsolatedApp 
                                      ? 'Using isolated custom Meta App ID for this tenant.' 
                                      : 'Auto-configured to Kangavena Master Tech Provider App. You can type an isolated App ID if desired.'}
                                  </p>
                                </div>

                                <div className="space-y-1">
                                  <div className="flex items-center justify-between">
                                    <label className="font-bold text-slate-300 block flex items-center gap-1">
                                      <Phone size={11} className="text-emerald-400" />
                                      Registered WhatsApp Phone
                                    </label>
                                    {autoDetectedRegisteredPhone && whatsAppData?.phoneNumber !== autoDetectedRegisteredPhone && (
                                      <button
                                        type="button"
                                        onClick={() => setWhatsAppData({ ...whatsAppData, phoneNumber: autoDetectedRegisteredPhone })}
                                        className="text-[9px] font-mono text-emerald-400 hover:text-emerald-300 underline font-bold cursor-pointer"
                                      >
                                        Auto-Fill ({autoDetectedRegisteredPhone})
                                      </button>
                                    )}
                                  </div>
                                  <input
                                    type="text"
                                    value={whatsAppData?.phoneNumber || ''}
                                    onChange={(e) => setWhatsAppData({ ...whatsAppData, phoneNumber: e.target.value })}
                                    placeholder={autoDetectedRegisteredPhone || '+94 77 123 4567'}
                                    className="w-full px-3 py-2 rounded-xl bg-slate-950/90 border border-slate-800 text-white font-mono text-xs focus:border-cyan-400 focus:outline-none"
                                  />
                                  {autoDetectedRegisteredPhone && (
                                    <p className="text-[8px] text-emerald-400/80 font-mono">
                                      ✓ Auto-detected from Tenant Profile: {autoDetectedRegisteredPhone}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Meta Webhook Endpoint & Verify Token (Read-Only Copyable) */}
                              <div className="p-3.5 rounded-xl bg-slate-950/90 border border-cyan-500/25 space-y-2.5">
                                <div className="flex items-center justify-between">
                                  <span className="text-[10px] font-mono font-bold text-cyan-300 uppercase tracking-wider flex items-center gap-1.5">
                                    <Link2 size={12} className="text-cyan-400" />
                                    Meta Webhook Configuration
                                  </span>
                                  <span className="text-[9px] text-slate-400 font-mono">Meta Developer Console</span>
                                </div>

                                <div className="space-y-3 text-xs">
                                  {/* Universal Main Callback URL */}
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] text-emerald-400 font-mono font-bold">Main Callback URL:</span>
                                        <span className="text-[7.5px] font-mono px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 font-bold">
                                          UNIVERSAL / ALL TENANTS
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(universalWebhookUrl, 'universal')}
                                        className="px-2 py-1 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 text-[10px] font-mono font-bold flex items-center gap-1 transition cursor-pointer"
                                      >
                                        {copiedUniversalWebhookUrl ? <Check size={11} /> : <Copy size={11} />}
                                        <span>{copiedUniversalWebhookUrl ? 'Copied' : 'Copy Universal'}</span>
                                      </button>
                                    </div>
                                    <input
                                      type="text"
                                      readOnly
                                      value={universalWebhookUrl}
                                      className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-emerald-500/30 text-emerald-200 font-mono text-[11px] select-all focus:outline-none"
                                    />
                                    <p className="text-[9px] text-slate-400 mt-1 font-sans">
                                      ⭐ Set <strong>once</strong> in your Meta App Dashboard. Dynamically routes all tenants by Phone Number ID. Never needs changing!
                                    </p>
                                  </div>

                                  {/* Dedicated Tenant URL (Optional) */}
                                  <div className="pt-2 border-t border-slate-800">
                                    <div className="flex items-center justify-between mb-1">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] text-slate-400 font-mono font-bold">Dedicated Tenant URL:</span>
                                        <span className="text-[7.5px] font-mono px-1.5 py-0.2 rounded bg-slate-800 text-slate-400 font-bold">
                                          OPTIONAL / ISOLATED
                                        </span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(dedicatedWebhookUrl, 'url')}
                                        className="px-2 py-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 text-[10px] font-mono font-bold flex items-center gap-1 transition cursor-pointer"
                                      >
                                        {copiedMetaWebhookUrl ? <Check size={11} /> : <Copy size={11} />}
                                        <span>{copiedMetaWebhookUrl ? 'Copied' : 'Copy'}</span>
                                      </button>
                                    </div>
                                    <input
                                      type="text"
                                      readOnly
                                      value={dedicatedWebhookUrl}
                                      className="w-full px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-400 font-mono text-[10.5px] select-all focus:outline-none"
                                    />
                                    <p className="text-[8.5px] text-slate-500 mt-0.5 font-sans">
                                      Only needed if this business tenant creates their own isolated private Meta Developer App.
                                    </p>
                                  </div>

                                  {/* Verify Token */}
                                  <div>
                                    <span className="text-[10px] text-slate-400 font-mono block mb-1">Verify Token:</span>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        readOnly
                                        value={verifyToken}
                                        className="flex-1 px-2.5 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-slate-300 font-mono text-[11px] select-all focus:outline-none"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(verifyToken, 'token')}
                                        className="px-2.5 py-1.5 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 text-[11px] font-mono font-bold flex items-center gap-1 transition cursor-pointer"
                                      >
                                        {copiedMetaVerifyToken ? <Check size={12} /> : <Copy size={12} />}
                                        <span>{copiedMetaVerifyToken ? 'Copied' : 'Copy'}</span>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                          {/* Direct Voice Calling (SIP) [2-WAY AUDIO] */}
                          <div className="p-3.5 rounded-xl bg-gradient-to-br from-violet-950/40 via-slate-900 to-slate-950 border border-violet-500/30 space-y-3 shadow-inner">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Radio size={14} className="text-violet-400 animate-pulse" />
                                <span className="font-bold text-white text-xs">Direct Voice Calling (SIP)</span>
                                <span className="text-[8px] font-mono font-bold px-1.5 py-0.2 rounded bg-violet-500/20 text-violet-300 border border-violet-500/40">
                                  2-WAY AUDIO
                                </span>
                              </div>
                              <input
                                type="checkbox"
                                checked={enableSipCalling}
                                onChange={(e) => setWhatsAppData({ ...whatsAppData, enableSipCalling: e.target.checked })}
                                className="w-4 h-4 rounded bg-slate-950 border-slate-700 text-violet-500 focus:ring-violet-400/30 cursor-pointer"
                              />
                            </div>

                            {enableSipCalling && (
                              <div className="space-y-3 pt-2 border-t border-violet-500/20 text-xs">
                                {/* Server & Port */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <label className="text-[10px] text-slate-400 font-mono block">SIP SERVER</label>
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(sipServerHost, 'sipHost')}
                                        className="text-[9px] font-mono text-violet-400 hover:text-violet-300 flex items-center gap-0.5 cursor-pointer"
                                      >
                                        {copiedSipHost ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
                                        <span>{copiedSipHost ? 'Copied' : 'Copy'}</span>
                                      </button>
                                    </div>
                                    <input
                                      type="text"
                                      readOnly
                                      value={sipServerHost}
                                      className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-violet-500/30 text-violet-200 font-mono text-[11px] select-all focus:outline-none"
                                    />
                                  </div>

                                  <div>
                                    <div className="flex items-center justify-between mb-1">
                                      <label className="text-[10px] text-slate-400 font-mono block">SIP PORT</label>
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(String(sipPort), 'sipPort')}
                                        className="text-[9px] font-mono text-violet-400 hover:text-violet-300 flex items-center gap-0.5 cursor-pointer"
                                      >
                                        {copiedSipPort ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
                                        <span>{copiedSipPort ? 'Copied' : 'Copy'}</span>
                                      </button>
                                    </div>
                                    <input
                                      type="text"
                                      readOnly
                                      value={sipPort}
                                      className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-violet-500/30 text-violet-200 font-mono text-[11px] select-all focus:outline-none"
                                    />
                                  </div>
                                </div>

                                {/* Request URI Parameters */}
                                <div>
                                  <label className="text-[10px] text-slate-400 font-mono block mb-1">REQUEST URI PARAMETERS</label>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="relative">
                                      <input
                                        type="text"
                                        value={sipParamName}
                                        onChange={(e) => setWhatsAppData({ ...whatsAppData, sipCustomParamName: e.target.value })}
                                        className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-violet-500/30 text-violet-200 font-mono text-[11px] focus:outline-none"
                                        placeholder="Param (tenant_id)"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(sipParamName, 'sipParamName')}
                                        className="absolute right-1.5 top-2 text-violet-400 hover:text-violet-300 cursor-pointer"
                                      >
                                        {copiedSipParamName ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
                                      </button>
                                    </div>
                                    <div className="relative">
                                      <input
                                        type="text"
                                        value={sipParamValue}
                                        onChange={(e) => setWhatsAppData({ ...whatsAppData, sipCustomParamValue: e.target.value })}
                                        className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-violet-500/30 text-violet-200 font-mono text-[11px] focus:outline-none"
                                        placeholder="Value (17)"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => copyToClipboard(sipParamValue, 'sipParamValue')}
                                        className="absolute right-1.5 top-2 text-violet-400 hover:text-violet-300 cursor-pointer"
                                      >
                                        {copiedSipParamValue ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {/* Setup Instruction Tips */}
                                <div className="p-2.5 rounded-xl bg-violet-950/60 border border-violet-500/30 text-[9px] text-violet-300/90 leading-relaxed font-sans space-y-1">
                                  <div>💡 <strong>How to complete Meta setup:</strong> In WhatsApp Manager ➜ Phone numbers ➜ <strong>Call settings</strong> ➜ Turn ON <strong>&quot;Allow voice calls&quot;</strong> ➜ Click <strong>&quot;Set up&quot;</strong> under <em>Use Session Initiation Protocol (SIP)</em> ➜ Paste the Server (<code>wa.kangavena.com</code>), Port (<code>5061</code>), App ID (<code>{currentAppId}</code>), and Parameter values above ➜ Click <strong>Save</strong>.</div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                    ) : (
                      /* TIER 2: EVOLUTION API GATEWAY DEVICE LINKING CARD */
                      <div className="rounded-2xl bg-slate-950/90 border border-emerald-500/30 p-5 space-y-4 shadow-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <QrCode size={16} className="text-emerald-400" />
                            <h3 className="text-sm font-black text-white font-sans">
                              WhatsApp Gateway Device Linking (Evolution API)
                            </h3>
                          </div>
                          <span className={`text-[9px] font-mono font-bold px-2 py-0.5 rounded-full border ${
                            whatsAppData?.connectionStatus === 'connected' || stats.whatsappConnected
                              ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                              : 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                          }`}>
                            {whatsAppData?.connectionStatus === 'connected' || stats.whatsappConnected ? 'DEVICE PAIRED' : 'NEEDS QR SCAN'}
                          </span>
                        </div>

                        {whatsAppData?.connectionStatus === 'connected' || stats.whatsappConnected ? (
                          <div className="p-4 rounded-xl bg-emerald-950/40 border border-emerald-500/30 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center text-emerald-400">
                                <Smartphone size={20} />
                              </div>
                              <div>
                                <span className="text-xs font-black text-white block">
                                  +{whatsAppData?.phoneNumber || stats.whatsappPhone}
                                </span>
                                <span className="text-[10px] text-emerald-400 font-mono">
                                  Ready for 24/7 autonomous messaging
                                </span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={handleConnectWhatsApp}
                              disabled={isConnectingWhatsApp}
                              className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-700 hover:border-rose-400 text-rose-300 text-xs font-bold transition cursor-pointer"
                            >
                              {isConnectingWhatsApp ? <RefreshCw size={12} className="animate-spin" /> : 'Re-link Device'}
                            </button>
                          </div>
                        ) : (
                          <div className="text-center p-6 rounded-xl bg-slate-900/60 border border-slate-800 space-y-3">
                            {whatsAppQrCode ? (
                              <div className="space-y-3">
                                <div className="p-3 bg-white rounded-2xl w-48 h-48 mx-auto shadow-2xl flex items-center justify-center">
                                  <img src={whatsAppQrCode} alt="WhatsApp QR Code" className="w-full h-full object-contain" />
                                </div>
                                <p className="text-xs text-slate-300">
                                  Scan this QR code with WhatsApp on your phone (Linked Devices).
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                <Smartphone size={32} className="mx-auto text-emerald-400 opacity-60" />
                                <div>
                                  <h4 className="text-xs font-bold text-white uppercase">No Device Paired</h4>
                                  <p className="text-[11px] text-slate-400 mt-1">
                                    Click below to generate a real-time QR code via Evolution API.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={handleConnectWhatsApp}
                                  disabled={isConnectingWhatsApp}
                                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider transition-all shadow-md cursor-pointer disabled:opacity-50"
                                >
                                  {isConnectingWhatsApp ? 'Generating QR...' : 'Generate Pairing QR Code'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

              {/* WhatsApp Operational AI Settings */}
              <div className="rounded-2xl bg-slate-950/90 border border-emerald-500/30 p-5 space-y-4 shadow-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sliders size={16} className="text-emerald-400" />
                    <h3 className="text-sm font-black text-white font-sans">
                      WhatsApp AI Operational Controls
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveWhatsAppSettings}
                    disabled={isSavingWhatsAppSettings}
                    className="px-3.5 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 transition cursor-pointer shadow-md"
                  >
                    {isSavingWhatsAppSettings ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                    <span>Save Settings</span>
                  </button>
                </div>

                <div className="space-y-3.5 text-xs">
                  {/* Auto-Reply Switch */}
                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900 border border-slate-800">
                    <div>
                      <span className="font-bold text-white block">WhatsApp Auto-Reply</span>
                      <span className="text-[10px] text-slate-400">Luna AI answers customer messages automatically</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={whatsAppData?.autoReply !== false}
                      onChange={(e) => setWhatsAppData({ ...whatsAppData, autoReply: e.target.checked })}
                      className="w-4 h-4 rounded bg-slate-950 border-slate-700 text-emerald-500 focus:ring-emerald-400/30 cursor-pointer"
                    />
                  </div>

                  {/* Voice Note Audio Reply Switch */}
                  <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900 border border-slate-800">
                    <div>
                      <span className="font-bold text-white block">Voice Note Audio Responses</span>
                      <span className="text-[10px] text-slate-400">Respond with natural synthesized voice notes for voice inquiries</span>
                    </div>
                    <input
                      type="checkbox"
                      checked={whatsAppData?.voiceReply !== false}
                      onChange={(e) => setWhatsAppData({ ...whatsAppData, voiceReply: e.target.checked })}
                      className="w-4 h-4 rounded bg-slate-950 border-slate-700 text-emerald-500 focus:ring-emerald-400/30 cursor-pointer"
                    />
                  </div>

                  {/* WhatsApp Call Rejection Action */}
                  <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-1.5">
                    <label className="font-bold text-white block">Call Handling Action</label>
                    <select
                      value={whatsAppData?.callAction || 'reject_and_voice_note'}
                      onChange={(e) => setWhatsAppData({ ...whatsAppData, callAction: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 text-xs focus:border-emerald-400"
                    >
                      <option value="reject_and_voice_note">Reject call & send polite Voice Note</option>
                      <option value="reject_and_text">Reject call & send Text Message</option>
                      <option value="ignore">Ignore WhatsApp calls</option>
                    </select>
                  </div>

                  {/* Copywriting & Delivery Style */}
                  <div className="p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-1.5">
                    <label className="font-bold text-white block">WhatsApp Copywriting & Promo Format</label>
                    <select
                      value={whatsAppData?.whatsappFormattingStyle || 'sales_promo'}
                      onChange={(e) => setWhatsAppData({ ...whatsAppData, whatsappFormattingStyle: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-slate-200 text-xs focus:border-emerald-400"
                    >
                      <option value="sales_promo">🔥 High-Converting Sales Promo (Numbered Lists, Emojis, Clear CTA)</option>
                      <option value="business_corporate">🏢 Clean Corporate & Professional Executive</option>
                      <option value="friendly_concierge">💬 Friendly Conversational & Warm Concierge</option>
                      <option value="technical_expert">🔬 Technical & Deep Specification Details</option>
                      <option value="direct_short">⚡ Direct & Ultra-Short Bullet Points</option>
                    </select>
                  </div>

                  {/* 🛡️ Anti-Ban Human Emulation Controls */}
                  <div className="p-3.5 rounded-xl bg-gradient-to-br from-emerald-950/40 via-slate-900 to-slate-950 border border-emerald-500/30 space-y-3 shadow-inner">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <ShieldCheck size={14} className="text-emerald-400" />
                        <span className="font-bold text-white text-xs">Meta Anti-Ban Protection Suite</span>
                      </div>
                      <input
                        type="checkbox"
                        checked={whatsAppData?.antiBanEnabled !== false}
                        onChange={(e) => setWhatsAppData({ ...whatsAppData, antiBanEnabled: e.target.checked })}
                        className="w-4 h-4 rounded bg-slate-950 border-slate-700 text-emerald-500 focus:ring-emerald-400/30 cursor-pointer"
                      />
                    </div>

                    {whatsAppData?.antiBanEnabled !== false && (
                      <div className="space-y-2.5 pt-2 border-t border-white/5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                          <div>
                            <label className="text-[10px] text-slate-400 font-mono block mb-1">
                              Human Read Delay:
                            </label>
                            <select
                              value={whatsAppData?.antiBanReadDelay ?? 600}
                              onChange={(e) => setWhatsAppData({ ...whatsAppData, antiBanReadDelay: Number(e.target.value) })}
                              className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-800 text-emerald-300 font-mono text-[11px] focus:border-emerald-400 cursor-pointer"
                            >
                              <option value="300">300ms (Fastest)</option>
                              <option value="600">600ms (Recommended)</option>
                              <option value="1000">1000ms (Natural)</option>
                              <option value="1500">1500ms (Cautious)</option>
                            </select>
                          </div>

                          <div>
                            <label className="text-[10px] text-slate-400 font-mono block mb-1">
                              Typing Emulation Speed:
                            </label>
                            <select
                              value={whatsAppData?.antiBanTypingSpeed || 'natural'}
                              onChange={(e) => setWhatsAppData({ ...whatsAppData, antiBanTypingSpeed: e.target.value })}
                              className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-800 text-emerald-300 font-mono text-[11px] focus:border-emerald-400 cursor-pointer"
                            >
                              <option value="fast">⚡ Fast (15ms/char)</option>
                              <option value="natural">🟢 Natural (25ms/char)</option>
                              <option value="relaxed">☕ Relaxed (35ms/char)</option>
                            </select>
                          </div>
                        </div>

                        <p className="text-[10px] text-emerald-400/80 font-mono flex items-center gap-1">
                          <Check size={11} /> Simulates authentic human typing & suppresses bot fingerprints.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* 🛡️ Personal WhatsApp Mode & Friends Filter (Rule 16 Parity) */}
                  <div className="p-3.5 rounded-xl bg-gradient-to-br from-purple-950/40 via-slate-900 to-slate-950 border border-purple-500/30 space-y-3 shadow-inner">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Shield size={16} className="text-purple-400" />
                        <div>
                          <span className="font-bold text-white text-xs block">Personal WhatsApp Protection</span>
                          <span className="text-[10px] text-purple-300/80">Auto-filters personal contacts & mutes on owner manual reply</span>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={whatsAppData?.isPersonalWhatsApp === true}
                        onChange={(e) => setWhatsAppData({ ...whatsAppData, isPersonalWhatsApp: e.target.checked })}
                        className="w-4 h-4 rounded bg-slate-950 border-purple-700 text-purple-500 focus:ring-purple-400/30 cursor-pointer"
                      />
                    </div>

                    {/* Mode explanation */}
                    <div className="pt-2 border-t border-white/5 text-[10px] font-mono leading-relaxed">
                      {whatsAppData?.isPersonalWhatsApp ? (
                        <p className="text-purple-300/90">
                          🔒 <strong className="text-purple-200">Personal Mode Active:</strong> Friends & family AI filter active. Owner manual replies <span className="text-purple-300 font-bold underline">permanently mute</span> Luna for that contact.
                        </p>
                      ) : (
                        <p className="text-emerald-400/90">
                          ⚡ <strong className="text-emerald-300">Business Mode Active:</strong> 24/7 AI replies to all customers. Owner manual replies <span className="text-emerald-300 font-bold underline">pause Luna temporarily</span> for the duration below.
                        </p>
                      )}
                    </div>

                    {/* Snooze Duration Selector (Always visible) */}
                    <div className="space-y-2 pt-2 border-t border-white/5">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] text-slate-400 font-mono block">
                          Owner Reply Takeover Pause:
                        </label>
                        <span className={`text-[10px] font-mono font-bold ${whatsAppData?.isPersonalWhatsApp ? 'text-purple-300' : 'text-emerald-400'}`}>
                          {whatsAppData?.isPersonalWhatsApp
                            ? 'Permanent (Personal Mode)'
                            : (whatsAppData?.ownerPauseMinutes ?? 30) >= 99999
                            ? 'Permanent'
                            : `${(whatsAppData?.ownerPauseMinutes ?? 30)} min`}
                        </span>
                      </div>
                      <select
                        value={whatsAppData?.ownerPauseMinutes ?? 30}
                        onChange={(e) => setWhatsAppData({ ...whatsAppData, ownerPauseMinutes: Number(e.target.value) })}
                        className={`w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border font-mono text-[11px] focus:outline-none cursor-pointer ${
                          whatsAppData?.isPersonalWhatsApp
                            ? 'border-purple-800 text-purple-300 focus:border-purple-400'
                            : 'border-slate-800 text-emerald-300 focus:border-emerald-400'
                        }`}
                      >
                        <option value="15">15 Minutes</option>
                        <option value="30">30 Minutes (Recommended)</option>
                        <option value="60">1 Hour</option>
                        <option value="120">2 Hours</option>
                        <option value="240">4 Hours</option>
                        <option value="720">12 Hours</option>
                        <option value="1440">24 Hours</option>
                        <option value="99999">Permanent (Until Resumed)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: WhatsApp Dedicated Conversations List & Inspector */}
            <div className="w-full lg:w-1/2 flex flex-col bg-[#080E0B] overflow-hidden">
              <div className="p-4 border-b border-emerald-500/20 bg-[#0B1510] space-y-3 shrink-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Smartphone size={16} className="text-emerald-400" />
                    <h3 className="text-sm font-black text-white font-sans">
                      WhatsApp & Voice Telemetry
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedModule('whatsapp_web')}
                      className="px-2.5 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-[10px] uppercase tracking-wide flex items-center gap-1 shadow-md shadow-emerald-500/20 active:scale-95 transition cursor-pointer"
                      title="Launch authentic WhatsApp Web Console"
                    >
                      <span>💬 WA Web</span>
                      <ArrowRight size={11} className="text-slate-950" />
                    </button>
                    <div className="relative w-44">
                      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        value={whatsAppSearchQuery}
                        onChange={(e) => setWhatsAppSearchQuery(e.target.value)}
                        placeholder="Search phone, name, chat..."
                        className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-slate-950 border border-emerald-500/30 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-400"
                      />
                    </div>
                  </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex items-center gap-1.5 text-[10px] font-mono flex-wrap">
                  <button
                    type="button"
                    onClick={() => setChannelFilter('all')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition cursor-pointer ${
                      channelFilter === 'all'
                        ? 'bg-emerald-500 text-slate-950 font-black'
                        : 'bg-slate-900 text-slate-400 hover:text-white'
                    }`}
                  >
                    All ({activeInstanceWhatsAppConversations.length + voiceConversations.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setChannelFilter('whatsapp')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition flex items-center gap-1 cursor-pointer ${
                      channelFilter === 'whatsapp'
                        ? 'bg-emerald-500 text-slate-950 font-black'
                        : 'bg-slate-900 text-slate-400 hover:text-emerald-300'
                    }`}
                  >
                    <Smartphone size={10} /> WhatsApp ({activeInstanceWhatsAppConversations.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setChannelFilter('voice')}
                    className={`px-2.5 py-1 rounded-lg font-bold transition flex items-center gap-1 cursor-pointer ${
                      channelFilter === 'voice'
                        ? 'bg-purple-500 text-white font-black'
                        : 'bg-slate-900 text-purple-300 hover:text-purple-100'
                    }`}
                  >
                    <PhoneCall size={10} /> Voice Calls ({voiceConversations.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => setChannelFilter('ignored' as any)}
                    className={`px-2.5 py-1 rounded-lg font-bold transition flex items-center gap-1 cursor-pointer ${
                      (channelFilter as any) === 'ignored'
                        ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-black shadow-[0_0_12px_rgba(168,85,247,0.4)]'
                        : 'bg-slate-900 text-purple-300 hover:text-purple-100'
                    }`}
                  >
                    <Shield size={10} /> Ignored ({ignoredContacts.length})
                  </button>

                  {/* Multi-line filtering pill */}
                  {whatsAppInstances.length > 1 && selectedWhatsAppNodeId !== 'all' && (
                    <div className="flex items-center gap-1.5 py-0.5 px-2 rounded-md bg-emerald-950/60 border border-emerald-500/30 text-[9px]">
                      <span className="text-slate-400">Line:</span>
                      <span className="text-emerald-300 font-bold">
                        {whatsAppData?.phoneNumber ? (String(whatsAppData.phoneNumber).startsWith('+') ? String(whatsAppData.phoneNumber) : `+${whatsAppData.phoneNumber}`) : (whatsAppData?.displayName || whatsAppData?.instanceName || 'Active Line')}
                      </span>
                      <button
                        type="button"
                        onClick={() => setSelectedWhatsAppNodeId('all')}
                        className="text-[9px] text-cyan-400 hover:text-cyan-200 underline cursor-pointer ml-1"
                        title="Show chats from all linked numbers"
                      >
                        Show All
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2 tenet-mobile-scroll">
                {/* 🛡️ Ignored Contacts Studio View */}
                {(channelFilter as any) === 'ignored' ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-purple-950/20 border border-purple-500/20">
                      <div>
                        <h4 className="text-xs font-black text-white flex items-center gap-1.5">
                          <Shield size={13} className="text-purple-400" />
                          <span>Ignored Contacts & Personal Numbers</span>
                        </h4>
                        <p className="text-[10px] text-slate-400">
                          Numbers below are completely blocked from Luna AI replies (0 tokens used).
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowAddIgnoredModal(true)}
                        className="px-2.5 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-bold text-[10px] flex items-center gap-1 shadow transition cursor-pointer"
                      >
                        <Plus size={11} />
                        <span>Add Number</span>
                      </button>
                    </div>

                    {ignoredContacts.length === 0 ? (
                      <div className="p-12 text-center text-slate-500 space-y-2">
                        <UserX size={32} className="mx-auto opacity-40 text-purple-400" />
                        <p className="text-xs font-medium">No ignored contacts yet.</p>
                        <p className="text-[10px] text-slate-600">When Personal Mode is active, casual personal chats are automatically added here.</p>
                      </div>
                    ) : (
                      ignoredContacts
                        .filter(c => {
                          if (!whatsAppSearchQuery.trim()) return true;
                          const q = whatsAppSearchQuery.toLowerCase();
                          return (c.phone || '').includes(q) || (c.name || '').toLowerCase().includes(q) || (c.lastMessageSnippet || '').toLowerCase().includes(q);
                        })
                        .map((c) => (
                          <div
                            key={c.id || c.phone}
                            className="p-3 rounded-xl bg-slate-900/90 border border-purple-500/20 hover:border-purple-500/40 transition flex items-start justify-between gap-3 shadow"
                          >
                            <div className="space-y-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-xs font-black text-purple-300">
                                  +{c.phone}
                                </span>
                                {c.name && (
                                  <span className="text-[11px] text-slate-300 font-medium truncate max-w-[120px]">
                                    {c.name}
                                  </span>
                                )}
                                <span className={`text-[8.5px] font-mono px-2 py-0.5 rounded-full font-bold uppercase border ${
                                  c.reason === 'manual'
                                    ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                                    : 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                                }`}>
                                  {c.reason === 'manual' ? 'Manual Block' : 'AI Personal Detected'}
                                </span>
                              </div>
                              {c.lastMessageSnippet && (
                                <p className="text-[10.5px] text-slate-400 italic line-clamp-2 bg-slate-950/60 p-1.5 rounded-lg border border-white/5 font-mono">
                                  "{c.lastMessageSnippet}"
                                </p>
                              )}
                              <div className="text-[9px] text-slate-500 font-mono">
                                Ignored at: {c.detectedAt ? new Date(c.detectedAt).toLocaleString('en-GB', { timeZone: 'Asia/Colombo' }) : 'Unknown'}
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() => handleRemoveIgnoredContact(c.phone)}
                              className="px-2.5 py-1.5 rounded-lg bg-slate-800 hover:bg-emerald-600/30 text-slate-300 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 text-[10px] font-mono font-bold flex items-center gap-1 transition cursor-pointer shrink-0"
                              title="Unlock and let Luna AI reply to this contact"
                            >
                              <CheckCircle2 size={11} />
                              <span>Unignore</span>
                            </button>
                          </div>
                        ))
                    )}

                    {/* Add Ignored Number Modal */}
                    {showAddIgnoredModal && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
                        <div className="w-full max-w-md bg-slate-900 border border-purple-500/30 rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95">
                          <div className="p-4 bg-gradient-to-r from-purple-950/60 to-slate-900 border-b border-purple-500/20 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Shield size={16} className="text-purple-400" />
                              <h3 className="text-xs font-black text-white">Add Number to Ignored List</h3>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowAddIgnoredModal(false)}
                              className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition cursor-pointer"
                            >
                              <X size={14} />
                            </button>
                          </div>

                          <form onSubmit={handleAddIgnoredContact} className="p-5 space-y-4">
                            <div>
                              <label className="text-[10px] font-mono text-slate-300 block mb-1">
                                Phone Number (e.g. 94771234567 or 0771234567):
                              </label>
                              <input
                                type="text"
                                required
                                value={newIgnoredPhone}
                                onChange={(e) => setNewIgnoredPhone(e.target.value)}
                                placeholder="94771234567"
                                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs font-mono focus:border-purple-400 focus:outline-none"
                              />
                            </div>

                            <div>
                              <label className="text-[10px] font-mono text-slate-300 block mb-1">
                                Name / Label (Optional, e.g. "Friend Kasun"):
                              </label>
                              <input
                                type="text"
                                value={newIgnoredName}
                                onChange={(e) => setNewIgnoredName(e.target.value)}
                                placeholder="Contact Name"
                                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs focus:border-purple-400 focus:outline-none"
                              />
                            </div>

                            <div>
                              <label className="text-[10px] font-mono text-slate-300 block mb-1">
                                Reason:
                              </label>
                              <select
                                value={newIgnoredReason}
                                onChange={(e) => setNewIgnoredReason(e.target.value)}
                                className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-slate-200 text-xs focus:border-purple-400 focus:outline-none"
                              >
                                <option value="manual">Manual Blacklist / Personal</option>
                                <option value="friend_family">Friend / Family Member</option>
                                <option value="spam">Spam / Unwanted</option>
                              </select>
                            </div>

                            <div className="pt-2 flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setShowAddIgnoredModal(false)}
                                className="px-3 py-2 rounded-xl text-xs font-mono text-slate-400 hover:text-white bg-slate-800 transition cursor-pointer"
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                disabled={isSubmittingIgnoredContact}
                                className="px-4 py-2 rounded-xl text-xs font-mono font-bold text-white bg-purple-600 hover:bg-purple-500 shadow-md shadow-purple-500/20 transition disabled:opacity-50 cursor-pointer"
                              >
                                {isSubmittingIgnoredContact ? 'Adding...' : 'Block & Ignore'}
                              </button>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (() => {
                  const targetList = channelFilter === 'voice' 
                    ? voiceConversations 
                    : channelFilter === 'whatsapp' 
                    ? activeInstanceWhatsAppConversations 
                    : [...activeInstanceWhatsAppConversations, ...voiceConversations];

                  const filtered = targetList.filter((c) => {
                    if (!whatsAppSearchQuery.trim()) return true;
                    const q = whatsAppSearchQuery.toLowerCase();
                    return (c.phone || '').includes(q) || (c.visitorName || '').toLowerCase().includes(q) || c.messages.some((m) => (m.text || '').toLowerCase().includes(q));
                  });

                  if (filtered.length === 0) {
                    return (
                      <div className="p-12 text-center text-slate-500 space-y-2">
                        <Smartphone size={32} className="mx-auto opacity-40 text-emerald-400" />
                        <p className="text-xs font-medium">No customer interactions found.</p>
                      </div>
                    );
                  }

                  return filtered.map((chat) => {
                    const lastMsg = chat.messages?.[chat.messages.length - 1];
                    const isVoice = chat.isVoiceCall || chat.channel === 'sip_voice' || chat.sessionId.startsWith('call_');
                    const isMissed = isVoice && (chat.callStatus === 'missed' || (chat.callDuration || 0) === 0);

                    return (
                      <div
                        key={chat.sessionId}
                        onClick={() => {
                          setSelectedSessionId(chat.sessionId);
                          setSelectedModule('luna_core');
                        }}
                        className={`p-3.5 rounded-2xl transition-all cursor-pointer space-y-1.5 shadow-md group border ${
                          isVoice
                            ? 'bg-slate-900/80 hover:bg-slate-900 border-purple-500/30 hover:border-purple-400/60'
                            : 'bg-slate-900/80 hover:bg-slate-900 border-emerald-500/20 hover:border-emerald-400/60'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {isVoice ? (
                              <span className={`p-1 rounded-md border ${
                                isMissed ? 'bg-rose-500/20 text-rose-400 border-rose-500/40' : 'bg-purple-500/20 text-purple-300 border-purple-500/40'
                              }`}>
                                <PhoneCall size={12} />
                              </span>
                            ) : (
                              <span className="p-1 rounded-md bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                                <Smartphone size={12} />
                              </span>
                            )}
                            <span className="text-xs font-black text-white font-sans">
                              {chat.phone ? `+${chat.phone}` : chat.visitorName}
                            </span>
                            {whatsAppInstances.length > 1 && chat.instancePhoneNumber && (
                              <span className="text-[9px] font-mono text-emerald-400/90 bg-emerald-950/70 px-1.5 py-0.2 rounded border border-emerald-500/20" title={`Received on +${chat.instancePhoneNumber}`}>
                                📲 +{chat.instancePhoneNumber}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5">
                            {isVoice ? (
                              <span className={`text-[8px] font-mono font-bold px-2 py-0.5 rounded border ${
                                isMissed ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' : 'bg-purple-500/20 text-purple-300 border-purple-500/40'
                              }`}>
                                {isMissed ? 'MISSED CALL' : `⏱️ ${Math.floor((chat.callDuration || 0)/60)}m ${(chat.callDuration || 0)%60}s`}
                              </span>
                            ) : chat.isHumanTakeover ? (
                              <span className="text-[8px] font-mono font-bold px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                TAKEOVER
                              </span>
                            ) : (
                              <span className="text-[8px] font-mono font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                AI ACTIVE
                              </span>
                            )}
                            <span className="text-[10px] font-mono text-slate-500">{lastMsg?.time || 'Recent'}</span>
                          </div>
                        </div>
                        <p className="text-[11px] text-slate-300 line-clamp-2 leading-relaxed font-sans pl-1">
                          {lastMsg?.text || (isVoice ? 'Live Voice call recording processed.' : 'WhatsApp inquiry initiated.')}
                        </p>
                        <div className="flex items-center justify-between pt-2 border-t border-slate-800/60 text-[10px] font-mono">
                          <span className={isVoice ? 'text-purple-400 font-bold' : 'text-emerald-400 font-bold'}>
                            {isVoice ? `${chat.messages.length} spoken turns` : `${chat.messages.length} messages`}
                          </span>
                          <div className="flex items-center gap-2.5">
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSessionId(chat.sessionId);
                                setSelectedModule('whatsapp_web');
                              }}
                              className="text-emerald-400 hover:text-emerald-300 font-bold flex items-center gap-1 cursor-pointer hover:underline"
                              title="Open this conversation directly in WhatsApp Web Console"
                            >
                              <span>💬 WA Web</span>
                            </span>
                            <span className="text-cyan-400 font-bold group-hover:translate-x-0.5 transition-transform flex items-center gap-1">
                              <span>Inspect</span>
                              <ArrowRight size={10} />
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </>
      );
    })()}
  </div>
) : selectedModule === 'payment_gateway' ? (
  /* ═══════════════════════════════════════════════════════════════ */
  /* VIEW 10: PAYMENT GATEWAY & BANKING FULL-SCREEN STUDIO           */
  /* ═══════════════════════════════════════════════════════════════ */
  <div className="flex-1 flex flex-col overflow-hidden bg-[#07130C]">
    {/* Top KPI Summary Bar */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3.5 px-3 sm:px-6 py-3 sm:py-4 border-b border-emerald-500/20 bg-[#0B1E13]/90 shrink-0">
      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0E2819] to-[#0A1F13] border border-emerald-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Payment Requests
          </span>
          <span className="text-xl font-black text-white font-mono mt-0.5 block">
            {leadsList.filter(l => l.formType === 'payment_request').length}
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          <CreditCard size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0E2819] to-[#0A1F13] border border-emerald-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Bank Slips Received
          </span>
          <span className="text-xl font-black text-emerald-400 font-mono mt-0.5 block">
            {leadsList.filter(l => l.formType === 'payment_slip' || l.notes?.includes('slip')).length}
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          <Wallet size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0E2819] to-[#0A1F13] border border-emerald-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Dispatch Channel
          </span>
          <span className="text-sm font-black text-emerald-300 font-mono mt-0.5 block">
            WhatsApp & Web
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          <Send size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0E2819] to-[#0A1F13] border border-emerald-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Live Status
          </span>
          <span className="text-sm font-black text-emerald-400 font-mono mt-0.5 block flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping inline-block" />
            ACTIVE ON FLOW
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          <Zap size={20} />
        </div>
      </div>
    </div>

    {/* Payment Studio Content */}
    <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6 tenet-mobile-scroll">
      <div className="bg-[#0B1E13]/80 border border-emerald-500/30 rounded-3xl p-6 shadow-2xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-400">
              <Building2 size={22} />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Configured Settlement Accounts</h3>
              <p className="text-xs text-slate-400">Autonomous banking accounts dispatched to customers when ordering</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onBackToWorkspace}
            className="px-4 py-2 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/40 text-emerald-300 font-bold text-xs flex items-center gap-2 transition cursor-pointer"
          >
            <Edit3 size={14} />
            <span>Edit in Canvas Node</span>
          </button>
        </div>

        {/* Transactions / Leads List */}
        <div className="pt-4 border-t border-emerald-500/20 space-y-3">
          <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-slate-400">
            Recent Payment Requests & Inquiries ({leadsList.filter(l => l.formType === 'payment_request' || l.formType === 'payment_slip').length})
          </h4>
          {leadsList.filter(l => l.formType === 'payment_request' || l.formType === 'payment_slip').length === 0 ? (
            <div className="p-8 text-center bg-slate-950/60 rounded-2xl border border-emerald-500/15">
              <p className="text-xs text-slate-400 font-mono">No payment inquiries captured yet. Luna will automatically record requests when customers inquire about payments.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {leadsList.filter(l => l.formType === 'payment_request' || l.formType === 'payment_slip').map((lead) => (
                <div key={lead.id} className="p-3.5 rounded-2xl bg-slate-950/80 border border-emerald-500/20 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-white">{lead.visitorName || 'Customer'}</span>
                      {lead.visitorPhone && <span className="text-[10px] font-mono text-emerald-400">+{lead.visitorPhone}</span>}
                    </div>
                    <p className="text-[11px] text-slate-300 mt-1">{lead.notes || 'Payment details requested'}</p>
                  </div>
                  <span className="text-[10px] font-mono text-slate-500">{formatTime(lead.createdAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
) : selectedModule === 'call_guard' ? (
  /* ═══════════════════════════════════════════════════════════════ */
  /* VIEW 11: CALL GUARD & DEFENSE FULL-SCREEN STUDIO                */
  /* ═══════════════════════════════════════════════════════════════ */
  <div className="flex-1 flex flex-col overflow-hidden bg-[#140608]">
    {/* Top Defense Header */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3.5 px-3 sm:px-6 py-3 sm:py-4 border-b border-rose-500/20 bg-[#1E090D]/90 shrink-0">
      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#2D0D13] to-[#200A0E] border border-rose-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Silence Watchdog
          </span>
          <span className="text-sm font-black text-rose-400 font-mono mt-0.5 block">
            ENABLED (AUTO-HANGUP)
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-rose-500/20 text-rose-400 border border-rose-500/30">
          <PhoneOff size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#2D0D13] to-[#200A0E] border border-rose-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Anti-Token Burn
          </span>
          <span className="text-sm font-black text-rose-400 font-mono mt-0.5 block">
            SHIELD ACTIVE
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-rose-500/20 text-rose-400 border border-rose-500/30">
          <ShieldAlert size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#2D0D13] to-[#200A0E] border border-rose-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Anti-PII Leak
          </span>
          <span className="text-sm font-black text-emerald-400 font-mono mt-0.5 block">
            ZERO-LEAK SECURE
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          <ShieldCheck size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#2D0D13] to-[#200A0E] border border-rose-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Live Calls Guarded
          </span>
          <span className="text-xl font-black text-white font-mono mt-0.5 block">
            {voiceConversations.length}
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-rose-500/20 text-rose-400 border border-rose-500/30">
          <PhoneCall size={20} />
        </div>
      </div>
    </div>

    {/* Call Guard Studio Body */}
    <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6 tenet-mobile-scroll">
      <div className="bg-[#1E090D]/80 border border-rose-500/30 rounded-3xl p-6 shadow-2xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-rose-500/20 border border-rose-500/40 text-rose-400">
              <ShieldAlert size={22} />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Call Defense & Anti-Abuse Firewall</h3>
              <p className="text-xs text-slate-400">Protects live SIP phone lines from ghost calls, prolonged silence, and token drainage</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onBackToWorkspace}
            className="px-4 py-2 rounded-xl bg-rose-500/20 hover:bg-rose-500/30 border border-rose-500/40 text-rose-300 font-bold text-xs flex items-center gap-2 transition cursor-pointer"
          >
            <Edit3 size={14} />
            <span>Configure Guard in Node</span>
          </button>
        </div>

        <div className="pt-4 border-t border-rose-500/20 space-y-3">
          <h4 className="text-xs font-mono font-bold uppercase tracking-wider text-slate-400">
            Guarded Live Voice Calls ({voiceConversations.length})
          </h4>
          {voiceConversations.length === 0 ? (
            <div className="p-8 text-center bg-slate-950/60 rounded-2xl border border-rose-500/15">
              <p className="text-xs text-slate-400 font-mono">No live phone calls recorded yet. All incoming Meta SIP Voice calls will be monitored by Call Guard.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {voiceConversations.map((call) => (
                <div key={call.sessionId} className="p-3.5 rounded-2xl bg-slate-950/80 border border-rose-500/20 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-white">{call.visitorName || 'Caller'}</span>
                      {call.phone && <span className="text-[10px] font-mono text-rose-400">+{call.phone}</span>}
                      <span className="text-[8px] font-mono font-bold px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">
                        GUARDED
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-1">Session ID: {call.sessionId} | Duration: Protected</p>
                  </div>
                  <span className="text-[10px] font-mono text-slate-500">{formatTime(call.startedAt)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
) : selectedModule === 'followup_recovery' ? (
  /* ═══════════════════════════════════════════════════════════════ */
  /* VIEW 12: AUTONOMOUS RE-ENGAGEMENT & ABANDONED RECOVERY STUDIO   */
  /* ═══════════════════════════════════════════════════════════════ */
  <div className="flex-1 flex flex-col overflow-hidden bg-[#0c0714]">
    {/* Top Sub-Tab Switcher: Pending Queue vs History vs Follow-Up Settings */}
    <div className="px-6 pt-3 pb-2 border-b border-purple-500/20 bg-[#130b20] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setFollowUpStudioTab('pending')}
          className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
            followUpStudioTab === 'pending'
              ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-md shadow-purple-500/20 font-black'
              : 'bg-[#1a0e2e] text-slate-300 hover:text-white border border-purple-500/20'
          }`}
        >
          <Clock size={14} className={followUpData?.telemetry?.pendingQueue?.length ? 'text-purple-300 animate-pulse' : ''} />
          <span>Pending Queue ({followUpData?.telemetry?.pendingQueue?.length || 0})</span>
        </button>

        <button
          type="button"
          onClick={() => setFollowUpStudioTab('history')}
          className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
            followUpStudioTab === 'history'
              ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-md shadow-purple-500/20 font-black'
              : 'bg-[#1a0e2e] text-slate-300 hover:text-white border border-purple-500/20'
          }`}
        >
          <History size={14} />
          <span>Sent History ({followUpData?.telemetry?.recentFollowUps?.length || 0})</span>
        </button>

        <button
          type="button"
          onClick={() => setFollowUpStudioTab('settings')}
          className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
            followUpStudioTab === 'settings'
              ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-md shadow-purple-500/20 font-black'
              : 'bg-[#1a0e2e] text-slate-300 hover:text-white border border-purple-500/20'
          }`}
        >
          <SlidersHorizontal size={14} />
          <span>Follow-Up Automation Rules</span>
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={isTriggeringScan}
          onClick={handleTriggerRecoveryScan}
          className="px-3.5 py-1.5 rounded-xl bg-purple-500/20 hover:bg-purple-500/30 border border-purple-400/50 text-purple-200 text-xs font-bold flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50"
          title="Force an immediate background scan for dropped conversations"
        >
          <Zap size={14} className={`text-purple-400 ${isTriggeringScan ? 'animate-spin' : ''}`} />
          <span>{isTriggeringScan ? 'Scanning...' : 'Trigger Recovery Scan'}</span>
        </button>

        <button
          type="button"
          onClick={onBackToWorkspace}
          className="px-3 py-1.5 rounded-xl bg-slate-900/80 hover:bg-slate-800 border border-slate-700 text-slate-300 text-xs font-bold flex items-center gap-1.5 transition cursor-pointer"
        >
          <ExternalLink size={13} />
          <span>Flow Graph</span>
        </button>
      </div>
    </div>

    {/* Top KPI Metrics Bar */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3.5 px-3 sm:px-6 py-3 sm:py-4 border-b border-purple-500/20 bg-[#10091c]/90 shrink-0">
      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1d0e2d] to-[#140a20] border border-purple-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Detected Abandoned
          </span>
          <span className="text-xl font-black text-white font-mono mt-0.5 block">
            {followUpData?.telemetry?.totalAbandonedDetected || 0}
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30">
          <Clock size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1d0e2d] to-[#140a20] border border-purple-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Follow-Ups Sent
          </span>
          <span className="text-xl font-black text-purple-400 font-mono mt-0.5 block">
            {followUpData?.telemetry?.totalFollowUpsSent || 0}
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-purple-500/20 text-purple-400 border border-purple-500/30">
          <MessageSquare size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1d0e2d] to-[#140a20] border border-purple-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Customers Recovered
          </span>
          <span className="text-xl font-black text-emerald-400 font-mono mt-0.5 block">
            {followUpData?.telemetry?.totalRecoveredCustomers || 0}
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          <Sparkles size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#1d0e2d] to-[#140a20] border border-purple-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Conversion Rate
          </span>
          <span className="text-xl font-black text-cyan-400 font-mono mt-0.5 block">
            {followUpData?.telemetry?.recoveryRatePercentage || 0}%
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
          <ShieldCheck size={20} />
        </div>
      </div>
    </div>

    {/* Studio Tab Content */}
    <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6 tenet-mobile-scroll">
      {followUpStudioTab === 'pending' ? (
        /* Pending Recovery Queue Tab */
        <div className="bg-[#150c24]/90 border border-purple-500/30 rounded-3xl p-6 shadow-2xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-2xl bg-purple-500/20 border border-purple-500/40 text-purple-400">
                <Clock size={22} className="animate-pulse" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Live Abandoned Chat Recovery Queue</h3>
                <p className="text-xs text-slate-400">
                  Customers who dropped out of conversation — waiting for delay window or ready for immediate re-engagement
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-purple-300 bg-purple-500/10 px-2.5 py-1 rounded-full border border-purple-500/30">
                {followUpData?.telemetry?.pendingQueue?.length || 0} In Queue
              </span>
            </div>
          </div>

          <div className="pt-4 border-t border-purple-500/20 space-y-3">
            {!followUpData?.telemetry?.pendingQueue || followUpData.telemetry.pendingQueue.length === 0 ? (
              <div className="p-12 text-center bg-slate-950/60 rounded-2xl border border-purple-500/15 space-y-3">
                <Clock size={36} className="mx-auto text-purple-400/40" />
                <h4 className="text-sm font-bold text-white uppercase">Queue is Empty</h4>
                <p className="text-xs text-slate-400 font-mono max-w-md mx-auto">
                  No dropped conversations currently waiting for re-engagement. When customers abandon chats, they will automatically appear here until their {followUpData?.delayHours || 24}-hour delay elapses.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {followUpData.telemetry.pendingQueue.map((item: any) => {
                  const convId = item.conversationId || item.id;
                  const custPhone = item.customerPhone || item.phone;
                  return (
                  <div
                    key={convId}
                    className="p-4 rounded-2xl bg-slate-950/80 border border-purple-500/20 hover:border-purple-400/50 transition-all space-y-3"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="text-xs font-bold text-white">{item.visitorName || 'Customer'}</span>
                        <span className="text-[11px] font-mono text-purple-300 font-bold">+{custPhone}</span>
                        {item.status === 'ready' ? (
                          <span className="text-[9px] font-mono font-black px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center gap-1 shadow-[0_0_10px_rgba(16,185,129,0.3)]">
                            <Zap size={10} /> READY FOR DISPATCH
                          </span>
                        ) : (
                          <span className="text-[9px] font-mono font-bold px-2.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                            <Clock size={10} /> WAITING DELAY ({item.hoursRemaining}h left)
                          </span>
                        )}
                        <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded bg-purple-950/80 text-purple-300 border border-purple-500/30">
                          {item.stageLabel}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-slate-500">
                          {item.hoursSinceActive}h inactive
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDismissFollowUpCandidate(convId)}
                          className="px-2.5 py-1.5 rounded-xl bg-slate-900/80 hover:bg-slate-800 border border-slate-700/60 text-slate-400 hover:text-rose-300 text-xs font-medium transition cursor-pointer"
                          title="Dismiss this customer from re-engagement queue"
                        >
                          Dismiss
                        </button>
                        <button
                          type="button"
                          disabled={isDispatchingFollowUp === convId}
                          onClick={() => handleDispatchSingleFollowUp(convId)}
                          className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 border border-purple-400 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer transition shadow-md disabled:opacity-50 active:scale-95"
                          title="Trigger Luna to send AI re-engagement message to this customer immediately"
                        >
                          <Send size={12} className={isDispatchingFollowUp === convId ? 'animate-spin' : ''} />
                          <span>{isDispatchingFollowUp === convId ? 'Sending...' : '⚡ Send Follow-Up Now'}</span>
                        </button>
                      </div>
                    </div>

                    {item.lastMessageText && (
                      <div className="p-2.5 rounded-xl bg-purple-950/20 border border-purple-500/15 text-xs text-slate-300 font-sans">
                        <span className="text-[10px] font-mono text-slate-500 uppercase block mb-0.5">Last Customer Message:</span>
                        <span className="italic text-purple-200">"{item.lastMessageText}"</span>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : followUpStudioTab === 'history' ? (
        /* Sent Follow-Up History Tab */
        <div className="bg-[#150c24]/90 border border-purple-500/30 rounded-3xl p-6 shadow-2xl space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-2xl bg-purple-500/20 border border-purple-500/40 text-purple-400">
                <History size={22} />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Dispatched Follow-Up History</h3>
                <p className="text-xs text-slate-400">
                  Customers re-contacted by Luna AI after dropping out of conversations
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-purple-300 bg-purple-500/10 px-2.5 py-1 rounded-full border border-purple-500/30">
                Anti-Spam: 1-Message Cap Active
              </span>
            </div>
          </div>

          <div className="pt-4 border-t border-purple-500/20 space-y-3">
            {!followUpData?.telemetry?.recentFollowUps || followUpData.telemetry.recentFollowUps.length === 0 ? (
              <div className="p-12 text-center bg-slate-950/60 rounded-2xl border border-purple-500/15 space-y-3">
                <Clock size={36} className="mx-auto text-purple-400/40 animate-pulse" />
                <h4 className="text-sm font-bold text-white uppercase">No Follow-Ups Dispatched Yet</h4>
                <p className="text-xs text-slate-400 font-mono max-w-md mx-auto">
                  When customers pause or drop conversations midway, Luna AI will automatically wait for the configured delay ({followUpData?.delayHours || 24} hours) and dispatch a warm 1-sentence reminder.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {followUpData.telemetry.recentFollowUps.map((item: any) => (
                  <div
                    key={item.id}
                    className="p-4 rounded-2xl bg-slate-950/80 border border-purple-500/20 hover:border-purple-400/50 transition-all space-y-2.5"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <span className="text-xs font-bold text-white">{item.visitorName || 'Customer'}</span>
                        <span className="text-[11px] font-mono text-purple-300 font-bold">+{item.phone}</span>
                        {item.isRecovered ? (
                          <span className="text-[9px] font-mono font-black px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center gap-1 shadow-[0_0_10px_rgba(16,185,129,0.3)]">
                            <CheckCircle2 size={10} /> RECOVERED
                          </span>
                        ) : (
                          <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30 flex items-center gap-1">
                            <Clock size={10} /> SENT — WAITING FOR REPLY
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] font-mono text-slate-500">
                        {item.followUpSentAt ? formatTime(item.followUpSentAt) : 'Recently'}
                      </span>
                    </div>

                    {item.followUpText && (
                      <div className="p-2.5 rounded-xl bg-purple-950/30 border border-purple-500/20 text-xs text-purple-200 font-sans italic">
                        "{item.followUpText}"
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Settings Tab */
        <div className="bg-[#150c24]/90 border border-purple-500/30 rounded-3xl p-6 shadow-2xl space-y-6">
          <div className="flex items-center justify-between pb-4 border-b border-purple-500/20">
            <div>
              <h3 className="text-base font-bold text-white">Autonomous Re-Engagement Rules</h3>
              <p className="text-xs text-slate-400">Configure delays, incentive offers, and trigger conditions</p>
            </div>
            <button
              type="button"
              disabled={isSavingFollowUp}
              onClick={() => handleSaveFollowUpConfig({})}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white font-bold text-xs flex items-center gap-2 shadow-lg cursor-pointer disabled:opacity-50"
            >
              <Save size={14} />
              <span>{isSavingFollowUp ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Delay Window Selector */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-300 block">Re-engagement Delay Window</label>
              <div className="grid grid-cols-4 gap-2">
                {[6, 12, 24, 48].map((hrs) => (
                  <button
                    key={hrs}
                    type="button"
                    onClick={() => handleSaveFollowUpConfig({ delayHours: hrs })}
                    className={`py-2 px-3 rounded-xl border text-xs font-mono font-bold transition cursor-pointer ${
                      (followUpData?.delayHours ?? 24) === hrs
                        ? 'bg-purple-500/25 border-purple-400 text-purple-200 shadow-[0_0_12px_rgba(168,85,247,0.4)]'
                        : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    {hrs}h {hrs === 24 ? '(1 Day)' : hrs === 48 ? '(2 Days)' : ''}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-500 font-mono">Time Luna AI waits after customer drops conversation before dispatching</p>
            </div>

            {/* Quiet Hours Guard */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-300 block">Quiet Hours Sleep Guard</label>
              <div
                onClick={() => handleSaveFollowUpConfig({ quietHours: !(followUpData?.quietHours ?? true) })}
                className="p-3 rounded-xl bg-slate-900 border border-purple-500/20 hover:border-purple-500/40 flex items-center justify-between cursor-pointer transition"
              >
                <div className="flex items-center gap-2.5">
                  <Moon size={16} className="text-purple-400" />
                  <div>
                    <span className="text-xs font-bold text-white block">10:00 PM – 08:00 AM Mute</span>
                    <span className="text-[10px] text-slate-400 block">Never disturb resting customers overnight</span>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={followUpData?.quietHours !== false}
                  readOnly
                  className="rounded text-purple-500 focus:ring-0 cursor-pointer"
                />
              </div>
            </div>

            {/* Scope Triggers */}
            <div className="space-y-3 col-span-1 md:col-span-2 pt-2 border-t border-purple-500/20">
              <label className="text-xs font-bold text-slate-300 block">Trigger Conditions & Filters</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div
                  onClick={() => handleSaveFollowUpConfig({ filterAbandonedOrders: !(followUpData?.filterAbandonedOrders ?? true) })}
                  className="p-3 rounded-xl bg-slate-900 border border-purple-500/20 hover:border-purple-500/40 flex items-center justify-between cursor-pointer transition"
                >
                  <span className="text-xs font-bold text-slate-200">Unconfirmed Orders & Carts</span>
                  <input
                    type="checkbox"
                    checked={followUpData?.filterAbandonedOrders !== false}
                    readOnly
                    className="rounded text-purple-500 focus:ring-0 cursor-pointer"
                  />
                </div>

                <div
                  onClick={() => handleSaveFollowUpConfig({ filterAbandonedInquiries: !(followUpData?.filterAbandonedInquiries ?? true) })}
                  className="p-3 rounded-xl bg-slate-900 border border-purple-500/20 hover:border-purple-500/40 flex items-center justify-between cursor-pointer transition"
                >
                  <span className="text-xs font-bold text-slate-200">Price & Product Inquiries</span>
                  <input
                    type="checkbox"
                    checked={followUpData?.filterAbandonedInquiries !== false}
                    readOnly
                    className="rounded text-purple-500 focus:ring-0 cursor-pointer"
                  />
                </div>

                <div
                  onClick={() => handleSaveFollowUpConfig({ filterUnfinishedForms: !(followUpData?.filterUnfinishedForms ?? true) })}
                  className="p-3 rounded-xl bg-slate-900 border border-purple-500/20 hover:border-purple-500/40 flex items-center justify-between cursor-pointer transition"
                >
                  <span className="text-xs font-bold text-slate-200">Unfinished Bookings & Forms</span>
                  <input
                    type="checkbox"
                    checked={followUpData?.filterUnfinishedForms !== false}
                    readOnly
                    className="rounded text-purple-500 focus:ring-0 cursor-pointer"
                  />
                </div>
              </div>
            </div>

            {/* Courtesy Recovery Incentive */}
            <div className="space-y-2 col-span-1 md:col-span-2 pt-2 border-t border-purple-500/20">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                  <Gift size={14} className="text-purple-400" />
                  <span>Courtesy Recovery Incentive (Special Offer)</span>
                </label>
                <button
                  type="button"
                  onClick={() => handleSaveFollowUpConfig({ includeIncentive: !followUpData?.includeIncentive })}
                  className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border transition cursor-pointer ${
                    followUpData?.includeIncentive
                      ? 'bg-purple-500/20 border-purple-500/40 text-purple-300'
                      : 'bg-slate-900 border-slate-800 text-slate-500'
                  }`}
                >
                  {followUpData?.includeIncentive ? 'ENABLED' : 'DISABLED'}
                </button>
              </div>

              {followUpData?.includeIncentive && (
                <input
                  type="text"
                  defaultValue={followUpData?.incentiveText || 'Free express delivery if confirmed today!'}
                  onBlur={(e) => handleSaveFollowUpConfig({ incentiveText: e.target.value })}
                  placeholder="e.g. Free delivery or 5% extra discount if confirmed today!"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-purple-500/30 text-xs text-white placeholder-slate-500 focus:border-purple-400 focus:outline-none"
                />
              )}
            </div>

            {/* Custom AI Tone Template */}
            <div className="space-y-2 col-span-1 md:col-span-2 pt-2 border-t border-purple-500/20">
              <label className="text-xs font-bold text-slate-300 block">Custom Follow-Up Tone Guidance</label>
              <textarea
                rows={2}
                defaultValue={followUpData?.customTemplate || ''}
                onBlur={(e) => handleSaveFollowUpConfig({ customTemplate: e.target.value })}
                placeholder="Optional guidance for Luna AI (e.g. Mention that stock is limited, or invite them to call if they need help)..."
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-purple-500/30 text-xs text-white placeholder-slate-500 focus:border-purple-400 focus:outline-none resize-none"
              />
            </div>

            {/* Live Interactive Test Dispatcher */}
            <div className="p-4 rounded-2xl bg-purple-950/40 border border-purple-500/30 col-span-1 md:col-span-2 space-y-3 shadow-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-purple-500/20 text-purple-300">
                    <Sparkles size={16} />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-white">Interactive Test AI Re-Engagement Dispatch</h4>
                    <p className="text-[10px] text-slate-400">Send an instant test message to any WhatsApp number to verify real delivery & tone</p>
                  </div>
                </div>
                <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                  LIVE TEST
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="text-[10px] font-mono text-slate-400 block mb-1">Target Phone (e.g. 94712345678):</label>
                  <input
                    type="text"
                    value={testReEngagePhone}
                    onChange={(e) => setTestReEngagePhone(e.target.value)}
                    placeholder="947..."
                    className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-purple-500/30 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-purple-400 font-mono"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-mono text-slate-400 block mb-1">Inquiry Topic (Optional):</label>
                  <input
                    type="text"
                    value={testReEngageTopic}
                    onChange={(e) => setTestReEngageTopic(e.target.value)}
                    placeholder="e.g. Blue Denim cargo shorts"
                    className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-purple-500/30 text-xs text-white placeholder-slate-600 focus:outline-none focus:border-purple-400"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  disabled={isSendingTestReEngage || !testReEngagePhone.trim()}
                  onClick={handleSendTestReEngagement}
                  className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white font-bold text-xs flex items-center gap-2 cursor-pointer shadow-lg disabled:opacity-50 active:scale-95 transition"
                >
                  <Send size={13} className={isSendingTestReEngage ? 'animate-spin' : ''} />
                  <span>{isSendingTestReEngage ? 'Dispatching...' : '🧪 Dispatch Test Re-Engagement'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  </div>
) : selectedModule === 'review_booster' ? (
  /* ═══════════════════════════════════════════════════════════════ */
  /* VIEW 13: AUTONOMOUS GOOGLE MAPS REVIEW BOOSTER & FIREWALL STUDIO */
  /* ═══════════════════════════════════════════════════════════════ */
  <div className="flex-1 flex flex-col overflow-hidden bg-[#100902]">
    {/* Top Sub-Tab Switcher: Feedback Stream vs Booster Settings */}
    <div className="px-6 pt-3 pb-2 border-b border-amber-500/20 bg-[#1a0f04] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shrink-0">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setReviewStudioTab('feedback')}
          className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
            reviewStudioTab === 'feedback'
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/20 font-black'
              : 'bg-[#2a1705] text-slate-300 hover:text-white border border-amber-500/20'
          }`}
        >
          <Star size={14} className="fill-amber-400/50" />
          <span>Customer Feedback Stream ({reviewBoosterData?.telemetry?.recentFeedback?.length || 0})</span>
        </button>

        <button
          type="button"
          onClick={() => setReviewStudioTab('settings')}
          className={`px-3.5 py-1.5 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
            reviewStudioTab === 'settings'
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/20 font-black'
              : 'bg-[#2a1705] text-slate-300 hover:text-white border border-amber-500/20'
          }`}
        >
          <SlidersHorizontal size={14} />
          <span>Review Booster Settings & Firewall</span>
        </button>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={isTriggeringReviewScan}
          onClick={handleTriggerReviewScan}
          className="px-3.5 py-1.5 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/50 text-amber-200 text-xs font-bold flex items-center gap-1.5 transition cursor-pointer disabled:opacity-50"
          title="Scan delivered orders and send review invitations immediately"
        >
          <Zap size={13} className={isTriggeringReviewScan ? 'animate-spin' : ''} />
          <span>{isTriggeringReviewScan ? 'Scanning Orders...' : 'Trigger Review Scan'}</span>
        </button>

        <button
          type="button"
          onClick={() => setSelectedModule(null)}
          className="px-3 py-1.5 rounded-xl bg-slate-900/90 border border-slate-700 text-slate-300 hover:text-white text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
        >
          <ArrowLeft size={13} />
          <span>Back to Hub</span>
        </button>
      </div>
    </div>

    {/* Metric Ribbon */}
    <div className="px-6 py-2.5 bg-[#140b03] border-b border-amber-500/10 grid grid-cols-2 sm:grid-cols-4 gap-3 shrink-0">
      <div className="flex items-center gap-2.5">
        <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300">
          <Star size={16} className="fill-amber-400/30 text-amber-400" />
        </div>
        <div>
          <span className="text-[10px] font-mono text-slate-400 uppercase block">Review Invites</span>
          <span className="text-sm font-black text-amber-200 font-mono">
            {reviewBoosterData?.telemetry?.totalReviewsRequested || 0}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2.5">
        <div className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
          <Sparkles size={16} className="text-emerald-400" />
        </div>
        <div>
          <span className="text-[10px] font-mono text-slate-400 uppercase block">5★ Reviews Captured</span>
          <span className="text-sm font-black text-emerald-300 font-mono">
            {reviewBoosterData?.telemetry?.totalPositiveCaptured || 0}
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2.5">
        <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-300">
          <CheckCircle2 size={16} className="text-cyan-400" />
        </div>
        <div>
          <span className="text-[10px] font-mono text-slate-400 uppercase block">Positive Rating %</span>
          <span className="text-sm font-black text-cyan-300 font-mono">
            {reviewBoosterData?.telemetry?.positiveRatePercentage ?? 100}%
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2.5">
        <div className="p-2 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300">
          <ShieldAlert size={16} className="text-rose-400" />
        </div>
        <div>
          <span className="text-[10px] font-mono text-slate-400 uppercase block">Firewalled Complaints</span>
          <span className="text-sm font-black text-rose-300 font-mono">
            {reviewBoosterData?.telemetry?.totalFirewalledNegative || 0}
          </span>
        </div>
      </div>
    </div>

    {/* Tab Content */}
    {reviewStudioTab === 'feedback' ? (
      /* Sub-Tab 1: Customer Feedback Stream */
      <div className="flex-1 overflow-auto p-6 space-y-4">
        {(!reviewBoosterData?.telemetry?.recentFeedback || reviewBoosterData.telemetry.recentFeedback.length === 0) ? (
          <div className="p-12 text-center max-w-md mx-auto bg-slate-900/40 border border-amber-500/20 rounded-3xl space-y-3">
            <Star size={44} className="mx-auto text-amber-500/40 fill-amber-500/10" />
            <h4 className="text-sm font-bold text-white uppercase">No Review Inquiries Yet</h4>
            <p className="text-xs text-slate-400 leading-relaxed">
              When orders are marked delivered or completed, Luna AI automatically invites customers to share feedback and routes 5-star ratings to your Google profile.
            </p>
            <button
              type="button"
              disabled={isTriggeringReviewScan}
              onClick={handleTriggerReviewScan}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-xs cursor-pointer shadow-lg shadow-amber-500/20"
            >
              Scan Orders Now
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {reviewBoosterData.telemetry.recentFeedback.map((fb: any) => {
              const isPositive = fb.status === '5_star_positive';
              const isFirewalled = fb.status === 'firewall_alerted';
              return (
                <div
                  key={fb.id}
                  className={`p-4 rounded-2xl border transition-all ${
                    isPositive
                      ? 'bg-[#0f1f14] border-emerald-500/30 shadow-[0_0_20px_rgba(16,185,129,0.1)]'
                      : isFirewalled
                      ? 'bg-[#220d12] border-rose-500/30 shadow-[0_0_20px_rgba(244,63,94,0.1)]'
                      : 'bg-slate-900/60 border-slate-800'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2 border-b border-slate-800/60">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-bold text-xs ${
                        isPositive ? 'bg-emerald-500/20 text-emerald-300' : isFirewalled ? 'bg-rose-500/20 text-rose-300' : 'bg-amber-500/20 text-amber-300'
                      }`}>
                        {fb.rating ? `${fb.rating}★` : '★'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h5 className="text-xs font-bold text-white">{fb.customerName}</h5>
                          <span className="text-[10px] font-mono text-slate-400">{fb.customerPhone}</span>
                          {fb.orderNumber && (
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">
                              #{fb.orderNumber}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] font-mono font-bold px-2 py-0.5 rounded-full border ${
                        isPositive
                          ? 'bg-emerald-950 text-emerald-300 border-emerald-500/40'
                          : isFirewalled
                          ? 'bg-rose-950 text-rose-300 border-rose-500/40'
                          : 'bg-amber-950 text-amber-300 border-amber-500/40'
                      }`}>
                        {isPositive ? '5★ GOOGLE LINK PROVIDED' : isFirewalled ? '🛡️ FIREWALLED & ALERTED' : 'INVITE SENT'}
                      </span>
                      <span className="text-[10px] font-mono text-slate-500">
                        {new Date(fb.submittedAt).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <p className="pt-2 text-xs text-slate-200 leading-relaxed font-sans">
                    {fb.feedbackText}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    ) : (
      /* Sub-Tab 2: Booster Settings & Firewall */
      <div className="flex-1 overflow-auto p-6 space-y-6 max-w-4xl">
        <div className="p-6 rounded-3xl bg-[#170e03] border border-amber-500/30 space-y-6 shadow-xl">
          <div className="flex items-center justify-between pb-4 border-b border-amber-500/20">
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Star size={18} className="text-amber-400 fill-amber-400" />
                Google Review Automation Settings
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Configure timing, Google Maps destination, and negative feedback safety rules.
              </p>
            </div>
            <button
              type="button"
              disabled={isSavingReviewBooster}
              onClick={() => handleSaveReviewBoosterConfig({ isEnabled: !(reviewBoosterData?.isEnabled !== false) })}
              className={`px-3 py-1.5 rounded-xl font-mono text-xs font-bold border transition cursor-pointer ${
                reviewBoosterData?.isEnabled !== false
                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-300'
                  : 'bg-slate-900 border-slate-700 text-slate-500'
              }`}
            >
              {reviewBoosterData?.isEnabled !== false ? 'ENABLED' : 'MUTED'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Google Maps Review URL */}
            <div className="space-y-1.5 md:col-span-2">
              <label className="text-xs font-bold text-slate-300 flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-amber-300">
                  <ExternalLink size={13} /> Google Maps Review Profile Link
                </span>
                <span className="text-[11px] font-mono text-slate-400">Direct 5-Star Link</span>
              </label>
              <input
                type="text"
                defaultValue={reviewBoosterData?.googleReviewUrl || ''}
                onBlur={(e) => handleSaveReviewBoosterConfig({ googleReviewUrl: e.target.value })}
                placeholder="https://g.page/r/your-google-place-id/review"
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-amber-500/30 text-xs text-amber-200 placeholder-slate-500 focus:border-amber-400 focus:outline-none font-mono"
              />
            </div>

            {/* Timing Delay */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-300 block">
                Post-Delivery Trigger Delay
              </label>
              <select
                value={reviewBoosterData?.delayHours ?? 2}
                onChange={(e) => handleSaveReviewBoosterConfig({ delayHours: Number(e.target.value) })}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-amber-500/30 text-xs text-white focus:border-amber-400 focus:outline-none"
              >
                <option value={1}>1 Hour After Delivery</option>
                <option value={2}>2 Hours After Delivery (Recommended)</option>
                <option value={6}>6 Hours After Delivery</option>
                <option value={24}>24 Hours (1 Day Post-Delivery)</option>
              </select>
            </div>

            {/* Quiet Hours */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-300 block">Quiet Hours Protection</label>
              <div
                onClick={() => handleSaveReviewBoosterConfig({ quietHours: !(reviewBoosterData?.quietHours ?? true) })}
                className="p-2.5 rounded-xl bg-slate-900 border border-amber-500/20 flex items-center justify-between cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <Moon size={14} className="text-indigo-400" />
                  <span className="text-xs text-slate-200">Mute 10:00 PM – 08:00 AM</span>
                </div>
                <input
                  type="checkbox"
                  checked={reviewBoosterData?.quietHours !== false}
                  readOnly
                  className="rounded text-amber-500 focus:ring-0 cursor-pointer"
                />
              </div>
            </div>

            {/* Negative Feedback Firewall */}
            <div className="space-y-2 md:col-span-2 p-4 rounded-2xl bg-[#241005] border border-amber-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <ShieldAlert size={18} className="text-amber-400" />
                  <div>
                    <h4 className="text-xs font-bold text-white">Negative Feedback Firewall</h4>
                    <p className="text-[11px] text-slate-300">
                      Shield your Google profile from low ratings & unresolved customer disputes
                    </p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={reviewBoosterData?.negativeFeedbackFirewall !== false}
                  onChange={(e) => handleSaveReviewBoosterConfig({ negativeFeedbackFirewall: e.target.checked })}
                  className="rounded text-amber-500 focus:ring-0 cursor-pointer"
                />
              </div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                If the customer rates 1-3 stars or mentions a complaint, Luna AI suppresses the Google link, expresses sincere empathy, and immediately alerts the store owner's WhatsApp so the problem can be resolved privately.
              </p>
            </div>

            {/* Courtesy Incentive Offer */}
            <div className="space-y-2 md:col-span-2 pt-2 border-t border-amber-500/20">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-300 flex items-center gap-1.5">
                  <Gift size={13} className="text-emerald-400" /> Courtesy Review Reward
                </label>
                <input
                  type="checkbox"
                  checked={reviewBoosterData?.includeIncentive === true}
                  onChange={(e) => handleSaveReviewBoosterConfig({ includeIncentive: e.target.checked })}
                  className="rounded text-emerald-500 focus:ring-0 cursor-pointer"
                />
              </div>
              {reviewBoosterData?.includeIncentive && (
                <input
                  type="text"
                  defaultValue={reviewBoosterData?.incentiveText || 'Get 5% discount on your next visit!'}
                  onBlur={(e) => handleSaveReviewBoosterConfig({ incentiveText: e.target.value })}
                  placeholder="e.g. 5% discount on your next order"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-amber-500/30 text-xs text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
                />
              )}
            </div>

            {/* Custom AI Tone Guidance */}
            <div className="space-y-2 md:col-span-2 pt-2 border-t border-amber-500/20">
              <label className="text-xs font-bold text-slate-300 block">Custom Review Guidance for Luna AI</label>
              <textarea
                rows={2}
                defaultValue={reviewBoosterData?.customGuidance || ''}
                onBlur={(e) => handleSaveReviewBoosterConfig({ customGuidance: e.target.value })}
                placeholder="e.g. Always thank the customer warmly in Sinhala/English, asking if their meal or product met their expectations."
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-900 border border-amber-500/30 text-xs text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none resize-none"
              />
            </div>
          </div>
        </div>
      </div>
    )}
  </div>
) : selectedModule === 'sip_call' ? (
  /* ═══════════════════════════════════════════════════════════════ */
  /* VIEW 14: ENTERPRISE SIP PBX & GRANDSTREAM CALL STUDIO           */
  /* ═══════════════════════════════════════════════════════════════ */
  <div className="flex-1 flex flex-col overflow-hidden bg-[#06131c]">
    {/* Top PBX Header */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3.5 px-3 sm:px-6 py-3 sm:py-4 border-b border-cyan-500/20 bg-[#081824]/90 shrink-0">
      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0c2332] to-[#081824] border border-cyan-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            PBX Trunk Status
          </span>
          <span className="text-sm font-black text-cyan-400 font-mono mt-0.5 block flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-ping"></span>
            ACTIVE PEER TRUNK
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
          <Server size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0c2332] to-[#081824] border border-cyan-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Office Secretary
          </span>
          <span className="text-sm font-black text-emerald-400 font-mono mt-0.5 block">
            EXTENSION 101
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
          <PhoneForwarded size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0c2332] to-[#081824] border border-cyan-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            Trunk Endpoints
          </span>
          <span className="text-sm font-black text-cyan-300 font-mono mt-0.5 block">
            5060 UDP / 5061 TLS
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
          <Radio size={20} />
        </div>
      </div>

      <div className="p-3.5 rounded-2xl bg-gradient-to-br from-[#0c2332] to-[#081824] border border-cyan-500/30 shadow-lg flex items-center justify-between">
        <div>
          <span className="text-[10px] uppercase font-mono font-bold text-slate-400 block tracking-wider">
            VoIP Codecs
          </span>
          <span className="text-sm font-black text-amber-400 font-mono mt-0.5 block">
            OPUS / G.711u / G.711a
          </span>
        </div>
        <div className="p-2.5 rounded-xl bg-amber-500/20 text-amber-400 border border-amber-500/30">
          <Zap size={20} />
        </div>
      </div>
    </div>

    {/* Studio Content Area */}
    <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4 sm:space-y-6 custom-scrollbar tenet-mobile-scroll">
      {/* Grandstream UCM Peer Trunk Credentials Card */}
      <div className="p-5 rounded-3xl bg-gradient-to-br from-[#091b27] via-[#0b1f2d] to-[#07151e] border-2 border-cyan-500/30 shadow-2xl relative overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-cyan-500/20">
          <div>
            <h3 className="text-base font-black text-white font-sans flex items-center gap-2">
              <Server size={18} className="text-cyan-400" />
              Grandstream UCM / IP-PBX Peer SIP Trunk Settings
            </h3>
            <p className="text-xs text-slate-400 mt-1">
              Give these exact connection parameters to your PBX technician to route office telephone calls directly to Luna AI.
            </p>
          </div>
          <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-400/40 w-fit">
            RFC 3261 COMPLIANT
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
          <div className="p-3.5 rounded-2xl bg-slate-900/80 border border-cyan-500/20 flex flex-col justify-between">
            <span className="text-[10px] font-mono text-slate-400 uppercase">Trunk Server Host</span>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm font-mono font-bold text-cyan-300">wa.kangavena.com</span>
              <button
                type="button"
                onClick={() => copyToClipboard('wa.kangavena.com', 'Host')}
                className="p-1.5 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/40 text-cyan-300 transition cursor-pointer"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-900/80 border border-cyan-500/20 flex flex-col justify-between">
            <span className="text-[10px] font-mono text-slate-400 uppercase">SIP Ports & Transport</span>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm font-mono font-bold text-emerald-400">5060 (UDP) / 5061 (TLS)</span>
              <button
                type="button"
                onClick={() => copyToClipboard('5060', 'Port')}
                className="p-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-300 transition cursor-pointer"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>

          <div className="p-3.5 rounded-2xl bg-slate-900/80 border border-cyan-500/20 flex flex-col justify-between">
            <span className="text-[10px] font-mono text-slate-400 uppercase">Trunk SIP URI</span>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs font-mono font-bold text-sky-300 truncate max-w-[170px]">
                sip:wa.kangavena.com:5060;tenant_id={tenant?.id || 'TENANT_ID'}
              </span>
              <button
                type="button"
                onClick={() => copyToClipboard(`sip:wa.kangavena.com:5060;tenant_id=${tenant?.id || 'TENANT_ID'}`, 'SIP URI')}
                className="p-1.5 rounded-lg bg-sky-500/20 hover:bg-sky-500/40 text-sky-300 transition cursor-pointer"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Step-by-step Technician Guide */}
        <div className="mt-5 p-4 rounded-2xl bg-[#07131b] border border-cyan-500/20 space-y-2 text-xs text-slate-300">
          <span className="font-bold text-cyan-300 block text-xs uppercase tracking-wider font-mono">
            📋 3-Step Setup for Grandstream UCM Technician:
          </span>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 text-[11px]">
            <div className="p-2.5 rounded-xl bg-slate-900/50 border border-cyan-500/10">
              <span className="text-cyan-400 font-bold font-mono">1. Create VoIP Trunk</span>
              <p className="text-slate-400 mt-1">In Grandstream UCM GUI, go to <b>Extension / Trunk</b> ➔ <b>VoIP Trunks</b> ➔ Create <b>Peer SIP Trunk</b> pointing to <code>wa.kangavena.com:5060</code>.</p>
            </div>
            <div className="p-2.5 rounded-xl bg-slate-900/50 border border-cyan-500/10">
              <span className="text-cyan-400 font-bold font-mono">2. Set Codecs</span>
              <p className="text-slate-400 mt-1">In Trunk Audio Settings, select <b>Opus</b>, <b>PCMU</b> (G.711u), and <b>PCMA</b> (G.711a). Enable SIP REFER transfers.</p>
            </div>
            <div className="p-2.5 rounded-xl bg-slate-900/50 border border-cyan-500/10">
              <span className="text-cyan-400 font-bold font-mono">3. Route Inbound Calls</span>
              <p className="text-slate-400 mt-1">Set the office Landline / DID Inbound Route to send calls directly to the <b>Luna AI Trunk</b>.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Secretary Transfer & Live Routing Directory */}
      <div className="p-5 rounded-3xl bg-gradient-to-br from-[#091b27] via-[#0b1f2d] to-[#07151e] border border-cyan-500/20 shadow-xl space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-cyan-500/15">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
              <PhoneForwarded size={18} />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white font-sans flex items-center gap-2">
                Authorized Call Forwarding Numbers Directory
                <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-400/30">
                  {sipCallData?.forwardingDirectory?.length || 2} Contacts
                </span>
              </h4>
              <p className="text-[11px] text-slate-400">
                Luna AI autonomously matches caller requests (e.g. "Sara", "Damayanthi", or "Secretary") and executes instant blind transfer via SIP REFER / PBX routing.
              </p>
            </div>
          </div>
          <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 rounded-full font-bold w-fit">
            SIP REFER & UCM API ACTIVE
          </span>
        </div>

        {/* Directory Contacts Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
          {((sipCallData?.forwardingDirectory && sipCallData.forwardingDirectory.length > 0)
            ? sipCallData.forwardingDirectory
            : [
                { id: 'contact_1', name: 'Sara', position: 'Secretary', number: '011165554656', whatsappPhone: '011165554656', enabled: true },
                { id: 'contact_2', name: 'Damayanthi', position: 'Secretary', number: '001654646416', whatsappPhone: '001654646416', enabled: true },
              ]
          ).map((contact: any, cIdx: number) => (
            <div
              key={contact.id || `contact_${cIdx}`}
              className={`p-4 rounded-2xl border transition-all duration-200 ${
                contact.enabled !== false
                  ? 'bg-[#0a1e2b]/90 border-cyan-500/30 shadow-lg hover:border-cyan-400/50'
                  : 'bg-slate-900/40 border-slate-800 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-bold text-white font-sans">{contact.name}</span>
                    <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md bg-cyan-500/20 text-cyan-300 border border-cyan-400/30">
                      {contact.position || 'Staff'}
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-400 font-mono mt-0.5 block">
                    Target: Ext / Direct Number
                  </span>
                </div>
                <span className={`text-[9px] font-mono uppercase font-bold px-2 py-0.5 rounded-full ${
                  contact.enabled !== false
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-slate-700/50 text-slate-400'
                }`}>
                  {contact.enabled !== false ? '● ACTIVE' : 'DISABLED'}
                </span>
              </div>

              <div className="mt-3 p-2.5 rounded-xl bg-[#06141d] border border-cyan-500/20 flex items-center justify-between">
                <div>
                  <span className="text-[9px] uppercase font-mono text-slate-400 block">Forwarding Number</span>
                  <span className="text-xs font-mono font-black text-cyan-300">
                    {contact.number}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(contact.number, `${contact.name} Number`)}
                  className="p-1.5 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/40 text-cyan-300 transition cursor-pointer"
                  title="Copy Number"
                >
                  <Copy size={13} />
                </button>
              </div>

              <div className="mt-2 text-[10px] font-mono text-slate-400 flex items-center justify-between">
                <span>WhatsApp Alert:</span>
                <span className="text-emerald-400 font-bold truncate max-w-[130px]">
                  {contact.whatsappPhone ? `+${contact.whatsappPhone}` : 'Same as Phone'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Pre-transfer courtesy announcement & dispatch details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <div className="p-4 rounded-2xl bg-slate-900/60 border border-cyan-500/20 space-y-2">
            <span className="text-xs font-bold text-white block">Pre-Transfer Spoken Courtesy Announcement</span>
            <div className="p-2.5 rounded-xl bg-slate-950/80 border border-cyan-500/30 text-xs text-cyan-200 font-medium italic">
              "{sipCallData?.preTransferSpokenCourtesy || 'මම ඔබගේ ඇමතුම අපගේ ප්‍රධාන ලේකම්තුමිය වෙත යොමු කරනවා, කරුණාකර මඳක් රැඳී සිටින්න.'}"
            </div>
            <p className="text-[10px] text-slate-400 leading-relaxed">
              Spoken naturally by Luna AI to reassure the caller right before initiating the blind transfer.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-slate-900/60 border border-cyan-500/20 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-white">Transfer Governance & Protocols</span>
              <span className="text-xs font-mono font-black text-emerald-400 px-2 py-0.5 rounded-md bg-emerald-500/20 border border-emerald-400/30">
                RFC 3515 / UCM API
              </span>
            </div>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Trunk transfers send <code>REFER sip:[number]@PBX_IP</code> packets. If enabled, Grandstream UCM HTTPS API executes failover transfer automatically.
            </p>
            <div className="pt-2 border-t border-slate-800 text-[10px] font-mono text-slate-400 flex items-center justify-between">
              <span>WhatsApp Manager Alert:</span>
              <span className="text-emerald-400 font-bold">Instant Dispatch Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
) : null}

      {/* ── High-Res Media Lightbox Modal ── */}
      {previewMedia && (
        <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-200">
          <div className="relative w-full max-w-4xl max-h-[92vh] bg-[#0E0B1F] border border-purple-500/40 rounded-3xl p-5 shadow-[0_0_60px_rgba(168,85,247,0.35)] flex flex-col space-y-3.5 overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-3 border-b border-purple-500/20">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-purple-500/20 border border-purple-500/35 flex items-center justify-center text-purple-300 font-bold shadow-inner">
                  {previewMedia.type === 'video' ? (
                    <Film size={18} />
                  ) : previewMedia.type === 'pdf' ? (
                    <FileText size={18} />
                  ) : (
                    <ImageIcon size={18} />
                  )}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white font-sans truncate max-w-md">
                    {previewMedia.title || 'Media Asset Preview'}
                  </h3>
                  <span className="text-[10px] font-mono text-purple-400 uppercase font-bold tracking-wider">
                    {previewMedia.type} asset
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={previewMedia.url}
                  target="_blank"
                  rel="noreferrer"
                  className="px-3 py-1.5 rounded-xl bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 text-purple-200 text-xs font-bold flex items-center gap-1.5 transition"
                >
                  <ExternalLink size={13} />
                  <span>Open Tab</span>
                </a>
                <button
                  type="button"
                  onClick={() => copyToClipboard(previewMedia.url, 'Asset URL')}
                  className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 hover:text-white text-xs font-bold flex items-center gap-1.5 transition cursor-pointer"
                >
                  <Copy size={13} />
                  <span>Copy URL</span>
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewMedia(null)}
                  className="p-1.5 rounded-xl bg-slate-900 hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 border border-slate-800 transition cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-auto flex items-center justify-center min-h-[320px] max-h-[70vh] bg-slate-950/90 rounded-2xl border border-purple-500/15 p-2">
              {previewMedia.type === 'image' ? (
                <img
                  src={previewMedia.url}
                  alt={previewMedia.title}
                  className="max-h-[68vh] max-w-full rounded-xl object-contain shadow-2xl"
                />
              ) : previewMedia.type === 'video' ? (
                <video
                  src={previewMedia.url}
                  controls
                  autoPlay
                  className="max-h-[68vh] max-w-full rounded-xl shadow-2xl"
                />
              ) : (
                <iframe
                  src={previewMedia.url}
                  className="w-full h-[68vh] rounded-xl border border-slate-800 bg-white"
                  title={previewMedia.title}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Luna Neural Context Cache & Token Engine Inspection Modal ── */}
      <LunaCacheInspectionModal
        isOpen={isCacheModalOpen}
        onClose={() => setIsCacheModalOpen(false)}
        tenantId={tenant?.id || tenant?.tenantId || 'global'}
        authToken={token}
        tenantApiKey={tenant?.apiKey || null}
        channel="default"
        onCachePurged={() => {
          fetchPortalData();
        }}
      />

      {/* ── WhatsApp Mobile Direct Access & QR Modal ── */}
      <KangavenaWhatsAppMobileModal
        isOpen={showWhatsAppMobileModal}
        onClose={() => setShowWhatsAppMobileModal(false)}
        token={token}
        tenant={tenant}
      />

      {/* ── Real-Time WhatsApp AI Test Sandbox Modal ── */}
      <KangavenaWhatsAppSimulatorModal
        isOpen={showWhatsAppSimulatorModal}
        onClose={() => setShowWhatsAppSimulatorModal(false)}
        token={token}
        tenant={tenant}
        agentName={tenant?.customAvatarName || tenant?.name}
      />
    </div>
  );
};

export default TenetPortalData;
